import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { relationalSettingsSections, singletonSettingsSections } from "@booking-seconds/db";
import { defaultCustomerSuppliesEquipmentItems } from "@booking-seconds/types";
import type { PriceAdjusterInput, PriceAdjusterResponse, StaffFeedbackFeedItem, StaffLoginAccess } from "@booking-seconds/types";
import { Badge, Button, Card, DataTable, Field, SectionHeader } from "@booking-seconds/ui";
import { formatCurrency } from "@booking-seconds/utils";
import {
  AlertTriangle,
  BadgeDollarSign,
  CalendarCheck2,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Database,
  MapPin,
  Settings as SettingsIcon,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  UserCheck,
  Users,
  UserX,
  WalletCards
} from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  defaultBuildingCommonAreasMinimumChargeCents,
  normalizeBuildingApartmentsBucket,
  normalizeBuildingCommonAreasPricingRules
} from "../building-common-areas";
import {
  defaultResidentialComponentConditionMultipliers,
  defaultResidentialComponentFormulaRules,
  defaultResidentialSkipAreaDurationRules,
  normalizeResidentialComponentConditionMultipliers,
  normalizeResidentialComponentFormulaRules,
  normalizeResidentialSkipAreaDurationRules,
  type ResidentialComponentConditionMultipliers,
  type ResidentialComponentFormulaRule,
  type ResidentialConditionArea,
  type ResidentialFormulaBucket,
  type ResidentialSkipAreaDurationAreaCode,
  type ResidentialSkipAreaDurationRule,
  type ResidentialSkipAreaPropertyGroup
} from "@booking-seconds/pricebook";
import { parseCsvLine, splitLooseDelimitedText, summarizeStaffCsv } from "../shared/staff-csv";
import { DateInput } from "./date-input";
import { apiFetch } from "./lib/api";
import { OpsHeader, OpsMetricCard, OpsMetricGrid, OpsPageShell } from "./pages/admin/ops-ui";

type SettingsValue = Record<string, unknown>;
type StaffLoginCreateDraft = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  active: boolean;
};
type MfaSettingsUser = {
  userId: string;
  email: string;
  name: string;
  roles: string[];
  isActive: boolean;
  lastLoginAt: string | null;
  staffId: string | null;
  mfaPhoneHint: string;
  mfaVerifiedAt: string | null;
  mfaSmsEnabled: boolean;
  mfaRequiredByRole: boolean;
};
type MfaSettingsResponse = {
  users: MfaSettingsUser[];
  twilioConfigured: boolean;
  hasVerifiedOwner: boolean;
  officeRoleMfaRequired: boolean;
};
type MfaEnrollmentDraft = {
  phone: string;
  code: string;
  challengeToken: string;
  status: string;
};
type FieldKind = "text" | "email" | "number" | "textarea" | "checkbox" | "select" | "csv" | "time" | "date" | "url" | "color" | "json" | "password";
type FieldConfig = {
  key: string;
  label: string;
  kind: FieldKind;
  hint?: string;
  placeholder?: string;
  multiline?: boolean;
  options?: Array<{ value: string; label: string }>;
  rows?: number;
  step?: string;
  min?: number;
  max?: number;
};

type SectionConfig = {
  title: string;
  description: string;
  fields: FieldConfig[];
  defaults: SettingsValue;
  columns?: Array<{ label: string; render: (value: SettingsValue) => ReactNode }>;
};

type StaffTabKey = "personal" | "availability" | "financial" | "vehicle" | "status" | "equipment";
type StaffDirectoryCategory = "maids" | "drivers" | "office";
type BookingSpotTabKey = "general" | "duration" | "daily" | "holidays" | "capacity" | "date-overrides" | "diagnostics";
type SchedulingRouteKey =
  | "scheduling-overview"
  | "scheduling-booking-availability"
  | "scheduling-staff-availability"
  | "scheduling-exceptions-holidays"
  | "scheduling-diagnostics";
type LocationServiceAreaRuleDraft = {
  zipCode: string;
  earliestStartTime: string;
  latestStartTime: string;
  allowedWeekdays: string[];
  active: boolean;
  note: string;
};
type AvailabilityDiagnosticsResponse = {
  items: Array<{ startsAt: string; startsAtLocal: string; remainingCapacity: number }>;
  diagnostics: Array<{
    startsAt: string;
    startsAtLocal: string;
    status: "available" | "blocked";
    remainingCapacity: number;
    derivedCapacity: number;
    effectiveCapacity: number;
    overlappingBookings: number;
    availableStaffNames: string[];
    formedTeams: Array<{ size: number; usesFallback: boolean; memberNames: string[] }>;
    unassignedStaffNames: string[];
    reason: string;
  }>;
};
type AdminWaitlistItem = {
  id: string;
  status: string;
  requestedDate: string;
  preferredWindow: "any" | "morning" | "midday" | "afternoon";
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
  locationName: string;
  serviceName: string;
  lastNotifiedAt: string;
  fulfilledPublicReference: string;
};
type ExtraPricingRuleDraft = {
  id: string;
  serviceIds: string[];
  squareFootageFrom: string;
  squareFootageTo: string;
  bedroomsFrom: string;
  bedroomsTo: string;
  levelsFrom: string;
  levelsTo: string;
  priceDeltaDollars: string;
  durationMinutes: string;
  displayOrder: number;
  active: boolean;
};
type CarpetShampooingUnitDraft = {
  code: string;
  label: string;
  priceDeltaDollars: string;
  durationMinutes: string;
  maxQuantity: string;
  displayOrder: number;
  active: boolean;
};
type CarpetShampooingConfigDraft = {
  enabled: boolean;
  minimumOrderDollars: string;
  units: CarpetShampooingUnitDraft[];
};
type BuildingCommonAreasPricingRuleDraft = {
  bucket: string;
  label: string;
  pricingMode: "instant" | "manual";
  basePriceDollars: string;
  durationMinutes: string;
  displayOrder: number;
};
type ServiceEditorCatalog = {
  services: SettingsValue[];
  serviceOptions: SettingsValue[];
  locations: SettingsValue[];
  frequencyRules: SettingsValue[];
  calendarProfiles: SettingsValue[];
};
type PriceAdjusterDraft = {
  direction: PriceAdjusterInput["direction"];
  percent: string;
  roundToNearestDollar: boolean;
  serviceIds: string[];
  extraIds: string[];
};
type BookingSpotDateOverrideDraft = {
  date: string;
  closed: boolean;
  start: string;
  end: string;
  maxJobs: number | "";
  note: string;
};

function normalizeFrequencyDisplayText(value: unknown) {
  const text = String(value ?? "").trim();
  return /^one time$/i.test(text) ? "One-time" : text;
}

const bookingSpotWeekdayOptions = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" }
] as const;

const promotionWeekdayOptions = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" }
] as const;

const scenarioSkipAreaOptions = [
  { value: "kitchen", label: "Kitchen" },
  { value: "bathrooms", label: "Bathrooms" },
  { value: "bedrooms", label: "Bedrooms / offices / dens" },
  { value: "living_areas", label: "Living areas" },
  { value: "living_room", label: "Living room" },
  { value: "dining_room", label: "Dining room" },
  { value: "hallways", label: "Hallways" }
] as const;

const residentialRemodelPricingOptions = [
  { value: "new_construction_heavy", label: "New construction (heavy debris present)" },
  { value: "new_construction_precleaned", label: "New construction (pre-cleaned by construction company)" },
  { value: "full_remodeling", label: "Full renovation" },
  { value: "partial_remodeling", label: "Partial renovation" },
  { value: "kitchen", label: "Kitchen" },
  { value: "bathrooms", label: "Bathrooms" },
  { value: "bedrooms", label: "Bedrooms" },
  { value: "living_areas", label: "Living areas" },
  { value: "floors_replacement", label: "Floors replacement" },
  { value: "floors_refinishing", label: "Floors refinishing" },
  { value: "walls_painting", label: "Walls painting" },
  { value: "other", label: "Other" }
] as const;

const officeCommercialRemodelPricingOptions = [
  { value: "new_construction_heavy", label: "New construction (heavy debris present)" },
  { value: "new_construction_precleaned", label: "New construction (pre-cleaned by construction company)" },
  { value: "full_remodeling", label: "Full renovation" },
  { value: "partial_remodeling", label: "Partial renovation" },
  { value: "kitchen", label: "Kitchen renovation" },
  { value: "bathrooms", label: "Restrooms renovation" },
  { value: "workstations", label: "Workstations renovation" },
  { value: "floors_replacement", label: "Floors replacement" },
  { value: "carpets_replacement", label: "Carpets replacement" },
  { value: "walls_painting", label: "Walls painting" },
  { value: "other", label: "Other" }
] as const;

const buildingCommonAreasRemodelPricingOptions = [
  { value: "new_construction_heavy", label: "New construction (heavy debris present)" },
  { value: "new_construction_precleaned", label: "New construction (pre-cleaned by construction company)" },
  { value: "full_remodeling", label: "Full renovation" },
  { value: "partial_remodeling", label: "Partial renovation" },
  { value: "floors_replacement", label: "Floors replacement" },
  { value: "carpets_replacement", label: "Carpets replacement" },
  { value: "walls_painting", label: "Walls painting" },
  { value: "other", label: "Other" }
] as const;

const paymentsAcceptedOptions = [
  { value: "credit_card", label: "Credit card" },
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "zelle", label: "Zelle" },
  { value: "paypal", label: "PayPal" },
  { value: "venmo", label: "Venmo" },
  { value: "other", label: "Other (please specify below)" }
] as const;

const generalCancellationFieldKeys = [
  "cancellationFeeEnabled",
  "cancellationPolicyMode",
  "cancellationPresetDaysBefore",
  "cancellationPresetTime",
  "cancellationCutoffValue",
  "cancellationCutoffUnit",
  "cancellationPenaltyType",
  "cancellationPenaltyValue",
  "requireCancellationReason",
  "applyCancellationFeeToCustomerInitiatedOnly",
  "waiveCancellationFeeForFirstTimeNewCustomers",
  "waiveCancellationFeeForFirstRecurringCancellation",
  "requireInternalNoteWhenCancellationFeeWaived"
] as const;

const staffWeekdayOptions = [
  { value: "mon", label: "Monday", short: "Mon" },
  { value: "tue", label: "Tuesday", short: "Tue" },
  { value: "wed", label: "Wednesday", short: "Wed" },
  { value: "thu", label: "Thursday", short: "Thu" },
  { value: "fri", label: "Friday", short: "Fri" },
  { value: "sat", label: "Saturday", short: "Sat" },
  { value: "sun", label: "Sunday", short: "Sun" }
] as const;

const extraRuleBedroomOptions = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const extraRuleLevelOptions = ["1", "1.5", "2 (duplex)", "3", "4", "5"];
const extraRuleSquareFootageOptions = [
  "400–600",
  "601–800",
  "801–1,000",
  "1,001–1,200",
  "1,201–1,400",
  "1,401–1,600",
  "1,601–1,800",
  "1,801–2,000",
  "2,001–2,200",
  "2,201–2,400",
  "2,401–2,600",
  "2,601–2,800",
  "2,801–3,000",
  "3,001–3,200",
  "3,201–3,400",
  "3,401–3,600",
  "3,601–3,800",
  "3,801–4,000",
  "4,000–5,000",
  "5,000–6,000"
];

const carpetShampooingUnitDefaults: CarpetShampooingUnitDraft[] = [
  {
    code: "rooms",
    label: "Rooms",
    priceDeltaDollars: "49.00",
    durationMinutes: "0",
    maxQuantity: "5",
    displayOrder: 0,
    active: true
  },
  {
    code: "hallways",
    label: "Hallways",
    priceDeltaDollars: "19.00",
    durationMinutes: "0",
    maxQuantity: "5",
    displayOrder: 1,
    active: true
  },
  {
    code: "steps",
    label: "Steps",
    priceDeltaDollars: "3.00",
    durationMinutes: "0",
    maxQuantity: "25",
    displayOrder: 2,
    active: true
  },
  {
    code: "area_rugs",
    label: "Area rugs",
    priceDeltaDollars: "24.00",
    durationMinutes: "0",
    maxQuantity: "5",
    displayOrder: 3,
    active: true
  }
];

const notificationEventOptions = [
  { value: "booking_confirmation", label: "Booking confirmation" },
  { value: "admin_new_booking", label: "Admin new booking alert" },
  { value: "booking_cancellation", label: "Booking cancellation" },
  { value: "booking_rescheduled", label: "Booking rescheduled" },
  { value: "booking_extras_changed", label: "Booking extras changed" }
];

const notificationTemplateOptions = [
  { value: "booking_confirmation", label: "booking_confirmation" },
  { value: "admin_new_booking", label: "admin_new_booking" },
  { value: "booking_reminder", label: "booking_reminder" },
  { value: "booking_cancellation", label: "booking_cancellation" },
  { value: "booking_rescheduled", label: "booking_rescheduled" },
  { value: "booking_extras_changed", label: "booking_extras_changed" },
  { value: "payment_receipt", label: "payment_receipt" }
];

const singletonSectionConfigs: Record<string, SectionConfig> = {
    general: {
      title: "General",
      description: "Business identity, locale, pricing defaults, and booking lead rules.",
      defaults: {
        businessName: "",
        logoUrl: "",
        companyWebsite: "",
        customerFacingBaseUrl: "",
        defaultTimezone: "America/Chicago",
        currency: "USD",
        locale: "en-US",
        paymentsAccepted: [],
        paymentsAcceptedOther: "",
        supportPhone: "",
        supportEmail: "",
        bookingLeadMinutes: 120,
        cancellationPolicy: "",
        cancellationFeeEnabled: false,
        cancellationPolicyMode: "day_before_5pm",
        cancellationPresetDaysBefore: 1,
        cancellationPresetTime: "17:00",
        cancellationCutoffValue: 47,
        cancellationCutoffUnit: "hours",
        cancellationPenaltyType: "flat",
        cancellationPenaltyValue: 99,
        requireCancellationReason: true,
        applyCancellationFeeToCustomerInitiatedOnly: true,
        waiveCancellationFeeForFirstTimeNewCustomers: false,
        waiveCancellationFeeForFirstRecurringCancellation: false,
        requireInternalNoteWhenCancellationFeeWaived: false,
        taxRate: 0.0825,
        showTaxOnBookingForm: true,
        invoicePrefix: "BS"
      },
    fields: [
      { key: "businessName", label: "Business name", kind: "text" },
      { key: "logoUrl", label: "Logo", kind: "url", hint: "Upload a logo to use on invoices and generated documents." },
      { key: "companyWebsite", label: "Company website", kind: "url", placeholder: "https://example.com" },
      {
        key: "customerFacingBaseUrl",
        label: "Customer-facing base URL",
        kind: "url",
        placeholder: "https://accessmaids.bookingseconds.com",
        hint: "Used for booking, manage-booking, sign-in, and unsubscribe links sent to customers."
      },
        { key: "defaultTimezone", label: "Default timezone", kind: "text" },
        {
          key: "currency",
          label: "Currency",
          kind: "select",
        options: [
          { value: "USD", label: "USD - US Dollar" },
          { value: "CAD", label: "CAD - Canadian Dollar" },
          { value: "EUR", label: "EUR - Euro" },
          { value: "GBP", label: "GBP - British Pound" },
            { value: "AUD", label: "AUD - Australian Dollar" }
          ]
        },
        { key: "locale", label: "Locale", kind: "text" },
        { key: "paymentsAccepted", label: "Payments accepted", kind: "csv", hint: "Choose every payment method you accept." },
        { key: "paymentsAcceptedOther", label: "Other payment method", kind: "text", placeholder: "Describe the other accepted payment method" },
        { key: "supportPhone", label: "Support phone", kind: "text" },
        { key: "supportEmail", label: "Support email", kind: "email" },
        { key: "bookingLeadMinutes", label: "Booking lead minutes", kind: "number" },
        { key: "cancellationFeeEnabled", label: "Enable cancellation fees", kind: "checkbox" },
        { key: "cancellationPolicyMode", label: "Cancellation policy mode", kind: "text" },
        { key: "cancellationPresetDaysBefore", label: "Cancellation preset days before", kind: "number" },
        { key: "cancellationPresetTime", label: "Cancellation preset time", kind: "time" },
        { key: "cancellationCutoffValue", label: "Cancellation cutoff value", kind: "number" },
        { key: "cancellationCutoffUnit", label: "Cancellation cutoff unit", kind: "text" },
        { key: "cancellationPenaltyType", label: "Cancellation penalty type", kind: "text" },
        { key: "cancellationPenaltyValue", label: "Cancellation penalty value", kind: "number", step: "0.01" },
        { key: "requireCancellationReason", label: "Require cancellation reason", kind: "checkbox" },
        { key: "applyCancellationFeeToCustomerInitiatedOnly", label: "Apply only to customer-initiated cancellations", kind: "checkbox" },
        { key: "waiveCancellationFeeForFirstTimeNewCustomers", label: "Waive fee for first-time exception for new customers", kind: "checkbox" },
        { key: "waiveCancellationFeeForFirstRecurringCancellation", label: "Waive fee for first-time cancellation for recurring customers", kind: "checkbox" },
        { key: "requireInternalNoteWhenCancellationFeeWaived", label: "Require internal note when fee is waived", kind: "checkbox" },
        { key: "taxRate", label: "Tax rate", kind: "number", step: "0.0001" },
        { key: "showTaxOnBookingForm", label: "Show tax on booking form", kind: "checkbox" },
        { key: "invoicePrefix", label: "Invoice prefix", kind: "text" },
        { key: "cancellationPolicy", label: "Cancellation policy", kind: "textarea", rows: 4 }
      ]
    },
    pricebook: {
      title: "Pricebook Advanced Rules",
      description: "Global typed pricing rules used by the centralized Pricebook engine.",
      defaults: {
        version: 1,
        advancedRules: {
          buildingCommonAreasMinimumChargeCents: 9600,
          residentialFormulaRules: [
            {
              bucket: "apartment_like",
              label: "Apartment / condo",
              baseTeamMinutes: 30,
              minutesPerSquareFoot: 0.0405,
              minutesPerBedroom: 7.5,
              minutesPerFullBathroom: 15,
              minutesPerHalfBathroom: 7.5,
              minutesPerAdditionalLevel: 7.5
            },
            {
              bucket: "townhouse",
              label: "Townhouse",
              baseTeamMinutes: 0,
              minutesPerSquareFoot: 0.035,
              minutesPerBedroom: 7.5,
              minutesPerFullBathroom: 15,
              minutesPerHalfBathroom: 7.5,
              minutesPerAdditionalLevel: 15
            },
            {
              bucket: "single_family",
              label: "Single-family house",
              baseTeamMinutes: 0,
              minutesPerSquareFoot: 0.0375,
              minutesPerBedroom: 15,
              minutesPerFullBathroom: 15,
              minutesPerHalfBathroom: 7.5,
              minutesPerAdditionalLevel: 15
            }
          ],
          residentialComponentFormulaRules: defaultResidentialComponentFormulaRules,
          residentialHourlyServiceProfiles: [
            {
              serviceType: "standard_residential_cleaning",
              label: "Basic Cleaning",
              hourlyRateCentsPerMaidHour: 4800,
              minimumTeamMinutes: 120,
              serviceTimeMultiplier: 1
            },
            {
              serviceType: "deep_cleaning",
              label: "Deep Cleaning",
              hourlyRateCentsPerMaidHour: 5100,
              minimumTeamMinutes: 120,
              serviceTimeMultiplier: 1
            },
            {
              serviceType: "move_in_cleaning",
              label: "Move-In / Move-Out Cleaning",
              hourlyRateCentsPerMaidHour: 5300,
              minimumTeamMinutes: 120,
              serviceTimeMultiplier: 1
            },
            {
              serviceType: "move_out_cleaning",
              label: "Move-In / Move-Out Cleaning",
              hourlyRateCentsPerMaidHour: 5300,
              minimumTeamMinutes: 120,
              serviceTimeMultiplier: 1
            }
          ],
          residentialConditionAdjustmentPercentages: {
            kitchen: { 1: 10, 2: 5, 3: 0, 4: -5, 5: -10 },
            bathrooms: { 1: 10, 2: 5, 3: 0, 4: -5, 5: -10 },
            bedrooms: { 1: 5, 2: 3, 3: 0, 4: -3, 5: -5 },
            otherAreas: { 1: 5, 2: 3, 3: 0, 4: -3, 5: -5 }
          },
          residentialComponentConditionMultipliers: defaultResidentialComponentConditionMultipliers,
          residentialSkipAreaDurationRules: defaultResidentialSkipAreaDurationRules,
          residentialFormulaLevelSquareFootageMinimums: [
            { levels: 1, minimumSquareFeet: 1400 },
            { levels: 1.5, minimumSquareFeet: 1600 },
            { levels: 2, minimumSquareFeet: 1800 },
            { levels: 2.5, minimumSquareFeet: 2000 },
            { levels: 3, minimumSquareFeet: 2200 }
          ],
          residentialFormulaMissingHalfLevelIncrement: 200
        }
      },
      fields: [
        { key: "version", label: "Version", kind: "number", min: 1 },
        { key: "advancedRules", label: "Advanced rules JSON", kind: "json", rows: 14 }
      ]
    },
    invoices: {
    title: "Invoices",
    description: "Set invoice numbering, default due terms, PDF display details, and the default invoice email copy.",
    defaults: {
      invoicePrefix: "INV",
      nextInvoiceNumber: 1001,
      defaultDueTerms: "due_on_receipt",
      defaultMemo: "Thank you for choosing {{businessName}}.",
      defaultFooter: "Please contact us with any invoice questions.",
      defaultPaymentInstructions: "Pay securely online using the payment link in this email.",
      defaultEmailSubject: "Invoice {{invoiceNumber}} from {{businessName}}",
      defaultEmailBody:
        "Hi {{customerName}},\n\nYour invoice {{invoiceNumber}} for {{bookingReference}} is attached. Amount due: {{amountDue}}.\n\nPay online: {{paymentUrl}}\n\nThank you,\n{{businessName}}",
      logoUrl: "",
      businessDisplayName: "",
      businessAddress: "",
      templateAccentColor: "#123d32",
      showCompanyContact: true,
      showBillTo: true,
      showServiceAddress: true,
      showAppointment: true,
      showMaidNames: true,
      showLineItems: true,
      showPayments: true,
      showMemo: true,
      showPaymentInstructions: true,
      showTermsLink: true,
      showFooter: true
    },
    fields: [
      { key: "invoicePrefix", label: "Invoice prefix", kind: "text", hint: "Example: INV creates invoice numbers like INV-1001." },
      { key: "nextInvoiceNumber", label: "Next invoice number", kind: "number", min: 1 },
      {
        key: "defaultDueTerms",
        label: "Default due terms",
        kind: "select",
        options: [{ value: "due_on_receipt", label: "Due on receipt" }]
      },
      { key: "businessDisplayName", label: "Business display name", kind: "text", hint: "Leave blank to use the General business name." },
      { key: "businessAddress", label: "Business address", kind: "textarea", rows: 2, hint: "Shown under the business name on invoice PDFs." },
      { key: "logoUrl", label: "Invoice logo", kind: "url", hint: "Upload a PNG/JPG logo for PDF invoices, or leave blank to use the General logo." },
      { key: "templateAccentColor", label: "Brand accent color", kind: "color", hint: "Used for the invoice header color." },
      { key: "showCompanyContact", label: "Company contact", kind: "checkbox", hint: "Show website, support phone, and support email." },
      { key: "showBillTo", label: "Bill to", kind: "checkbox", hint: "Show customer name, address, phone, and email." },
      { key: "showServiceAddress", label: "Service address", kind: "checkbox", hint: "Show the cleaning/service address." },
      { key: "showAppointment", label: "Appointment", kind: "checkbox", hint: "Show service date and arrival details." },
      { key: "showMaidNames", label: "Maid names", kind: "checkbox", hint: "Show assigned maid names when available." },
      { key: "showLineItems", label: "Line items", kind: "checkbox", hint: "Show the services table." },
      { key: "showPayments", label: "Totals and payments", kind: "checkbox", hint: "Show subtotal, discount, tax, paid, and amount due." },
      { key: "showMemo", label: "Memo", kind: "checkbox", hint: "Show the default invoice memo." },
      { key: "showPaymentInstructions", label: "Payment instructions", kind: "checkbox", hint: "Show payment instructions." },
      { key: "showTermsLink", label: "Terms link", kind: "checkbox", hint: "Show Terms & Conditions link when configured." },
      { key: "showFooter", label: "Footer", kind: "checkbox", hint: "Show footer text, company name, and website." },
      { key: "defaultMemo", label: "Default memo", kind: "textarea", rows: 3 },
      { key: "defaultPaymentInstructions", label: "Default payment instructions", kind: "textarea", rows: 3 },
      { key: "defaultFooter", label: "Default PDF footer", kind: "textarea", rows: 3 },
      { key: "defaultEmailSubject", label: "Default email subject", kind: "text" },
      {
        key: "defaultEmailBody",
        label: "Default email body",
        kind: "textarea",
        rows: 8,
        hint: "Supported tokens: {{customerName}}, {{invoiceNumber}}, {{bookingReference}}, {{amountDue}}, {{paymentUrl}}, {{pdfUrl}}, {{businessName}}."
      }
    ]
  },
  "booking-form": {
    title: "Booking Form Designer",
    description: "Control booking step order and connect the live form to services, extras, frequency logic, and calendar rules.",
    defaults: {
      theme: "sunrise",
      visibleFields: ["name", "email", "phone", "address"],
      requiredFields: ["name", "phone", "address"],
      layout: "multi-step",
      stepOrder: ["service", "extras", "frequency", "calendar", "details", "review"],
      enableExtrasStep: true,
      enableFrequencyStep: true,
      enableCalendarStep: true,
      enableLocationSelection: true,
      enablePromoCodes: true,
      enablePaymentPreference: false,
      enableTurnstile: true,
      arrivalFlexibility: {
        enabled: false,
        startTime: "07:30",
        endTime: "15:30",
        discountType: "fixed",
        discountValue: 0,
        excludedWindowStartTimes: ["07:30"]
      },
      customerSuppliesEquipment: {
        enabled: false,
        discountAmount: 0,
        requiredItems: defaultCustomerSuppliesEquipmentItems
      },
      depositRate: 0.2,
      successRedirectUrl: ""
    },
    fields: [
      {
        key: "theme",
        label: "Theme",
        kind: "select",
        options: [
          { value: "sunrise", label: "Sunrise" },
          { value: "ocean", label: "Ocean" },
          { value: "clean", label: "Clean" }
        ]
      },
      {
        key: "layout",
        label: "Layout",
        kind: "select",
        options: [
          { value: "multi-step", label: "Multi-step" },
          { value: "single-step", label: "Single-step" }
        ]
      },
      { key: "stepOrder", label: "Step order", kind: "csv", hint: "Recommended: service, extras, frequency, calendar, details, review." },
      { key: "enableExtrasStep", label: "Enable extras step", kind: "checkbox" },
      { key: "enableFrequencyStep", label: "Enable frequency step", kind: "checkbox" },
      { key: "enableCalendarStep", label: "Enable calendar step", kind: "checkbox" },
      { key: "visibleFields", label: "Visible fields", kind: "csv", hint: "Comma-separated field keys." },
      { key: "requiredFields", label: "Required fields", kind: "csv", hint: "Comma-separated field keys." },
      { key: "enableLocationSelection", label: "Enable location selection", kind: "checkbox" },
      { key: "enablePromoCodes", label: "Enable promo codes", kind: "checkbox" },
      { key: "enablePaymentPreference", label: "Show payment preference", kind: "checkbox" },
      { key: "enableTurnstile", label: "Enable Turnstile", kind: "checkbox" },
      { key: "arrivalFlexibility.enabled", label: "Enable arrival flexibility", kind: "checkbox" },
      { key: "arrivalFlexibility.startTime", label: "Arrival flexibility start time", kind: "time" },
      { key: "arrivalFlexibility.endTime", label: "Arrival flexibility end time", kind: "time" },
      {
        key: "arrivalFlexibility.discountType",
        label: "Arrival flexibility discount type",
        kind: "select",
        options: [
          { value: "fixed", label: "Flat fee discount" },
          { value: "percentage", label: "Percentage discount" }
        ]
      },
      {
        key: "arrivalFlexibility.discountValue",
        label: "Arrival flexibility discount value",
        kind: "number",
        step: "0.01",
        hint: "Use dollars for flat discounts and percent for percentage discounts."
      },
      {
        key: "arrivalFlexibility.excludedWindowStartTimes",
        label: "Excluded arrival window start times",
        kind: "csv",
        hint: "Comma-separated local slot start times such as 07:30, 09:30."
      },
      {
        key: "customerSuppliesEquipment.enabled",
        label: "Enable customer-provided supplies discount",
        kind: "checkbox"
      },
      {
        key: "customerSuppliesEquipment.discountAmount",
        label: "Customer supplies discount amount",
        kind: "number",
        step: "0.01",
        hint: "Dollar amount off when the customer provides all required supplies and equipment."
      },
      {
        key: "customerSuppliesEquipment.requiredItems",
        label: "Required supplies/equipment",
        kind: "csv",
        multiline: true,
        rows: 6,
        hint: "Comma-separated or one item per line."
      },
      { key: "depositRate", label: "Deposit rate", kind: "number", step: "0.01" },
      { key: "successRedirectUrl", label: "Success redirect URL", kind: "url" }
    ]
  },
  "google-maps-api": {
    title: "Google Maps API",
    description: "Store the Google Maps Places API key used for booking-form address autocomplete.",
    defaults: {
      apiKey: "",
      enableAddressAutocomplete: true
    },
    fields: [
      { key: "apiKey", label: "Google Maps API key", kind: "text" },
      { key: "enableAddressAutocomplete", label: "Enable address autocomplete", kind: "checkbox" }
    ]
  },
  emails: {
    title: "Emails",
    description: "Sender profile and enabled transactional templates.",
    defaults: {
      senderName: "",
      senderEmail: "",
      provider: "resend",
      resendApiKey: "",
      replyToEmail: "",
      marketingCompliance: {
        legalBusinessName: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
        supportEmail: "",
        supportPhone: ""
      },
      enabledTemplates: []
    },
    fields: [
      { key: "senderName", label: "Sender name", kind: "text" },
      { key: "senderEmail", label: "Sender email", kind: "email" },
      {
        key: "provider",
        label: "Provider",
        kind: "select",
        options: [
          { value: "resend", label: "Resend" },
          { value: "smtp", label: "SMTP" }
        ]
      },
      { key: "resendApiKey", label: "Resend API key", kind: "password" },
      { key: "replyToEmail", label: "Reply-to email", kind: "email" },
      { key: "marketingCompliance.legalBusinessName", label: "Legal/business name", kind: "text" },
      { key: "marketingCompliance.addressLine1", label: "Physical address line 1", kind: "text" },
      { key: "marketingCompliance.addressLine2", label: "Address line 2", kind: "text" },
      { key: "marketingCompliance.city", label: "City", kind: "text" },
      { key: "marketingCompliance.state", label: "State", kind: "text" },
      { key: "marketingCompliance.postalCode", label: "Postal code", kind: "text" },
      { key: "marketingCompliance.country", label: "Country", kind: "text" },
      { key: "marketingCompliance.supportEmail", label: "Compliance support email", kind: "email" },
      { key: "marketingCompliance.supportPhone", label: "Compliance support phone", kind: "text" },
      { key: "enabledTemplates", label: "Enabled templates", kind: "csv" }
    ]
  },
  "text-messages": {
    title: "Text Messages",
    description: "Quiet hours, opt-in behavior, and SMS notification templates.",
    defaults: {
      provider: "twilio",
      quietHoursStart: "20:00",
      quietHoursEnd: "08:00",
      optInRequired: true,
      offerReminderOptInOnBookingForm: false,
      preselectReminderOptInOnBookingForm: false,
      bookingReminderOffsetsMinutes: [1440, 120],
      enabledTemplates: ["booking_confirmation", "booking_reminder", "on_the_way", "payment_reminder"]
    },
    fields: [
      {
        key: "provider",
        label: "Provider",
        kind: "select",
        options: [{ value: "twilio", label: "Twilio" }]
      },
      { key: "quietHoursStart", label: "Quiet hours start", kind: "time" },
      { key: "quietHoursEnd", label: "Quiet hours end", kind: "time" },
      { key: "optInRequired", label: "Require opt-in", kind: "checkbox" },
      {
        key: "offerReminderOptInOnBookingForm",
        label: "Show reminder opt-in on booking form",
        kind: "checkbox"
      },
      {
        key: "preselectReminderOptInOnBookingForm",
        label: "Pre-select by default",
        kind: "checkbox"
      },
      {
        key: "bookingReminderOffsetsMinutes",
        label: "Booking reminder offsets (minutes)",
        kind: "csv",
        hint: "Comma-separated reminder offsets such as 1440, 120 for 24 hours and 2 hours before the visit."
      },
      { key: "enabledTemplates", label: "Enabled templates", kind: "csv" }
    ]
  },
  waitlist: {
    title: "Waitlist",
    description: "Control whether customers can join the waitlist, which channels send alerts, and the default message copy.",
    defaults: {
      enabled: true,
      sendEmailNotifications: true,
      sendSmsNotifications: true,
      emailSubjectTemplate: "{{businessName}}: a waitlist opening is available",
      emailBodyTemplate:
        "Hi {{customerName}},\n\nA matching {{serviceName}} opening is available for {{openingLabel}} at {{locationName}}.\nYour waitlist preference: {{preferredWindow}}.\n\nBook this opening: {{claimUrl}}\n\nOpen times are not held, so please book as soon as possible.\n\n{{supportLine}}",
      smsBodyTemplate:
        "{{businessName}}: a {{serviceName}} waitlist opening is available for {{openingLabel}} at {{locationName}}. Claim it here: {{claimUrl}} Open times are not held, so please book as soon as possible."
    },
    fields: [
      { key: "enabled", label: "Enable waitlist on booking form", kind: "checkbox" },
      { key: "sendEmailNotifications", label: "Send email alerts when a match opens", kind: "checkbox" },
      { key: "sendSmsNotifications", label: "Send SMS alerts when a match opens", kind: "checkbox" },
      {
        key: "emailSubjectTemplate",
        label: "Email subject template",
        kind: "text",
        hint: "Variables: {{businessName}}."
      },
      {
        key: "emailBodyTemplate",
        label: "Email body template",
        kind: "textarea",
        rows: 8,
        hint: "Variables: {{customerName}}, {{serviceName}}, {{openingLabel}}, {{locationName}}, {{preferredWindow}}, {{claimUrl}}, {{supportLine}}."
      },
      {
        key: "smsBodyTemplate",
        label: "SMS body template",
        kind: "textarea",
        rows: 5,
        hint: "Variables: {{businessName}}, {{serviceName}}, {{openingLabel}}, {{locationName}}, {{claimUrl}}."
      }
    ]
  },
  leads: {
    title: "Leads",
    description: "Website capture domains, default lead source, and prospect dedupe behavior.",
    defaults: {
      allowedCaptureDomains: [],
      defaultLeadSourceId: "",
      dedupeWindowDays: 90
    },
    fields: [
      {
        key: "allowedCaptureDomains",
        label: "Allowed capture domains",
        kind: "csv",
        hint: "Comma-separated hostnames such as accessmaids.com, www.accessmaids.com."
      },
      { key: "defaultLeadSourceId", label: "Default lead source ID", kind: "text" },
      { key: "dedupeWindowDays", label: "Dedupe window days", kind: "number", min: 1 }
    ]
  },
  marketing: {
    title: "Marketing",
    description: "Lead source capture, abandoned booking follow-up, and rebooking defaults.",
    defaults: {
      leadSources: ["website", "google", "referral"],
      captureUtm: true,
      enableAbandonedBookingFollowup: true,
      enableReviewRequests: true,
      reviewRequestDelayHours: 24,
      reviewRequestLowRatingThreshold: 3,
      notifyAssignedStaffOnFeedbackEmail: true,
      rebookingCouponCode: "WELCOME10"
    },
    fields: [
      { key: "leadSources", label: "Lead sources", kind: "csv" },
      { key: "captureUtm", label: "Capture UTM params", kind: "checkbox" },
      { key: "enableAbandonedBookingFollowup", label: "Abandoned booking follow-up", kind: "checkbox" },
      { key: "enableReviewRequests", label: "Review requests", kind: "checkbox" },
      { key: "reviewRequestDelayHours", label: "Review delay (hours)", kind: "number", min: 0 },
      { key: "reviewRequestLowRatingThreshold", label: "Low rating threshold", kind: "number", min: 1, max: 5 },
      { key: "notifyAssignedStaffOnFeedbackEmail", label: "Email maids on feedback", kind: "checkbox" },
      { key: "rebookingCouponCode", label: "Rebooking coupon", kind: "text" }
    ]
  },
  other: {
    title: "Miscellaneous",
    description: "Integration keys and utility modules for maps, analytics, messaging, calendar sync, and automations.",
    defaults: {
      apiKey: "",
      enableAddressAutocomplete: true,
      googleAnalyticsTrackingId: "",
      twilio: {
        accountSid: "",
        authToken: "",
        phoneNumber: ""
      },
      googleCalendar: {
        integrationMode: "disabled",
        selectedCalendarId: "",
        syncBookings: false
      },
      zapier: {
        enabled: false,
        webhookUrl: ""
      },
      automatedTasks: {
        enabled: false,
        notes: ""
      },
      security: {
        mfaRequiredForOfficeRoles: false
      },
      dataRetentionDays: 365,
      customCss: "",
      featureFlags: {},
      legalPages: {
        termsUrl: "",
        privacyUrl: "",
        termsVersion: "",
        privacyVersion: ""
      }
    },
    fields: [
      { key: "apiKey", label: "Google Maps API key", kind: "text" },
      { key: "enableAddressAutocomplete", label: "Enable address autocomplete", kind: "checkbox" },
      { key: "googleAnalyticsTrackingId", label: "Google tag ID (GA4)", kind: "text", placeholder: "G-XXXXXXXXXX" },
      { key: "twilio.accountSid", label: "Account SID", kind: "text" },
      { key: "twilio.authToken", label: "Auth token", kind: "text" },
      { key: "twilio.phoneNumber", label: "Phone number", kind: "text" },
      { key: "security.mfaRequiredForOfficeRoles", label: "Require SMS MFA for office roles", kind: "checkbox" },
      {
        key: "googleCalendar.integrationMode",
        label: "Calendar connection",
        kind: "select",
        options: [
          { value: "disabled", label: "Not connected" },
          { value: "authorize_bookingstack", label: "Authorize Booking Seconds to manage Google calendars" },
          { value: "select_calendar", label: "Choose one Google Calendar" }
        ]
      },
      { key: "googleCalendar.selectedCalendarId", label: "Google Calendar", kind: "text" },
      { key: "googleCalendar.syncBookings", label: "Synchronize bookings", kind: "checkbox" },
      { key: "zapier.enabled", label: "Enable Zapier integration", kind: "checkbox" },
      { key: "zapier.webhookUrl", label: "Zapier webhook URL", kind: "url" },
      { key: "automatedTasks.enabled", label: "Enable automated tasks", kind: "checkbox" },
      { key: "automatedTasks.notes", label: "Automated tasks notes", kind: "textarea", rows: 4 },
      { key: "dataRetentionDays", label: "Data retention days", kind: "number" },
      { key: "legalPages.termsUrl", label: "Terms URL", kind: "url" },
      { key: "legalPages.privacyUrl", label: "Privacy URL", kind: "url" },
      { key: "legalPages.termsVersion", label: "Terms version", kind: "text", hint: "Examples: 2026-04-22 or v3." },
      { key: "legalPages.privacyVersion", label: "Privacy version", kind: "text", hint: "Examples: 2026-04-22 or v3." },
      { key: "customCss", label: "Custom CSS", kind: "textarea", rows: 6 },
      { key: "featureFlags", label: "Feature flags JSON", kind: "json", rows: 6 }
    ]
  }
};

const relationalSectionConfigs: Record<string, SectionConfig> = {
  locations: {
    title: "Locations",
    description: "Manage service regions, address details, and availability windows.",
    defaults: {
      name: "",
      timezone: "America/Chicago",
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US",
      bookingLeadMinutes: 120,
      bookingWindowDays: 60,
      serviceArea: [],
      serviceAreaRules: [],
      active: true
    },
    fields: [
      { key: "name", label: "Location name", kind: "text" },
      { key: "timezone", label: "Timezone", kind: "text" },
      { key: "line1", label: "Address line 1", kind: "text" },
      { key: "city", label: "City", kind: "text" },
      { key: "state", label: "State", kind: "text" },
      { key: "postalCode", label: "Postal code", kind: "text" },
      { key: "bookingLeadMinutes", label: "Lead minutes", kind: "number" },
      { key: "bookingWindowDays", label: "Advance booking days", kind: "number" },
      { key: "serviceArea", label: "Service area", kind: "csv", multiline: true, rows: 4, hint: "Comma-separated postal codes (ZIPs)." },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Name", render: (record) => String(record.name ?? "") },
      { label: "Timezone", render: (record) => String(record.timezone ?? "") },
      { label: "City", render: (record) => String(record.city ?? "") },
      { label: "Status", render: (record) => <Badge tone={record.active ? "success" : "warning"}>{record.active ? "Active" : "Inactive"}</Badge> }
    ]
  },
  services: {
    title: "Services",
    description: "Pricing, duration, and booking-form logic for service offerings.",
    defaults: {
      name: "",
      icon: "",
      category: "cleaning",
      description: "",
      displayOrder: 0,
      durationMinutes: 180,
      basePriceAmount: 12000,
      bufferBeforeMinutes: 0,
      bufferAfterMinutes: 30,
      pricingRules: {},
      assignedExtraIds: [],
      availableLocationIds: [],
      allowAllActiveFrequencyRules: true,
      allowedFrequencyRuleIds: [],
      defaultCalendarProfileId: "",
      extraVisibilityRules: {},
      staffOnly: false,
      active: true
    },
    fields: [
      { key: "name", label: "Service name", kind: "text" },
      { key: "icon", label: "Icon", kind: "text", hint: "Emoji, short icon label, or URL." },
      { key: "description", label: "Description", kind: "textarea", rows: 4 },
      { key: "displayOrder", label: "Display order", kind: "number" },
      { key: "durationMinutes", label: "Duration minutes", kind: "number" },
      { key: "basePriceAmount", label: "Base price", kind: "number" },
      { key: "bufferBeforeMinutes", label: "Buffer before", kind: "number" },
      { key: "bufferAfterMinutes", label: "Buffer after", kind: "number" },
      { key: "assignedExtraIds", label: "Assigned extras", kind: "csv" },
      { key: "availableLocationIds", label: "Available locations", kind: "csv" },
      { key: "allowAllActiveFrequencyRules", label: "Allow all active frequencies", kind: "checkbox" },
      { key: "allowedFrequencyRuleIds", label: "Allowed frequency rule IDs", kind: "csv", hint: "Frequency options available for this service when not using all active frequencies." },
      { key: "defaultCalendarProfileId", label: "Default calendar profile ID", kind: "text" },
      { key: "pricingRules", label: "Pricing rules JSON", kind: "json", rows: 5 },
      { key: "extraVisibilityRules", label: "Extra visibility rules JSON", kind: "json", rows: 5, hint: "Use this for conditional extras by type/service." },
      { key: "staffOnly", label: "Display service to staff only", kind: "checkbox" },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Name", render: (record) => String(record.name ?? "") },
      { label: "Order", render: (record) => String(record.displayOrder ?? 0) },
      { label: "Duration", render: (record) => `${record.durationMinutes ?? 0} min` },
      { label: "Price", render: (record) => formatCurrency(Number(record.basePriceAmount ?? 0)) }
      ]
    },
  "service-scenarios": {
    title: "Service Scenarios",
    description: "Hidden logic layers that resolve from the selected service and square footage bucket.",
    defaults: {
      baseServiceId: "",
      name: "",
      code: "",
      displayOrder: 0,
      pricingMode: "exact",
      questionProfile: "",
      remodelProfile: "",
      extraFilterProfile: "",
      customerNote: "",
      internalNote: "",
      squareFootageMin: "",
      squareFootageMax: "",
      priority: 0,
      showFrequency: true,
      showHomeDetails: true,
      showConditionSection: true,
      showRemodelingSection: true,
      showExtras: true,
      showTip: true,
      allowedSkipAreas: [],
      visibleExtraIds: [],
      remodelingPricingRules: [],
      buildingCommonAreasPricingRules: [],
      active: true
    },
    fields: [
      { key: "baseServiceId", label: "Base service", kind: "select" },
      { key: "name", label: "Name", kind: "text" },
      { key: "code", label: "Code", kind: "text", hint: "Internal code like apartment_under_1200." },
      { key: "displayOrder", label: "Display order", kind: "number" },
      {
        key: "pricingMode",
        label: "Pricing mode",
        kind: "select",
        options: [
          { value: "exact", label: "Exact" },
          { value: "approximate", label: "Approximate" },
          { value: "manual_review", label: "Manual review" }
        ]
      },
      {
        key: "questionProfile",
        label: "Question profile",
        kind: "select",
        options: [
          { value: "", label: "Auto-detect from service / code" },
          { value: "residential", label: "Residential" },
          { value: "office_commercial", label: "Office / commercial" },
          { value: "building_common_areas", label: "Building common areas" },
          { value: "carpet_standalone", label: "Carpet standalone" }
        ]
      },
      {
        key: "remodelProfile",
        label: "Renovation profile",
        kind: "select",
        options: [
          { value: "", label: "Auto-detect from service / code" },
          { value: "residential", label: "Residential" },
          { value: "office_commercial", label: "Office / commercial" },
          { value: "building_common_areas", label: "Building common areas" },
          { value: "none", label: "No renovation question" }
        ]
      },
      {
        key: "extraFilterProfile",
        label: "Extra filter profile",
        kind: "select",
        options: [
          { value: "", label: "Auto-detect from service / code" },
          { value: "residential", label: "Residential" },
          { value: "office_commercial", label: "Office / commercial" },
          { value: "building_common_areas", label: "Building common areas" },
          { value: "carpet_standalone", label: "Carpet standalone" }
        ]
      },
      { key: "squareFootageMin", label: "Square footage min", kind: "number" },
      { key: "squareFootageMax", label: "Square footage max", kind: "number" },
      { key: "priority", label: "Priority", kind: "number" },
      { key: "customerNote", label: "Customer note", kind: "textarea", rows: 3 },
      { key: "internalNote", label: "Internal note", kind: "textarea", rows: 3 },
      { key: "showFrequency", label: "Show frequency", kind: "checkbox" },
      { key: "showHomeDetails", label: "Show home details", kind: "checkbox" },
      { key: "showConditionSection", label: "Show condition section", kind: "checkbox" },
      { key: "showRemodelingSection", label: "Show renovation section", kind: "checkbox" },
      { key: "showExtras", label: "Show extras", kind: "checkbox" },
      { key: "showTip", label: "Show tip", kind: "checkbox" },
      { key: "allowedSkipAreas", label: "Allowed skip areas", kind: "csv", hint: "Choose which areas this scenario allows the customer to skip." },
      { key: "visibleExtraIds", label: "Visible extras", kind: "csv", hint: "Optional scenario-specific extra visibility override." },
      { key: "remodelingPricingRules", label: "Renovation pricing rules", kind: "text", hint: "Set hourly rate and minimum crew time per renovation option." },
      { key: "buildingCommonAreasPricingRules", label: "Building common areas pricing", kind: "text", hint: "Set apartment-bucket pricing, duration, and manual-review cutoffs for building common areas." },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Name", render: (record) => String(record.name ?? "") },
      { label: "Base service", render: (record) => String(record.baseServiceId ?? "") },
      { label: "Pricing mode", render: (record) => String(record.pricingMode ?? "") },
      { label: "Order", render: (record) => String(record.displayOrder ?? 0) },
      { label: "Status", render: (record) => <Badge tone={record.active ? "success" : "warning"}>{record.active ? "Active" : "Inactive"}</Badge> }
    ]
  },
    extras: {
      title: "Extras",
      description: "Manage add-ons, their pricing, duration, recurring behavior, and service assignments.",
      defaults: {
        name: "",
        description: "",
        icon: "",
        displayOrder: 0,
        quantityBased: false,
        quantityLabel: "",
        quantityUnitLabel: "",
        quantityAdminDetailKey: "",
        minQuantity: 1,
        maxQuantity: 10,
        priceDeltaAmount: 0,
        durationDeltaMinutes: 0,
        carpetShampooingConfig: {
          enabled: false,
          minimumOrderAmount: 4900,
          units: carpetShampooingUnitDefaults.map((unit) => ({
            code: unit.code,
            label: unit.label,
            priceDeltaAmount: dollarsToCents(unit.priceDeltaDollars),
            durationDeltaMinutes: Number(unit.durationMinutes || 0),
            maxQuantity: Number(unit.maxQuantity || 0),
            displayOrder: unit.displayOrder,
            active: unit.active
          }))
        },
        assignedServiceIds: [],
        excludeFromFrequencyDiscount: false,
        excludeFromPromoDiscount: false,
        recurringPolicy: "every_booking",
        active: true
      },
      fields: [
        { key: "name", label: "Extra name", kind: "text" },
        { key: "icon", label: "Icon", kind: "text", hint: "Emoji, short icon label, or URL." },
        { key: "description", label: "Description", kind: "textarea", rows: 4 },
        { key: "displayOrder", label: "Display order", kind: "number" },
        { key: "quantityBased", label: "Quantity based", kind: "checkbox" },
        { key: "quantityLabel", label: "Quantity question", kind: "text", hint: "Example: How many sinkfuls?" },
        { key: "quantityUnitLabel", label: "Quantity unit", kind: "text", hint: "Example: sinkful" },
        { key: "quantityAdminDetailKey", label: "Quantity detail key", kind: "text", hint: "Example: dishes_hand_washing_sinkfuls" },
        { key: "minQuantity", label: "Minimum quantity", kind: "number" },
        { key: "maxQuantity", label: "Maximum quantity", kind: "number" },
        { key: "priceDeltaAmount", label: "Price delta", kind: "number" },
        { key: "durationDeltaMinutes", label: "Duration minutes", kind: "number" },
        { key: "assignedServiceIds", label: "Assigned service IDs", kind: "csv" },
        { key: "excludeFromFrequencyDiscount", label: "Exclude from frequency discounts", kind: "checkbox" },
        { key: "excludeFromPromoDiscount", label: "Exclude from promo discounts", kind: "checkbox" },
        {
          key: "recurringPolicy",
          label: "Recurring policy",
          kind: "select",
          options: [
            { value: "every_booking", label: "Add to every booking in a recurring series" },
            { value: "first_booking_only", label: "Add to first booking only" }
          ]
        },
        { key: "active", label: "Active", kind: "checkbox" }
      ],
      columns: [
        { label: "Name", render: (record) => String(record.name ?? "") },
        { label: "Order", render: (record) => String(record.displayOrder ?? 0) },
        { label: "Price", render: (record) => formatCurrency(Number(record.priceDeltaAmount ?? 0)) },
        { label: "Duration", render: (record) => `${record.durationDeltaMinutes ?? 0} min` }
      ]
    },
  "booking-spots": {
    title: "Availability",
    description: "Capacity units, interval sizing, and service coverage per booking team or lane.",
    defaults: {
      name: "",
      locationId: "",
      linkedServiceIds: [],
      availabilityMode: "manual",
      teamSize: 2,
      fallbackTeamSizes: [1],
      allowSingleStaffFallback: true,
      teamBasedCapacityCap: "",
      displayMode: "arrival_windows",
      capacity: 6,
      intervalMinutes: 120,
      maxSimultaneousBookings: 6,
      operatingHours: [],
      blackoutDates: [],
      dailySchedule: [],
      holidayOverrides: [],
      capacityRules: [],
      dateOverrides: [],
      durationPolicyEnabled: true,
      allowDurationOverlapIntoNextSpot: false,
      blockIfDurationExceedsAvailableTime: true,
      outsideNormalAvailabilityPolicy: "count_against_previous_slot",
      durationRoundingMode: "round_up_to_next_slot",
      active: true
    },
    fields: [
      { key: "name", label: "Spot name", kind: "text" },
      { key: "locationId", label: "Location ID", kind: "text", hint: "Use a location record id, like loc_downtown_demo." },
      { key: "linkedServiceIds", label: "Linked service IDs", kind: "csv" },
      { key: "capacity", label: "Capacity", kind: "number" },
      { key: "intervalMinutes", label: "Interval minutes", kind: "number" },
      { key: "maxSimultaneousBookings", label: "Max simultaneous bookings", kind: "number" },
      { key: "blackoutDates", label: "Blackout dates", kind: "csv", hint: "YYYY-MM-DD values separated by commas." },
      { key: "operatingHours", label: "Operating hours JSON", kind: "json", rows: 5 },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Name", render: (record) => String(record.name ?? "") },
      { label: "Location", render: (record) => String(record.locationId ?? "") },
      { label: "Capacity", render: (record) => String(record.capacity ?? "") },
      { label: "Interval", render: (record) => `${record.intervalMinutes ?? 0} min` }
    ]
  },
  "calendar-profiles": {
    title: "Calendar Profiles",
    description: "Reusable calendar rules that can be assigned to services.",
    defaults: {
      name: "",
      description: "",
      linkedLocationIds: [],
      linkedBookingSpotIds: [],
      allowedWeekdays: [],
      blackoutDates: [],
      advanceNoticeMinutes: 120,
      bookingWindowDays: 60,
      active: true
    },
    fields: [
      { key: "name", label: "Profile name", kind: "text" },
      { key: "description", label: "Description", kind: "textarea", rows: 4 },
      { key: "linkedLocationIds", label: "Linked location IDs", kind: "csv" },
      { key: "linkedBookingSpotIds", label: "Linked booking spot IDs", kind: "csv" },
      { key: "allowedWeekdays", label: "Allowed weekdays", kind: "csv", hint: "Use monday, tuesday, etc." },
      { key: "blackoutDates", label: "Blackout dates", kind: "csv", hint: "YYYY-MM-DD values separated by commas." },
      { key: "advanceNoticeMinutes", label: "Advance notice minutes", kind: "number" },
      { key: "bookingWindowDays", label: "Booking window days", kind: "number" },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Name", render: (record) => String(record.name ?? "") },
      { label: "Advance notice", render: (record) => `${record.advanceNoticeMinutes ?? 0} min` },
      { label: "Window", render: (record) => `${record.bookingWindowDays ?? 0} days` },
      { label: "Status", render: (record) => <Badge tone={record.active ? "success" : "warning"}>{record.active ? "Active" : "Inactive"}</Badge> }
    ]
  },
  "frequency-rules": {
    title: "Frequencies",
    description: "Control booking frequencies and their recurring discounts.",
    defaults: {
      name: "",
      cadenceLabel: "",
      displayOrder: 0,
      isDefault: false,
      discountValue: 0,
      active: true
    },
    fields: [
      { key: "name", label: "Name", kind: "text" },
      { key: "cadenceLabel", label: "Frequency", kind: "text", hint: "For example: One-time or Every 2 Weeks." },
      { key: "discountValue", label: "Discount (%)", kind: "number", step: "0.01" },
      { key: "displayOrder", label: "Display order", kind: "number" },
      { key: "isDefault", label: "Default frequency", kind: "checkbox" },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Name", render: (record) => normalizeFrequencyDisplayText(record.name) },
      { label: "Frequency", render: (record) => normalizeFrequencyDisplayText(record.cadenceLabel) },
      { label: "Discount", render: (record) => `${Number(record.discountValue ?? 0).toFixed(2)}%` },
      { label: "Order", render: (record) => String(record.displayOrder ?? 0) },
      { label: "Default", render: (record) => (record.isDefault ? "✓" : "") },
      { label: "Status", render: (record) => <Badge tone={record.active ? "success" : "warning"}>{record.active ? "Active" : "Inactive"}</Badge> }
    ]
  },
  promotions: {
    title: "Discount Codes",
    description: "Discount code behavior, spend thresholds, and service targeting.",
    defaults: {
      code: "",
      name: "",
      discountType: "percentage",
      discountValue: 10,
      applicableServiceIds: [],
      serviceDateStart: "",
      serviceDateEnd: "",
      validWeekdays: [],
      endsAt: "",
      usageLimit: 100,
      minimumSpendAmount: 0,
      sourceRestriction: "",
      firstTimeOnly: false,
      active: true
    },
    fields: [
      { key: "code", label: "Promo code", kind: "text" },
      { key: "name", label: "Name", kind: "text" },
      {
        key: "discountType",
        label: "Discount type",
        kind: "select",
        options: [
          { value: "percentage", label: "Percentage" },
          { value: "fixed", label: "Fixed amount" }
        ]
      },
      { key: "discountValue", label: "Discount value", kind: "number" },
      { key: "applicableServiceIds", label: "Applicable services", kind: "csv" },
      { key: "serviceDateStart", label: "Service date start", kind: "date" },
      { key: "serviceDateEnd", label: "Service date end", kind: "date" },
      { key: "validWeekdays", label: "Valid weekdays", kind: "csv" },
      { key: "endsAt", label: "Expiration date", kind: "date" },
      { key: "usageLimit", label: "Usage limit", kind: "number" },
      { key: "minimumSpendAmount", label: "Minimum spend ($)", kind: "number", step: "0.01" },
      { key: "sourceRestriction", label: "Source restriction", kind: "text" },
      { key: "firstTimeOnly", label: "First-time customer only", kind: "checkbox" },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Code", render: (record) => String(record.code ?? "") },
      { label: "Type", render: (record) => String(record.discountType ?? "") },
      { label: "Value", render: (record) => String(record.discountValue ?? "") },
      { label: "Status", render: (record) => <Badge tone={record.active ? "success" : "warning"}>{record.active ? "Active" : "Inactive"}</Badge> }
    ]
  },
  staff: {
    title: "Staff",
    description: "Core contact details, compensation links, and assignment ids for scheduling.",
    defaults: {
      staffTypeId: "",
      workerGroup: "",
      code: "",
      name: "",
      businessName: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US",
      phoneNumber: "",
      emergencyContact: "",
      email: "",
      dateOfBirth: "",
      hireDate: "",
      teamLeader: false,
      englishSpeaking: false,
      availabilityDays: [] as string[],
      availabilityText: "",
      availabilityStartTime: "08:00",
      availabilityEndTime: "17:00",
      assignedLocationIds: [] as string[],
      assignedServiceIds: [] as string[],
      preferredTeamMemberId: "",
      cannotWorkWithStaffIds: [] as string[],
      tagIds: [] as string[],
      notes: "",
      payType: "hourly",
      compensation: 0,
      bankRoutingNumber: "",
      bankAccountNumber: "",
      driverLicenseNumber: "",
      licensePlate: "",
      vehicleYear: "",
      vehicleMake: "",
      vehicleModel: "",
      vehicleColor: "",
      odometer: "",
      isArchived: false,
      active: true,
      equipmentRecords: [] as Array<{ assignedDate: string; equipmentName: string }>,
      shirtSize: ""
    },
    fields: [
      { key: "staffTypeId", label: "Staff type", kind: "select" },
      { key: "workerGroup", label: "Staff type label", kind: "text" },
      { key: "firstName", label: "First name", kind: "text" },
      { key: "lastName", label: "Last name", kind: "text" },
      { key: "code", label: "Worker code", kind: "text" },
      { key: "name", label: "Short name", kind: "text" },
      { key: "businessName", label: "Business name", kind: "text" },
      { key: "address1", label: "Address 1", kind: "text" },
      { key: "address2", label: "Address 2", kind: "text" },
      { key: "city", label: "City", kind: "text" },
      { key: "state", label: "State", kind: "text" },
      { key: "postalCode", label: "Postal / zip code", kind: "text" },
      { key: "country", label: "Country", kind: "text" },
      { key: "phoneNumber", label: "Phone number", kind: "text" },
      { key: "emergencyContact", label: "Emergency contact", kind: "text" },
      { key: "email", label: "Email", kind: "email" },
      { key: "dateOfBirth", label: "Date of birth", kind: "date" },
      { key: "hireDate", label: "Hire date", kind: "date" },
      { key: "teamLeader", label: "Team leader", kind: "checkbox", hint: "Mark if this staff member leads a team." },
      { key: "englishSpeaking", label: "English speaking", kind: "checkbox", hint: "Mark if this staff member is English speaking." },
      { key: "availabilityDays", label: "Available work days", kind: "text" },
      { key: "availabilityText", label: "Available work days summary", kind: "text" },
      { key: "availabilityStartTime", label: "Earliest start time", kind: "time" },
      { key: "availabilityEndTime", label: "Latest end time", kind: "time" },
      { key: "assignedLocationIds", label: "Assigned locations", kind: "csv", hint: "Leave empty to allow this worker in all active locations." },
      { key: "assignedServiceIds", label: "Assigned cleaning types", kind: "csv", hint: "Leave empty to allow this worker on all active cleaning types." },
      { key: "preferredTeamMemberId", label: "Preferred team member", kind: "select" },
      { key: "cannotWorkWithStaffIds", label: "Can't work with team member", kind: "text" },
      { key: "tagIds", label: "Tags", kind: "csv" },
      { key: "notes", label: "Notes", kind: "textarea", rows: 4 },
      {
        key: "payType",
        label: "Pay type",
        kind: "select",
        options: [
          { value: "hourly", label: "Hourly" },
          { value: "flat-fee", label: "Flat" },
          { value: "revenue-share", label: "Revenue share (%)" }
        ]
      },
      { key: "compensation", label: "Amount", kind: "number", step: "0.01" },
      { key: "bankRoutingNumber", label: "Bank routing number", kind: "text" },
      { key: "bankAccountNumber", label: "Bank account number", kind: "text" },
      { key: "driverLicenseNumber", label: "Driver license number", kind: "text" },
      { key: "licensePlate", label: "License plate", kind: "text" },
      { key: "vehicleYear", label: "Vehicle year", kind: "text" },
      { key: "vehicleMake", label: "Vehicle make", kind: "text" },
      { key: "vehicleModel", label: "Vehicle model", kind: "text" },
      { key: "vehicleColor", label: "Color", kind: "text" },
      { key: "odometer", label: "Odometer", kind: "text" },
      { key: "active", label: "Active", kind: "checkbox" },
      { key: "isArchived", label: "Archived", kind: "checkbox", hint: "Archived staff are kept in records but hidden from active scheduling." },
      { key: "equipmentRecords", label: "Equipment records", kind: "text" },
      { key: "shirtSize", label: "Shirt size", kind: "text" }
    ],
    columns: [
      { label: "Code", render: (record) => String(record.code ?? "") },
      { label: "Name", render: (record) => `${String(record.firstName ?? "")} ${String(record.lastName ?? "")}`.trim() || String(record.name ?? "") },
      { label: "Staff type", render: (record) => String(record.workerGroup ?? "") },
      { label: "Phone", render: (record) => String(record.phoneNumber ?? "") },
      { label: "Email", render: (record) => String(record.email ?? "") },
      {
        label: "Pay",
        render: (record) => {
          const payType = String(record.payType ?? "");
          if (payType === "revenue-share") {
            return `Revenue share ${Number(record.compensation ?? 0).toFixed(2)}%`;
          }
          return `${payType === "flat-fee" ? "Flat" : "Hourly"} $${Number(record.compensation ?? 0).toFixed(2)}`.trim();
        }
      },
      { label: "Status", render: (record) => <Badge tone={record.active ? "success" : "warning"}>{record.active ? "Active" : "Inactive"}</Badge> }
    ]
  },
  "staff-time-off": {
    title: "Vacations",
    description: "Date-based vacation and time-off blocks that remove staff from booking availability.",
    defaults: {
      staffId: "",
      startDate: "",
      endDate: "",
      reason: ""
    },
    fields: [
      { key: "staffId", label: "Staff member", kind: "select" },
      { key: "startDate", label: "Start date", kind: "date" },
      { key: "endDate", label: "End date", kind: "date" },
      { key: "reason", label: "Reason", kind: "textarea", rows: 3 }
    ],
    columns: [
      { label: "Staff member", render: (record) => String(record.staffId ?? "") },
      { label: "Start", render: (record) => String(record.startDate ?? "") },
      { label: "End", render: (record) => String(record.endDate ?? "") },
      { label: "Reason", render: (record) => String(record.reason ?? "") || "Vacation" }
    ]
  },
  tags: {
    title: "Tags",
    description: "Reusable labels for customers, bookings, discount codes, services, and staff.",
    defaults: {
      entityType: "customer",
      name: "",
      displayOrder: 0,
      directoryCategory: "all",
      color: "#1D6B57",
      active: true
    },
    fields: [
      {
        key: "entityType",
        label: "Entity type",
        kind: "select",
        options: [
          { value: "customer", label: "Customer" },
          { value: "booking", label: "Booking" },
          { value: "promotion", label: "Discount code" },
          { value: "service", label: "Service" },
          { value: "staff", label: "Staff" }
        ]
      },
      { key: "name", label: "Name", kind: "text" },
      { key: "displayOrder", label: "Display order", kind: "number" },
      {
        key: "directoryCategory",
        label: "Staff directory target",
        kind: "select",
        options: [
          { value: "all", label: "All staff" },
          { value: "maids", label: "Maids" },
          { value: "drivers", label: "Drivers" },
          { value: "office", label: "Office staff" }
        ]
      },
      { key: "color", label: "Color", kind: "color" },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Order", render: (record) => String(record.displayOrder ?? 0) },
      { label: "Name", render: (record) => String(record.name ?? "") },
      { label: "Entity", render: (record) => String(record.entityType ?? "") },
      {
        label: "Target",
        render: (record) => (String(record.entityType ?? "") === "staff" ? String(record.directoryCategory ?? "all") : "—")
      },
      { label: "Color", render: (record) => <span style={{ color: String(record.color ?? "#1D6B57") }}>{String(record.color ?? "")}</span> },
      { label: "Status", render: (record) => <Badge tone={record.active ? "success" : "warning"}>{record.active ? "Active" : "Inactive"}</Badge> }
    ]
  },
  "notification-rules": {
    title: "Notification Rules",
    description: "Choose which booking events send which templates to customers, admins, or the office.",
    defaults: {
      name: "",
      eventKey: "booking_confirmation",
      channel: "email",
      recipientType: "customer",
      templateKey: "booking_confirmation",
      timingMode: "immediate",
      timingOffsetMinutes: 0,
      conditions: {},
      displayOrder: 0,
      active: true
    },
    fields: [
      { key: "name", label: "Rule name", kind: "text" },
      { key: "eventKey", label: "Event", kind: "select", options: notificationEventOptions as unknown as FieldConfig["options"] },
      {
        key: "channel",
        label: "Channel",
        kind: "select",
        options: [
          { value: "email", label: "Email" },
          { value: "sms", label: "SMS" }
        ]
      },
      {
        key: "recipientType",
        label: "Recipient",
        kind: "select",
        options: [
          { value: "customer", label: "Customer" },
          { value: "admin", label: "Admin users" },
          { value: "office", label: "Office" }
        ]
      },
      {
        key: "templateKey",
        label: "Template key",
        kind: "select",
        options: notificationTemplateOptions as unknown as FieldConfig["options"]
      },
      { key: "displayOrder", label: "Display order", kind: "number" },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Order", render: (record) => String(record.displayOrder ?? 0) },
      { label: "Name", render: (record) => String(record.name ?? "") },
      { label: "Event", render: (record) => String(record.eventKey ?? "").replaceAll("_", " ") },
      { label: "Channel", render: (record) => String(record.channel ?? "") },
      { label: "Recipient", render: (record) => String(record.recipientType ?? "") },
      { label: "Status", render: (record) => <Badge tone={record.active ? "success" : "warning"}>{record.active ? "Active" : "Inactive"}</Badge> }
    ]
  },
  "salary-methods": {
    title: "Salary Methods",
    description: "Compensation formulas used for payroll previews and staff pay rules.",
    defaults: {
      name: "",
      methodType: "hourly",
      config: {},
      active: true
    },
    fields: [
      { key: "name", label: "Name", kind: "text" },
      {
        key: "methodType",
        label: "Method type",
        kind: "select",
        options: [
          { value: "hourly", label: "Hourly" },
          { value: "fixed_per_booking", label: "Fixed per booking" },
          { value: "percentage_commission", label: "Percentage commission" },
          { value: "service_rate", label: "Service rate" },
          { value: "bonus_plus_base", label: "Bonus + base" },
          { value: "team_split", label: "Team split" }
        ]
      },
      { key: "config", label: "Config JSON", kind: "json", rows: 6 },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Name", render: (record) => String(record.name ?? "") },
      { label: "Method", render: (record) => String(record.methodType ?? "") },
      { label: "Status", render: (record) => <Badge tone={record.active ? "success" : "warning"}>{record.active ? "Active" : "Inactive"}</Badge> }
    ]
  },
  "staff-types": {
    title: "Staff Types",
    description: "Create reusable staff types for the directory, availability matching, and staff creation forms.",
    defaults: {
      name: "",
      code: "",
      directoryCategory: "maids",
      displayOrder: 0,
      active: true
    },
    fields: [
      { key: "name", label: "Name", kind: "text" },
      { key: "code", label: "Code", kind: "text" },
      {
        key: "directoryCategory",
        label: "Directory category",
        kind: "select",
        options: [
          { value: "maids", label: "Maids" },
          { value: "drivers", label: "Drivers" },
          { value: "office", label: "Office Staff" }
        ]
      },
      { key: "displayOrder", label: "Display order", kind: "number" },
      { key: "active", label: "Active", kind: "checkbox" }
    ],
    columns: [
      { label: "Name", render: (record) => String(record.name ?? "") },
      { label: "Code", render: (record) => String(record.code ?? "") },
      { label: "Directory", render: (record) => String(record.directoryCategory ?? "") },
      { label: "Order", render: (record) => String(record.displayOrder ?? 0) },
      { label: "Status", render: (record) => <Badge tone={record.active ? "success" : "warning"}>{record.active ? "Active" : "Inactive"}</Badge> }
    ]
  }
};

const schedulingSections: Array<{
  routeKey: SchedulingRouteKey;
  label: string;
  description: string;
}> = [
  {
    routeKey: "scheduling-overview",
    label: "Overview",
    description: "See booking spots, staffing coverage, closures, and quick actions in one place."
  },
  {
    routeKey: "scheduling-booking-availability",
    label: "Booking Availability",
    description: "Manage booking spots, slot schedules, duration rules, linked services, and reusable calendar profiles."
  },
  {
    routeKey: "scheduling-staff-availability",
    label: "Staff Availability",
    description: "Control weekly staff availability, working windows, and time-off records for scheduling."
  },
  {
    routeKey: "scheduling-exceptions-holidays",
    label: "Exceptions & Holidays",
    description: "Review one-off closures, holiday overrides, and reusable calendar-profile blackout rules."
  },
  {
    routeKey: "scheduling-diagnostics",
    label: "Diagnostics",
    description: "Troubleshoot why a date or service is available, blocked, or missing compatible teams."
  }
];

const syntheticSettingsSections = [
  { routeKey: "scheduling-overview", label: "Scheduling" },
  { routeKey: "customer-portal", label: "Customer Portal" },
  ...schedulingSections
] as const;

const allSections = [...syntheticSettingsSections, ...singletonSettingsSections, ...relationalSettingsSections];

function getPathValue(input: SettingsValue, path: string): unknown {
  return path.split(".").reduce<unknown>((current, key) => {
    if (current && typeof current === "object" && key in (current as Record<string, unknown>)) {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, input);
}

function setPathValue(input: SettingsValue, path: string, value: unknown): SettingsValue {
  const keys = path.split(".");
  const next = structuredClone(input);
  let cursor: Record<string, unknown> = next;

  for (const key of keys.slice(0, -1)) {
    const current = cursor[key];
    if (!current || typeof current !== "object" || Array.isArray(current)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }

  cursor[keys[keys.length - 1]] = value;
  return next;
}

function normalizeDateInputValue(value: unknown) {
  const text = String(value ?? "").trim();
  if (!text) {
    return "";
  }

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const yearFirstMatch = text.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})(?:$|\s)/);
  if (yearFirstMatch) {
    return `${yearFirstMatch[1]}-${yearFirstMatch[2].padStart(2, "0")}-${yearFirstMatch[3].padStart(2, "0")}`;
  }

  const monthFirstMatch = text.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:$|\s)/);
  if (monthFirstMatch) {
    return `${monthFirstMatch[3]}-${monthFirstMatch[1].padStart(2, "0")}-${monthFirstMatch[2].padStart(2, "0")}`;
  }

  return text;
}

function toDraftValue(field: FieldConfig, value: unknown): unknown {
  if (
    field.key === "paymentsAccepted" ||
    field.key === "applicableServiceIds" ||
    field.key === "validWeekdays" ||
    field.key === "tagIds" ||
    field.key === "assignedLocationIds" ||
    field.key === "assignedServiceIds" ||
    field.key === "allowedSkipAreas"
  ) {
    return Array.isArray(value) ? value.map((item) => String(item)) : [];
  }
  if (field.key === "minimumSpendAmount") {
    return centsToDollarInput(value);
  }
  if (field.key === "serviceDateStart" || field.key === "serviceDateEnd") {
    return String(value ?? "").trim().slice(0, 10);
  }
  if (field.key === "endsAt") {
    return String(value ?? "").trim().slice(0, 10);
  }
  if (field.kind === "date") {
    return normalizeDateInputValue(value);
  }
  switch (field.kind) {
    case "checkbox":
      return Boolean(value);
    case "csv":
      return Array.isArray(value) ? value.join(", ") : "";
    case "json":
      return JSON.stringify(value ?? {}, null, 2);
    default:
      return value ?? "";
  }
}

function fromDraftValue(field: FieldConfig, value: unknown): unknown {
  if (
    field.key === "paymentsAccepted" ||
    field.key === "applicableServiceIds" ||
    field.key === "validWeekdays" ||
    field.key === "tagIds" ||
    field.key === "assignedLocationIds" ||
    field.key === "assignedServiceIds" ||
    field.key === "allowedSkipAreas"
  ) {
    return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
  }
  if (field.key === "minimumSpendAmount") {
    return dollarsToCents(value);
  }
  if (field.key === "serviceDateStart" || field.key === "serviceDateEnd") {
    const text = String(value ?? "").trim();
    return text || undefined;
  }
  if (field.key === "endsAt") {
    const text = String(value ?? "").trim();
    return text ? `${text}T23:59:59.999Z` : undefined;
  }
  switch (field.kind) {
    case "number":
      return value === "" || value == null ? undefined : Number(value);
    case "checkbox":
      return Boolean(value);
    case "csv":
      return String(value ?? "")
        .split(/[,\n|]/)
        .map((part) => part.trim())
        .filter(Boolean);
    case "json":
      return String(value ?? "").trim() === "" ? {} : JSON.parse(String(value));
    default:
      return value;
  }
}

function hydrateDraft(config: SectionConfig, source: SettingsValue): SettingsValue {
  return config.fields.reduce<SettingsValue>((draft, field) => {
    return setPathValue(draft, field.key, toDraftValue(field, getPathValue(source, field.key)));
  }, {});
}

function buildPayload(config: SectionConfig, base: SettingsValue, draft: SettingsValue): SettingsValue {
  return config.fields.reduce<SettingsValue>((payload, field) => {
    return setPathValue(payload, field.key, fromDraftValue(field, getPathValue(draft, field.key)));
  }, structuredClone(base));
}

function marketingCompliancePreview(draft: SettingsValue) {
  const clean = (path: string) => String(getPathValue(draft, path) ?? "").trim();
  const businessName = clean("marketingCompliance.legalBusinessName") || clean("senderName") || "Business name";
  const city = clean("marketingCompliance.city");
  const state = clean("marketingCompliance.state");
  const postalCode = clean("marketingCompliance.postalCode");
  const cityStatePostal = [city, [state, postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const country = clean("marketingCompliance.country") || "US";
  const addressLines = [
    clean("marketingCompliance.addressLine1"),
    clean("marketingCompliance.addressLine2"),
    cityStatePostal,
    country
  ].filter(Boolean);
  const missing = [
    clean("marketingCompliance.addressLine1") ? "" : "physical address line 1",
    city ? "" : "city",
    state ? "" : "state",
    postalCode ? "" : "postal code",
    country ? "" : "country"
  ].filter(Boolean);

  return {
    businessName,
    addressLines,
    supportEmail: clean("marketingCompliance.supportEmail"),
    supportPhone: clean("marketingCompliance.supportPhone"),
    missing
  };
}

const defaultResidentialLevelMinimumRows = [
  { levels: 1, minimumSquareFeet: 1400 },
  { levels: 1.5, minimumSquareFeet: 1600 },
  { levels: 2, minimumSquareFeet: 1800 },
  { levels: 2.5, minimumSquareFeet: 2000 },
  { levels: 3, minimumSquareFeet: 2200 }
];

const residentialSkipAreaPropertyGroupOptions: Array<{ value: ResidentialSkipAreaPropertyGroup; label: string }> = [
  { value: "apartment_condo", label: "Apartment / condo" },
  { value: "house", label: "Townhouse / single-family" }
];

const residentialSkipAreaDurationAreaOptions: Array<{ value: ResidentialSkipAreaDurationAreaCode; label: string }> = [
  { value: "kitchen", label: "Kitchen" },
  { value: "full_bathroom", label: "Full bathroom" },
  { value: "half_bathroom", label: "Half bathroom" },
  { value: "bedroom", label: "Bedroom / office / den" },
  { value: "living_areas", label: "Living areas" },
  { value: "living_room", label: "Living room" },
  { value: "dining_room", label: "Dining room" },
  { value: "hallways", label: "Hallways" }
];

const residentialComponentFormulaBucketOptions: Array<{ value: ResidentialFormulaBucket; label: string }> = [
  { value: "apartment_like", label: "Apartment / condo" },
  { value: "townhouse", label: "Townhouse" },
  { value: "single_family", label: "Single-family house" }
];

const residentialConditionAreaOptions: Array<{ value: ResidentialConditionArea; label: string }> = [
  { value: "kitchen", label: "Kitchen" },
  { value: "bathrooms", label: "Bathrooms" },
  { value: "bedrooms", label: "Bedrooms" },
  { value: "otherAreas", label: "Other common areas" }
];

const residentialConditionScoreOptions = [
  { value: 1, label: "Very dirty" },
  { value: 2, label: "Needs work" },
  { value: 3, label: "Balanced" },
  { value: 4, label: "Fairly clean" },
  { value: 5, label: "Very clean" }
];

function labelForSkipAreaDurationArea(areaCode: ResidentialSkipAreaDurationAreaCode) {
  return residentialSkipAreaDurationAreaOptions.find((option) => option.value === areaCode)?.label ?? areaCode;
}

function parseAdvancedRulesDraft(value: unknown): SettingsValue {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as SettingsValue;
  }

  const text = String(value ?? "").trim();
  if (!text) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(text);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as SettingsValue) : {};
  } catch {
    return {};
  }
}

function PricebookResidentialComponentFormulaEditor({
  draft,
  setDraft
}: {
  draft: SettingsValue;
  setDraft: (updater: (current: SettingsValue) => SettingsValue) => void;
}) {
  const advancedRules = parseAdvancedRulesDraft(getPathValue(draft, "advancedRules"));
  const rows = normalizeResidentialComponentFormulaRules(advancedRules.residentialComponentFormulaRules);
  const conditionMultipliers = normalizeResidentialComponentConditionMultipliers(
    advancedRules.residentialComponentConditionMultipliers
  );
  const updateAdvancedRules = (updater: (currentRules: SettingsValue) => SettingsValue) => {
    setDraft((current) => {
      const currentRules = parseAdvancedRulesDraft(getPathValue(current, "advancedRules"));
      const nextRules = updater(currentRules);
      return setPathValue(current, "advancedRules", JSON.stringify(nextRules, null, 2));
    });
  };
  const setFormulaRows = (nextRows: ResidentialComponentFormulaRule[]) => {
    updateAdvancedRules((currentRules) => ({
      ...currentRules,
      residentialComponentFormulaRules: nextRows.map((row) => ({
        bucket: row.bucket,
        label: String(row.label || residentialComponentFormulaBucketOptions.find((option) => option.value === row.bucket)?.label || row.bucket),
        setupTeamMinutes: Math.max(0, Number(row.setupTeamMinutes) || 0),
        kitchenTeamMinutes: Math.max(0, Number(row.kitchenTeamMinutes) || 0),
        minutesPerSquareFoot: Math.max(0, Number(row.minutesPerSquareFoot) || 0),
        minutesPerBedroom: Math.max(0, Number(row.minutesPerBedroom) || 0),
        minutesPerFullBathroom: Math.max(0, Number(row.minutesPerFullBathroom) || 0),
        minutesPerHalfBathroom: Math.max(0, Number(row.minutesPerHalfBathroom) || 0),
        minutesPerExtraHalfLevel: Math.max(0, Number(row.minutesPerExtraHalfLevel) || 0)
      }))
    }));
  };
  const updateFormulaRow = (bucket: ResidentialFormulaBucket, patch: Partial<ResidentialComponentFormulaRule>) => {
    setFormulaRows(rows.map((row) => (row.bucket === bucket ? { ...row, ...patch } : row)));
  };
  const updateConditionMultiplier = (area: ResidentialConditionArea, score: number, value: number) => {
    const nextMultipliers: ResidentialComponentConditionMultipliers = {
      kitchen: { ...conditionMultipliers.kitchen },
      bathrooms: { ...conditionMultipliers.bathrooms },
      bedrooms: { ...conditionMultipliers.bedrooms },
      otherAreas: { ...conditionMultipliers.otherAreas }
    };
    nextMultipliers[area][score] = Math.max(0, Number(value) || 0);
    updateAdvancedRules((currentRules) => ({
      ...currentRules,
      residentialComponentConditionMultipliers: nextMultipliers
    }));
  };

  return (
    <Card title="Residential component formula" eyebrow="Non-flat homes">
      <p className="card-copy">
        Formula-priced residential quotes calculate setup, kitchen, square footage, bedrooms, bathrooms, and half-levels
        separately. Flat-fee apartment packages are not changed.
      </p>
      <div className="stack stack--tight">
        {rows.map((row) => (
          <div key={row.bucket} className="settings-inline-card">
            <h3 className="booking-section-title booking-section-title--small">
              {residentialComponentFormulaBucketOptions.find((option) => option.value === row.bucket)?.label ?? row.label}
            </h3>
            <div className="grid three">
              <Field label="Setup minutes">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={String(row.setupTeamMinutes)}
                  onChange={(event) => updateFormulaRow(row.bucket, { setupTeamMinutes: Number(event.target.value) })}
                />
              </Field>
              <Field label="Kitchen minutes">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={String(row.kitchenTeamMinutes)}
                  onChange={(event) => updateFormulaRow(row.bucket, { kitchenTeamMinutes: Number(event.target.value) })}
                />
              </Field>
              <Field label="Minutes per square foot">
                <input
                  type="number"
                  min="0"
                  step="0.0005"
                  value={String(row.minutesPerSquareFoot)}
                  onChange={(event) => updateFormulaRow(row.bucket, { minutesPerSquareFoot: Number(event.target.value) })}
                />
              </Field>
            </div>
            <div className="grid four">
              <Field label="Minutes per bedroom">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={String(row.minutesPerBedroom)}
                  onChange={(event) => updateFormulaRow(row.bucket, { minutesPerBedroom: Number(event.target.value) })}
                />
              </Field>
              <Field label="Minutes per full bathroom">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={String(row.minutesPerFullBathroom)}
                  onChange={(event) => updateFormulaRow(row.bucket, { minutesPerFullBathroom: Number(event.target.value) })}
                />
              </Field>
              <Field label="Minutes per half bathroom">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={String(row.minutesPerHalfBathroom)}
                  onChange={(event) => updateFormulaRow(row.bucket, { minutesPerHalfBathroom: Number(event.target.value) })}
                />
              </Field>
              <Field label="Minutes per extra half-level">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={String(row.minutesPerExtraHalfLevel)}
                  onChange={(event) => updateFormulaRow(row.bucket, { minutesPerExtraHalfLevel: Number(event.target.value) })}
                />
              </Field>
            </div>
          </div>
        ))}
      </div>
      <div className="stack stack--tight">
        <h3 className="booking-section-title booking-section-title--small">Condition multipliers</h3>
        {residentialConditionAreaOptions.map((area) => (
          <div key={area.value} className="settings-inline-card">
            <strong>{area.label}</strong>
            <div className="grid five">
              {residentialConditionScoreOptions.map((score) => (
                <Field key={score.value} label={score.label}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={String(conditionMultipliers[area.value][score.value] ?? 1)}
                    onChange={(event) => updateConditionMultiplier(area.value, score.value, Number(event.target.value))}
                  />
                </Field>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="customer-directory-actions">
        <Button
          variant="ghost"
          onClick={() =>
            updateAdvancedRules((currentRules) => ({
              ...currentRules,
              residentialComponentFormulaRules: defaultResidentialComponentFormulaRules,
              residentialComponentConditionMultipliers: defaultResidentialComponentConditionMultipliers
            }))
          }
        >
          Reset component defaults
        </Button>
      </div>
    </Card>
  );
}

function normalizeLevelMinimumRows(value: unknown) {
  const rows = Array.isArray(value) ? value : defaultResidentialLevelMinimumRows;
  return rows
    .map((row) => {
      const source = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
      return {
        levels: Number(source.levels ?? source.level ?? 1),
        minimumSquareFeet: Number(source.minimumSquareFeet ?? source.minimumBillableSquareFeet ?? 0)
      };
    })
    .filter((row) => Number.isFinite(row.levels) && row.levels > 0 && Number.isFinite(row.minimumSquareFeet))
    .sort((left, right) => left.levels - right.levels);
}

function PricebookLevelMinimumEditor({
  draft,
  setDraft
}: {
  draft: SettingsValue;
  setDraft: (updater: (current: SettingsValue) => SettingsValue) => void;
}) {
  const advancedRules = parseAdvancedRulesDraft(getPathValue(draft, "advancedRules"));
  const rows = normalizeLevelMinimumRows(advancedRules.residentialFormulaLevelSquareFootageMinimums);
  const missingIncrement = Number(advancedRules.residentialFormulaMissingHalfLevelIncrement ?? 200);
  const updateAdvancedRules = (updater: (currentRules: SettingsValue) => SettingsValue) => {
    setDraft((current) => {
      const currentRules = parseAdvancedRulesDraft(getPathValue(current, "advancedRules"));
      const nextRules = updater(currentRules);
      return setPathValue(current, "advancedRules", JSON.stringify(nextRules, null, 2));
    });
  };
  const setRows = (nextRows: Array<{ levels: number; minimumSquareFeet: number }>) => {
    updateAdvancedRules((currentRules) => ({
      ...currentRules,
      residentialFormulaLevelSquareFootageMinimums: nextRows
        .map((row) => ({
          levels: Math.max(1, Math.ceil(Number(row.levels || 1) * 2) / 2),
          minimumSquareFeet: Math.max(0, Math.round(Number(row.minimumSquareFeet || 0)))
        }))
        .sort((left, right) => left.levels - right.levels)
    }));
  };

  return (
    <Card title="Residential formula minimum square footage" eyebrow="Level floors">
      <p className="card-copy">
        Formula-priced residential quotes use the larger of the customer-selected square footage and this level minimum.
        Flat-fee apartment packages are not changed.
      </p>
      <div className="stack stack--tight">
        {rows.map((row, index) => (
          <div key={`${row.levels}-${index}`} className="settings-inline-card">
            <div className="grid two">
              <Field label="Level">
                <input
                  type="number"
                  min="1"
                  step="0.5"
                  value={String(row.levels)}
                  onChange={(event) => {
                    const nextRows = [...rows];
                    nextRows[index] = { ...row, levels: Number(event.target.value) };
                    setRows(nextRows);
                  }}
                />
              </Field>
              <Field label="Minimum billable square feet">
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={String(row.minimumSquareFeet)}
                  onChange={(event) => {
                    const nextRows = [...rows];
                    nextRows[index] = { ...row, minimumSquareFeet: Number(event.target.value) };
                    setRows(nextRows);
                  }}
                />
              </Field>
            </div>
            <div className="customer-directory-actions">
              <Button variant="ghost" onClick={() => setRows(rows.filter((_, rowIndex) => rowIndex !== index))}>
                Delete row
              </Button>
            </div>
          </div>
        ))}
        <div className="customer-directory-actions">
          <Button
            variant="secondary"
            onClick={() => {
              const lastRow = rows[rows.length - 1] ?? { levels: 1, minimumSquareFeet: 1400 };
              const increment = Number.isFinite(missingIncrement) ? missingIncrement : 200;
              setRows([
                ...rows,
                {
                  levels: Math.ceil((lastRow.levels + 0.5) * 2) / 2,
                  minimumSquareFeet: Math.max(0, Math.round(lastRow.minimumSquareFeet + increment))
                }
              ]);
            }}
          >
            Add level row
          </Button>
        </div>
      </div>
      <Field label="Missing half-level increment" hint="Used when a quote has a level above the highest row in the table.">
        <input
          type="number"
          min="0"
          step="50"
          value={Number.isFinite(missingIncrement) ? String(missingIncrement) : "200"}
          onChange={(event) =>
            updateAdvancedRules((currentRules) => ({
              ...currentRules,
              residentialFormulaMissingHalfLevelIncrement: Math.max(0, Math.round(Number(event.target.value) || 0))
            }))
          }
        />
      </Field>
    </Card>
  );
}

function normalizeOptionalSquareFootageDraftValue(value: unknown) {
  if (value == null || value === "") {
    return undefined;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? Math.round(numeric) : undefined;
}

function normalizeSkipAreaDurationRows(value: unknown): ResidentialSkipAreaDurationRule[] {
  return normalizeResidentialSkipAreaDurationRules(value).map((row, index) => ({
    ...row,
    displayOrder: Number.isFinite(Number(row.displayOrder)) ? Number(row.displayOrder) : index
  }));
}

function PricebookSkipAreaDurationEditor({
  draft,
  setDraft
}: {
  draft: SettingsValue;
  setDraft: (updater: (current: SettingsValue) => SettingsValue) => void;
}) {
  const advancedRules = parseAdvancedRulesDraft(getPathValue(draft, "advancedRules"));
  const rows = normalizeSkipAreaDurationRows(advancedRules.residentialSkipAreaDurationRules);
  const updateAdvancedRules = (updater: (currentRules: SettingsValue) => SettingsValue) => {
    setDraft((current) => {
      const currentRules = parseAdvancedRulesDraft(getPathValue(current, "advancedRules"));
      const nextRules = updater(currentRules);
      return setPathValue(current, "advancedRules", JSON.stringify(nextRules, null, 2));
    });
  };
  const setRows = (nextRows: ResidentialSkipAreaDurationRule[]) => {
    updateAdvancedRules((currentRules) => ({
      ...currentRules,
      residentialSkipAreaDurationRules: nextRows
        .map((row, index) => ({
          propertyGroup: row.propertyGroup,
          areaCode: row.areaCode,
          label: String(row.label || labelForSkipAreaDurationArea(row.areaCode)),
          laborMinutes: Math.max(0, Number(row.laborMinutes) || 0),
          ...(row.squareFootageMin == null ? {} : { squareFootageMin: Math.max(0, Math.round(row.squareFootageMin)) }),
          ...(row.squareFootageMax == null ? {} : { squareFootageMax: Math.max(0, Math.round(row.squareFootageMax)) }),
          displayOrder: Number.isFinite(Number(row.displayOrder)) ? Number(row.displayOrder) : index
        }))
        .sort((left, right) => Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0))
    }));
  };
  const updateRow = (index: number, patch: Partial<ResidentialSkipAreaDurationRule>) => {
    const nextRows = rows.map((row, rowIndex) => {
      if (rowIndex !== index) {
        return row;
      }
      const nextRow = { ...row, ...patch };
      return {
        ...nextRow,
        label: patch.areaCode && !patch.label ? labelForSkipAreaDurationArea(patch.areaCode) : nextRow.label
      };
    });
    setRows(nextRows);
  };

  return (
    <Card title="Skipped-area duration deductions" eyebrow="Labor minutes">
      <p className="card-copy">
        Enter one-maid labor minutes. The quote engine converts that labor time by the selected crew size, so 30 labor
        minutes saves 15 visit minutes with a 2-person crew.
      </p>
      <div className="stack stack--tight">
        {rows.map((row, index) => (
          <div key={`${row.propertyGroup}-${row.areaCode}-${row.squareFootageMin ?? ""}-${row.squareFootageMax ?? ""}-${index}`} className="settings-inline-card">
            <div className="grid three">
              <Field label="Property type">
                <select
                  value={row.propertyGroup}
                  onChange={(event) =>
                    updateRow(index, { propertyGroup: event.target.value as ResidentialSkipAreaPropertyGroup })
                  }
                >
                  {residentialSkipAreaPropertyGroupOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Area">
                <select
                  value={row.areaCode}
                  onChange={(event) =>
                    updateRow(index, { areaCode: event.target.value as ResidentialSkipAreaDurationAreaCode })
                  }
                >
                  {residentialSkipAreaDurationAreaOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Labor minutes">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={String(row.laborMinutes)}
                  onChange={(event) => updateRow(index, { laborMinutes: Number(event.target.value) })}
                />
              </Field>
            </div>
            <div className="grid three">
              <Field label="Label">
                <input
                  value={String(row.label ?? "")}
                  onChange={(event) => updateRow(index, { label: event.target.value })}
                />
              </Field>
              <Field label="Square footage min">
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={row.squareFootageMin == null ? "" : String(row.squareFootageMin)}
                  placeholder="Any"
                  onChange={(event) =>
                    updateRow(index, { squareFootageMin: normalizeOptionalSquareFootageDraftValue(event.target.value) })
                  }
                />
              </Field>
              <Field label="Square footage max">
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={row.squareFootageMax == null ? "" : String(row.squareFootageMax)}
                  placeholder="Any"
                  onChange={(event) =>
                    updateRow(index, { squareFootageMax: normalizeOptionalSquareFootageDraftValue(event.target.value) })
                  }
                />
              </Field>
            </div>
            <div className="customer-directory-actions">
              <Button variant="ghost" onClick={() => setRows(rows.filter((_, rowIndex) => rowIndex !== index))}>
                Delete row
              </Button>
            </div>
          </div>
        ))}
        <div className="customer-directory-actions">
          <Button
            variant="secondary"
            onClick={() =>
              setRows([
                ...rows,
                {
                  propertyGroup: "apartment_condo",
                  areaCode: "kitchen",
                  label: "Kitchen",
                  laborMinutes: 30,
                  displayOrder: rows.length ? Math.max(...rows.map((row) => Number(row.displayOrder ?? 0))) + 1 : 0
                }
              ])
            }
          >
            Add skip-area row
          </Button>
          <Button
            variant="ghost"
            onClick={() => setRows(defaultResidentialSkipAreaDurationRules)}
          >
            Reset defaults
          </Button>
        </div>
      </div>
    </Card>
  );
}

function singularizeTitle(title: string) {
  if (title.endsWith("ies")) {
    return `${title.slice(0, -3)}y`;
  }

  if (title.endsWith("s")) {
    return title.slice(0, -1);
  }

  return title;
}

function sortSectionItems(section: string, items: SettingsValue[]) {
  if (section === "frequency-rules" || section === "notification-rules" || section === "service-scenarios") {
    return [...items].sort((left, right) => {
      const orderDelta = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
      if (orderDelta !== 0) {
        return orderDelta;
      }

      if (section === "frequency-rules" && Boolean(left.isDefault) !== Boolean(right.isDefault)) {
        return left.isDefault ? -1 : 1;
      }

      if (section === "service-scenarios") {
        const priorityDelta = Number(right.priority ?? 0) - Number(left.priority ?? 0);
        if (priorityDelta !== 0) {
          return priorityDelta;
        }
      }

      return String(left.name ?? "").localeCompare(String(right.name ?? ""));
    });
  }

  if (section === "staff-types") {
    return [...items].sort((left, right) => {
      const orderDelta = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
      if (orderDelta !== 0) {
        return orderDelta;
      }

      return String(left.name ?? "").localeCompare(String(right.name ?? ""));
    });
  }

  if (section === "tags") {
    return [...items].sort((left, right) => {
      const orderDelta = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
      if (orderDelta !== 0) {
        return orderDelta;
      }

      return String(left.name ?? "").localeCompare(String(right.name ?? ""));
    });
  }

  return items;
}

const staffSettingsRouteTitles: Record<string, string> = {
  staff: "Staff Directory",
  "staff-time-off": "Staff Vacations",
  "salary-methods": "Salary Methods",
  "staff-types": "Staff Types"
};

function isStaffSettingsRoute(section: string) {
  return section in staffSettingsRouteTitles;
}

function getSettingsSectionTitle(section: string, label: string) {
  return staffSettingsRouteTitles[section] ?? `Settings: ${label}`;
}

function getSettingsSectionDescription(section: string, isSingleton: boolean) {
  if (section === "staff") {
    return "Manage worker profiles, availability, compensation links, and scheduling eligibility from the staff directory.";
  }
  if (section === "staff-time-off") {
    return "Track vacations and time-off records that affect scheduling availability.";
  }
  if (section === "salary-methods") {
    return "Maintain compensation formulas used for payroll previews and staff pay rules.";
  }
  if (section === "staff-types") {
    return "Maintain reusable staff categories for directory views, availability matching, and staff creation.";
  }
  return isSingleton
    ? "Tune this settings profile while keeping the existing editor and save behavior intact."
    : "Manage this settings directory with record counts, active status, and the existing structured editor.";
}

function isActiveSettingsRecord(record: SettingsValue) {
  const raw = record.active;
  if (raw === false || raw === 0) {
    return false;
  }
  const normalized = String(raw ?? "true").trim().toLowerCase();
  return normalized !== "false" && normalized !== "0" && normalized !== "inactive";
}

function isArchivedStaffRecord(record: SettingsValue) {
  const raw = record.isArchived;
  if (raw === true || raw === 1) {
    return true;
  }
  const normalized = String(raw ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "archived";
}

function hasMeaningfulSettingsValue(value: unknown) {
  if (value == null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "object") {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }
  if (typeof value === "boolean") {
    return value;
  }
  return String(value).trim().length > 0;
}

function metricValue(queryLoading: boolean, value: number | string) {
  return queryLoading ? "..." : value;
}

function normalizeStaffDirectoryCategory(value: unknown): StaffDirectoryCategory | "" {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) {
    return "";
  }
  if (normalized.includes("driver")) {
    return "drivers";
  }
  if (normalized.includes("office") || normalized.includes("admin")) {
    return "office";
  }
  if (normalized.includes("maid") || normalized.includes("cleaner") || normalized.includes("cleaning")) {
    return "maids";
  }
  return "";
}

function resolveStaffDirectoryMetricCategory(
  staffMember: SettingsValue,
  staffTypeById: Map<string, SettingsValue>
): StaffDirectoryCategory {
  const type = staffTypeById.get(String(staffMember.staffTypeId ?? ""));
  return (
    normalizeStaffDirectoryCategory(type?.directoryCategory) ||
    normalizeStaffDirectoryCategory(type?.name) ||
    normalizeStaffDirectoryCategory(staffMember.directoryCategory) ||
    normalizeStaffDirectoryCategory(staffMember.workerGroup) ||
    "maids"
  );
}

function getTimeOffEndDate(record: SettingsValue) {
  return String(record.endDate ?? record.endsAt ?? record.end ?? record.startDate ?? record.date ?? "").slice(0, 10);
}

function SettingsSectionMetrics({ section, isSingleton }: { section: string; isSingleton: boolean }) {
  const isRelational = section in relationalSectionConfigs;
  const relationalQuery = useQuery({
    queryKey: ["settings", section],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>(`/api/settings/${section}`),
    enabled: isRelational
  });
  const singletonQuery = useQuery({
    queryKey: ["settings", section],
    queryFn: () => apiFetch<SettingsValue>(`/api/settings/${section}`),
    enabled: isSingleton
  });
  const staffTypesQuery = useQuery({
    queryKey: ["settings", "staff-types"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/staff-types"),
    enabled: section === "staff"
  });
  const staffLookupQuery = useQuery({
    queryKey: ["settings", "staff", "lookup"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/staff"),
    enabled: section === "staff-time-off"
  });

  if (section === "staff") {
    const staff = sortSectionItems("staff", relationalQuery.data?.items ?? []);
    const activeStaff = staff.filter((item) => isActiveSettingsRecord(item) && !isArchivedStaffRecord(item));
    const staffTypeById = new Map(
      (staffTypesQuery.data?.items ?? []).map((item) => [String(item.id ?? ""), item] as const)
    );
    const maids = activeStaff.filter((item) => resolveStaffDirectoryMetricCategory(item, staffTypeById) === "maids").length;
    const drivers = activeStaff.filter((item) => resolveStaffDirectoryMetricCategory(item, staffTypeById) === "drivers").length;
    const office = activeStaff.filter((item) => resolveStaffDirectoryMetricCategory(item, staffTypeById) === "office").length;
    const lowRatingFollowUp = activeStaff.filter((item) => {
      const lowRatingCount = Number(item.lowRatingCount ?? 0);
      const ratingCount = Number(item.ratingCount ?? 0);
      const averageRating = Number(item.averageRating ?? 0);
      return lowRatingCount > 0 || (ratingCount > 0 && averageRating > 0 && averageRating < 4);
    }).length;

    return (
      <OpsMetricGrid>
        <OpsMetricCard label="Total staff" value={metricValue(relationalQuery.isLoading, staff.length)} icon={Users} tone="blue" />
        <OpsMetricCard
          label="Active staff"
          value={metricValue(relationalQuery.isLoading, activeStaff.length)}
          detail={`${maids} maids, ${drivers} drivers, ${office} office`}
          icon={UserCheck}
          tone="emerald"
        />
        <OpsMetricCard
          label="Inactive / archived"
          value={metricValue(relationalQuery.isLoading, Math.max(0, staff.length - activeStaff.length))}
          detail="Kept for records but not active for scheduling."
          icon={UserX}
          tone={staff.length - activeStaff.length > 0 ? "amber" : "slate"}
        />
        <OpsMetricCard
          label="Low-rating follow-up"
          value={metricValue(relationalQuery.isLoading, lowRatingFollowUp)}
          detail="Staff with low-rating signals already present in the directory data."
          icon={Star}
          tone={lowRatingFollowUp > 0 ? "amber" : "emerald"}
        />
      </OpsMetricGrid>
    );
  }

  if (section === "staff-time-off") {
    const records = sortSectionItems("staff-time-off", relationalQuery.data?.items ?? []);
    const activeStaff = (staffLookupQuery.data?.items ?? []).filter(
      (item) => isActiveSettingsRecord(item) && !isArchivedStaffRecord(item)
    );
    const today = getLocalIsoDate();
    const upcoming = records.filter((record) => {
      const date = getTimeOffEndDate(record);
      return date ? date >= today : false;
    });
    const affectedStaff = new Set(upcoming.map((record) => String(record.staffId ?? "")).filter(Boolean));

    return (
      <OpsMetricGrid>
        <OpsMetricCard label="Time-off records" value={metricValue(relationalQuery.isLoading, records.length)} icon={CalendarDays} tone="blue" />
        <OpsMetricCard label="Upcoming time off" value={metricValue(relationalQuery.isLoading, upcoming.length)} icon={CalendarClock} tone="amber" />
        <OpsMetricCard label="Staff affected" value={metricValue(relationalQuery.isLoading, affectedStaff.size)} icon={Users} tone="violet" />
        <OpsMetricCard label="Active staff" value={metricValue(staffLookupQuery.isLoading, activeStaff.length)} icon={UserCheck} tone="emerald" />
      </OpsMetricGrid>
    );
  }

  if (section === "salary-methods" || section === "staff-types") {
    const records = sortSectionItems(section, relationalQuery.data?.items ?? []);
    const activeRecords = records.filter(isActiveSettingsRecord);
    const config = relationalSectionConfigs[section];
    return (
      <OpsMetricGrid>
        <OpsMetricCard label="Total records" value={metricValue(relationalQuery.isLoading, records.length)} icon={Database} tone="blue" />
        <OpsMetricCard label="Active records" value={metricValue(relationalQuery.isLoading, activeRecords.length)} icon={CheckCircle2} tone="emerald" />
        <OpsMetricCard
          label="Inactive records"
          value={metricValue(relationalQuery.isLoading, Math.max(0, records.length - activeRecords.length))}
          icon={AlertTriangle}
          tone={records.length - activeRecords.length > 0 ? "amber" : "slate"}
        />
        <OpsMetricCard label="Editor fields" value={config.fields.length} icon={SlidersHorizontal} tone="slate" />
      </OpsMetricGrid>
    );
  }

  if (isRelational) {
    const config = relationalSectionConfigs[section];
    const records = sortSectionItems(section, relationalQuery.data?.items ?? []);
    const activeRecords = records.filter(isActiveSettingsRecord);
    return (
      <OpsMetricGrid>
        <OpsMetricCard label="Total records" value={metricValue(relationalQuery.isLoading, records.length)} icon={Database} tone="blue" />
        <OpsMetricCard label="Active records" value={metricValue(relationalQuery.isLoading, activeRecords.length)} icon={CheckCircle2} tone="emerald" />
        <OpsMetricCard
          label="Needs review"
          value={metricValue(relationalQuery.isLoading, Math.max(0, records.length - activeRecords.length))}
          detail="Inactive or disabled records in this section."
          icon={AlertTriangle}
          tone={records.length - activeRecords.length > 0 ? "amber" : "slate"}
        />
        <OpsMetricCard label="Editor fields" value={config.fields.length} icon={SlidersHorizontal} tone="slate" />
      </OpsMetricGrid>
    );
  }

  if (isSingleton) {
    const config = singletonSectionConfigs[section];
    const settings = singletonQuery.data ?? {};
    const configuredFields = config.fields.filter((field) => hasMeaningfulSettingsValue(getPathValue(settings, field.key))).length;
    const enabledSwitches = config.fields.filter(
      (field) => field.kind === "checkbox" && Boolean(getPathValue(settings, field.key))
    ).length;
    return (
      <OpsMetricGrid>
        <OpsMetricCard
          label="Section status"
          value={singletonQuery.isLoading ? "Loading" : singletonQuery.error ? "Review" : "Ready"}
          detail="Loaded from the existing settings profile."
          icon={ShieldCheck}
          tone={singletonQuery.error ? "amber" : "emerald"}
        />
        <OpsMetricCard label="Configured fields" value={metricValue(singletonQuery.isLoading, configuredFields)} icon={ClipboardList} tone="blue" />
        <OpsMetricCard label="Editable fields" value={config.fields.length} icon={SlidersHorizontal} tone="slate" />
        <OpsMetricCard label="Enabled switches" value={metricValue(singletonQuery.isLoading, enabledSwitches)} icon={CheckCircle2} tone="violet" />
      </OpsMetricGrid>
    );
  }

  return null;
}

function buildDuplicateLabel(base: string, existingValues: string[]) {
  const trimmedBase = base.trim() || "Copy";
  const existingSet = new Set(existingValues.map((value) => value.trim().toLowerCase()).filter(Boolean));
  if (!existingSet.has(trimmedBase.toLowerCase())) {
    return trimmedBase;
  }

  let counter = 2;
  while (existingSet.has(`${trimmedBase} ${counter}`.toLowerCase())) {
    counter += 1;
  }

  return `${trimmedBase} ${counter}`;
}

function normalizeSettingsProfileValue(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getScenarioRemodelPricingOptionsForDraft(draft: SettingsValue) {
  const remodelProfile = normalizeSettingsProfileValue(getPathValue(draft, "remodelProfile"));
  const questionProfile = normalizeSettingsProfileValue(getPathValue(draft, "questionProfile"));
  const resolvedProfile =
    remodelProfile && remodelProfile !== "none"
      ? remodelProfile
      : questionProfile === "office_commercial" || questionProfile === "building_common_areas"
        ? questionProfile
        : "residential";

  switch (resolvedProfile) {
    case "office_commercial":
      return officeCommercialRemodelPricingOptions;
    case "building_common_areas":
      return buildingCommonAreasRemodelPricingOptions;
    default:
      return residentialRemodelPricingOptions;
  }
}

function renderFieldValue(field: FieldConfig, draft: SettingsValue, setDraft: (updater: (current: SettingsValue) => SettingsValue) => void) {
  const value = getPathValue(draft, field.key);
  if (field.kind === "csv" && field.multiline) {
    return (
      <textarea
        rows={field.rows ?? 4}
        value={String(value ?? "")}
        onChange={(event) => setDraft((current) => setPathValue(current, field.key, event.target.value))}
        placeholder={field.placeholder}
      />
    );
  }
  switch (field.kind) {
    case "textarea":
    case "json":
      return (
        <textarea
          rows={field.rows ?? 5}
          value={String(value ?? "")}
          onChange={(event) => setDraft((current) => setPathValue(current, field.key, event.target.value))}
          placeholder={field.placeholder}
        />
      );
    case "checkbox":
      return (
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(event) => setDraft((current) => setPathValue(current, field.key, event.target.checked))}
          />
          <span>{field.hint ?? field.label}</span>
        </label>
      );
    case "select":
      return (
        <select
          value={String(value ?? "")}
          onChange={(event) => setDraft((current) => setPathValue(current, field.key, event.target.value))}
        >
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    default:
      if (field.kind === "date") {
        return (
          <DateInput
            step={field.step}
            value={String(value ?? "")}
            onChange={(event) => setDraft((current) => setPathValue(current, field.key, event.target.value))}
            placeholder={field.placeholder}
          />
        );
      }

      return (
        <input
          type={field.kind === "number" ? "number" : field.kind}
          step={field.step}
          value={String(value ?? "")}
          onChange={(event) => setDraft((current) => setPathValue(current, field.key, event.target.value))}
          placeholder={field.placeholder}
        />
      );
  }
}

function isSvgInvoiceLogoFile(file: File) {
  return String(file.type ?? "").toLowerCase().includes("svg") || String(file.name ?? "").toLowerCase().endsWith(".svg");
}

function isRasterInvoiceLogoFile(file: File) {
  const contentType = String(file.type ?? "").toLowerCase();
  const name = String(file.name ?? "").toLowerCase();
  return contentType.includes("png") || contentType.includes("jpeg") || contentType.includes("jpg") || name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg");
}

function fileToInvoiceLogoDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Your browser could not read this logo file."));
      }
    };
    reader.onerror = () => reject(new Error("Your browser could not read this logo file."));
    reader.readAsDataURL(file);
  });
}

function readSvgLogoSize(svgText: string) {
  const numberFromAttribute = (name: string) => {
    const match = svgText.match(new RegExp(`${name}\\s*=\\s*["']?([0-9.]+)`, "i"));
    return match ? Number(match[1]) : 0;
  };
  const viewBoxMatch = svgText.match(/viewBox\s*=\s*["']\s*[-0-9.]+\s+[-0-9.]+\s+([0-9.]+)\s+([0-9.]+)/i);
  const width = numberFromAttribute("width") || (viewBoxMatch ? Number(viewBoxMatch[1]) : 0) || 640;
  const height = numberFromAttribute("height") || (viewBoxMatch ? Number(viewBoxMatch[2]) : 0) || 320;
  const scale = Math.min(1, 1200 / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
}

async function convertSvgInvoiceLogoToPng(file: File) {
  const svgText = await file.text();
  const { width, height } = readSvgLogoSize(svgText);
  const svgUrl = URL.createObjectURL(new Blob([svgText], { type: "image/svg+xml;charset=utf-8" }));
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("This SVG logo could not be converted. Try exporting it as a PNG and upload that file."));
      element.src = svgUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || width;
    canvas.height = image.naturalHeight || height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Your browser could not prepare the logo for upload.");
    }
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => (result ? resolve(result) : reject(new Error("Your browser could not convert this SVG logo to PNG."))), "image/png");
    });
    const baseName = String(file.name || "invoice-logo").replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_.-]+/g, "_") || "invoice-logo";
    return new File([blob], `${baseName}.png`, { type: "image/png" });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

async function prepareInvoiceLogoDataUrl(file: File) {
  const logoFile = isSvgInvoiceLogoFile(file) ? await convertSvgInvoiceLogoToPng(file) : file;
  if (!isRasterInvoiceLogoFile(logoFile)) {
    throw new Error("Upload a PNG, JPG, or SVG logo file.");
  }
  if (Number(logoFile.size ?? 0) > 2 * 1024 * 1024) {
    throw new Error("Invoice logos must be 2 MB or smaller.");
  }
  return fileToInvoiceLogoDataUrl(logoFile);
}

function InvoiceTemplatePreview({ draft }: { draft: SettingsValue }) {
  const getText = (key: string, fallback = "") => String(getPathValue(draft, key) ?? fallback).trim() || fallback;
  const isShown = (key: string) => getPathValue(draft, key) !== false;
  const businessName = getText("businessDisplayName", "AccessMaids");
  const businessAddress = getText("businessAddress", "9119 McVicker Ave, Morton Grove, IL 60053");
  const logoUrl = getText("logoUrl");
  const [logoFailed, setLogoFailed] = useState(false);
  const accentColor = /^#[0-9a-fA-F]{6}$/.test(getText("templateAccentColor")) ? getText("templateAccentColor") : "#123d32";
  const renderTemplateText = (value: string) =>
    value
      .replace(/\{\{businessName\}\}/g, businessName)
      .replace(/\{\{customerName\}\}/g, "Emily Escarra")
      .replace(/\{\{invoiceNumber\}\}/g, `${getText("invoicePrefix", "INV")}-1001`)
      .replace(/\{\{bookingReference\}\}/g, "BK-0426")
      .replace(/\{\{amountDue\}\}/g, "$145.00")
      .replace(/\{\{paymentUrl\}\}/g, "secure payment link")
      .replace(/\{\{pdfUrl\}\}/g, "PDF link");

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  return (
    <div className="invoice-template-preview">
      <div className="invoice-template-preview__page">
        <div className="invoice-template-preview__header" style={{ background: accentColor }}>
          <div className="invoice-template-preview__brand">
            {logoUrl ? (
              <span className={`invoice-template-preview__logo${logoFailed ? " invoice-template-preview__logo--failed" : ""}`}>
                {logoFailed ? (
                  <span>Logo unavailable</span>
                ) : (
                  <img src={logoUrl} alt="Invoice logo preview" onError={() => setLogoFailed(true)} />
                )}
              </span>
            ) : null}
            <div>
              <strong>{businessName}</strong>
              {isShown("showCompanyContact") ? (
                <>
                  <span>{businessAddress}</span>
                  <span>accessmaids.com - (312) 363-8714 - support@accessmaids.com</span>
                </>
              ) : null}
            </div>
          </div>
          <div className="invoice-template-preview__summary">
            <strong>{getText("invoicePrefix", "INV")}-1001</strong>
            <span>Due on receipt</span>
            <b>$145.00</b>
          </div>
        </div>

        <div className="invoice-template-preview__grid">
          {isShown("showBillTo") ? (
            <section>
              <h4>Bill to</h4>
              <p>Emily Escarra</p>
              <p>2332 W Augusta Blvd</p>
              <p>Chicago, IL 60622</p>
              <p>(773) 698-0744</p>
              <p>emily@example.com</p>
            </section>
          ) : null}
          <section>
            {isShown("showServiceAddress") ? (
              <>
                <h4>Service address</h4>
                <p>2332 W Augusta Blvd, Chicago, IL</p>
              </>
            ) : null}
            {isShown("showAppointment") ? (
              <>
                <h4>Appointment</h4>
                <p>Apr 26, 2026 · 7:30-8 AM</p>
                <p>Apartment / condo · One-time</p>
              </>
            ) : null}
            {isShown("showMaidNames") ? (
              <>
                <h4>Maids</h4>
                <p>Svitlana B., Olena M.</p>
              </>
            ) : null}
          </section>
        </div>

        {isShown("showLineItems") ? (
          <table className="invoice-template-preview__table">
            <thead>
              <tr>
                <th>Services</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Apartment / condo cleaning
                  <small>1 hour 30 minutes · 2 maids</small>
                </td>
                <td>1</td>
                <td>$145.00</td>
                <td>$145.00</td>
              </tr>
            </tbody>
          </table>
        ) : null}

        {isShown("showPayments") ? (
          <div className="invoice-template-preview__totals">
            <span>Subtotal</span>
            <strong>$145.00</strong>
            <span>Paid</span>
            <strong>$0.00</strong>
            <b>Amount due</b>
            <b>$145.00</b>
          </div>
        ) : null}

        <div className="invoice-template-preview__notes">
          {isShown("showMemo") && getText("defaultMemo") ? <p>{renderTemplateText(getText("defaultMemo"))}</p> : null}
          {isShown("showPaymentInstructions") && getText("defaultPaymentInstructions") ? (
            <p>{renderTemplateText(getText("defaultPaymentInstructions"))}</p>
          ) : null}
          {isShown("showTermsLink") ? <p className="invoice-template-preview__link">Terms & Conditions</p> : null}
        </div>

        {isShown("showFooter") ? (
          <footer>
            <span>{renderTemplateText(getText("defaultFooter", "Please contact us with any invoice questions."))}</span>
            <span>{businessName}</span>
          </footer>
        ) : null}
      </div>
    </div>
  );
}

function normalizeAvailabilityDays(value: unknown, fallbackText?: string) {
  const normalized = (Array.isArray(value) ? value.map((entry) => String(entry ?? "").trim()) : splitLooseDelimitedText(value ?? fallbackText ?? ""))
    .map((entry) => entry.toLowerCase())
    .map((entry) => {
      const match = staffWeekdayOptions.find(
        (option) =>
          option.value.toLowerCase() === entry ||
          option.short.toLowerCase() === entry ||
          option.label.toLowerCase() === entry
      );
      return match?.value ?? "";
    })
    .filter(Boolean);

  return Array.from(new Set(normalized));
}

function describeAvailabilityDays(days: string[]) {
  const labels = days
    .map((day) => staffWeekdayOptions.find((option) => option.value === day)?.short ?? "")
    .filter(Boolean);
  return labels.join(", ");
}

function getStaffDraftBase(source: SettingsValue | undefined, defaults: SettingsValue) {
  const merged = { ...defaults, ...(source ?? {}) };
  const fullName = String(source?.fullName ?? source?.name ?? "").trim();
  const [derivedFirstName = "", ...rest] = fullName.split(/\s+/).filter(Boolean);
  const availabilityDays = normalizeAvailabilityDays(source?.availabilityDays, String(source?.availabilityText ?? ""));
  const equipmentRecords = Array.isArray(source?.equipmentRecords)
    ? source?.equipmentRecords
    : String(source?.equipmentAssigned ?? "")
        .split(/\r?\n/)
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map((equipmentName) => ({ assignedDate: "", equipmentName }));
  return hydrateDraft(relationalSectionConfigs.staff, {
    ...merged,
    firstName: String(source?.firstName ?? derivedFirstName),
    lastName: String(source?.lastName ?? rest.join(" ")),
    address1: String(source?.address1 ?? source?.homeAddress ?? ""),
    address2: String(source?.address2 ?? ""),
    city: String(source?.city ?? ""),
    state: String(source?.state ?? ""),
    postalCode: String(source?.postalCode ?? ""),
    country: String(source?.country ?? "US"),
    availabilityDays,
    availabilityText: source?.availabilityText ?? describeAvailabilityDays(availabilityDays),
    availabilityStartTime: String(source?.availabilityStartTime ?? "08:00"),
    availabilityEndTime: String(source?.availabilityEndTime ?? "17:00"),
    assignedLocationIds: normalizeStringList(source?.assignedLocationIds),
    assignedServiceIds: normalizeStringList(source?.assignedServiceIds ?? source?.serviceSkillIds),
    preferredTeamMemberId: String(source?.preferredTeamMemberId ?? ""),
    tagIds: Array.isArray(source?.tagIds) ? source?.tagIds : [],
    cannotWorkWithStaffIds: Array.isArray(source?.cannotWorkWithStaffIds)
      ? source?.cannotWorkWithStaffIds
      : String(source?.cannotWorkWithCode ?? "")
          .split(",")
          .map((entry) => entry.trim())
          .filter(Boolean),
    vehicleColor: String(source?.vehicleColor ?? ""),
    odometer: String(source?.odometer ?? ""),
    equipmentRecords
  });
}

function centsToDollarInput(value: unknown) {
  return (Number(value ?? 0) / 100).toFixed(2);
}

function dollarsToCents(value: unknown) {
  const normalized = Number.parseFloat(String(value ?? "0").replace(/[^0-9.]/g, ""));
  return Number.isFinite(normalized) ? Math.round(normalized * 100) : 0;
}

function buildPriceAdjusterPayload(draft: PriceAdjusterDraft): PriceAdjusterInput {
  const rawPercent = Number.parseFloat(String(draft.percent ?? "0").replace(/[^0-9.]/g, ""));
  const percent = Number.isFinite(rawPercent) ? Math.max(0, rawPercent) : 0;
  return {
    direction: draft.direction,
    percent: draft.direction === "decrease" ? Math.min(percent, 100) : Math.min(percent, 500),
    roundToNearestDollar: draft.roundToNearestDollar,
    serviceIds: normalizeStringList(draft.serviceIds),
    extraIds: normalizeStringList(draft.extraIds)
  };
}

function normalizeStringList(value: unknown) {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }

  return String(value ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function normalizeNumberList(value: unknown) {
  if (!Array.isArray(value)) {
    return [] as number[];
  }

  return value
    .map((entry) => Number(entry))
    .filter((entry) => Number.isFinite(entry))
    .map((entry) => Math.floor(entry));
}

function getServiceDraftBase(source: SettingsValue | undefined, defaults: SettingsValue) {
  const merged = { ...defaults, ...(source ?? {}) };
  return {
    ...hydrateDraft(relationalSectionConfigs.services, merged),
    basePriceDollars: centsToDollarInput(source?.basePriceAmount ?? merged.basePriceAmount),
    assignedExtraIds: normalizeStringList(source?.assignedExtraIds ?? merged.assignedExtraIds),
    availableLocationIds: normalizeStringList(source?.availableLocationIds ?? merged.availableLocationIds),
    allowedFrequencyRuleIds: normalizeStringList(source?.allowedFrequencyRuleIds ?? merged.allowedFrequencyRuleIds),
    allowAllActiveFrequencyRules:
      source?.allowAllActiveFrequencyRules == null
        ? merged.allowAllActiveFrequencyRules == null
          ? true
          : Boolean(merged.allowAllActiveFrequencyRules)
        : Boolean(source.allowAllActiveFrequencyRules),
    pricingRules:
      typeof (source?.pricingRules ?? merged.pricingRules) === "string"
        ? String(source?.pricingRules ?? merged.pricingRules ?? "{}")
        : JSON.stringify(source?.pricingRules ?? merged.pricingRules ?? {}, null, 2),
    extraVisibilityRules:
      typeof (source?.extraVisibilityRules ?? merged.extraVisibilityRules) === "string"
        ? String(source?.extraVisibilityRules ?? merged.extraVisibilityRules ?? "{}")
        : JSON.stringify(source?.extraVisibilityRules ?? merged.extraVisibilityRules ?? {}, null, 2)
  };
}

function normalizeBuildingCommonAreasPricingRulesDraft(
  value: unknown
): BuildingCommonAreasPricingRuleDraft[] {
  const provided = Array.isArray(value)
    ? value.filter(
        (entry): entry is Record<string, unknown> =>
          Boolean(entry) && typeof entry === "object" && !Array.isArray(entry)
      )
    : [];
  const providedByBucket = new Map<string, Record<string, unknown>>();

  for (const entry of provided) {
    const bucket = normalizeBuildingApartmentsBucket(
      entry.bucket ?? entry.apartmentBucket ?? entry.buildingApartmentsBucket ?? entry.label
    );
    if (bucket) {
      providedByBucket.set(bucket, entry);
    }
  }

  return normalizeBuildingCommonAreasPricingRules(value).map((rule) => {
    const source = providedByBucket.get(rule.bucket);
    return {
      bucket: rule.bucket,
      label: String(source?.label ?? rule.label),
      pricingMode: rule.pricingMode,
      basePriceDollars:
        source?.basePriceDollars != null
          ? String(source.basePriceDollars)
          : centsToDollarInput(rule.basePriceCents),
      durationMinutes:
        source?.durationMinutes != null
          ? String(source.durationMinutes)
          : String(rule.baseMinutes),
      displayOrder: Number(source?.displayOrder ?? rule.displayOrder)
    };
  });
}

function getServiceScenarioDraftBase(
  source: SettingsValue | undefined,
  defaults: SettingsValue
): SettingsValue {
  const merged = { ...defaults, ...(source ?? {}) };
  return {
    ...hydrateDraft(relationalSectionConfigs["service-scenarios"], merged),
    buildingCommonAreasMinimumChargeDollars: centsToDollarInput(
      source?.buildingCommonAreasMinimumCharge ?? defaultBuildingCommonAreasMinimumChargeCents
    ),
    buildingCommonAreasPricingRules: normalizeBuildingCommonAreasPricingRulesDraft(
      source?.buildingCommonAreasPricingRules ?? merged.buildingCommonAreasPricingRules
    )
  };
}

function isBuildingCommonAreasScenarioDraft(draft: SettingsValue, services: SettingsValue[]) {
  const questionProfile = normalizeSettingsProfileValue(getPathValue(draft, "questionProfile"));
  if (questionProfile === "building_common_areas") {
    return true;
  }

  const scenarioName = String(getPathValue(draft, "name") ?? "")
    .trim()
    .toLowerCase();
  const scenarioCode = normalizeSettingsProfileValue(getPathValue(draft, "code"));
  if (scenarioName.includes("building common areas") || scenarioCode.includes("building_common_areas")) {
    return true;
  }

  const baseServiceId = String(getPathValue(draft, "baseServiceId") ?? "");
  const serviceName = String(
    services.find((service) => String(service.id ?? "") === baseServiceId)?.name ?? ""
  )
    .trim()
    .toLowerCase();
  return serviceName.includes("building common areas");
}

function buildServiceScenarioPayload(
  payload: SettingsValue,
  draft: SettingsValue,
  services: SettingsValue[]
): SettingsValue {
  const nextPayload = { ...payload };
  if (!isBuildingCommonAreasScenarioDraft(draft, services)) {
    delete nextPayload.buildingCommonAreasMinimumCharge;
    delete nextPayload.buildingCommonAreasPricingRules;
    return nextPayload;
  }

  const minimumChargeInput = String(
    getPathValue(draft, "buildingCommonAreasMinimumChargeDollars") ?? ""
  ).trim();

  nextPayload.buildingCommonAreasMinimumCharge = minimumChargeInput
    ? dollarsToCents(minimumChargeInput)
    : defaultBuildingCommonAreasMinimumChargeCents;
  nextPayload.buildingCommonAreasPricingRules = normalizeBuildingCommonAreasPricingRulesDraft(
    getPathValue(draft, "buildingCommonAreasPricingRules")
  ).map((rule) => ({
    bucket: rule.bucket,
    label: rule.label,
    pricingMode: rule.pricingMode,
    basePriceAmount: dollarsToCents(rule.basePriceDollars),
    durationMinutes: Math.max(0, Number(rule.durationMinutes || 0)),
    displayOrder: Number(rule.displayOrder ?? 0)
  }));

  return nextPayload;
}

function getExtraDraftBase(source: SettingsValue | undefined, defaults: SettingsValue) {
  const merged = { ...defaults, ...(source ?? {}) };
  const durationMinutes = Number(source?.durationDeltaMinutes ?? merged.durationDeltaMinutes ?? 0);
  return {
    ...hydrateDraft(relationalSectionConfigs.extras, merged),
    priceDeltaDollars: centsToDollarInput(source?.priceDeltaAmount ?? merged.priceDeltaAmount),
    durationHours: Math.floor(durationMinutes / 60),
    durationMinutesRemainder: durationMinutes % 60,
    assignedServiceIds: normalizeStringList(source?.assignedServiceIds ?? merged.assignedServiceIds),
    extraPricingRules: normalizeExtraPricingRules(source?.extraPricingRules ?? merged.extraPricingRules),
    carpetShampooingConfig: normalizeCarpetShampooingConfig(source?.carpetShampooingConfig ?? merged.carpetShampooingConfig)
  };
}

function normalizeExtraPricingRules(value: unknown): ExtraPricingRuleDraft[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object" && !Array.isArray(entry))
    .map((entry, index) => ({
      id: String(entry.id ?? `rule_${index + 1}`),
      serviceIds: normalizeStringList(entry.serviceIds),
      squareFootageFrom: String(entry.squareFootageFrom ?? ""),
      squareFootageTo: String(entry.squareFootageTo ?? ""),
      bedroomsFrom: String(entry.bedroomsFrom ?? ""),
      bedroomsTo: String(entry.bedroomsTo ?? ""),
      levelsFrom: String(entry.levelsFrom ?? ""),
      levelsTo: String(entry.levelsTo ?? ""),
      priceDeltaDollars: centsToDollarInput(entry.priceDeltaAmount ?? 0),
      durationMinutes: String(entry.durationDeltaMinutes ?? 0),
      displayOrder: Number(entry.displayOrder ?? index),
      active: entry.active !== false && entry.active !== 0
    }));
}

function normalizeCarpetShampooingUnits(value: unknown): CarpetShampooingUnitDraft[] {
  const provided = Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object" && !Array.isArray(entry))
    : [];

  const providedByCode = new Map(provided.map((entry) => [String(entry.code ?? ""), entry]));

  return carpetShampooingUnitDefaults.map((fallbackUnit) => {
    const source = providedByCode.get(fallbackUnit.code);
    const priceDeltaAmount = source?.priceDeltaAmount ?? source?.priceDeltaDollars ?? "";
    const durationDeltaMinutes = source?.durationDeltaMinutes ?? source?.durationMinutes ?? "";
    const maxQuantity = source?.maxQuantity ?? "";

    return {
      code: fallbackUnit.code,
      label: String(source?.label ?? fallbackUnit.label),
      priceDeltaDollars:
        typeof priceDeltaAmount === "number"
          ? centsToDollarInput(priceDeltaAmount)
          : String(priceDeltaAmount || fallbackUnit.priceDeltaDollars),
      durationMinutes: String(durationDeltaMinutes || fallbackUnit.durationMinutes),
      maxQuantity: String(maxQuantity || fallbackUnit.maxQuantity),
      displayOrder: Number(source?.displayOrder ?? fallbackUnit.displayOrder),
      active: source?.active == null ? fallbackUnit.active : Boolean(source.active)
    };
  });
}

function normalizeCarpetShampooingConfig(value: unknown): CarpetShampooingConfigDraft {
  const source = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  const minimumOrderAmount = source.minimumOrderAmount ?? source.minimumOrderDollars ?? "";

  return {
    enabled: Boolean(source.enabled),
    minimumOrderDollars:
      typeof minimumOrderAmount === "number"
        ? centsToDollarInput(minimumOrderAmount)
        : String(minimumOrderAmount || "49.00"),
    units: normalizeCarpetShampooingUnits(source.units)
  };
}

function normalizeLocationServiceAreaRules(value: unknown): LocationServiceAreaRuleDraft[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object" && !Array.isArray(entry))
    .map((entry) => ({
      zipCode: String(entry.zipCode ?? "").replace(/\D/g, "").slice(0, 5),
      earliestStartTime: String(entry.earliestStartTime ?? ""),
      latestStartTime: String(entry.latestStartTime ?? ""),
      allowedWeekdays: normalizeStringList(entry.allowedWeekdays),
      active: entry.active !== false && entry.active !== 0,
      note: String(entry.note ?? "")
    }));
}

function getLocationDraftBase(source: SettingsValue | undefined, defaults: SettingsValue) {
  const merged = { ...defaults, ...(source ?? {}) };
  return {
    ...hydrateDraft(relationalSectionConfigs.locations, merged),
    serviceArea: normalizeStringList(source?.serviceArea ?? merged.serviceArea),
    serviceAreaRules: normalizeLocationServiceAreaRules(source?.serviceAreaRules ?? merged.serviceAreaRules)
  };
}

function getDefaultBookingSpotDailySchedule() {
  return bookingSpotWeekdayOptions.map((day) => ({
    day: day.value,
    enabled: day.value !== "sunday",
    start: "07:30",
    end: day.value === "saturday" ? "13:00" : "15:30",
    breakStart: "",
    breakEnd: ""
  }));
}

const accessMaidsCapacity = 6;
const accessMaidsArrivalWindowEndByStart: Record<string, string> = {
  "07:30": "08:00",
  "09:30": "11:00",
  "11:30": "13:00",
  "13:30": "15:30"
};

function normalizeClockTime(value: unknown, fallback = "00:00") {
  const normalized = String(value ?? "").trim();
  return /^\d{2}:\d{2}$/.test(normalized) ? normalized : fallback;
}

function clockTimeToMinutes(value: unknown) {
  const [hoursText = "0", minutesText = "0"] = normalizeClockTime(value).split(":");
  const hours = Number(hoursText);
  const minutes = Number(minutesText);
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : 0;
}

function minutesToClockTime(value: number) {
  const safeValue = Math.max(0, Math.min(24 * 60 - 1, Math.floor(value)));
  const hours = String(Math.floor(safeValue / 60)).padStart(2, "0");
  const minutes = String(safeValue % 60).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function formatClockTime(value: unknown) {
  const normalized = normalizeClockTime(value);
  const [hoursText = "0", minutesText = "0"] = normalized.split(":");
  const date = new Date(2000, 0, 1, Number(hoursText), Number(minutesText), 0);
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: minutesText === "00" ? undefined : "2-digit",
    hour12: true
  }).format(date);
}

function formatArrivalWindowRange(start: string, end: string) {
  return `${formatClockTime(start)} - ${formatClockTime(end)}`;
}

function getArrivalWindowEnd(start: string, intervalMinutes: number) {
  const normalizedStart = normalizeClockTime(start);
  return accessMaidsArrivalWindowEndByStart[normalizedStart] ?? minutesToClockTime(clockTimeToMinutes(normalizedStart) + intervalMinutes);
}

function buildArrivalWindowPreview(entry: Record<string, unknown>, intervalMinutes: number) {
  if (!Boolean(entry.enabled)) {
    return [] as Array<{ start: string; end: string; label: string }>;
  }

  const startMinutes = clockTimeToMinutes(entry.start ?? "08:00");
  const endMinutes = clockTimeToMinutes(entry.end ?? "17:00");
  const safeInterval = Number.isFinite(intervalMinutes) && intervalMinutes > 0 ? intervalMinutes : 30;
  const windows: Array<{ start: string; end: string; label: string }> = [];

  for (let cursor = startMinutes; cursor <= endMinutes; cursor += safeInterval) {
    const start = minutesToClockTime(cursor);
    const end = getArrivalWindowEnd(start, safeInterval);
    if (clockTimeToMinutes(end) > endMinutes) {
      continue;
    }
    windows.push({ start, end, label: formatArrivalWindowRange(start, end) });
  }

  return windows;
}

function getLocalIsoDate(offsetDays = 0) {
  const value = new Date();
  value.setHours(0, 0, 0, 0);
  value.setDate(value.getDate() + offsetDays);
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDaysToIsoDate(date: string, offsetDays: number) {
  const value = new Date(`${date}T00:00:00`);
  value.setDate(value.getDate() + offsetDays);
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function weekdayForIsoDate(date: string) {
  const value = new Date(`${date}T00:00:00`);
  const day = value.getDay();
  return bookingSpotWeekdayOptions[(day + 6) % 7]?.value ?? "monday";
}

function formatDateLabel(date: string) {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(new Date(`${date}T00:00:00`));
}

function formatDateTimeLabel(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(value));
}

function formatWaitlistPreferredWindowLabel(value: AdminWaitlistItem["preferredWindow"]) {
  switch (value) {
    case "morning":
      return "Morning";
    case "midday":
      return "Midday";
    case "afternoon":
      return "Afternoon";
    default:
      return "Any time";
  }
}

function normalizeBookingSpotDateOverrides(value: unknown): BookingSpotDateOverrideDraft[] {
  return Array.isArray(value)
    ? value
        .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object" && !Array.isArray(entry))
        .map((entry) => {
          const raw = Number(entry.maxJobs ?? "");
          const maxJobs: number | "" = Number.isFinite(raw) && raw > 0 ? raw : "";
          return {
            date: String(entry.date ?? ""),
            closed: Boolean(entry.closed ?? false),
            start: String(entry.start ?? ""),
            end: String(entry.end ?? ""),
            maxJobs,
            note: String(entry.note ?? "")
          };
        })
        .filter((entry) => entry.date)
    : [];
}

function getBookingSpotDraftBase(source: SettingsValue | undefined, defaults: SettingsValue) {
  const merged = { ...defaults, ...(source ?? {}) };
  const operatingHours = Array.isArray(source?.operatingHours) ? source.operatingHours : [];
  const dailyScheduleSource = Array.isArray(source?.dailySchedule) && source?.dailySchedule.length
    ? (source.dailySchedule as Array<Record<string, unknown>>)
    : getDefaultBookingSpotDailySchedule().map((day) => {
        const legacyMatch = operatingHours.find((entry) => String((entry as Record<string, unknown>).day ?? "") === day.day) as
          | Record<string, unknown>
          | undefined;
        if (!legacyMatch) {
          return day;
        }

        return {
          day: day.day,
          enabled: Boolean(legacyMatch.enabled ?? true),
          start: String(legacyMatch.start ?? day.start),
          end: String(legacyMatch.end ?? day.end),
          breakStart: "",
          breakEnd: ""
        };
      });

  const dailySchedule = bookingSpotWeekdayOptions.map((day) => {
    const found = dailyScheduleSource.find((entry) => String(entry.day ?? "").toLowerCase() === day.value);
    return {
      day: day.value,
      enabled: Boolean(found?.enabled ?? false),
      start: String(found?.start ?? "08:00"),
      end: String(found?.end ?? "17:00"),
      breakStart: String(found?.breakStart ?? ""),
      breakEnd: String(found?.breakEnd ?? "")
    };
  });

  const holidayOverrides =
    Array.isArray(source?.holidayOverrides) && source?.holidayOverrides.length
      ? (source.holidayOverrides as Array<Record<string, unknown>>).map((entry) => ({
          date: String(entry.date ?? ""),
          closed: Boolean(entry.closed ?? true),
          start: String(entry.start ?? ""),
          end: String(entry.end ?? ""),
          note: String(entry.note ?? "")
        }))
      : normalizeStringList(source?.blackoutDates ?? merged.blackoutDates).map((date) => ({
          date,
          closed: true,
          start: "",
          end: "",
          note: ""
        }));

  const capacityRules =
    Array.isArray(source?.capacityRules) && source?.capacityRules.length
      ? (source.capacityRules as Array<Record<string, unknown>>).map((entry) => ({
          day: String(entry.day ?? "monday"),
          start: String(entry.start ?? "08:00"),
          end: String(entry.end ?? "17:00"),
          maxJobs: Number(entry.maxJobs ?? 1),
          linkedServiceIds: normalizeStringList(entry.linkedServiceIds)
        }))
      : [];
  const dateOverrides = normalizeBookingSpotDateOverrides(source?.dateOverrides ?? merged.dateOverrides);

  return {
    ...hydrateDraft(relationalSectionConfigs["booking-spots"], merged),
    availabilityMode: String(source?.availabilityMode ?? merged.availabilityMode ?? "manual"),
    teamSize: Number(source?.teamSize ?? merged.teamSize ?? 2),
    fallbackTeamSizes:
      Array.isArray(source?.fallbackTeamSizes) && source?.fallbackTeamSizes.length
        ? source.fallbackTeamSizes.map((entry) => Number(entry)).filter((entry) => Number.isFinite(entry))
        : Boolean(source?.allowSingleStaffFallback ?? merged.allowSingleStaffFallback ?? true)
          ? [1]
          : [],
    allowSingleStaffFallback: Boolean(source?.allowSingleStaffFallback ?? merged.allowSingleStaffFallback ?? true),
    teamBasedCapacityCap:
      source?.teamBasedCapacityCap == null || String(source?.teamBasedCapacityCap ?? "").trim() === ""
        ? ""
        : Number(source?.teamBasedCapacityCap),
    displayMode: String(source?.displayMode ?? merged.displayMode ?? "specific_times"),
    linkedServiceIds: normalizeStringList(source?.linkedServiceIds ?? merged.linkedServiceIds),
    blackoutDates: normalizeStringList(source?.blackoutDates ?? merged.blackoutDates),
    durationPolicyEnabled: Boolean(source?.durationPolicyEnabled ?? merged.durationPolicyEnabled ?? true),
    allowDurationOverlapIntoNextSpot: Boolean(source?.allowDurationOverlapIntoNextSpot ?? merged.allowDurationOverlapIntoNextSpot ?? false),
    blockIfDurationExceedsAvailableTime: Boolean(
      source?.blockIfDurationExceedsAvailableTime ?? merged.blockIfDurationExceedsAvailableTime ?? true
    ),
    outsideNormalAvailabilityPolicy: String(
      source?.outsideNormalAvailabilityPolicy ?? merged.outsideNormalAvailabilityPolicy ?? "count_against_previous_slot"
    ),
    durationRoundingMode: String(source?.durationRoundingMode ?? merged.durationRoundingMode ?? "round_up_to_next_slot"),
    dailySchedule,
    holidayOverrides,
    capacityRules,
    dateOverrides
  };
}

function buildBookingSpotPayloadFromDraft(draft: SettingsValue, defaults: SettingsValue, source?: SettingsValue) {
  const linkedServiceIds = normalizeStringList(getPathValue(draft, "linkedServiceIds"));
  const dailySchedule = (getPathValue(draft, "dailySchedule") as Array<Record<string, unknown>> | undefined) ?? [];
  const holidayOverrides = (getPathValue(draft, "holidayOverrides") as Array<Record<string, unknown>> | undefined) ?? [];
  const capacityRules = (getPathValue(draft, "capacityRules") as Array<Record<string, unknown>> | undefined) ?? [];
  const dateOverrides = normalizeBookingSpotDateOverrides(getPathValue(draft, "dateOverrides"));

  const normalizedDailySchedule = dailySchedule.map((entry) => ({
    day: String(entry.day ?? "monday"),
    enabled: Boolean(entry.enabled),
    start: String(entry.start ?? "08:00"),
    end: String(entry.end ?? "17:00"),
    breakStart: String(entry.breakStart ?? ""),
    breakEnd: String(entry.breakEnd ?? "")
  }));
  const normalizedHolidayOverrides = holidayOverrides
    .map((entry) => ({
      date: String(entry.date ?? ""),
      closed: Boolean(entry.closed ?? true),
      start: String(entry.start ?? ""),
      end: String(entry.end ?? ""),
      note: String(entry.note ?? "")
    }))
    .filter((entry) => entry.date);
  const normalizedCapacityRules = capacityRules
    .map((entry) => ({
      day: String(entry.day ?? "monday"),
      start: String(entry.start ?? "08:00"),
      end: String(entry.end ?? "17:00"),
      maxJobs: Number(entry.maxJobs ?? 1),
      linkedServiceIds: normalizeStringList(entry.linkedServiceIds)
    }))
    .filter((entry) => entry.start && entry.end && Number.isFinite(entry.maxJobs) && entry.maxJobs > 0);
  const normalizedDateOverrides = dateOverrides
    .map((entry) => ({
      date: String(entry.date ?? ""),
      closed: Boolean(entry.closed),
      start: String(entry.start ?? ""),
      end: String(entry.end ?? ""),
      maxJobs: entry.maxJobs === "" ? undefined : Number(entry.maxJobs),
      note: String(entry.note ?? "")
    }))
    .filter((entry) => entry.date);
  const normalizedOperatingHours = normalizedDailySchedule
    .filter((entry) => entry.enabled)
    .map((entry) => ({
      day: entry.day,
      start: entry.start,
      end: entry.end,
      enabled: true
    }));
  const fallbackTeamSizes = Array.from(
    new Set(normalizeNumberList(getPathValue(draft, "fallbackTeamSizes")).filter((entry) => entry >= 1 && entry <= 4))
  );

  return {
    ...defaults,
    ...(source ?? {}),
    name: String(getPathValue(draft, "name") ?? ""),
    locationId: String(getPathValue(draft, "locationId") ?? ""),
    linkedServiceIds,
    availabilityMode: String(getPathValue(draft, "availabilityMode") ?? "manual"),
    teamSize: Number(getPathValue(draft, "teamSize") ?? 2),
    fallbackTeamSizes,
    allowSingleStaffFallback: fallbackTeamSizes.includes(1),
    teamBasedCapacityCap:
      getPathValue(draft, "teamBasedCapacityCap") === "" ? undefined : Number(getPathValue(draft, "teamBasedCapacityCap") ?? 0),
    displayMode: String(getPathValue(draft, "displayMode") ?? "specific_times"),
    capacity: Number(getPathValue(draft, "capacity") ?? 1),
    intervalMinutes: Number(getPathValue(draft, "intervalMinutes") ?? 30),
    maxSimultaneousBookings: Number(getPathValue(draft, "maxSimultaneousBookings") ?? 1),
    operatingHours: normalizedOperatingHours,
    blackoutDates: normalizedHolidayOverrides.filter((entry) => entry.closed).map((entry) => entry.date),
    dailySchedule: normalizedDailySchedule,
    holidayOverrides: normalizedHolidayOverrides,
    capacityRules: normalizedCapacityRules,
    dateOverrides: normalizedDateOverrides,
    durationPolicyEnabled: Boolean(getPathValue(draft, "durationPolicyEnabled") ?? true),
    allowDurationOverlapIntoNextSpot: Boolean(getPathValue(draft, "allowDurationOverlapIntoNextSpot") ?? false),
    blockIfDurationExceedsAvailableTime: Boolean(getPathValue(draft, "blockIfDurationExceedsAvailableTime") ?? true),
    outsideNormalAvailabilityPolicy: String(
      getPathValue(draft, "outsideNormalAvailabilityPolicy") ?? "count_against_previous_slot"
    ),
    durationRoundingMode: String(getPathValue(draft, "durationRoundingMode") ?? "round_up_to_next_slot"),
    active: Boolean(getPathValue(draft, "active"))
  };
}

function parseBooleanValue(value: unknown) {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "y";
}

function parseDelimitedCell(value: unknown) {
  return String(value ?? "")
    .split("|")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function escapeCsvCell(value: unknown) {
  const text = String(value ?? "");
  if (text.includes(",") || text.includes('"') || text.includes("\n") || text.includes("\r")) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function buildServicesCsv(items: SettingsValue[]) {
  const headers = [
    "id",
    "name",
    "icon",
    "category",
    "description",
    "display_order",
    "duration_minutes",
    "base_price_dollars",
    "buffer_before_minutes",
    "buffer_after_minutes",
    "staff_only",
    "active",
    "assigned_extra_ids",
    "available_location_ids",
    "allow_all_active_frequencies",
    "allowed_frequency_rule_ids",
    "default_calendar_profile_id",
    "pricing_rules_json",
    "extra_visibility_rules_json"
  ];

  const rows = items.map((item) =>
    [
      item.id,
      item.name,
      item.icon,
      item.category,
      item.description,
      item.displayOrder ?? 0,
      item.durationMinutes ?? 180,
      centsToDollarInput(item.basePriceAmount ?? 0),
      item.bufferBeforeMinutes ?? 0,
      item.bufferAfterMinutes ?? 30,
      item.staffOnly ? "true" : "false",
      item.active ? "true" : "false",
      normalizeStringList(item.assignedExtraIds).join(" | "),
      normalizeStringList(item.availableLocationIds).join(" | "),
      item.allowAllActiveFrequencyRules ? "true" : "false",
      normalizeStringList(item.allowedFrequencyRuleIds).join(" | "),
      item.defaultCalendarProfileId ?? "",
      JSON.stringify(item.pricingRules ?? {}),
      JSON.stringify(item.extraVisibilityRules ?? {})
    ].map(escapeCsvCell).join(",")
  );

  return [headers.join(","), ...rows].join("\r\n");
}

function buildExtrasCsv(items: SettingsValue[]) {
  const headers = [
    "id",
    "name",
    "icon",
    "description",
    "display_order",
    "quantity_based",
    "price_dollars",
    "duration_hours",
    "duration_minutes",
    "assigned_service_ids",
    "exclude_from_frequency_discount",
    "exclude_from_promo_discount",
    "recurring_policy",
    "active",
    "extra_pricing_rules_json",
    "carpet_shampooing_config_json"
  ];

  const rows = items.map((item) => {
    const totalMinutes = Number(item.durationDeltaMinutes ?? 0);
    return [
      item.id,
      item.name,
      item.icon,
      item.description,
      item.displayOrder ?? 0,
      item.quantityBased ? "true" : "false",
      centsToDollarInput(item.priceDeltaAmount ?? 0),
      Math.floor(totalMinutes / 60),
      totalMinutes % 60,
      normalizeStringList(item.assignedServiceIds).join(" | "),
      item.excludeFromFrequencyDiscount ? "true" : "false",
      item.excludeFromPromoDiscount ? "true" : "false",
      item.recurringPolicy ?? "every_booking",
      item.active ? "true" : "false",
      JSON.stringify(item.extraPricingRules ?? []),
      JSON.stringify(item.carpetShampooingConfig ?? null)
    ]
      .map(escapeCsvCell)
      .join(",");
  });

  return [headers.join(","), ...rows].join("\r\n");
}

function buildStaffCsv(items: SettingsValue[], getStaffTypeLabel?: (item: SettingsValue) => string) {
  const headers = [
    "id",
    "staff_type_label",
    "worker_group",
    "worker_code",
    "first_name",
    "last_name",
    "short_name",
    "business_name",
    "address_1",
    "address_2",
    "city",
    "state",
    "postal_code",
    "country",
    "phone_number",
    "emergency_contact",
    "email",
    "date_of_birth",
    "hire_date",
    "team_leader",
    "english_speaking",
    "available_work_days",
    "availability_start_time",
    "availability_end_time",
    "assigned_service_ids",
    "preferred_team_member_id",
    "cant_work_with_team_member_ids",
    "pay_type",
    "compensation",
    "bank_routing_number",
    "bank_account_number",
    "driver_license_number",
    "license_plate",
    "vehicle_year",
    "vehicle_make",
    "vehicle_model",
    "vehicle_color",
    "odometer",
    "active",
    "archived",
    "shirt_size",
    "equipment_records_json",
    "system_staff_type_id"
  ];

  const rows = items.map((item) =>
    [
      item.id,
      getStaffTypeLabel ? getStaffTypeLabel(item) : item.workerGroup ?? "",
      item.workerGroup ?? "",
      item.code ?? "",
      item.firstName ?? "",
      item.lastName ?? "",
      item.name ?? "",
      item.businessName ?? "",
      item.address1 ?? "",
      item.address2 ?? "",
      item.city ?? "",
      item.state ?? "",
      item.postalCode ?? "",
      item.country ?? "",
      item.phoneNumber ?? "",
      item.emergencyContact ?? "",
      item.email ?? "",
      normalizeDateInputValue(item.dateOfBirth),
      normalizeDateInputValue(item.hireDate),
      item.teamLeader ?? "",
      item.englishSpeaking ? "true" : "false",
      normalizeAvailabilityDays(item.availabilityDays, String(item.availabilityText ?? "")).join(" | "),
      item.availabilityStartTime ?? "",
      item.availabilityEndTime ?? "",
      normalizeStringList(item.assignedServiceIds).join(" | "),
      item.preferredTeamMemberId ?? "",
      normalizeStringList(item.cannotWorkWithStaffIds).join(" | "),
      item.payType ?? "",
      item.compensation ?? "",
      item.bankRoutingNumber ?? "",
      item.bankAccountNumber ?? "",
      item.driverLicenseNumber ?? "",
      item.licensePlate ?? "",
      item.vehicleYear ?? "",
      item.vehicleMake ?? "",
      item.vehicleModel ?? "",
      item.vehicleColor ?? "",
      item.odometer ?? "",
      item.active ? "true" : "false",
      item.isArchived ? "true" : "false",
      item.shirtSize ?? "",
      JSON.stringify(Array.isArray(item.equipmentRecords) ? item.equipmentRecords : []),
      item.staffTypeId ?? ""
    ].map(escapeCsvCell).join(",")
  );

  return [headers.join(","), ...rows].join("\r\n");
}

function LocationsSettingsEditor() {
  const config = relationalSectionConfigs.locations;
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string>("new");
  const [draft, setDraft] = useState<SettingsValue>(getLocationDraftBase(undefined, config.defaults));
  const [saveError, setSaveError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["settings", "locations"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/locations")
  });
  const items = query.data?.items ?? [];

  const selectedRecord = useMemo(
    () => items.find((item) => String(item.id ?? "") === selectedId),
    [items, selectedId]
  );

  useEffect(() => {
    if (selectedId !== "new" && !selectedRecord) {
      setSelectedId(items[0]?.id ? String(items[0].id) : "new");
    }
  }, [items, selectedId, selectedRecord]);

  useEffect(() => {
    const base = selectedRecord ?? config.defaults;
    setDraft(getLocationDraftBase(base, config.defaults));
    setSaveError(null);
  }, [config.defaults, selectedRecord]);

  const mutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      const base = { ...config.defaults, ...(selectedRecord ?? {}) };
      const cleanedRules = normalizeLocationServiceAreaRules(getPathValue(draft, "serviceAreaRules")).filter(
        (rule) => rule.zipCode.length === 5
      );
      const nextDraft = setPathValue(draft, "serviceAreaRules", cleanedRules);
      const payload = buildPayload(config, base, nextDraft);
      try {
        return await apiFetch(`/api/settings/locations${selectedRecord ? `/${selectedRecord.id}` : ""}`, {
          method: selectedRecord ? "PUT" : "POST",
          body: JSON.stringify(payload)
        });
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "locations"] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      apiFetch(`/api/settings/locations/${id}`, {
        method: "DELETE"
      }),
    onSuccess: async (_, deletedId) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "locations"] });
      setSelectedId((current) => (current === deletedId ? "new" : current));
    }
  });

  const serviceAreaRules = normalizeLocationServiceAreaRules(getPathValue(draft, "serviceAreaRules"));

  const updateRule = (index: number, patch: Partial<LocationServiceAreaRuleDraft>) => {
    const next = serviceAreaRules.map((rule, ruleIndex) =>
      ruleIndex === index
        ? {
            ...rule,
            ...patch
          }
        : rule
    );
    setDraft((current) => setPathValue(current, "serviceAreaRules", next));
  };

  return (
    <div className="settings-editor-grid">
      <Card
        title={config.title}
        eyebrow="Records"
        actions={
          <div className="customer-directory-actions">
            <Button variant="secondary" onClick={() => setSelectedId("new")}>New record</Button>
          </div>
        }
      >
        <p className="card-copy">{config.description}</p>
        <DataTable
          columns={["Name", "Timezone", "City", "Service ZIPs", "Status", "Action"]}
          rows={items.map((item) => [
            String(item.name ?? ""),
            String(item.timezone ?? ""),
            String(item.city ?? ""),
            String(normalizeStringList(item.serviceArea).length),
            <Badge key={`status-${String(item.id ?? "")}`} tone={item.active ? "success" : "warning"}>
              {item.active ? "Active" : "Inactive"}
            </Badge>,
            <div key={String(item.id ?? "")} className="table-action-row">
              <Button variant="ghost" onClick={() => setSelectedId(String(item.id ?? ""))}>Edit</Button>
              <Button variant="ghost" onClick={() => deleteMutation.mutate(String(item.id ?? ""))} disabled={deleteMutation.isPending}>
                Delete
              </Button>
            </div>
          ])}
        />
      </Card>
      <Card
        title={selectedRecord ? "Edit Location" : "New Location"}
        eyebrow={selectedRecord ? String(selectedRecord.id ?? "") : "Create record"}
        actions={
          <div className="customer-directory-actions">
            {selectedRecord ? (
              <Button
                variant="ghost"
                onClick={() => deleteMutation.mutate(String(selectedRecord.id ?? ""))}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            ) : null}
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? "Saving..." : selectedRecord ? "Save changes" : "Create record"}
            </Button>
          </div>
        }
      >
        <div className="stack">
          <div className="settings-fields-grid">
            {config.fields.map((field) => (
              <Field key={field.key} label={field.label} hint={field.kind === "checkbox" ? undefined : field.hint}>
                {renderFieldValue(field, draft, setDraft)}
              </Field>
            ))}
          </div>

          <Card title="Service area rules" eyebrow="ZIP restrictions">
            <p className="card-copy">
              Add optional ZIP-specific booking windows and weekday restrictions. If a ZIP rule matches,
              only start times inside that rule will be offered.
            </p>
            <div className="customer-directory-actions">
              <Button
                variant="secondary"
                onClick={() =>
                  setDraft((current) =>
                    setPathValue(current, "serviceAreaRules", [
                      ...serviceAreaRules,
                      {
                        zipCode: "",
                        earliestStartTime: "",
                        latestStartTime: "",
                        allowedWeekdays: [],
                        active: true,
                        note: ""
                      }
                    ])
                  )
                }
              >
                Add ZIP rule
              </Button>
            </div>
            {serviceAreaRules.length ? (
              <div className="stack">
                {serviceAreaRules.map((rule, index) => (
                  <div key={`location-rule-${index}`} className="spot-editor-card">
                    <div className="spot-editor-card__header">
                      <strong>{rule.zipCode || `ZIP rule #${index + 1}`}</strong>
                      <Button
                        variant="ghost"
                        onClick={() =>
                          setDraft((current) =>
                            setPathValue(
                              current,
                              "serviceAreaRules",
                              serviceAreaRules.filter((_, ruleIndex) => ruleIndex !== index)
                            )
                          )
                        }
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="settings-fields-grid">
                      <Field label="ZIP code">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={5}
                          value={rule.zipCode}
                          onChange={(event) => updateRule(index, { zipCode: event.target.value.replace(/\D/g, "").slice(0, 5) })}
                        />
                      </Field>
                      <Field label="Earliest start time" hint="Optional: leave blank to use the normal earliest time.">
                        <input
                          type="time"
                          value={rule.earliestStartTime}
                          onChange={(event) => updateRule(index, { earliestStartTime: event.target.value })}
                        />
                      </Field>
                      <Field label="Latest start time" hint="Optional: leave blank to use the normal latest time.">
                        <input
                          type="time"
                          value={rule.latestStartTime}
                          onChange={(event) => updateRule(index, { latestStartTime: event.target.value })}
                        />
                      </Field>
                      <Field label="Status">
                        <label className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={rule.active}
                            onChange={(event) => updateRule(index, { active: event.target.checked })}
                          />
                          <span>{rule.active ? "Active" : "Inactive"}</span>
                        </label>
                      </Field>
                    </div>
                    <Field label="Allowed weekdays" hint="Optional: leave blank to allow this ZIP every day.">
                      <div className="checkbox-grid">
                        {bookingSpotWeekdayOptions.map((day) => {
                          const checked = rule.allowedWeekdays.includes(day.value);
                          return (
                            <label key={`${index}-${day.value}`} className="checkbox-row">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  const next = event.target.checked
                                    ? Array.from(new Set([...rule.allowedWeekdays, day.value]))
                                    : rule.allowedWeekdays.filter((value) => value !== day.value);
                                  updateRule(index, { allowedWeekdays: next });
                                }}
                              />
                              <span>{day.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </Field>
                    <Field label="Notes">
                      <textarea rows={3} value={rule.note} onChange={(event) => updateRule(index, { note: event.target.value })} />
                    </Field>
                  </div>
                ))}
              </div>
            ) : (
              <p className="card-copy">No ZIP-specific time restrictions yet.</p>
            )}
            <div className="customer-directory-actions">
              <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                {mutation.isPending ? "Saving..." : selectedRecord ? "Save location" : "Create location"}
              </Button>
            </div>
            {saveError ? <p className="error-text">{saveError}</p> : null}
          </Card>
        </div>
        <div className="customer-directory-actions">
          {selectedRecord ? (
            <Button
              variant="ghost"
              onClick={() => deleteMutation.mutate(String(selectedRecord.id ?? ""))}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          ) : null}
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : selectedRecord ? "Save changes" : "Create record"}
          </Button>
        </div>
        {deleteMutation.error ? <p className="error-text">{(deleteMutation.error as Error).message}</p> : null}
      </Card>
    </div>
  );
}

function SingletonSettingsEditor({ section }: { section: string }) {
  const config = singletonSectionConfigs[section];
  const queryClient = useQueryClient();
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const invoiceLogoInputRef = useRef<HTMLInputElement | null>(null);
  const query = useQuery({
    queryKey: ["settings", section],
    queryFn: () => apiFetch<SettingsValue>(`/api/settings/${section}`)
  });
  const googleMapsQuery = useQuery({
    queryKey: ["settings", "google-maps-api"],
    queryFn: () => apiFetch<SettingsValue>("/api/settings/google-maps-api"),
    enabled: section === "other"
  });
  const [draft, setDraft] = useState<SettingsValue>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [emailTestTo, setEmailTestTo] = useState("");
  const [emailTestStatus, setEmailTestStatus] = useState<string | null>(null);
  const [twilioTestPhone, setTwilioTestPhone] = useState("");
  const [twilioTestStatus, setTwilioTestStatus] = useState<string | null>(null);
  const [mfaEnrollmentDrafts, setMfaEnrollmentDrafts] = useState<Record<string, MfaEnrollmentDraft>>({});
  const [invoiceLogoStatus, setInvoiceLogoStatus] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) {
      const source =
        section === "other"
          ? { ...config.defaults, ...query.data, ...(googleMapsQuery.data ?? {}) }
          : { ...config.defaults, ...query.data };
      setDraft(hydrateDraft(config, source));
      setSaveError(null);
    }
  }, [config, googleMapsQuery.data, query.data, section]);

  const mutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      try {
        const payload = buildPayload(
          config,
          section === "other"
            ? { ...config.defaults, ...(query.data ?? {}), ...(googleMapsQuery.data ?? {}) }
            : { ...config.defaults, ...(query.data ?? {}) },
          draft
        );
        if (section === "other") {
          const { apiKey, enableAddressAutocomplete, ...otherPayload } = payload;
          await Promise.all([
            apiFetch("/api/settings/google-maps-api", {
              method: "PUT",
              body: JSON.stringify({
                apiKey,
                enableAddressAutocomplete
              })
            }),
            apiFetch("/api/settings/other", {
              method: "PUT",
              body: JSON.stringify(otherPayload)
            })
          ]);
          return;
        }

        return await apiFetch(`/api/settings/${section}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", section] });
      if (section === "other") {
        await queryClient.invalidateQueries({ queryKey: ["settings", "google-maps-api"] });
      }
    }
  });

  const twilioTestMutation = useMutation({
    mutationFn: async () => {
      setTwilioTestStatus(null);
      const to = twilioTestPhone.trim();
      if (!to) {
        throw new Error("Enter a phone number to receive the test SMS.");
      }

      return apiFetch<{ success: boolean; sid?: string }>("/api/settings/other/twilio/test", {
        method: "POST",
        body: JSON.stringify({
          to,
          accountSid: String(getPathValue(draft, "twilio.accountSid") ?? ""),
          authToken: String(getPathValue(draft, "twilio.authToken") ?? ""),
          phoneNumber: String(getPathValue(draft, "twilio.phoneNumber") ?? "")
        })
      });
    },
    onSuccess: (payload) => {
      setTwilioTestStatus(payload.sid ? `Test SMS sent. Message SID: ${payload.sid}` : "Test SMS sent.");
    },
    onError: (error) => {
      setTwilioTestStatus(error instanceof Error ? error.message : String(error));
    }
  });

  const emailTestMutation = useMutation({
    mutationFn: async () => {
      setEmailTestStatus(null);
      const to = emailTestTo.trim();
      if (!to) {
        throw new Error("Enter an email address to receive the test email.");
      }

      return apiFetch<{ success: boolean; id?: string }>("/api/settings/emails/test", {
        method: "POST",
        body: JSON.stringify({
          to,
          senderName: String(getPathValue(draft, "senderName") ?? ""),
          senderEmail: String(getPathValue(draft, "senderEmail") ?? ""),
          provider: String(getPathValue(draft, "provider") ?? "resend"),
          resendApiKey: String(getPathValue(draft, "resendApiKey") ?? ""),
          replyToEmail: String(getPathValue(draft, "replyToEmail") ?? "")
        })
      });
    },
    onSuccess: (payload) => {
      setEmailTestStatus(payload.id ? `Test email sent. Delivery ID: ${payload.id}` : "Test email sent.");
    },
    onError: (error) => {
      setEmailTestStatus(error instanceof Error ? error.message : String(error));
    }
  });

  const mfaUsersQuery = useQuery({
    queryKey: ["settings", "security", "mfa", "users"],
    queryFn: () => apiFetch<MfaSettingsResponse>("/api/settings/security/mfa/users"),
    enabled: section === "other"
  });

  const updateMfaDraft = (userId: string, patch: Partial<MfaEnrollmentDraft>) => {
    setMfaEnrollmentDrafts((current) => {
      const existing = current[userId] ?? { phone: "", code: "", challengeToken: "", status: "" };
      return {
        ...current,
        [userId]: {
          ...existing,
          ...patch
        }
      };
    });
  };

  const startMfaEnrollmentMutation = useMutation({
    mutationFn: async (userId: string) => {
      const draft = mfaEnrollmentDrafts[userId] ?? { phone: "", code: "", challengeToken: "", status: "" };
      const phone = draft.phone.trim();
      if (!phone) {
        throw new Error("Enter a phone number for MFA.");
      }
      return apiFetch<{ challengeToken: string; phoneHint: string; expiresAt: string }>(
        `/api/settings/security/mfa/users/${encodeURIComponent(userId)}/enrollment`,
        {
          method: "POST",
          body: JSON.stringify({ phone })
        }
      );
    },
    onSuccess: (payload, userId) => {
      updateMfaDraft(userId, {
        challengeToken: payload.challengeToken,
        code: "",
        status: `Code sent to ${payload.phoneHint}.`
      });
    },
    onError: (error, userId) => {
      updateMfaDraft(userId, { status: error instanceof Error ? error.message : String(error) });
    }
  });

  const verifyMfaEnrollmentMutation = useMutation({
    mutationFn: async (userId: string) => {
      const draft = mfaEnrollmentDrafts[userId] ?? { phone: "", code: "", challengeToken: "", status: "" };
      if (!draft.challengeToken) {
        throw new Error("Send an MFA code first.");
      }
      return apiFetch<MfaSettingsResponse>(`/api/settings/security/mfa/users/${encodeURIComponent(userId)}/verify`, {
        method: "POST",
        body: JSON.stringify({
          challengeToken: draft.challengeToken,
          code: draft.code.trim()
        })
      });
    },
    onSuccess: async (_payload, userId) => {
      updateMfaDraft(userId, { code: "", challengeToken: "", status: "MFA phone verified." });
      await mfaUsersQuery.refetch();
    },
    onError: (error, userId) => {
      updateMfaDraft(userId, { status: error instanceof Error ? error.message : String(error) });
    }
  });

  const invoiceLogoUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setInvoiceLogoStatus(null);
      setInvoiceLogoStatus(isSvgInvoiceLogoFile(file) ? "Converting SVG logo..." : "Preparing logo...");
      return { logoUrl: await prepareInvoiceLogoDataUrl(file) };
    },
    onSuccess: (payload) => {
      setDraft((current) => setPathValue(current, "logoUrl", payload.logoUrl));
      setInvoiceLogoStatus("Logo ready. Save changes to use it on invoice previews and sends.");
    },
    onError: (error) => {
      setInvoiceLogoStatus(error instanceof Error ? error.message : String(error));
    }
  });

  if (query.isLoading || (section === "other" && googleMapsQuery.isLoading)) {
    return <div className="page-loading">Loading settings...</div>;
  }

  const getField = (key: string) => config.fields.find((field) => field.key === key);
  const renderSingletonField = (key: string) => {
    const field = getField(key);
    if (!field) {
      return null;
    }

    if (section === "general" && generalCancellationFieldKeys.includes(field.key as (typeof generalCancellationFieldKeys)[number])) {
      return null;
    }
    if (
      section === "general" &&
      field.key === "paymentsAcceptedOther" &&
      !valueAsArray(getPathValue(draft, "paymentsAccepted")).includes("other")
    ) {
      return null;
    }

    return (
      <Field key={field.key} label={field.label} hint={field.kind === "checkbox" ? undefined : field.hint}>
        {section === "general" && field.key === "logoUrl" ? (
          <div className="stack stack--tight">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  return;
                }

                const reader = new FileReader();
                reader.onload = () => {
                  if (typeof reader.result === "string") {
                    setDraft((current) => setPathValue(current, field.key, reader.result));
                  }
                };
                reader.readAsDataURL(file);
                event.currentTarget.value = "";
              }}
            />
            {String(getPathValue(draft, field.key) ?? "").trim() ? (
              <div className="settings-logo-preview">
                <img src={String(getPathValue(draft, field.key) ?? "")} alt="Company logo preview" />
              </div>
            ) : null}
            <div className="customer-directory-actions">
              <Button variant="secondary" onClick={() => logoInputRef.current?.click()}>
                Upload logo
              </Button>
              {String(getPathValue(draft, field.key) ?? "").trim() ? (
                <Button variant="ghost" onClick={() => setDraft((current) => setPathValue(current, field.key, ""))}>
                  Remove logo
                </Button>
              ) : null}
            </div>
          </div>
        ) : section === "invoices" && field.key === "logoUrl" ? (
          <div className="stack stack--tight">
            <input
              ref={invoiceLogoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,.svg"
              style={{ display: "none" }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  invoiceLogoUploadMutation.mutate(file);
                }
                event.currentTarget.value = "";
              }}
            />
            {String(getPathValue(draft, field.key) ?? "").trim() ? (
              <div className="settings-logo-preview">
                <img
                  src={String(getPathValue(draft, field.key) ?? "")}
                  alt="Invoice logo preview"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ) : null}
            <input
              type="text"
              value={String(getPathValue(draft, field.key) ?? "")}
              onChange={(event) => setDraft((current) => setPathValue(current, field.key, event.target.value))}
              placeholder="Upload a logo or paste a PNG/JPG URL"
            />
            <div className="customer-directory-actions">
              <Button variant="secondary" onClick={() => invoiceLogoInputRef.current?.click()} disabled={invoiceLogoUploadMutation.isPending}>
                {invoiceLogoUploadMutation.isPending ? "Uploading..." : "Upload logo"}
              </Button>
              {String(getPathValue(draft, field.key) ?? "").trim() ? (
                <Button variant="ghost" onClick={() => setDraft((current) => setPathValue(current, field.key, ""))}>
                  Remove logo
                </Button>
              ) : null}
            </div>
            <p className="card-copy">
              Upload PNG, JPG, or SVG. SVG files are converted to PNG before upload so invoice PDFs can embed the logo.
            </p>
            {invoiceLogoStatus ? (
              <p className={invoiceLogoUploadMutation.isError ? "error-text" : "card-copy"}>{invoiceLogoStatus}</p>
            ) : null}
          </div>
        ) : section === "general" && field.key === "paymentsAccepted" ? (
          <div className="stack stack--tight">
            <div className="checkbox-grid">
              {paymentsAcceptedOptions.map((option) => {
                const selected = Array.isArray(valueAsArray(getPathValue(draft, field.key)))
                  ? valueAsArray(getPathValue(draft, field.key))
                  : [];
                const isChecked = selected.includes(option.value);
                return (
                  <label key={option.value} className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(event) =>
                        setDraft((current) => {
                          const currentValues = valueAsArray(getPathValue(current, field.key));
                          const nextValues = event.target.checked
                            ? [...new Set([...currentValues, option.value])]
                            : currentValues.filter((item) => item !== option.value);
                          const nextDraft = setPathValue(current, field.key, nextValues);
                          if (option.value === "other" && !event.target.checked) {
                            return setPathValue(nextDraft, "paymentsAcceptedOther", "");
                          }
                          return nextDraft;
                        })
                      }
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ) : (
          renderFieldValue(field, draft, setDraft)
        )}
      </Field>
    );
  };

  const renderMfaSecurityCard = () => {
    const mfaData = mfaUsersQuery.data;
    const users = mfaData?.users ?? [];
    const requireOfficeRoles = Boolean(getPathValue(draft, "security.mfaRequiredForOfficeRoles"));
    const requirementBlocked = requireOfficeRoles && (!mfaData?.twilioConfigured || !mfaData?.hasVerifiedOwner);

    return (
      <Card title="Security/MFA" eyebrow="SMS login codes">
        <div className="stack">
          <div className="grid two">
            {renderSingletonField("security.mfaRequiredForOfficeRoles")}
          </div>
          {mfaUsersQuery.isLoading ? (
            <p className="card-copy">Loading MFA users...</p>
          ) : mfaUsersQuery.error ? (
            <p className="error-text">{(mfaUsersQuery.error as Error).message}</p>
          ) : (
            <>
              {requirementBlocked ? (
                <p className="error-text">
                  {mfaData?.twilioConfigured
                    ? "Verify SMS MFA for at least one active owner before saving required office-role MFA."
                    : "Configure Twilio before saving required office-role MFA."}
                </p>
              ) : null}
              <div className="settings-compliance-preview">
                <p className="card-copy">
                  Twilio {mfaData?.twilioConfigured ? "ready" : "not configured"} | Owner bootstrap{" "}
                  {mfaData?.hasVerifiedOwner ? "ready" : "needed"} | Office role requirement{" "}
                  {mfaData?.officeRoleMfaRequired ? "on" : "off"}
                </p>
              </div>
              <div className="stack stack--tight">
                {users.map((user) => {
                  const draftValue = mfaEnrollmentDrafts[user.userId] ?? {
                    phone: "",
                    code: "",
                    challengeToken: "",
                    status: ""
                  };
                  const isSending =
                    startMfaEnrollmentMutation.isPending && startMfaEnrollmentMutation.variables === user.userId;
                  const isVerifying =
                    verifyMfaEnrollmentMutation.isPending && verifyMfaEnrollmentMutation.variables === user.userId;
                  return (
                    <div key={user.userId} className="booking-meta-card">
                      <p className="eyebrow">{user.mfaRequiredByRole ? "Office role" : "User"}</p>
                      <strong>{user.name || user.email}</strong>
                      <p>{user.email} | {user.roles.join(", ") || "No roles"}</p>
                      <p className="card-copy">
                        {user.mfaSmsEnabled && user.mfaVerifiedAt
                          ? `Verified ${user.mfaPhoneHint || "phone"}`
                          : user.mfaPhoneHint
                            ? `${user.mfaPhoneHint} not verified`
                            : "No MFA phone verified"}
                      </p>
                      <div className="grid two">
                        <Field label="MFA phone">
                          <input
                            value={draftValue.phone}
                            onChange={(event) => updateMfaDraft(user.userId, { phone: event.target.value })}
                            placeholder="+13125551234"
                          />
                        </Field>
                        <Field label="Verification code">
                          <input
                            inputMode="numeric"
                            maxLength={6}
                            value={draftValue.code}
                            onChange={(event) =>
                              updateMfaDraft(user.userId, { code: event.target.value.replace(/\D/g, "").slice(0, 6) })
                            }
                            placeholder="123456"
                          />
                        </Field>
                      </div>
                      <div className="customer-directory-actions">
                        <Button
                          variant="secondary"
                          onClick={() => startMfaEnrollmentMutation.mutate(user.userId)}
                          disabled={isSending || !mfaData?.twilioConfigured || !draftValue.phone.trim()}
                        >
                          {isSending ? "Sending..." : "Send code"}
                        </Button>
                        <Button
                          onClick={() => verifyMfaEnrollmentMutation.mutate(user.userId)}
                          disabled={isVerifying || !draftValue.challengeToken || draftValue.code.trim().length !== 6}
                        >
                          {isVerifying ? "Verifying..." : "Verify phone"}
                        </Button>
                      </div>
                      {draftValue.status ? (
                        <p
                          className={
                            /failed|invalid|unable|error|configure|too many|wait/i.test(draftValue.status)
                              ? "error-text"
                              : "card-copy"
                          }
                        >
                          {draftValue.status}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
                {!users.length ? <p className="card-copy">No active users are available for MFA setup.</p> : null}
              </div>
            </>
          )}
        </div>
      </Card>
    );
  };

  return (
    <Card title={config.title} eyebrow="Singleton settings" actions={<Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>{mutation.isPending ? "Saving..." : "Save changes"}</Button>}>
      <p className="card-copy">{config.description}</p>
      {section === "other" ? (
        <div className="stack">
          <Card title="Google Maps API" eyebrow="Address autocomplete">
            <div className="grid two">
              {renderSingletonField("apiKey")}
              {renderSingletonField("enableAddressAutocomplete")}
            </div>
          </Card>
          <Card title="Google Analytics" eyebrow="Tracking">
            <div className="grid two">{renderSingletonField("googleAnalyticsTrackingId")}</div>
          </Card>
          <Card title="Google Tag Manager" eyebrow="Future option">
            <p className="card-copy">
              Use the Google tag ID above for the primary setup. Add Google Tag Manager later if you want a container-based
              marketing stack for more advanced tags and pixels.
            </p>
          </Card>
          <Card title="Twilio" eyebrow="Messaging credentials">
            <div className="stack">
              <div className="grid two">
                {renderSingletonField("twilio.accountSid")}
                {renderSingletonField("twilio.authToken")}
                {renderSingletonField("twilio.phoneNumber")}
              </div>
              <Card title="Send test SMS" eyebrow="Connection check">
                <div className="grid two">
                  <Field label="Send test to">
                    <input
                      value={twilioTestPhone}
                      onChange={(event) => setTwilioTestPhone(event.target.value)}
                      placeholder="+13125551234"
                    />
                  </Field>
                </div>
                <div className="customer-directory-actions">
                  <Button onClick={() => twilioTestMutation.mutate()} disabled={twilioTestMutation.isPending}>
                    {twilioTestMutation.isPending ? "Sending..." : "Send test SMS"}
                  </Button>
                </div>
                {twilioTestStatus ? (
                  <p className={twilioTestMutation.isError ? "error-text" : "card-copy"}>{twilioTestStatus}</p>
                ) : null}
              </Card>
            </div>
          </Card>
          {renderMfaSecurityCard()}
          <Card title="Google Calendar" eyebrow="Calendar sync">
            <div className="grid two">
              {renderSingletonField("googleCalendar.integrationMode")}
              {String(getPathValue(draft, "googleCalendar.integrationMode") ?? "disabled") === "select_calendar"
                ? renderSingletonField("googleCalendar.selectedCalendarId")
                : null}
              {renderSingletonField("googleCalendar.syncBookings")}
            </div>
          </Card>
          <Card title="Zapier Integration" eyebrow="Automation">
            <div className="grid two">
              {renderSingletonField("zapier.enabled")}
              {Boolean(getPathValue(draft, "zapier.enabled")) ? renderSingletonField("zapier.webhookUrl") : null}
            </div>
          </Card>
          <Card title="Automated Tasks" eyebrow="Coming soon">
            <div className="grid two">
              {renderSingletonField("automatedTasks.enabled")}
              {renderSingletonField("automatedTasks.notes")}
            </div>
          </Card>
          <Card title="Legal Pages" eyebrow="Booking consent">
            <div className="grid two">
              {renderSingletonField("legalPages.termsUrl")}
              {renderSingletonField("legalPages.privacyUrl")}
              {renderSingletonField("legalPages.termsVersion")}
              {renderSingletonField("legalPages.privacyVersion")}
            </div>
          </Card>
        </div>
      ) : section === "emails" ? (
        <div className="stack">
          <Card title="Provider" eyebrow="Connection">
            <div className="grid two">
              {renderSingletonField("provider")}
              {renderSingletonField("resendApiKey")}
            </div>
            <p className="card-copy">
              {String(getPathValue(draft, "provider") ?? "resend") !== "resend"
                ? "Only Resend is wired for transactional email right now."
                : String(getPathValue(draft, "resendApiKey") ?? "").trim()
                  ? "Resend API key entered. Save changes to use it for confirmations and reminders."
                  : "Add your Resend API key to activate email sending for this company."}
            </p>
          </Card>
          <Card title="Sender profile" eyebrow="From and reply-to">
            <div className="grid two">
              {renderSingletonField("senderName")}
              {renderSingletonField("senderEmail")}
              {renderSingletonField("replyToEmail")}
            </div>
          </Card>
          <Card title="Marketing Compliance" eyebrow="Required footer">
            <div className="grid two">
              {renderSingletonField("marketingCompliance.legalBusinessName")}
              {renderSingletonField("marketingCompliance.supportEmail")}
              {renderSingletonField("marketingCompliance.addressLine1")}
              {renderSingletonField("marketingCompliance.addressLine2")}
              {renderSingletonField("marketingCompliance.city")}
              {renderSingletonField("marketingCompliance.state")}
              {renderSingletonField("marketingCompliance.postalCode")}
              {renderSingletonField("marketingCompliance.country")}
              {renderSingletonField("marketingCompliance.supportPhone")}
            </div>
            {(() => {
              const preview = marketingCompliancePreview(draft);
              return (
                <div className="settings-compliance-preview">
                  <p className="card-copy">
                    Marketing campaigns and automations are blocked until the physical address is complete.
                  </p>
                  {preview.missing.length ? (
                    <p className="error-text">Missing: {preview.missing.join(", ")}.</p>
                  ) : null}
                  <div className="settings-compliance-footer-preview">
                    <p><strong>{preview.businessName}</strong></p>
                    {preview.addressLines.map((line) => <p key={line}>{line}</p>)}
                    {preview.supportEmail || preview.supportPhone ? (
                      <p>{[preview.supportEmail, preview.supportPhone].filter(Boolean).join(" | ")}</p>
                    ) : null}
                    <p>You are receiving this marketing email from {preview.businessName}. <a href="#">Unsubscribe</a></p>
                  </div>
                </div>
              );
            })()}
          </Card>
          <Card title="Email templates" eyebrow="Transactional messages">
            <div className="grid two">{renderSingletonField("enabledTemplates")}</div>
          </Card>
          <Card title="Send test email" eyebrow="Connection check">
            <div className="grid two">
              <Field label="Send test to">
                <input
                  value={emailTestTo}
                  onChange={(event) => setEmailTestTo(event.target.value)}
                  placeholder="name@example.com"
                />
              </Field>
            </div>
            <div className="customer-directory-actions">
              <Button
                onClick={() => emailTestMutation.mutate()}
                disabled={emailTestMutation.isPending || String(getPathValue(draft, "provider") ?? "resend") !== "resend"}
              >
                {emailTestMutation.isPending ? "Sending..." : "Send test email"}
              </Button>
            </div>
            {emailTestStatus ? (
              <p className={emailTestMutation.isError ? "error-text" : "card-copy"}>{emailTestStatus}</p>
            ) : null}
          </Card>
        </div>
      ) : section === "invoices" ? (
        <div className="stack">
          <Card title="Invoice preview" eyebrow="Live sample">
            <p className="card-copy">
              This is a sample customer invoice using the settings below. For a real booking PDF, open the booking invoice and preview/send it there.
            </p>
            <InvoiceTemplatePreview draft={draft} />
          </Card>
          <Card title="Numbering & terms" eyebrow="Invoice defaults">
            <div className="grid two">
              {renderSingletonField("invoicePrefix")}
              {renderSingletonField("nextInvoiceNumber")}
              {renderSingletonField("defaultDueTerms")}
              {renderSingletonField("businessDisplayName")}
              {renderSingletonField("businessAddress")}
            </div>
          </Card>
          <Card title="Template branding" eyebrow="Logo and color">
            <div className="grid two">
              {renderSingletonField("logoUrl")}
              {renderSingletonField("templateAccentColor")}
            </div>
          </Card>
          <Card title="PDF sections" eyebrow="Show or hide">
            <p className="card-copy">Choose which customer-facing sections appear on invoice PDFs. Existing sent PDFs stay unchanged.</p>
            <div className="grid two">
              {renderSingletonField("showCompanyContact")}
              {renderSingletonField("showBillTo")}
              {renderSingletonField("showServiceAddress")}
              {renderSingletonField("showAppointment")}
              {renderSingletonField("showMaidNames")}
              {renderSingletonField("showLineItems")}
              {renderSingletonField("showPayments")}
              {renderSingletonField("showMemo")}
              {renderSingletonField("showPaymentInstructions")}
              {renderSingletonField("showTermsLink")}
              {renderSingletonField("showFooter")}
            </div>
          </Card>
          <Card title="Default invoice copy" eyebrow="Memo, footer, and email">
            <div className="grid two">
              {renderSingletonField("defaultMemo")}
              {renderSingletonField("defaultPaymentInstructions")}
              {renderSingletonField("defaultFooter")}
              {renderSingletonField("defaultEmailSubject")}
            </div>
            <div className="stack stack--tight">{renderSingletonField("defaultEmailBody")}</div>
          </Card>
        </div>
      ) : section === "pricebook" ? (
        <div className="stack">
          <PricebookResidentialComponentFormulaEditor draft={draft} setDraft={setDraft} />
          <PricebookLevelMinimumEditor draft={draft} setDraft={setDraft} />
          <PricebookSkipAreaDurationEditor draft={draft} setDraft={setDraft} />
          <Card title="Advanced rules JSON" eyebrow="Raw config">
            {renderSingletonField("advancedRules")}
          </Card>
        </div>
      ) : (
        <div className="grid two">{config.fields.map((field) => renderSingletonField(field.key))}</div>
      )}
        {section === "general" ? (
          <Card title="Booking Cancellations" eyebrow="Cancellation policy">
            <div className="stack">
              <Field label="Enable cancellation fees">
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(getPathValue(draft, "cancellationFeeEnabled"))}
                    onChange={(event) =>
                      setDraft((current) => setPathValue(current, "cancellationFeeEnabled", event.target.checked))
                    }
                  />
                  <span>Charge the customer a fee if they cancel their booking after a certain time.</span>
                </label>
              </Field>
              <Field label="Policy">
                <div className="stack stack--tight">
                  <label className="checkbox-row">
                    <input
                      type="radio"
                      name="cancellationPolicyMode"
                      checked={String(getPathValue(draft, "cancellationPolicyMode") ?? "day_before_5pm") === "day_before_5pm"}
                      onChange={() =>
                        setDraft((current) => setPathValue(current, "cancellationPolicyMode", "day_before_5pm"))
                      }
                    />
                    <span>Cancel by a set time before the booking</span>
                  </label>
                  {String(getPathValue(draft, "cancellationPolicyMode") ?? "day_before_5pm") === "day_before_5pm" ? (
                    <div className="settings-inline-field">
                      <select
                        value={String(getPathValue(draft, "cancellationPresetDaysBefore") ?? "1")}
                        onChange={(event) =>
                          setDraft((current) => setPathValue(current, "cancellationPresetDaysBefore", event.target.value))
                        }
                      >
                        <option value="1">1 day before</option>
                        <option value="2">2 days before</option>
                      </select>
                      <input
                        type="time"
                        value={String(getPathValue(draft, "cancellationPresetTime") ?? "17:00")}
                        onChange={(event) =>
                          setDraft((current) => setPathValue(current, "cancellationPresetTime", event.target.value))
                        }
                      />
                    </div>
                  ) : null}
                  <label className="checkbox-row">
                    <input
                      type="radio"
                      name="cancellationPolicyMode"
                      checked={String(getPathValue(draft, "cancellationPolicyMode") ?? "") === "custom"}
                      onChange={() =>
                        setDraft((current) => setPathValue(current, "cancellationPolicyMode", "custom"))
                      }
                    />
                    <span>Set your own terms</span>
                  </label>
                  {String(getPathValue(draft, "cancellationPolicyMode") ?? "day_before_5pm") === "custom" ? (
                    <div className="settings-inline-field">
                      <input
                        type="number"
                        min="1"
                        value={String(getPathValue(draft, "cancellationCutoffValue") ?? "47")}
                        onChange={(event) =>
                          setDraft((current) => setPathValue(current, "cancellationCutoffValue", event.target.value))
                        }
                      />
                      <select
                        value={String(getPathValue(draft, "cancellationCutoffUnit") ?? "hours")}
                        onChange={(event) =>
                          setDraft((current) => setPathValue(current, "cancellationCutoffUnit", event.target.value))
                        }
                      >
                        <option value="hours">hours</option>
                        <option value="days">days</option>
                      </select>
                      <span className="settings-inline-field__suffix">before the appointment</span>
                    </div>
                  ) : null}
                </div>
              </Field>
              <Field label="Penalty fee">
                <div className="stack stack--tight">
                  <label className="checkbox-row">
                    <input
                      type="radio"
                      name="cancellationPenaltyType"
                      checked={String(getPathValue(draft, "cancellationPenaltyType") ?? "flat") === "flat"}
                      onChange={() =>
                        setDraft((current) => setPathValue(current, "cancellationPenaltyType", "flat"))
                      }
                    />
                    <span>Flat fee</span>
                  </label>
                  <label className="checkbox-row">
                    <input
                      type="radio"
                      name="cancellationPenaltyType"
                      checked={String(getPathValue(draft, "cancellationPenaltyType") ?? "") === "percentage"}
                      onChange={() =>
                        setDraft((current) => setPathValue(current, "cancellationPenaltyType", "percentage"))
                      }
                    />
                    <span>Percentage of pre-tax total</span>
                  </label>
                  {String(getPathValue(draft, "cancellationPenaltyType") ?? "flat") === "flat" ? (
                    <label className="settings-money-field">
                      <span className="settings-money-field__prefix">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={String(getPathValue(draft, "cancellationPenaltyValue") ?? "99")}
                        onChange={(event) =>
                          setDraft((current) => setPathValue(current, "cancellationPenaltyValue", event.target.value))
                        }
                        placeholder="0.00"
                      />
                    </label>
                  ) : (
                    <div className="settings-inline-field">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={String(getPathValue(draft, "cancellationPenaltyValue") ?? "0")}
                        onChange={(event) =>
                          setDraft((current) => setPathValue(current, "cancellationPenaltyValue", event.target.value))
                        }
                        placeholder="0"
                      />
                      <span className="settings-inline-field__suffix">% of pre-tax total</span>
                    </div>
                  )}
                </div>
              </Field>
              <div className="stack stack--tight">
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(getPathValue(draft, "requireCancellationReason"))}
                    onChange={(event) =>
                      setDraft((current) => setPathValue(current, "requireCancellationReason", event.target.checked))
                    }
                  />
                  <span>Reason for cancelling the booking must be given</span>
                </label>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(getPathValue(draft, "applyCancellationFeeToCustomerInitiatedOnly"))}
                    onChange={(event) =>
                      setDraft((current) =>
                        setPathValue(current, "applyCancellationFeeToCustomerInitiatedOnly", event.target.checked)
                      )
                    }
                  />
                  <span>Apply only to customer-initiated cancellations</span>
                </label>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(getPathValue(draft, "waiveCancellationFeeForFirstTimeNewCustomers"))}
                    onChange={(event) =>
                      setDraft((current) =>
                        setPathValue(current, "waiveCancellationFeeForFirstTimeNewCustomers", event.target.checked)
                      )
                    }
                  />
                  <span>Waive fee for first-time exception for new customers</span>
                </label>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(getPathValue(draft, "waiveCancellationFeeForFirstRecurringCancellation"))}
                    onChange={(event) =>
                      setDraft((current) =>
                        setPathValue(current, "waiveCancellationFeeForFirstRecurringCancellation", event.target.checked)
                      )
                    }
                  />
                  <span>Waive fee for first-time cancellation for customers who have frequency other than one-time</span>
                </label>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={Boolean(getPathValue(draft, "requireInternalNoteWhenCancellationFeeWaived"))}
                    onChange={(event) =>
                      setDraft((current) =>
                        setPathValue(current, "requireInternalNoteWhenCancellationFeeWaived", event.target.checked)
                      )
                    }
                  />
                  <span>Internal note required when fee is waived</span>
                </label>
              </div>
            </div>
          </Card>
        ) : null}
        {saveError ? <p className="error-text">{saveError}</p> : null}
      </Card>
    );
}

function valueAsArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)) : [];
}

function StaffSettingsEditor() {
  const config = relationalSectionConfigs.staff;
  const queryClient = useQueryClient();
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("staff") ?? (searchParams.get("new") ? "new" : "");
  const [draft, setDraft] = useState<SettingsValue>(getStaffDraftBase(undefined, config.defaults));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [staffImportFeedback, setStaffImportFeedback] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<StaffTabKey>("personal");
  const [directoryCategory, setDirectoryCategory] = useState<StaffDirectoryCategory>("maids");
  const [loginCreateDraft, setLoginCreateDraft] = useState<StaffLoginCreateDraft>({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    active: true
  });

  const [passwordResetDraft, setPasswordResetDraft] = useState("");
  const query = useQuery({
    queryKey: ["settings", "staff"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/staff")
  });
  const locationsQuery = useQuery({
    queryKey: ["settings", "locations", "staff-editor"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/locations")
  });
  const servicesQuery = useQuery({
    queryKey: ["settings", "services", "staff-editor"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/services")
  });
  const items = query.data?.items ?? [];
  const staffTypesQuery = useQuery({
    queryKey: ["settings", "staff-types"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/staff-types")
  });
  const tagsQuery = useQuery({
    queryKey: ["settings", "tags", "lookup"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/tags")
  });
  const activeLocations = useMemo(
    () =>
      sortSectionItems("locations", locationsQuery.data?.items ?? []).filter((item) =>
        Boolean(item.active ?? true)
      ),
    [locationsQuery.data?.items]
  );
  const activeServices = useMemo(
    () =>
      sortSectionItems("services", servicesQuery.data?.items ?? []).filter((item) => Boolean(item.active ?? true)),
    [servicesQuery.data?.items]
  );
  const staffTypes = sortSectionItems("staff-types", staffTypesQuery.data?.items ?? []);
  const staffTypeById = useMemo(
    () => new Map(staffTypes.map((item) => [String(item.id ?? ""), item] as const)),
    [staffTypes]
  );
  const getStaffTypeOptionLabel = (item: SettingsValue) => {
    const normalizedDirectoryCategory = String(item.directoryCategory ?? "")
      .trim()
      .toLowerCase();
    if (normalizedDirectoryCategory === "maid" || normalizedDirectoryCategory === "maids") {
      return "Maid";
    }
    if (normalizedDirectoryCategory === "driver" || normalizedDirectoryCategory === "drivers") {
      return "Driver";
    }
    if (normalizedDirectoryCategory === "office" || normalizedDirectoryCategory === "office staff") {
      return "Office";
    }

    const name = String(item.name ?? "").trim();
    if (name === "Maids") {
      return "Maid";
    }
    if (name === "Drivers") {
      return "Driver";
    }
    if (name === "Office Staff") {
      return "Office";
    }

    return name;
  };

  const getStaffGroupingKey = (item: SettingsValue) => {
    const staffTypeId = String(item.staffTypeId ?? "").trim();
    if (staffTypeId) {
      return `type:${staffTypeId}`;
    }

    const workerGroup = String(item.workerGroup ?? "").trim().toLowerCase();
    return workerGroup ? `group:${workerGroup}` : "";
  };

  const getStaffTypeLabel = (item: SettingsValue) => {
    const matched = staffTypeById.get(String(item.staffTypeId ?? ""));
    return matched ? getStaffTypeOptionLabel(matched) : String(item.workerGroup ?? "");
  };

  const resolveDirectoryCategory = (staffTypeId: unknown, workerGroup: unknown): StaffDirectoryCategory => {
    const type = staffTypeById.get(String(staffTypeId ?? ""));
    const normalizedWorkerGroup = String(type?.directoryCategory ?? workerGroup ?? "")
      .trim()
      .toLowerCase();
    if (normalizedWorkerGroup === "driver" || normalizedWorkerGroup === "drivers") {
      return "drivers";
    }
    if (
      normalizedWorkerGroup === "maid" ||
      normalizedWorkerGroup === "maids" ||
      normalizedWorkerGroup === "cleaner" ||
      normalizedWorkerGroup === "cleaners"
    ) {
      return "maids";
    }
    return "office";
  };

  const classifyStaffDirectoryCategory = (item: SettingsValue): StaffDirectoryCategory =>
    resolveDirectoryCategory(item.staffTypeId, item.workerGroup);

  const directoryCounts = useMemo(
    () => ({
      maids: items.filter((item) => classifyStaffDirectoryCategory(item) === "maids").length,
      drivers: items.filter((item) => classifyStaffDirectoryCategory(item) === "drivers").length,
      office: items.filter((item) => classifyStaffDirectoryCategory(item) === "office").length
    }),
    [items]
  );

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      if (classifyStaffDirectoryCategory(item) !== directoryCategory) {
        return false;
      }

      if (!term) {
        return true;
      }

      return [
        item.code,
        item.firstName,
        item.lastName,
        item.name,
        getStaffTypeLabel(item),
        item.phoneNumber,
        item.email,
        item.businessName
      ]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [directoryCategory, items, search]);

  const selectedRecord = useMemo(
    () => items.find((item) => String(item.id ?? "") === selectedId),
    [items, selectedId]
  );
  const staffFeedbackHistoryQuery = useQuery({
    queryKey: ["settings", "staff", "feedback", selectedRecord?.id],
    queryFn: () => apiFetch<{ items: StaffFeedbackFeedItem[] }>(`/api/settings/staff/${selectedRecord?.id}/feedback`),
    enabled: Boolean(selectedRecord?.id)
  });
  const staffLoginAccessQuery = useQuery({
    queryKey: ["settings", "staff", "login-access", selectedRecord?.id],
    queryFn: () => apiFetch<StaffLoginAccess>(`/api/settings/staff/${selectedRecord?.id}/login-access`),
    enabled: Boolean(selectedRecord?.id)
  });
  const currentGroupKey = useMemo(() => {
    const selectedStaffTypeId = String(getPathValue(draft, "staffTypeId") ?? "").trim();
    if (selectedStaffTypeId) {
      return `type:${selectedStaffTypeId}`;
    }

    const workerGroup = String(getPathValue(draft, "workerGroup") ?? "").trim().toLowerCase();
    return workerGroup ? `group:${workerGroup}` : "";
  }, [draft]);
  const currentDirectoryCategory = useMemo(
    () => resolveDirectoryCategory(getPathValue(draft, "staffTypeId"), getPathValue(draft, "workerGroup")),
    [draft, staffTypeById]
  );
  const getAvailabilityStaffLabel = (item: SettingsValue) =>
    String(item.code ?? "").trim() ||
    `${String(item.firstName ?? "")} ${String(item.lastName ?? "")}`.trim() ||
    String(item.name ?? "");
  const sameGroupStaffMembers = useMemo(
    () =>
      items
        .filter((item) => String(item.id ?? "") !== String(selectedRecord?.id ?? ""))
        .filter((item) => currentGroupKey && getStaffGroupingKey(item) === currentGroupKey)
        .sort((left, right) =>
          `${String(left.firstName ?? "")} ${String(left.lastName ?? "")}`.trim().localeCompare(
            `${String(right.firstName ?? "")} ${String(right.lastName ?? "")}`.trim()
          )
        ),
    [currentGroupKey, items, selectedRecord]
  );
  const availableStaffTags = useMemo(
    () =>
      sortSectionItems("tags", tagsQuery.data?.items ?? []).filter((item) => {
        if (item.active === false || String(item.entityType ?? "") !== "staff") {
          return false;
        }

        const target = String(item.directoryCategory ?? "all");
        return target === "all" || target === currentDirectoryCategory;
      }),
    [currentDirectoryCategory, tagsQuery.data?.items]
  );

  useEffect(() => {
    if (!selectedId) {
      setDraft(getStaffDraftBase(undefined, config.defaults));
      setSaveError(null);
      return;
    }

    const base = selectedRecord ?? config.defaults;
    setDraft(getStaffDraftBase(base, config.defaults));
    setSaveError(null);
  }, [config.defaults, selectedId, selectedRecord]);

  useEffect(() => {
    if (!selectedRecord) {
      setLoginCreateDraft({
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        active: true
      });
      setPasswordResetDraft("");
      return;
    }

    setLoginCreateDraft({
      email: String(selectedRecord.email ?? ""),
      password: "",
      firstName: String(selectedRecord.firstName ?? ""),
      lastName: String(selectedRecord.lastName ?? ""),
      active: true
    });
    setPasswordResetDraft("");
  }, [selectedRecord]);

  const mutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      const base = { ...config.defaults, ...(selectedRecord ?? {}) };
      const normalizedAvailability = normalizeAvailabilityDays(getPathValue(draft, "availabilityDays"), String(getPathValue(draft, "availabilityText") ?? ""));
      const nextDraft = setPathValue(
        setPathValue(draft, "availabilityDays", normalizedAvailability),
        "availabilityText",
        describeAvailabilityDays(normalizedAvailability)
      );
      const payload = buildPayload(config, base, nextDraft);
      try {
        return await apiFetch(`/api/settings/staff${selectedRecord ? `/${selectedRecord.id}` : ""}`, {
          method: selectedRecord ? "PUT" : "POST",
          body: JSON.stringify(payload)
        });
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async (result: any) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff"] });
      const nextId = selectedRecord ? String(selectedRecord.id ?? "") : String(result?.id ?? "");
      setSearchParams(nextId ? { staff: nextId } : {});
    }
  });

  const importStaffMutation = useMutation({
    mutationFn: async (csv: string) =>
      apiFetch<{ imported: number; created: number; updated: number; ids: string[] }>("/api/settings/staff/import", {
        method: "POST",
        body: JSON.stringify({ csv })
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff"] });
      setStaffImportFeedback(
        `Imported ${result.imported} row${result.imported === 1 ? "" : "s"}: ${result.created} new, ${result.updated} updated.`
      );
      setSearchParams({});
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      apiFetch<{ id: string; mode: "deleted" | "archived" }>(`/api/settings/staff/${id}`, {
        method: "DELETE"
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff"] });
      setSearchParams({});
      setStaffImportFeedback(
        result.mode === "archived"
          ? "Staff record was archived because it is linked to booking history."
          : "Staff record deleted."
      );
    }
  });
  const createLoginMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRecord?.id) {
        throw new Error("Select a saved staff member before creating a login.");
      }

      return apiFetch<StaffLoginAccess>(`/api/settings/staff/${selectedRecord.id}/login-access/create`, {
        method: "POST",
        body: JSON.stringify(loginCreateDraft)
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff", "login-access", selectedRecord?.id] });
      setLoginCreateDraft((current) => ({ ...current, password: "" }));
    }
  });
  const linkLoginMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!selectedRecord?.id) {
        throw new Error("Select a saved staff member before linking a login.");
      }

      return apiFetch<StaffLoginAccess>(`/api/settings/staff/${selectedRecord.id}/login-access/link`, {
        method: "POST",
        body: JSON.stringify({ userId })
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff", "login-access", selectedRecord?.id] });
    }
  });
  const unlinkLoginMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRecord?.id) {
        throw new Error("Select a saved staff member before unlinking a login.");
      }

      return apiFetch<StaffLoginAccess>(`/api/settings/staff/${selectedRecord.id}/login-access/unlink`, {
        method: "POST"
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff", "login-access", selectedRecord?.id] });
    }
  });
  const loginStatusMutation = useMutation({
    mutationFn: async (active: boolean) => {
      if (!selectedRecord?.id) {
        throw new Error("Select a saved staff member before changing login status.");
      }

      return apiFetch<StaffLoginAccess>(`/api/settings/staff/${selectedRecord.id}/login-access/status`, {
        method: "POST",
        body: JSON.stringify({ active })
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff", "login-access", selectedRecord?.id] });
    }
  });
  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRecord?.id) {
        throw new Error("Select a saved staff member before resetting a password.");
      }

      return apiFetch<StaffLoginAccess>(`/api/settings/staff/${selectedRecord.id}/login-access/password`, {
        method: "POST",
        body: JSON.stringify({ password: passwordResetDraft })
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff", "login-access", selectedRecord?.id] });
      setPasswordResetDraft("");
    }
  });

  const updateDraftValue = (key: string, value: unknown) => {
    setDraft((current) => {
      let next = setPathValue(current, key, value);
      if (key === "availabilityDays") {
        next = setPathValue(next, "availabilityText", describeAvailabilityDays(normalizeAvailabilityDays(value)));
      }
      if (key === "staffTypeId") {
        const selectedType = staffTypeById.get(String(value ?? ""));
        next = setPathValue(next, "workerGroup", String(selectedType?.name ?? ""));
        next = setPathValue(next, "preferredTeamMemberId", "");
        next = setPathValue(next, "cannotWorkWithStaffIds", []);
      }
      if (key === "active") {
        next = setPathValue(next, "isArchived", !Boolean(value));
      }
      if (key === "isArchived") {
        next = setPathValue(next, "active", !Boolean(value));
      }
      return next;
    });
  };

  const renderStaffField = (field: FieldConfig) => {
    const value = getPathValue(draft, field.key);
    if (field.key === "availabilityDays") {
      const selectedDays = normalizeAvailabilityDays(value, String(getPathValue(draft, "availabilityText") ?? ""));
      return (
        <div className="checkbox-grid">
          {staffWeekdayOptions.map((option) => (
            <label key={option.value} className="checkbox-row">
              <input
                type="checkbox"
                checked={selectedDays.includes(option.value)}
                onChange={(event) => {
                  const next = event.target.checked
                    ? [...selectedDays, option.value]
                    : selectedDays.filter((entry) => entry !== option.value);
                  updateDraftValue("availabilityDays", Array.from(new Set(next)));
                }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      );
    }

    if (field.key === "staffTypeId") {
      return (
        <select value={String(value ?? "")} onChange={(event) => updateDraftValue(field.key, event.target.value)}>
          <option value="">Select staff type</option>
          {staffTypes
            .filter((item) => item.active !== false)
            .map((item) => (
              <option key={String(item.id ?? "")} value={String(item.id ?? "")}>
                {getStaffTypeOptionLabel(item)}
              </option>
            ))}
        </select>
      );
    }

    if (field.key === "preferredTeamMemberId") {
      const selectedId = String(value ?? "");
      if (!sameGroupStaffMembers.length) {
        return <p className="card-copy">No same-type staff members are available yet.</p>;
      }

      return (
        <div className="tag-grid">
          {sameGroupStaffMembers.map((item) => {
            const staffId = String(item.id ?? "");
            const active = selectedId === staffId;
            return (
              <label key={staffId} className={`tag-choice ${active ? "is-active" : ""}`}>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(event) => {
                    updateDraftValue(field.key, event.target.checked ? staffId : "");
                  }}
                />
                <span>{getAvailabilityStaffLabel(item)}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (field.key === "cannotWorkWithStaffIds") {
      const selectedIds = Array.isArray(value) ? value.map((entry) => String(entry)) : [];
      if (!sameGroupStaffMembers.length) {
        return <p className="card-copy">No same-type staff members are available yet.</p>;
      }

      return (
        <div className="tag-grid">
          {sameGroupStaffMembers.map((item) => {
            const staffId = String(item.id ?? "");
            const active = selectedIds.includes(staffId);
            return (
              <label key={staffId} className={`tag-choice ${active ? "is-active" : ""}`}>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(event) => {
                    const next = event.target.checked
                      ? [...selectedIds, staffId]
                      : selectedIds.filter((entry) => entry !== staffId);
                    updateDraftValue(field.key, Array.from(new Set(next)));
                  }}
                />
                <span>{getAvailabilityStaffLabel(item)}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (field.key === "tagIds") {
      const selectedIds = Array.isArray(value) ? value.map((entry) => String(entry)) : [];
      if (!availableStaffTags.length) {
        return <p className="card-copy">No tags are available for this staff type yet.</p>;
      }

      return (
        <div className="tag-grid">
          {availableStaffTags.map((item) => {
            const tagId = String(item.id ?? "");
            const active = selectedIds.includes(tagId);
            return (
              <label key={tagId} className={`tag-choice ${active ? "is-active" : ""}`}>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(event) => {
                    const next = event.target.checked
                      ? [...selectedIds, tagId]
                      : selectedIds.filter((entry) => entry !== tagId);
                    updateDraftValue(field.key, Array.from(new Set(next)));
                  }}
                />
                <span>{String(item.name ?? "")}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (field.key === "assignedLocationIds") {
      const selectedIds = Array.isArray(value) ? value.map((entry) => String(entry)) : [];
      if (!activeLocations.length) {
        return <p className="card-copy">No active locations are available yet.</p>;
      }

      return (
        <div className="stack stack--tight">
          <div className="tag-grid">
            {activeLocations.map((item) => {
              const locationId = String(item.id ?? "");
              const active = selectedIds.includes(locationId);
              return (
                <label key={locationId} className={`tag-choice ${active ? "is-active" : ""}`}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...selectedIds, locationId]
                        : selectedIds.filter((entry) => entry !== locationId);
                      updateDraftValue(field.key, Array.from(new Set(next)));
                    }}
                  />
                  <span>{String(item.name ?? "")}</span>
                </label>
              );
            })}
          </div>
          <p className="card-copy">Leave this empty to allow the worker in all active locations.</p>
        </div>
      );
    }

    if (field.key === "assignedServiceIds") {
      const selectedIds = Array.isArray(value) ? value.map((entry) => String(entry)) : [];
      if (!activeServices.length) {
        return <p className="card-copy">No active cleaning types are available yet.</p>;
      }

      return (
        <div className="stack stack--tight">
          <div className="tag-grid">
            {activeServices.map((item) => {
              const serviceId = String(item.id ?? "");
              const active = selectedIds.includes(serviceId);
              return (
                <label key={serviceId} className={`tag-choice ${active ? "is-active" : ""}`}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...selectedIds, serviceId]
                        : selectedIds.filter((entry) => entry !== serviceId);
                      updateDraftValue(field.key, Array.from(new Set(next)));
                    }}
                  />
                  <span>{String(item.name ?? "")}</span>
                </label>
              );
            })}
          </div>
          <p className="card-copy">Leave this empty to allow the worker on all active cleaning types.</p>
        </div>
      );
    }

    if (field.key === "compensation") {
      const payType = String(getPathValue(draft, "payType") ?? "hourly");
      const amountLabel =
        payType === "flat-fee"
          ? "Enter flat amount."
          : payType === "revenue-share"
            ? "Enter percent of booking revenue for this team member."
            : "Enter amount per hour.";
      return (
        <div className="stack stack--tight">
          <label className="settings-money-field">
            <span className="settings-money-field__prefix">{payType === "revenue-share" ? "%" : "$"}</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={String(value ?? "")}
              onChange={(event) => updateDraftValue(field.key, event.target.value)}
              placeholder="0.00"
            />
          </label>
          <p className="card-copy">{amountLabel}</p>
        </div>
      );
    }

    if (field.key === "equipmentRecords") {
      const records = Array.isArray(value)
        ? value.map((entry) => ({
            assignedDate: String((entry as Record<string, unknown>)?.assignedDate ?? ""),
            equipmentName: String((entry as Record<string, unknown>)?.equipmentName ?? "")
          }))
        : [];
      return (
        <div className="stack stack--tight">
          {records.length ? (
            records.map((entry, index) => (
              <div key={`${entry.assignedDate}-${entry.equipmentName}-${index}`} className="settings-inline-card">
                <div className="grid two">
                  <Field label="Date">
                    <DateInput
                      value={entry.assignedDate}
                      onChange={(event) => {
                        const next = records.map((record, recordIndex) =>
                          recordIndex === index ? { ...record, assignedDate: event.target.value } : record
                        );
                        updateDraftValue(field.key, next);
                      }}
                    />
                  </Field>
                  <Field label="Equipment name">
                    <input
                      value={entry.equipmentName}
                      onChange={(event) => {
                        const next = records.map((record, recordIndex) =>
                          recordIndex === index ? { ...record, equipmentName: event.target.value } : record
                        );
                        updateDraftValue(field.key, next);
                      }}
                      placeholder="Vacuum, mop, kit, etc."
                    />
                  </Field>
                </div>
                <div className="customer-directory-actions">
                  <Button
                    variant="ghost"
                    onClick={() => updateDraftValue(field.key, records.filter((_, recordIndex) => recordIndex !== index))}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="card-copy">No equipment records yet.</p>
          )}
          <Button
            variant="secondary"
            onClick={() =>
              updateDraftValue(field.key, [
                ...records,
                {
                  assignedDate: "",
                  equipmentName: ""
                }
              ])
            }
          >
            Add equipment
          </Button>
        </div>
      );
    }

    switch (field.kind) {
      case "textarea":
      case "json":
        return (
          <textarea
            rows={field.rows ?? 5}
            value={String(value ?? "")}
            onChange={(event) => updateDraftValue(field.key, event.target.value)}
            placeholder={field.placeholder}
          />
        );
      case "checkbox":
        return (
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(event) => updateDraftValue(field.key, event.target.checked)}
            />
            <span>{field.hint ?? field.label}</span>
          </label>
        );
      case "select":
        return (
          <select value={String(value ?? "")} onChange={(event) => updateDraftValue(field.key, event.target.value)}>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      default:
        if (field.kind === "date") {
          return (
            <DateInput
              step={field.step}
              value={String(value ?? "")}
              onChange={(event) => updateDraftValue(field.key, event.target.value)}
              placeholder={field.placeholder}
            />
          );
        }

        return (
          <input
            type={field.kind === "number" ? "number" : field.kind}
            step={field.step}
            value={String(value ?? "")}
            onChange={(event) => updateDraftValue(field.key, event.target.value)}
            placeholder={field.placeholder}
          />
        );
    }
  };

  const sectionFields: Record<StaffTabKey, string[]> = {
    personal: ["firstName", "lastName", "staffTypeId", "assignedLocationIds", "assignedServiceIds", "tagIds", "code", "name", "businessName", "address1", "address2", "city", "state", "postalCode", "country", "phoneNumber", "emergencyContact", "email", "dateOfBirth", "hireDate", "teamLeader", "englishSpeaking"],
    availability: ["availabilityDays", "availabilityStartTime", "availabilityEndTime", "preferredTeamMemberId", "cannotWorkWithStaffIds"],
    financial: ["payType", "compensation", "bankRoutingNumber", "bankAccountNumber"],
    vehicle: ["driverLicenseNumber", "licensePlate", "vehicleYear", "vehicleMake", "vehicleModel", "vehicleColor", "odometer"],
    status: ["active", "isArchived"],
    equipment: ["equipmentRecords", "shirtSize"]
  };

  const staffTabs: Array<{ key: StaffTabKey; label: string; description: string }> = [
    { key: "personal", label: "Personal Information", description: "Core worker identity and contact information." },
    { key: "availability", label: "Availability", description: "Weekly work days, time limits, and same-type team preferences." },
    { key: "financial", label: "Financial", description: "Pay structure and payout details." },
    { key: "vehicle", label: "Vehicle", description: "Vehicle and driver licensing information." },
    { key: "status", label: "Status", description: "Active/archive state for scheduling." },
    { key: "equipment", label: "Equipment", description: "Assigned equipment and uniform details." }
  ];

  if (selectedId) {
    return (
      <div className="stack">
        <SectionHeader
          title={
            selectedRecord
              ? `${String(getPathValue(draft, "firstName") ?? "")} ${String(getPathValue(draft, "lastName") ?? "")}`.trim() ||
                String(getPathValue(draft, "name") || "Edit staff")
              : "New staff"
          }
          description="Edit staff details in grouped sections without leaving the Staff workflow."
          actions={
            <div className="customer-directory-actions">
              <Button variant="secondary" onClick={() => setSearchParams({})}>Back to directory</Button>
              {selectedRecord ? (
                <Button
                  variant="ghost"
                  onClick={() => deleteMutation.mutate(String(selectedRecord.id ?? ""))}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? "Deleting..." : "Delete"}
                </Button>
              ) : null}
              <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                {mutation.isPending ? "Saving..." : selectedRecord ? "Save changes" : "Create staff"}
              </Button>
            </div>
          }
        />
        <Card title="Staff tabs" eyebrow="Grouped editor">
          <div className="customer-tab-bar">
            {staffTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`customer-tab-button ${activeTab === tab.key ? "is-active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </Card>
        {staffTabs
          .filter((tab) => tab.key === activeTab)
          .map((tab) => (
            <Card key={tab.key} title={tab.label} eyebrow="Staff details">
              <p className="card-copy">{tab.description}</p>
              <div className="settings-fields-grid">
                {sectionFields[tab.key].map((fieldKey) => {
                  const field = config.fields.find((entry) => entry.key === fieldKey);
                  if (!field) {
                    return null;
                  }
                  return (
                    <Field key={field.key} label={field.label} hint={field.kind === "checkbox" ? undefined : field.hint}>
                      {renderStaffField(field)}
                    </Field>
                  );
                })}
              </div>
            </Card>
          ))}
        {selectedRecord ? (
          <>
            <Card title="Performance" eyebrow="Feedback visibility">
              <div className="feedback-summary-grid">
                <div className="booking-meta-card">
                  <p className="eyebrow">Average rating</p>
                  <strong>
                    {selectedRecord.averageRating == null ? "No ratings yet" : `${Number(selectedRecord.averageRating).toFixed(2)} / 5`}
                  </strong>
                  <p>Current-assignment feedback follows the live booking assignment list.</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Ratings</p>
                  <strong>{Number(selectedRecord.ratingCount ?? 0)}</strong>
                  <p>Total rating records currently applied to this worker.</p>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Low ratings</p>
                  <strong>{Number(selectedRecord.lowRatingCount ?? 0)}</strong>
                  <p>Ratings currently flagged for office follow-up.</p>
                </div>
              </div>
            </Card>

            <Card title="Login access" eyebrow="Staff app sign-in">
              {staffLoginAccessQuery.isLoading ? (
                <p className="card-copy">Loading login access...</p>
              ) : staffLoginAccessQuery.error ? (
                <p className="error-text">{(staffLoginAccessQuery.error as Error).message}</p>
              ) : (
                <div className="stack">
                  {staffLoginAccessQuery.data?.linkedUser ? (
                    <Card
                      title={staffLoginAccessQuery.data.linkedUser.name || staffLoginAccessQuery.data.linkedUser.email}
                      eyebrow="Linked login"
                      actions={
                        <Badge tone={staffLoginAccessQuery.data.linkedUser.isActive ? "success" : "warning"}>
                          {staffLoginAccessQuery.data.linkedUser.isActive ? "Active" : "Inactive"}
                        </Badge>
                      }
                    >
                      <div className="stack stack--tight">
                        <p className="card-copy">
                          {staffLoginAccessQuery.data.linkedUser.email} · {staffLoginAccessQuery.data.linkedUser.roles.join(", ") || "No roles"}
                        </p>
                        <p className="card-copy">
                          {staffLoginAccessQuery.data.linkedUser.lastLoginAt
                            ? `Last login ${String(staffLoginAccessQuery.data.linkedUser.lastLoginAt)}`
                            : "This login has not been used yet."}
                        </p>
                        <div className="hero__actions">
                          <Button
                            variant="secondary"
                            onClick={() => loginStatusMutation.mutate(!Boolean(staffLoginAccessQuery.data?.linkedUser?.isActive))}
                            disabled={loginStatusMutation.isPending}
                          >
                            {loginStatusMutation.isPending
                              ? "Saving..."
                              : staffLoginAccessQuery.data.linkedUser.isActive
                                ? "Deactivate login"
                                : "Activate login"}
                          </Button>
                          <Button variant="ghost" onClick={() => unlinkLoginMutation.mutate()} disabled={unlinkLoginMutation.isPending}>
                            {unlinkLoginMutation.isPending ? "Unlinking..." : "Unlink login"}
                          </Button>
                        </div>
                        <div className="settings-fields-grid">
                          <Field label="Reset password" hint="Set a new temporary password for this staff login.">
                            <input
                              type="password"
                              value={passwordResetDraft}
                              onChange={(event) => setPasswordResetDraft(event.target.value)}
                              placeholder="Minimum 8 characters"
                            />
                          </Field>
                        </div>
                        <div className="hero__actions">
                          <Button
                            onClick={() => resetPasswordMutation.mutate()}
                            disabled={resetPasswordMutation.isPending || passwordResetDraft.trim().length < 8}
                          >
                            {resetPasswordMutation.isPending ? "Resetting..." : "Reset password"}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ) : (
                    <Card title="Create login" eyebrow="No linked user yet">
                      <div className="settings-fields-grid">
                        <Field label="Email">
                          <input
                            type="email"
                            value={loginCreateDraft.email}
                            onChange={(event) => setLoginCreateDraft((current) => ({ ...current, email: event.target.value }))}
                          />
                        </Field>
                        <Field label="Temporary password">
                          <input
                            type="password"
                            value={loginCreateDraft.password}
                            onChange={(event) => setLoginCreateDraft((current) => ({ ...current, password: event.target.value }))}
                            placeholder="Minimum 8 characters"
                          />
                        </Field>
                        <Field label="First name">
                          <input
                            type="text"
                            value={loginCreateDraft.firstName}
                            onChange={(event) => setLoginCreateDraft((current) => ({ ...current, firstName: event.target.value }))}
                          />
                        </Field>
                        <Field label="Last name">
                          <input
                            type="text"
                            value={loginCreateDraft.lastName}
                            onChange={(event) => setLoginCreateDraft((current) => ({ ...current, lastName: event.target.value }))}
                          />
                        </Field>
                        <Field label="Active">
                          <label className="checkbox-row">
                            <input
                              type="checkbox"
                              checked={loginCreateDraft.active}
                              onChange={(event) => setLoginCreateDraft((current) => ({ ...current, active: event.target.checked }))}
                            />
                            <span>Create this login as active</span>
                          </label>
                        </Field>
                      </div>
                      <div className="hero__actions">
                        <Button
                          onClick={() => createLoginMutation.mutate()}
                          disabled={
                            createLoginMutation.isPending ||
                            loginCreateDraft.email.trim() === "" ||
                            loginCreateDraft.password.trim().length < 8
                          }
                        >
                          {createLoginMutation.isPending ? "Creating..." : "Create linked login"}
                        </Button>
                      </div>
                    </Card>
                  )}

                  <Card title="Link existing user" eyebrow="Manual link">
                    {staffLoginAccessQuery.data?.linkableUsers.length ? (
                      <div className="stack stack--tight">
                        {staffLoginAccessQuery.data.linkableUsers.map((user) => (
                          <div key={user.userId} className="booking-meta-card">
                            <p className="eyebrow">{user.name || user.email}</p>
                            <strong>{user.email}</strong>
                            <p>{user.roles.join(", ") || "No roles"}</p>
                            <div className="hero__actions">
                              <Button
                                variant="secondary"
                                onClick={() => linkLoginMutation.mutate(user.userId)}
                                disabled={linkLoginMutation.isPending}
                              >
                                {linkLoginMutation.isPending && linkLoginMutation.variables === user.userId ? "Linking..." : "Link this user"}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="card-copy">No unlinked users are available in this account right now.</p>
                    )}
                  </Card>

                  {createLoginMutation.error ? <p className="error-text">{(createLoginMutation.error as Error).message}</p> : null}
                  {linkLoginMutation.error ? <p className="error-text">{(linkLoginMutation.error as Error).message}</p> : null}
                  {unlinkLoginMutation.error ? <p className="error-text">{(unlinkLoginMutation.error as Error).message}</p> : null}
                  {loginStatusMutation.error ? <p className="error-text">{(loginStatusMutation.error as Error).message}</p> : null}
                  {resetPasswordMutation.error ? <p className="error-text">{(resetPasswordMutation.error as Error).message}</p> : null}
                </div>
              )}
            </Card>

            <Card title="Feedback history" eyebrow="What the maid can see">
              {staffFeedbackHistoryQuery.isLoading ? (
                <p className="card-copy">Loading feedback history...</p>
              ) : staffFeedbackHistoryQuery.error ? (
                <p className="error-text">{(staffFeedbackHistoryQuery.error as Error).message}</p>
              ) : staffFeedbackHistoryQuery.data?.items.length ? (
                <div className="stack stack--tight">
                  {staffFeedbackHistoryQuery.data.items.map((item) => (
                    <Card
                      key={`${item.bookingId}-${item.submittedAt ?? item.clickedAt ?? "feedback"}`}
                      title={item.bookingPublicReference ? `${item.bookingPublicReference} · ${item.serviceName}` : item.serviceName}
                      eyebrow={item.lowRatingFlaggedAt ? "Low rating" : "Feedback"}
                    >
                      <div className="stack stack--tight">
                        <p className="card-copy">
                          {item.startsAt ? new Date(item.startsAt).toLocaleString() : "Visit time unavailable"}
                          {item.locationLabel ? ` · ${item.locationLabel}` : ""}
                        </p>
                        <p className="card-copy">
                          Rating: {item.effectiveRating == null ? "Not rated yet" : `${item.effectiveRating}/5`}
                          {item.overallRating == null && item.clickedRating != null ? " (email click only)" : ""}
                        </p>
                        {item.showAreaRatings ? (
                          <DataTable
                            columns={["Kitchen", "Bathrooms", "Bedrooms", "Living areas"]}
                            rows={[
                              [
                                item.kitchenRating == null ? "" : `${item.kitchenRating}/5`,
                                item.bathroomsRating == null ? "" : `${item.bathroomsRating}/5`,
                                item.bedroomsRating == null ? "" : `${item.bedroomsRating}/5`,
                                item.livingAreasRating == null ? "" : `${item.livingAreasRating}/5`
                              ]
                            ]}
                          />
                        ) : null}
                        <p className="card-copy">{item.comment || "No comment included."}</p>
                        {item.photos.length ? (
                          <div className="feedback-photo-grid">
                            {item.photos.map((photo) => (
                              <a
                                key={photo.key}
                                href={`/api/bookings/${encodeURIComponent(item.bookingId)}/feedback/photos/download?key=${encodeURIComponent(photo.key)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="feedback-photo-card"
                              >
                                <img
                                  src={`/api/bookings/${encodeURIComponent(item.bookingId)}/feedback/photos/download?key=${encodeURIComponent(photo.key)}`}
                                  alt={photo.name}
                                />
                                <span>{photo.name}</span>
                              </a>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="card-copy">No feedback is currently visible for this staff member.</p>
              )}
            </Card>
          </>
        ) : null}
        {saveError ? <p className="error-text">{saveError}</p> : null}
        {deleteMutation.error ? <p className="error-text">{(deleteMutation.error as Error).message}</p> : null}
      </div>
    );
  }

  return (
    <div className="stack">
      <SectionHeader
        title={`Staff (${directoryCounts[directoryCategory]})`}
        description="Search the staff directory, import new records, or open a staff profile in the same tab."
        actions={
          <div className="customer-directory-actions">
            <input
              ref={importInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  return;
                }

                try {
                  const csv = await file.text();
                  const summary = summarizeStaffCsv(csv);
                  setStaffImportFeedback(
                    summary.count
                      ? `Importing ${summary.count} staff row${summary.count === 1 ? "" : "s"} from ${file.name}${summary.sample?.length ? ` (${summary.sample.join(", ")}${summary.count > summary.sample.length ? ", ..." : ""})` : ""}.`
                      : `No staff rows found in ${file.name}.`
                  );
                  if (summary.count) {
                    importStaffMutation.mutate(csv);
                  }
                } catch (error) {
                  setStaffImportFeedback(error instanceof Error ? error.message : "Staff import failed.");
                }

                event.currentTarget.value = "";
              }}
            />
            <Button variant="secondary" onClick={() => importInputRef.current?.click()} disabled={importStaffMutation.isPending}>
              {importStaffMutation.isPending ? "Importing..." : "Import Staff"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                const blob = new Blob([buildStaffCsv(items, getStaffTypeLabel)], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "staff-export.csv";
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export Staff
            </Button>
            <Button onClick={() => setSearchParams({ new: "1" })}>New staff</Button>
          </div>
        }
      />
      <Card title="Staff Directory" eyebrow="Records">
        <div className="customer-tab-bar">
          <button
            type="button"
            className={`customer-tab-button ${directoryCategory === "maids" ? "is-active" : ""}`}
            onClick={() => setDirectoryCategory("maids")}
          >
            Maids ({directoryCounts.maids})
          </button>
          <button
            type="button"
            className={`customer-tab-button ${directoryCategory === "drivers" ? "is-active" : ""}`}
            onClick={() => setDirectoryCategory("drivers")}
          >
            Drivers ({directoryCounts.drivers})
          </button>
          <button
            type="button"
            className={`customer-tab-button ${directoryCategory === "office" ? "is-active" : ""}`}
            onClick={() => setDirectoryCategory("office")}
          >
            Office Staff ({directoryCounts.office})
          </button>
        </div>
        <div className="customer-directory-toolbar">
          <input
            className="customer-directory-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search staff by code, name, email, phone, or business..."
          />
        </div>
        <p className="card-copy">Import Staff adds new staff from CSV and updates matching staff by code, email, or name without deleting the rest of your records.</p>
        {staffImportFeedback ? <p className="card-copy">{staffImportFeedback}</p> : null}
        {importStaffMutation.error ? <p className="error-text">{(importStaffMutation.error as Error).message}</p> : null}
        <DataTable
          columns={["Code", "Name", "Staff type", "Phone", "Email", "Pay", "Status", "Action"]}
          rows={filteredItems.map((item) => [
            String(item.code ?? ""),
            `${String(item.firstName ?? "")} ${String(item.lastName ?? "")}`.trim() || String(item.name ?? ""),
            getStaffTypeLabel(item),
            String(item.phoneNumber ?? ""),
            String(item.email ?? ""),
            `${String(item.payType ?? "")} ${item.compensation ?? ""}`.trim(),
            <Badge tone={item.active ? "success" : "warning"}>{item.active ? "Active" : "Inactive"}</Badge>,
            <div key={String(item.id ?? "")} className="table-action-row">
              <Button variant="ghost" onClick={() => setSearchParams({ staff: String(item.id ?? "") })}>Edit</Button>
              <Button
                variant="ghost"
                onClick={() => deleteMutation.mutate(String(item.id ?? ""))}
                disabled={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          ])}
        />
      </Card>
    </div>
  );
}

function ServicesSettingsEditor() {
  const config = relationalSectionConfigs.services;
  const queryClient = useQueryClient();
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const iconInputRef = useRef<HTMLInputElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("service") ?? "";
  const [draft, setDraft] = useState<SettingsValue>(getServiceDraftBase(undefined, config.defaults));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [importFeedback, setImportFeedback] = useState<string | null>(null);
  const [priceAdjusterDraft, setPriceAdjusterDraft] = useState<PriceAdjusterDraft>({
    direction: "increase",
    percent: "5",
    roundToNearestDollar: false,
    serviceIds: [],
    extraIds: []
  });
  const [priceAdjusterPreview, setPriceAdjusterPreview] = useState<PriceAdjusterResponse | null>(null);
  const [priceAdjusterFeedback, setPriceAdjusterFeedback] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["settings", "services"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/services")
  });
  const extrasQuery = useQuery({
    queryKey: ["settings", "extras"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/extras")
  });
  const catalogQuery = useQuery({
    queryKey: ["service-editor-catalog"],
    queryFn: () => apiFetch<ServiceEditorCatalog>("/api/booking-form/config")
  });
  const items = query.data?.items ?? [];
  const selectedRecord = useMemo(
    () => (selectedId && selectedId !== "new" ? items.find((item) => String(item.id ?? "") === selectedId) : undefined),
    [items, selectedId]
  );

  const openServiceEditor = (id: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("service", id);
      return next;
    });
  };

  const closeServiceEditor = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("service");
      return next;
    });
  };

  useEffect(() => {
    if (selectedId && selectedId !== "new" && !selectedRecord) {
      closeServiceEditor();
    }
  }, [items, selectedId, selectedRecord]);

  useEffect(() => {
    const base = selectedRecord ?? config.defaults;
    setDraft(getServiceDraftBase(base, config.defaults));
    setSaveError(null);
  }, [config.defaults, selectedRecord]);

  const mutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      const base = { ...config.defaults, ...(selectedRecord ?? {}) };
      const hydrated = buildPayload(config, base, draft);
      const payload = {
        ...hydrated,
        icon: String(getPathValue(draft, "icon") ?? ""),
        displayOrder: Number(getPathValue(draft, "displayOrder") ?? 0),
        durationMinutes: Number(getPathValue(draft, "durationMinutes") ?? 180),
        basePriceAmount: dollarsToCents(getPathValue(draft, "basePriceDollars")),
        bufferBeforeMinutes: Number(getPathValue(draft, "bufferBeforeMinutes") ?? 0),
        bufferAfterMinutes: Number(getPathValue(draft, "bufferAfterMinutes") ?? 30),
        assignedExtraIds: normalizeStringList(getPathValue(draft, "assignedExtraIds")),
        availableLocationIds: normalizeStringList(getPathValue(draft, "availableLocationIds")),
        allowAllActiveFrequencyRules: Boolean(getPathValue(draft, "allowAllActiveFrequencyRules") ?? true),
        allowedFrequencyRuleIds: normalizeStringList(getPathValue(draft, "allowedFrequencyRuleIds")),
        defaultCalendarProfileId: String(getPathValue(draft, "defaultCalendarProfileId") ?? ""),
        staffOnly: Boolean(getPathValue(draft, "staffOnly")),
        active: Boolean(getPathValue(draft, "active"))
      };

      try {
        return await apiFetch(`/api/settings/services${selectedRecord ? `/${selectedRecord.id}` : ""}`, {
          method: selectedRecord ? "PUT" : "POST",
          body: JSON.stringify(payload)
        });
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async (result: any) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "services"] });
      await queryClient.invalidateQueries({ queryKey: ["settings", "extras"] });
      await queryClient.invalidateQueries({ queryKey: ["service-editor-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["extra-editor-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["booking-form-config"] });
      const nextId = selectedRecord ? String(selectedRecord.id ?? "") : String(result?.id ?? "");
      openServiceEditor(nextId || "new");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      apiFetch<{ id: string }>(`/api/settings/services/${id}`, {
        method: "DELETE"
      }),
    onSuccess: async (_, deletedId) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "services"] });
      await queryClient.invalidateQueries({ queryKey: ["settings", "extras"] });
      await queryClient.invalidateQueries({ queryKey: ["service-editor-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["extra-editor-catalog"] });
      if (selectedId === deletedId) {
        closeServiceEditor();
      }
    }
  });

  const importMutation = useMutation({
    mutationFn: async (csv: string) => {
      const rows = csv
        .replace(/^\uFEFF/, "")
        .split(/\r?\n/)
        .filter((line) => line.trim() !== "");

      if (!rows.length) {
        return { imported: 0, created: 0, updated: 0 };
      }

      const headers = parseCsvLine(rows[0]).map((value) => value.trim());
      const existingById = new Map(items.map((item) => [String(item.id ?? ""), item] as const));
      const existingByName = new Map(items.map((item) => [String(item.name ?? "").trim().toLowerCase(), item] as const));
      let created = 0;
      let updated = 0;

      for (const row of rows.slice(1)) {
        const cells = parseCsvLine(row);
        const record = Object.fromEntries(headers.map((header, index) => [header, cells[index] ?? ""])) as Record<string, string>;
        const name = String(record.name ?? "").trim();
        if (!name) {
          continue;
        }

        const matched =
          existingById.get(String(record.id ?? "").trim()) ??
          existingByName.get(name.toLowerCase());

        const payload = {
          name,
          icon: record.icon ?? "",
          category: record.category ?? "cleaning",
          description: record.description ?? "",
          displayOrder: Number(record.display_order || 0),
          durationMinutes: Number(record.duration_minutes || 180),
          basePriceAmount: dollarsToCents(record.base_price_dollars || "0"),
          bufferBeforeMinutes: Number(record.buffer_before_minutes || 0),
          bufferAfterMinutes: Number(record.buffer_after_minutes || 30),
          staffOnly: parseBooleanValue(record.staff_only),
          active: record.active === "" ? true : parseBooleanValue(record.active),
          assignedExtraIds: parseDelimitedCell(record.assigned_extra_ids),
          availableLocationIds: parseDelimitedCell(record.available_location_ids),
          allowAllActiveFrequencyRules:
            record.allow_all_active_frequencies === ""
              ? true
              : parseBooleanValue(record.allow_all_active_frequencies),
          allowedFrequencyRuleIds: parseDelimitedCell(record.allowed_frequency_rule_ids),
          defaultCalendarProfileId: record.default_calendar_profile_id ?? "",
          pricingRules: String(record.pricing_rules_json ?? "").trim() ? JSON.parse(record.pricing_rules_json) : {},
          extraVisibilityRules: String(record.extra_visibility_rules_json ?? "").trim()
            ? JSON.parse(record.extra_visibility_rules_json)
            : {}
        };

        await apiFetch(`/api/settings/services${matched ? `/${matched.id}` : ""}`, {
          method: matched ? "PUT" : "POST",
          body: JSON.stringify(payload)
        });

        if (matched) {
          updated += 1;
        } else {
          created += 1;
        }
      }

      return { imported: created + updated, created, updated };
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "services"] });
      await queryClient.invalidateQueries({ queryKey: ["settings", "extras"] });
      await queryClient.invalidateQueries({ queryKey: ["service-editor-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["extra-editor-catalog"] });
      setImportFeedback(`Imported ${result.imported} service row${result.imported === 1 ? "" : "s"}: ${result.created} new, ${result.updated} updated.`);
    }
  });

  const priceAdjusterPreviewMutation = useMutation({
    mutationFn: async (payload: PriceAdjusterInput) =>
      apiFetch<PriceAdjusterResponse>("/api/settings/price-adjuster/preview", {
        method: "POST",
        body: JSON.stringify(payload)
      }),
    onSuccess: (result) => {
      setPriceAdjusterPreview(result);
      setPriceAdjusterFeedback(null);
    }
  });

  const priceAdjusterApplyMutation = useMutation({
    mutationFn: async (payload: PriceAdjusterInput) =>
      apiFetch<PriceAdjusterResponse>("/api/settings/price-adjuster/apply", {
        method: "POST",
        body: JSON.stringify(payload)
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "services"] });
      await queryClient.invalidateQueries({ queryKey: ["settings", "extras"] });
      await queryClient.invalidateQueries({ queryKey: ["service-editor-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["extra-editor-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["booking-form-config"] });
      setPriceAdjusterPreview(result);
      setPriceAdjusterFeedback(`Applied ${result.changedCount} price change${result.changedCount === 1 ? "" : "s"}.`);
    }
  });

  const serviceOptions = (catalogQuery.data?.serviceOptions ?? []).filter((item) => Boolean(item.active ?? true));
  const locations = (catalogQuery.data?.locations ?? []).filter((item) => Boolean(item.active ?? true));
  const frequencyRules = (catalogQuery.data?.frequencyRules ?? []).filter((item) => Boolean(item.active ?? true));
  const calendarProfiles = (catalogQuery.data?.calendarProfiles ?? []).filter((item) => Boolean(item.active ?? true));
  const sortedItems = useMemo(
    () =>
      [...items].sort((left, right) => {
        const orderDifference = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
        if (orderDifference !== 0) {
          return orderDifference;
        }
        return String(left.name ?? "").localeCompare(String(right.name ?? ""));
      }),
    [items]
  );
  const priceAdjusterServices = useMemo(
    () => sortedItems.filter((item) => Boolean(item.active ?? true) && !Boolean(item.staffOnly)),
    [sortedItems]
  );
  const publicServiceIds = useMemo(
    () => new Set(priceAdjusterServices.map((item) => String(item.id ?? "")).filter(Boolean)),
    [priceAdjusterServices]
  );
  const serviceAssignedExtraIds = useMemo(
    () => new Set(priceAdjusterServices.flatMap((item) => normalizeStringList(item.assignedExtraIds))),
    [priceAdjusterServices]
  );
  const priceAdjusterExtras = useMemo(() => {
    return [...(extrasQuery.data?.items ?? [])]
      .filter((extra) => {
        if (!Boolean(extra.active ?? true)) {
          return false;
        }
        const extraId = String(extra.id ?? "");
        const assignedServiceIds = normalizeStringList(extra.assignedServiceIds);
        return assignedServiceIds.some((serviceId) => publicServiceIds.has(serviceId)) || serviceAssignedExtraIds.has(extraId);
      })
      .sort((left, right) => String(left.name ?? "").localeCompare(String(right.name ?? "")));
  }, [extrasQuery.data?.items, publicServiceIds, serviceAssignedExtraIds]);
  const groupedPriceAdjusterServices = useMemo(() => {
    const groups = new Map<string, SettingsValue[]>();
    for (const service of priceAdjusterServices) {
      const label = String(service.category ?? "").trim() || "Services";
      groups.set(label, [...(groups.get(label) ?? []), service]);
    }
    return Array.from(groups.entries()).sort(([left], [right]) => left.localeCompare(right));
  }, [priceAdjusterServices]);
  const priceAdjusterPayload = buildPriceAdjusterPayload(priceAdjusterDraft);
  const priceAdjusterSelectionCount = priceAdjusterPayload.serviceIds.length + priceAdjusterPayload.extraIds.length;
  const canPreviewPriceAdjustment = priceAdjusterSelectionCount > 0 && priceAdjusterPayload.percent >= 0;
  const assignedExtraIds = normalizeStringList(getPathValue(draft, "assignedExtraIds"));
  const availableLocationIds = normalizeStringList(getPathValue(draft, "availableLocationIds"));
  const allowAllActiveFrequencyRules = Boolean(getPathValue(draft, "allowAllActiveFrequencyRules") ?? true);
  const allowedFrequencyRuleIds = normalizeStringList(getPathValue(draft, "allowedFrequencyRuleIds"));

  const updatePriceAdjusterDraft = (updater: (current: PriceAdjusterDraft) => PriceAdjusterDraft) => {
    setPriceAdjusterPreview(null);
    setPriceAdjusterFeedback(null);
    setPriceAdjusterDraft(updater);
  };

  const togglePriceAdjusterValue = (key: "serviceIds" | "extraIds", value: string, checked: boolean) => {
    updatePriceAdjusterDraft((current) => {
      const existing = normalizeStringList(current[key]);
      return {
        ...current,
        [key]: checked ? Array.from(new Set([...existing, value])) : existing.filter((entry) => entry !== value)
      };
    });
  };

  const setPriceAdjusterValues = (key: "serviceIds" | "extraIds", values: string[]) => {
    updatePriceAdjusterDraft((current) => ({
      ...current,
      [key]: normalizeStringList(values)
    }));
  };

  const toggleListValue = (key: string, value: string, checked: boolean) => {
    const current = normalizeStringList(getPathValue(draft, key));
    const next = checked ? Array.from(new Set([...current, value])) : current.filter((entry) => entry !== value);
    setDraft((existing) => setPathValue(existing, key, next));
  };

  return (
    <div className="stack">
      <SectionHeader
        title="Settings: Services"
        description="Manage icons, pricing, display order, extras, locations, and staff-only visibility for each service."
        actions={
          <div className="customer-directory-actions">
            <input
              ref={importInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  return;
                }

                try {
                  const csv = await file.text();
                  setImportFeedback(`Importing services from ${file.name}...`);
                  importMutation.mutate(csv);
                } catch (error) {
                  setImportFeedback(error instanceof Error ? error.message : "Service import failed.");
                }

                event.currentTarget.value = "";
              }}
            />
            <Button variant="secondary" onClick={() => importInputRef.current?.click()} disabled={importMutation.isPending}>
              {importMutation.isPending ? "Importing..." : "Import services"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                const blob = new Blob([buildServicesCsv(sortedItems)], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "services-export.csv";
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export services
            </Button>
            {!selectedId ? <Button variant="secondary" onClick={() => openServiceEditor("new")}>New service</Button> : null}
          </div>
        }
      />
      {!selectedId ? (
      <>
      <Card title="Service Price Adjuster" eyebrow="Bulk pricing tool">
        <p className="card-copy">
          Adjust active public service and extra prices in bulk. Preview the exact changes before applying them.
        </p>
        <div className="settings-fields-grid">
          <Field label="Adjustment">
            <div className="segmented-choice">
              {(["increase", "decrease"] as const).map((direction) => (
                <label key={direction} className="radio-card">
                  <input
                    type="radio"
                    checked={priceAdjusterDraft.direction === direction}
                    onChange={() =>
                      updatePriceAdjusterDraft((current) => ({
                        ...current,
                        direction,
                        percent:
                          direction === "decrease" && Number(current.percent || 0) > 100
                            ? "100"
                            : current.percent
                      }))
                    }
                  />
                  <span>{direction === "increase" ? "Increase" : "Decrease"}</span>
                </label>
              ))}
            </div>
          </Field>
          <Field label="Amount">
            <input
              type="number"
              min="0"
              max={priceAdjusterDraft.direction === "decrease" ? 100 : 500}
              step="0.01"
              value={priceAdjusterDraft.percent}
              onChange={(event) =>
                updatePriceAdjusterDraft((current) => ({
                  ...current,
                  percent: event.target.value
                }))
              }
            />
          </Field>
          <Field label="Rounding">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={priceAdjusterDraft.roundToNearestDollar}
                onChange={(event) =>
                  updatePriceAdjusterDraft((current) => ({
                    ...current,
                    roundToNearestDollar: event.target.checked
                  }))
                }
              />
              <span>Round to nearest dollar</span>
            </label>
          </Field>
        </div>

        <div className="settings-fields-grid">
          <Field label="Services" hint="Only active public services are shown.">
            <div className="stack stack--tight">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={
                    priceAdjusterServices.length > 0 &&
                    priceAdjusterServices.every((service) =>
                      priceAdjusterPayload.serviceIds.includes(String(service.id ?? ""))
                    )
                  }
                  onChange={(event) =>
                    setPriceAdjusterValues(
                      "serviceIds",
                      event.target.checked ? priceAdjusterServices.map((service) => String(service.id ?? "")).filter(Boolean) : []
                    )
                  }
                />
                <span>All public services</span>
              </label>
              {groupedPriceAdjusterServices.map(([category, services]) => (
                <div key={category} className="stack stack--tight">
                  <strong>{category}</strong>
                  {services.map((service) => {
                    const id = String(service.id ?? "");
                    return (
                      <label key={id} className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={priceAdjusterPayload.serviceIds.includes(id)}
                          onChange={(event) => togglePriceAdjusterValue("serviceIds", id, event.target.checked)}
                        />
                        <span>{String(service.name ?? id)} - {formatCurrency(Number(service.basePriceAmount ?? 0))}</span>
                      </label>
                    );
                  })}
                </div>
              ))}
              {!priceAdjusterServices.length ? <p className="card-copy">No active public services are available.</p> : null}
            </div>
          </Field>
          <Field label="Extras" hint="Only active extras assigned to active public services are shown.">
            <div className="stack stack--tight">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={
                    priceAdjusterExtras.length > 0 &&
                    priceAdjusterExtras.every((extra) =>
                      priceAdjusterPayload.extraIds.includes(String(extra.id ?? ""))
                    )
                  }
                  onChange={(event) =>
                    setPriceAdjusterValues(
                      "extraIds",
                      event.target.checked ? priceAdjusterExtras.map((extra) => String(extra.id ?? "")).filter(Boolean) : []
                    )
                  }
                />
                <span>All public extras</span>
              </label>
              {priceAdjusterExtras.map((extra) => {
                const id = String(extra.id ?? "");
                return (
                  <label key={id} className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={priceAdjusterPayload.extraIds.includes(id)}
                      onChange={(event) => togglePriceAdjusterValue("extraIds", id, event.target.checked)}
                    />
                    <span>{String(extra.name ?? id)} - {formatCurrency(Number(extra.priceDeltaAmount ?? 0))}</span>
                  </label>
                );
              })}
              {!priceAdjusterExtras.length ? <p className="card-copy">No active public extras are available.</p> : null}
            </div>
          </Field>
        </div>

        <div className="customer-directory-actions">
          <Button
            onClick={() => priceAdjusterPreviewMutation.mutate(priceAdjusterPayload)}
            disabled={!canPreviewPriceAdjustment || priceAdjusterPreviewMutation.isPending}
          >
            {priceAdjusterPreviewMutation.isPending ? "Previewing..." : "Preview changes"}
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              updatePriceAdjusterDraft((current) => ({
                ...current,
                serviceIds: [],
                extraIds: []
              }))
            }
            disabled={priceAdjusterSelectionCount === 0}
          >
            Clear selection
          </Button>
        </div>
        {priceAdjusterPreviewMutation.error ? <p className="error-text">{(priceAdjusterPreviewMutation.error as Error).message}</p> : null}
        {priceAdjusterApplyMutation.error ? <p className="error-text">{(priceAdjusterApplyMutation.error as Error).message}</p> : null}
        {priceAdjusterFeedback ? <p className="card-copy">{priceAdjusterFeedback}</p> : null}
        {priceAdjusterPreview ? (
          <div className="stack">
            <DataTable
              columns={["Type", "Name", "Current", "New", "Change", "Status"]}
              rows={priceAdjusterPreview.items.map((item) => [
                item.entityType === "service" ? "Service" : "Extra",
                item.name,
                formatCurrency(item.oldPriceAmount),
                formatCurrency(item.newPriceAmount),
                `${item.deltaAmount >= 0 ? "+" : "-"}${formatCurrency(Math.abs(item.deltaAmount))}`,
                item.skipped ? item.skipReason || "Skipped" : "Ready"
              ])}
            />
            <div className="customer-directory-actions">
              <Button
                onClick={() => priceAdjusterApplyMutation.mutate(priceAdjusterPayload)}
                disabled={priceAdjusterApplyMutation.isPending || priceAdjusterPreview.changedCount === 0}
              >
                {priceAdjusterApplyMutation.isPending ? "Applying..." : `Apply ${priceAdjusterPreview.changedCount} change${priceAdjusterPreview.changedCount === 1 ? "" : "s"}`}
              </Button>
              <span className="card-copy">
                {priceAdjusterPreview.skippedCount} skipped / no-op item{priceAdjusterPreview.skippedCount === 1 ? "" : "s"}.
              </span>
            </div>
          </div>
        ) : null}
      </Card>
      <Card title="Services" eyebrow="Records">
        <p className="card-copy">{config.description}</p>
        {importFeedback ? <p className="card-copy">{importFeedback}</p> : null}
        {importMutation.error ? <p className="error-text">{(importMutation.error as Error).message}</p> : null}
        <DataTable
          columns={["Icon", "Name", "Order", "Price", "Staff only", "Action"]}
          rows={sortedItems.map((item) => [
            String(item.icon ?? "") ? (
              String(item.icon).startsWith("data:image") || String(item.icon).startsWith("http") ? (
                <img
                  key={`${item.id}-icon`}
                  src={String(item.icon)}
                  alt={String(item.name ?? "Service icon")}
                  style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 6 }}
                />
              ) : (
                <span key={`${item.id}-icon-text`}>{String(item.icon ?? "")}</span>
              )
            ) : "—",
            String(item.name ?? ""),
            String(item.displayOrder ?? 0),
            formatCurrency(Number(item.basePriceAmount ?? 0)),
            <Badge tone={item.staffOnly ? "warning" : "success"}>{item.staffOnly ? "Staff only" : "Public"}</Badge>,
            <div key={String(item.id ?? "")} className="table-action-row">
              <Button variant="ghost" onClick={() => openServiceEditor(String(item.id ?? ""))}>Edit</Button>
              <Button variant="ghost" onClick={() => deleteMutation.mutate(String(item.id ?? ""))} disabled={deleteMutation.isPending}>Delete</Button>
            </div>
          ])}
        />
      </Card>
      </>
      ) : (
      <Card
        title={selectedRecord ? `Edit ${String(selectedRecord.name ?? "service")}` : "New service"}
        eyebrow={selectedRecord ? "Service details" : "Create record"}
        actions={
          <div className="customer-directory-actions">
            <Button variant="secondary" onClick={closeServiceEditor}>Back to services</Button>
            {selectedRecord ? (
              <Button variant="ghost" onClick={() => deleteMutation.mutate(String(selectedRecord.id ?? ""))} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            ) : null}
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? "Saving..." : selectedRecord ? "Save changes" : "Create service"}
            </Button>
          </div>
        }
      >
        <input
          ref={iconInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) {
              return;
            }

            const reader = new FileReader();
            reader.onload = () => {
              if (typeof reader.result === "string") {
                setDraft((current) => setPathValue(current, "icon", reader.result));
              }
            };
            reader.readAsDataURL(file);
            event.currentTarget.value = "";
          }}
        />
        <div className="settings-fields-grid">
          <Field label="Service name"><input value={String(getPathValue(draft, "name") ?? "")} onChange={(event) => setDraft((current) => setPathValue(current, "name", event.target.value))} /></Field>
          <Field label="Icon" hint="Upload an image, use an emoji, or paste an image URL.">
            <div className="stack stack--tight">
              <input value={String(getPathValue(draft, "icon") ?? "")} onChange={(event) => setDraft((current) => setPathValue(current, "icon", event.target.value))} placeholder="📦 or https://..." />
              <div className="customer-directory-actions">
                <Button variant="secondary" onClick={() => iconInputRef.current?.click()}>Upload icon</Button>
                {String(getPathValue(draft, "icon") ?? "") ? (
                  <Button variant="ghost" onClick={() => setDraft((current) => setPathValue(current, "icon", ""))}>Clear icon</Button>
                ) : null}
              </div>
              {String(getPathValue(draft, "icon") ?? "") ? (
                String(getPathValue(draft, "icon") ?? "").startsWith("data:image") || String(getPathValue(draft, "icon") ?? "").startsWith("http") ? (
                  <img
                    src={String(getPathValue(draft, "icon") ?? "")}
                    alt="Service icon preview"
                    style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 8 }}
                  />
                ) : (
                  <div style={{ fontSize: "2rem", lineHeight: 1 }}>{String(getPathValue(draft, "icon") ?? "")}</div>
                )
              ) : null}
            </div>
          </Field>
          <Field label="Display order"><input type="number" value={String(getPathValue(draft, "displayOrder") ?? 0)} onChange={(event) => setDraft((current) => setPathValue(current, "displayOrder", event.target.value))} /></Field>
          <Field label="Duration minutes"><input type="number" value={String(getPathValue(draft, "durationMinutes") ?? 180)} onChange={(event) => setDraft((current) => setPathValue(current, "durationMinutes", event.target.value))} /></Field>
          <Field label="Base price ($)"><input type="number" step="0.01" value={String(getPathValue(draft, "basePriceDollars") ?? "0.00")} onChange={(event) => setDraft((current) => setPathValue(current, "basePriceDollars", event.target.value))} /></Field>
          <Field label="Buffer before (minutes)"><input type="number" value={String(getPathValue(draft, "bufferBeforeMinutes") ?? 0)} onChange={(event) => setDraft((current) => setPathValue(current, "bufferBeforeMinutes", event.target.value))} /></Field>
          <Field label="Buffer after (minutes)"><input type="number" value={String(getPathValue(draft, "bufferAfterMinutes") ?? 30)} onChange={(event) => setDraft((current) => setPathValue(current, "bufferAfterMinutes", event.target.value))} /></Field>
          <Field label="Description" hint="Short internal description for this service.">
            <textarea rows={4} value={String(getPathValue(draft, "description") ?? "")} onChange={(event) => setDraft((current) => setPathValue(current, "description", event.target.value))} />
          </Field>
          <Field label="Default calendar profile">
            <select value={String(getPathValue(draft, "defaultCalendarProfileId") ?? "")} onChange={(event) => setDraft((current) => setPathValue(current, "defaultCalendarProfileId", event.target.value))}>
              <option value="">No default profile</option>
              {calendarProfiles.map((profile) => (
                <option key={String(profile.id ?? "")} value={String(profile.id ?? "")}>{String(profile.name ?? "")}</option>
              ))}
            </select>
          </Field>
          <Field label="Visibility">
            <div className="stack stack--tight">
              <label className="checkbox-row">
                <input type="checkbox" checked={Boolean(getPathValue(draft, "active"))} onChange={(event) => setDraft((current) => setPathValue(current, "active", event.target.checked))} />
                <span>Active</span>
              </label>
              <label className="checkbox-row">
                <input type="checkbox" checked={Boolean(getPathValue(draft, "staffOnly"))} onChange={(event) => setDraft((current) => setPathValue(current, "staffOnly", event.target.checked))} />
                <span>Display service to staff only</span>
              </label>
            </div>
          </Field>
        </div>
        <Field label="Available locations" hint="Only these locations will offer this service. Leave empty for all active locations.">
          <div className="checkbox-grid">
            {locations.map((location) => {
              const id = String(location.id ?? "");
              return (
                <label key={id} className="checkbox-row">
                  <input type="checkbox" checked={availableLocationIds.includes(id)} onChange={(event) => toggleListValue("availableLocationIds", id, event.target.checked)} />
                  <span>{String(location.name ?? id)}</span>
                </label>
              );
            })}
          </div>
        </Field>
        <Field label="Assigned extras" hint="Choose which extras should be offered with this service.">
          <div className="checkbox-grid">
            {serviceOptions.map((option) => {
              const id = String(option.id ?? "");
              return (
                <label key={id} className="checkbox-row">
                  <input type="checkbox" checked={assignedExtraIds.includes(id)} onChange={(event) => toggleListValue("assignedExtraIds", id, event.target.checked)} />
                  <span>{String(option.name ?? id)}</span>
                </label>
              );
            })}
          </div>
        </Field>
        <Field label="Frequency availability" hint="Use all active frequencies by default, or switch to a custom service-specific list.">
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={allowAllActiveFrequencyRules}
              onChange={(event) => setDraft((current) => setPathValue(current, "allowAllActiveFrequencyRules", event.target.checked))}
            />
            <span>Allow all active frequencies</span>
          </label>
        </Field>
        {!allowAllActiveFrequencyRules ? (
          <Field label="Allowed frequencies" hint="Choose which recurring frequencies should be available for this service.">
            <div className="checkbox-grid">
              {frequencyRules.map((rule) => {
                const id = String(rule.id ?? "");
                return (
                  <label key={id} className="checkbox-row">
                    <input type="checkbox" checked={allowedFrequencyRuleIds.includes(id)} onChange={(event) => toggleListValue("allowedFrequencyRuleIds", id, event.target.checked)} />
                    <span>{String(rule.name ?? id)}</span>
                  </label>
                );
              })}
            </div>
          </Field>
        ) : null}
        <Field label="Pricing rules JSON">
          <textarea rows={5} value={String(getPathValue(draft, "pricingRules") ?? "{}")} onChange={(event) => setDraft((current) => setPathValue(current, "pricingRules", event.target.value))} />
        </Field>
        <Field label="Extra visibility rules JSON" hint="Optional advanced visibility rules beyond the assigned extras list.">
          <textarea rows={5} value={String(getPathValue(draft, "extraVisibilityRules") ?? "{}")} onChange={(event) => setDraft((current) => setPathValue(current, "extraVisibilityRules", event.target.value))} />
        </Field>
        {saveError ? <p className="error-text">{saveError}</p> : null}
        {deleteMutation.error ? <p className="error-text">{(deleteMutation.error as Error).message}</p> : null}
      </Card>
      )}
    </div>
  );
}

function ExtrasSettingsEditor() {
  const config = relationalSectionConfigs.extras;
  const queryClient = useQueryClient();
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const iconInputRef = useRef<HTMLInputElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("extra") ?? "";
  const [draft, setDraft] = useState<SettingsValue>(getExtraDraftBase(undefined, config.defaults));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [importFeedback, setImportFeedback] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const query = useQuery({
    queryKey: ["settings", "extras"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/extras")
  });
  const catalogQuery = useQuery({
    queryKey: ["extra-editor-catalog"],
    queryFn: () => apiFetch<ServiceEditorCatalog>("/api/booking-form/config")
  });
  const items = useMemo(
    () =>
      [...(query.data?.items ?? [])].sort((left, right) => {
        const orderDifference = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
        if (orderDifference !== 0) {
          return orderDifference;
        }
        return String(left.name ?? "").localeCompare(String(right.name ?? ""));
      }),
    [query.data?.items]
  );
  const selectedRecord = useMemo(
    () => (selectedId && selectedId !== "new" ? items.find((item) => String(item.id ?? "") === selectedId) : undefined),
    [items, selectedId]
  );

  const services = (catalogQuery.data?.services ?? []).filter((item) => Boolean(item.active ?? true));
  const serviceNameById = useMemo(
    () => new Map(services.map((item) => [String(item.id ?? ""), String(item.name ?? "")] as const)),
    [services]
  );
  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return items;
    }

    return items.filter((item) =>
      [
        item.name,
        item.description,
        item.icon,
        normalizeStringList(item.assignedServiceIds)
          .map((id) => serviceNameById.get(id) ?? id)
          .join(" ")
      ]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [items, search, serviceNameById]);

  const openExtraEditor = (id: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("extra", id);
      return next;
    });
  };

  const closeExtraEditor = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("extra");
      return next;
    });
  };

  useEffect(() => {
    if (selectedId && selectedId !== "new" && !selectedRecord) {
      closeExtraEditor();
    }
  }, [items, selectedId, selectedRecord]);

  useEffect(() => {
    const base = selectedRecord ?? config.defaults;
    setDraft(getExtraDraftBase(base, config.defaults));
    setSaveError(null);
  }, [config.defaults, selectedId, selectedRecord]);

  const mutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      const assignedServiceIds = normalizeStringList(getPathValue(draft, "assignedServiceIds"));
      if (!assignedServiceIds.length) {
        const error = new Error("Select at least one service for this extra.");
        setSaveError(error.message);
        throw error;
      }
      if (carpetShampooingConfig.enabled && !carpetShampooingConfig.units.some((unit) => unit.active)) {
        const error = new Error("Turn on at least one carpet shampooing item before saving.");
        setSaveError(error.message);
        throw error;
      }

      const base = { ...config.defaults, ...(selectedRecord ?? {}) };
      const hydrated = buildPayload(config, base, draft);
      const payload = {
        ...hydrated,
        icon: String(getPathValue(draft, "icon") ?? ""),
        displayOrder: Number(getPathValue(draft, "displayOrder") ?? 0),
        quantityBased:
          Boolean(getPathValue(draft, "quantityBased")) || Boolean(carpetShampooingConfig.enabled),
        priceDeltaAmount: dollarsToCents(getPathValue(draft, "priceDeltaDollars")),
        durationDeltaMinutes:
          Number(getPathValue(draft, "durationHours") ?? 0) * 60 +
          Number(getPathValue(draft, "durationMinutesRemainder") ?? 0),
        assignedServiceIds,
        extraPricingRules: extraPricingRules.map((rule) => ({
          id: rule.id,
          serviceIds: rule.serviceIds,
          squareFootageFrom: rule.squareFootageFrom,
          squareFootageTo: rule.squareFootageTo,
          bedroomsFrom: rule.bedroomsFrom,
          bedroomsTo: rule.bedroomsTo,
          levelsFrom: rule.levelsFrom,
          levelsTo: rule.levelsTo,
          priceDeltaAmount: dollarsToCents(rule.priceDeltaDollars),
          durationDeltaMinutes: Number(rule.durationMinutes || 0),
          displayOrder: Number(rule.displayOrder ?? 0),
          active: Boolean(rule.active)
        })),
        carpetShampooingConfig: {
          enabled: Boolean(carpetShampooingConfig.enabled),
          minimumOrderAmount: dollarsToCents(carpetShampooingConfig.minimumOrderDollars),
          units: carpetShampooingConfig.units.map((unit) => ({
            code: unit.code,
            label: unit.label,
            priceDeltaAmount: dollarsToCents(unit.priceDeltaDollars),
            durationDeltaMinutes: Number(unit.durationMinutes || 0),
            maxQuantity: Number(unit.maxQuantity || 0),
            displayOrder: Number(unit.displayOrder ?? 0),
            active: Boolean(unit.active)
          }))
        },
        excludeFromFrequencyDiscount: Boolean(getPathValue(draft, "excludeFromFrequencyDiscount")),
        excludeFromPromoDiscount: Boolean(getPathValue(draft, "excludeFromPromoDiscount")),
        recurringPolicy: String(getPathValue(draft, "recurringPolicy") ?? "every_booking"),
        active: Boolean(getPathValue(draft, "active"))
      };

      try {
        return await apiFetch(`/api/settings/extras${selectedRecord ? `/${selectedRecord.id}` : ""}`, {
          method: selectedRecord ? "PUT" : "POST",
          body: JSON.stringify(payload)
        });
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async (result: any) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "extras"] });
      await queryClient.invalidateQueries({ queryKey: ["settings", "services"] });
      await queryClient.invalidateQueries({ queryKey: ["service-editor-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["extra-editor-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["booking-form-config"] });
      const nextId = selectedRecord ? String(selectedRecord.id ?? "") : String(result?.id ?? "");
      openExtraEditor(nextId || "new");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      apiFetch<{ id: string }>(`/api/settings/extras/${id}`, {
        method: "DELETE"
      }),
    onSuccess: async (_, deletedId) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "extras"] });
      await queryClient.invalidateQueries({ queryKey: ["settings", "services"] });
      await queryClient.invalidateQueries({ queryKey: ["service-editor-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["extra-editor-catalog"] });
      if (selectedId === deletedId) {
        closeExtraEditor();
      }
    }
  });

  const importMutation = useMutation({
    mutationFn: async (csv: string) => {
      const rows = csv
        .replace(/^\uFEFF/, "")
        .split(/\r?\n/)
        .filter((line) => line.trim() !== "");

      if (!rows.length) {
        return { imported: 0, created: 0, updated: 0 };
      }

      const headers = parseCsvLine(rows[0]).map((value) => value.trim());
      const existingById = new Map(items.map((item) => [String(item.id ?? ""), item] as const));
      const existingByName = new Map(items.map((item) => [String(item.name ?? "").trim().toLowerCase(), item] as const));
      let created = 0;
      let updated = 0;

      for (const row of rows.slice(1)) {
        const cells = parseCsvLine(row);
        const record = Object.fromEntries(headers.map((header, index) => [header, cells[index] ?? ""])) as Record<string, string>;
        const name = String(record.name ?? "").trim();
        if (!name) {
          continue;
        }

        const assignedServiceIds = parseDelimitedCell(record.assigned_service_ids);
        if (!assignedServiceIds.length) {
          continue;
        }

        const matched =
          existingById.get(String(record.id ?? "").trim()) ??
          existingByName.get(name.toLowerCase());

        const payload = {
          name,
          icon: record.icon ?? "",
          description: record.description ?? "",
          displayOrder: Number(record.display_order || 0),
          quantityBased: parseBooleanValue(record.quantity_based),
          priceDeltaAmount: dollarsToCents(record.price_dollars || "0"),
          durationDeltaMinutes:
            Number(record.duration_hours || 0) * 60 + Number(record.duration_minutes || 0),
          assignedServiceIds,
          extraPricingRules: (() => {
            try {
              const parsed = JSON.parse(String(record.extra_pricing_rules_json || "[]"));
              return normalizeExtraPricingRules(parsed).map((rule) => ({
                id: rule.id,
                serviceIds: rule.serviceIds,
                squareFootageFrom: rule.squareFootageFrom,
                squareFootageTo: rule.squareFootageTo,
                bedroomsFrom: rule.bedroomsFrom,
                bedroomsTo: rule.bedroomsTo,
                levelsFrom: rule.levelsFrom,
                levelsTo: rule.levelsTo,
                priceDeltaAmount: dollarsToCents(rule.priceDeltaDollars),
                durationDeltaMinutes: Number(rule.durationMinutes || 0),
                displayOrder: Number(rule.displayOrder ?? 0),
                active: Boolean(rule.active)
              }));
            } catch {
              return [];
            }
          })(),
          carpetShampooingConfig: (() => {
            try {
              return {
                ...normalizeCarpetShampooingConfig(JSON.parse(String(record.carpet_shampooing_config_json || "{}"))),
                units: normalizeCarpetShampooingConfig(JSON.parse(String(record.carpet_shampooing_config_json || "{}"))).units.map(
                  (unit) => ({
                    code: unit.code,
                    label: unit.label,
                    priceDeltaAmount: dollarsToCents(unit.priceDeltaDollars),
                    durationDeltaMinutes: Number(unit.durationMinutes || 0),
                    maxQuantity: Number(unit.maxQuantity || 0),
                    displayOrder: Number(unit.displayOrder ?? 0),
                    active: Boolean(unit.active)
                  })
                )
              };
            } catch {
              return {
                enabled: false,
                minimumOrderAmount: 4900,
                units: carpetShampooingUnitDefaults.map((unit) => ({
                  code: unit.code,
                  label: unit.label,
                  priceDeltaAmount: dollarsToCents(unit.priceDeltaDollars),
                  durationDeltaMinutes: Number(unit.durationMinutes || 0),
                  maxQuantity: Number(unit.maxQuantity || 0),
                  displayOrder: unit.displayOrder,
                  active: unit.active
                }))
              };
            }
          })(),
          excludeFromFrequencyDiscount: parseBooleanValue(record.exclude_from_frequency_discount),
          excludeFromPromoDiscount: parseBooleanValue(record.exclude_from_promo_discount),
          recurringPolicy: record.recurring_policy === "first_booking_only" ? "first_booking_only" : "every_booking",
          active: record.active === "" ? true : parseBooleanValue(record.active)
        };

        await apiFetch(`/api/settings/extras${matched ? `/${matched.id}` : ""}`, {
          method: matched ? "PUT" : "POST",
          body: JSON.stringify(payload)
        });

        if (matched) {
          updated += 1;
        } else {
          created += 1;
        }
      }

      return { imported: created + updated, created, updated };
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "extras"] });
      await queryClient.invalidateQueries({ queryKey: ["settings", "services"] });
      await queryClient.invalidateQueries({ queryKey: ["service-editor-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["extra-editor-catalog"] });
      setImportFeedback(`Imported ${result.imported} extra row${result.imported === 1 ? "" : "s"}: ${result.created} new, ${result.updated} updated.`);
      if (result.imported) {
        closeExtraEditor();
      }
    }
  });
  const assignedServiceIds = normalizeStringList(getPathValue(draft, "assignedServiceIds"));
  const extraPricingRules = normalizeExtraPricingRules(getPathValue(draft, "extraPricingRules"));
  const carpetShampooingConfig = normalizeCarpetShampooingConfig(getPathValue(draft, "carpetShampooingConfig"));

  const toggleListValue = (key: string, value: string, checked: boolean) => {
    const current = normalizeStringList(getPathValue(draft, key));
    const next = checked ? Array.from(new Set([...current, value])) : current.filter((entry) => entry !== value);
    setDraft((existing) => setPathValue(existing, key, next));
  };

  const updateExtraPricingRule = (ruleId: string, updater: (rule: ExtraPricingRuleDraft) => ExtraPricingRuleDraft) => {
    setDraft((current) => {
      const nextRules = normalizeExtraPricingRules(getPathValue(current, "extraPricingRules")).map((rule) =>
        rule.id === ruleId ? updater(rule) : rule
      );
      return setPathValue(current, "extraPricingRules", nextRules);
    });
  };

  const addExtraPricingRule = () => {
    setDraft((current) => {
      const currentRules = normalizeExtraPricingRules(getPathValue(current, "extraPricingRules"));
      return setPathValue(current, "extraPricingRules", [
        ...currentRules,
        {
          id: `rule_${crypto.randomUUID().replace(/-/g, "")}`,
          serviceIds: [],
          squareFootageFrom: "",
          squareFootageTo: "",
          bedroomsFrom: "",
          bedroomsTo: "",
          levelsFrom: "",
          levelsTo: "",
          priceDeltaDollars: "0.00",
          durationMinutes: "0",
          displayOrder: currentRules.length,
          active: true
        } as ExtraPricingRuleDraft
      ]);
    });
  };

  const updateCarpetShampooingUnit = (
    unitCode: string,
    updater: (unit: CarpetShampooingUnitDraft) => CarpetShampooingUnitDraft
  ) => {
    setDraft((current) => {
      const nextConfig = normalizeCarpetShampooingConfig(getPathValue(current, "carpetShampooingConfig"));
      return setPathValue(current, "carpetShampooingConfig", {
        ...nextConfig,
        units: nextConfig.units.map((unit) => (unit.code === unitCode ? updater(unit) : unit))
      });
    });
  };

  const removeExtraPricingRule = (ruleId: string) => {
    setDraft((current) =>
      setPathValue(
        current,
        "extraPricingRules",
        normalizeExtraPricingRules(getPathValue(current, "extraPricingRules")).filter((rule) => rule.id !== ruleId)
      )
    );
  };

  if (!selectedId) {
    return (
      <div className="stack">
        <SectionHeader
          title="Settings: Extras"
          description="Manage add-ons, pricing, duration, discount behavior, recurring rules, and service assignments."
          actions={
            <div className="customer-directory-actions">
              <input
                ref={importInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }

                  try {
                    const csv = await file.text();
                    setImportFeedback(`Importing extras from ${file.name}...`);
                    importMutation.mutate(csv);
                  } catch (error) {
                    setImportFeedback(error instanceof Error ? error.message : "Extra import failed.");
                  }

                  event.currentTarget.value = "";
                }}
              />
              <Button variant="secondary" onClick={() => importInputRef.current?.click()} disabled={importMutation.isPending}>
                {importMutation.isPending ? "Importing..." : "Import extras"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  const blob = new Blob([buildExtrasCsv(items)], { type: "text/csv;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = "extras-export.csv";
                  link.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Export extras
              </Button>
              <Button variant="secondary" onClick={() => openExtraEditor("new")}>New extra</Button>
            </div>
          }
        />
        <Card title="Extras" eyebrow="Records">
          <div className="customer-directory-toolbar">
            <input
              className="customer-directory-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search extras by name, description, icon, or assigned service"
            />
          </div>
          <p className="card-copy">{config.description}</p>
          {importFeedback ? <p className="card-copy">{importFeedback}</p> : null}
          {importMutation.error ? <p className="error-text">{(importMutation.error as Error).message}</p> : null}
          <DataTable
            columns={["Icon", "Name", "Order", "Price", "Duration", "Assigned services", "Action"]}
            rows={filteredItems.map((item) => [
              String(item.icon ?? "") ? (
                String(item.icon).startsWith("data:image") || String(item.icon).startsWith("http") ? (
                  <img
                    key={`${item.id}-icon`}
                    src={String(item.icon)}
                    alt={String(item.name ?? "Extra icon")}
                    style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 6 }}
                  />
                ) : (
                  <span key={`${item.id}-icon-text`}>{String(item.icon ?? "")}</span>
                )
              ) : "—",
              String(item.name ?? ""),
              String(item.displayOrder ?? 0),
              formatCurrency(Number(item.priceDeltaAmount ?? 0)),
              `${Number(item.durationDeltaMinutes ?? 0)} min`,
              normalizeStringList(item.assignedServiceIds)
                .map((id) => String(serviceNameById.get(id) ?? id))
                .join(", "),
              <div key={String(item.id ?? "")} className="table-action-row">
                <Button variant="ghost" onClick={() => openExtraEditor(String(item.id ?? ""))}>Edit</Button>
                <Button variant="ghost" onClick={() => deleteMutation.mutate(String(item.id ?? ""))} disabled={deleteMutation.isPending}>Delete</Button>
              </div>
            ])}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="stack">
      <SectionHeader
        title={selectedRecord ? `Settings: ${String(selectedRecord.name ?? "extra")}` : "Settings: New extra"}
        description="Edit extra pricing, duration, service assignments, recurring behavior, and rule overrides in the same tab."
        actions={
          <div className="customer-directory-actions">
            <Button variant="secondary" onClick={closeExtraEditor}>Back to extras</Button>
          </div>
        }
      />
      {selectedId ? null : (
      <Card title="Extras" eyebrow="Records">
        <p className="card-copy">{config.description}</p>
        {importFeedback ? <p className="card-copy">{importFeedback}</p> : null}
        {importMutation.error ? <p className="error-text">{(importMutation.error as Error).message}</p> : null}
        <DataTable
          columns={["Icon", "Name", "Order", "Price", "Duration", "Assigned services", "Action"]}
          rows={items.map((item) => [
            String(item.icon ?? "") ? (
              String(item.icon).startsWith("data:image") || String(item.icon).startsWith("http") ? (
                <img
                  key={`${item.id}-icon`}
                  src={String(item.icon)}
                  alt={String(item.name ?? "Extra icon")}
                  style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 6 }}
                />
              ) : (
                <span key={`${item.id}-icon-text`}>{String(item.icon ?? "")}</span>
              )
            ) : "—",
            String(item.name ?? ""),
            String(item.displayOrder ?? 0),
            formatCurrency(Number(item.priceDeltaAmount ?? 0)),
            `${Number(item.durationDeltaMinutes ?? 0)} min`,
            normalizeStringList(item.assignedServiceIds)
              .map((id) => String(serviceNameById.get(id) ?? id))
              .join(", "),
            <div key={String(item.id ?? "")} className="table-action-row">
              <Button variant="ghost" onClick={() => openExtraEditor(String(item.id ?? ""))}>Edit</Button>
              <Button variant="ghost" onClick={() => deleteMutation.mutate(String(item.id ?? ""))} disabled={deleteMutation.isPending}>Delete</Button>
            </div>
          ])}
        />
      </Card>
      )}
      <Card
        title={selectedRecord ? `Edit ${String(selectedRecord.name ?? "extra")}` : "New extra"}
        eyebrow={selectedRecord ? "Extra details" : "Create record"}
        actions={
          <div className="customer-directory-actions">
            {selectedRecord ? (
              <Button variant="ghost" onClick={() => deleteMutation.mutate(String(selectedRecord.id ?? ""))} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            ) : null}
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? "Saving..." : selectedRecord ? "Save changes" : "Create extra"}
            </Button>
          </div>
        }
      >
        <input
          ref={iconInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) {
              return;
            }

            const reader = new FileReader();
            reader.onload = () => {
              if (typeof reader.result === "string") {
                setDraft((current) => setPathValue(current, "icon", reader.result));
              }
            };
            reader.readAsDataURL(file);
            event.currentTarget.value = "";
          }}
        />
        <div className="settings-fields-grid">
          <Field label="Extra name"><input value={String(getPathValue(draft, "name") ?? "")} onChange={(event) => setDraft((current) => setPathValue(current, "name", event.target.value))} /></Field>
          <Field label="Icon" hint="Upload an image, use an emoji, or paste an image URL.">
            <div className="stack stack--tight">
              <input value={String(getPathValue(draft, "icon") ?? "")} onChange={(event) => setDraft((current) => setPathValue(current, "icon", event.target.value))} placeholder="✨ or https://..." />
              <div className="customer-directory-actions">
                <Button variant="secondary" onClick={() => iconInputRef.current?.click()}>Upload icon</Button>
                {String(getPathValue(draft, "icon") ?? "") ? (
                  <Button variant="ghost" onClick={() => setDraft((current) => setPathValue(current, "icon", ""))}>Clear icon</Button>
                ) : null}
              </div>
              {String(getPathValue(draft, "icon") ?? "") ? (
                String(getPathValue(draft, "icon") ?? "").startsWith("data:image") || String(getPathValue(draft, "icon") ?? "").startsWith("http") ? (
                  <img
                    src={String(getPathValue(draft, "icon") ?? "")}
                    alt="Extra icon preview"
                    style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 8 }}
                  />
                ) : (
                  <div style={{ fontSize: "2rem", lineHeight: 1 }}>{String(getPathValue(draft, "icon") ?? "")}</div>
                )
              ) : null}
            </div>
          </Field>
          <Field label="Display order"><input type="number" value={String(getPathValue(draft, "displayOrder") ?? 0)} onChange={(event) => setDraft((current) => setPathValue(current, "displayOrder", event.target.value))} /></Field>
          <Field label="Price ($)"><input type="number" step="0.01" value={String(getPathValue(draft, "priceDeltaDollars") ?? "0.00")} onChange={(event) => setDraft((current) => setPathValue(current, "priceDeltaDollars", event.target.value))} /></Field>
          <Field label="Duration hours"><input type="number" min="0" value={String(getPathValue(draft, "durationHours") ?? 0)} onChange={(event) => setDraft((current) => setPathValue(current, "durationHours", event.target.value))} /></Field>
          <Field label="Duration minutes"><input type="number" min="0" max="59" value={String(getPathValue(draft, "durationMinutesRemainder") ?? 0)} onChange={(event) => setDraft((current) => setPathValue(current, "durationMinutesRemainder", event.target.value))} /></Field>
          <Field label="Description">
            <textarea rows={4} value={String(getPathValue(draft, "description") ?? "")} onChange={(event) => setDraft((current) => setPathValue(current, "description", event.target.value))} />
          </Field>
          <Field label="Options">
            <div className="stack stack--tight">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={Boolean(getPathValue(draft, "quantityBased")) || carpetShampooingConfig.enabled}
                  disabled={carpetShampooingConfig.enabled}
                  onChange={(event) => setDraft((current) => setPathValue(current, "quantityBased", event.target.checked))}
                />
                <span>
                  Quantity-based
                  {carpetShampooingConfig.enabled ? " (required for carpet breakdown)" : ""}
                </span>
              </label>
              <label className="checkbox-row">
                <input type="checkbox" checked={Boolean(getPathValue(draft, "active"))} onChange={(event) => setDraft((current) => setPathValue(current, "active", event.target.checked))} />
                <span>Active</span>
              </label>
            </div>
          </Field>
        </div>
        <Field label="Assigned services" hint="Choose which services should offer this extra.">
          <div className="checkbox-grid">
            {services.map((service) => {
              const id = String(service.id ?? "");
              return (
                <label key={id} className="checkbox-row">
                  <input type="checkbox" checked={assignedServiceIds.includes(id)} onChange={(event) => toggleListValue("assignedServiceIds", id, event.target.checked)} />
                  <span>{String(service.name ?? id)}</span>
                </label>
              );
            })}
          </div>
        </Field>
        <Card
          title="Carpet shampooing setup"
          eyebrow="Optional structured pricing"
        >
          <p className="card-copy">
            Use this when one extra needs room, hallway, step, and area-rug pricing with its own minimum order and per-item limits.
          </p>
          <div className="stack stack--tight">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={carpetShampooingConfig.enabled}
                onChange={(event) =>
                  setDraft((current) => setPathValue(current, "carpetShampooingConfig", {
                    ...normalizeCarpetShampooingConfig(getPathValue(current, "carpetShampooingConfig")),
                    enabled: event.target.checked
                  }))
                }
              />
              <span>Use carpet shampooing breakdown for this extra</span>
            </label>
            <p className="card-copy" style={{ margin: 0 }}>
              Parent active state, service assignment, and extra display order still come from this extra record. The fields below control carpet-specific minimums and item pricing.
            </p>
          </div>
          {carpetShampooingConfig.enabled ? (
            <div className="stack">
              <div className="settings-fields-grid">
                <Field label="Minimum order ($)">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={carpetShampooingConfig.minimumOrderDollars}
                    onChange={(event) =>
                      setDraft((current) => setPathValue(current, "carpetShampooingConfig", {
                        ...normalizeCarpetShampooingConfig(getPathValue(current, "carpetShampooingConfig")),
                        minimumOrderDollars: event.target.value
                      }))
                    }
                  />
                </Field>
              </div>
              {carpetShampooingConfig.units
                .slice()
                .sort((left, right) => Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0))
                .map((unit) => (
                  <Card key={unit.code} title={unit.label} eyebrow="Carpet unit">
                    <div className="settings-fields-grid">
                      <Field label="Display order">
                        <input
                          type="number"
                          min="0"
                          value={String(unit.displayOrder ?? 0)}
                          onChange={(event) =>
                            updateCarpetShampooingUnit(unit.code, (current) => ({
                              ...current,
                              displayOrder: Number(event.target.value ?? 0)
                            }))
                          }
                        />
                      </Field>
                      <Field label="Price ($)">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={unit.priceDeltaDollars}
                          onChange={(event) =>
                            updateCarpetShampooingUnit(unit.code, (current) => ({
                              ...current,
                              priceDeltaDollars: event.target.value
                            }))
                          }
                        />
                      </Field>
                      <Field label="Duration (minutes)">
                        <input
                          type="number"
                          min="0"
                          value={unit.durationMinutes}
                          onChange={(event) =>
                            updateCarpetShampooingUnit(unit.code, (current) => ({
                              ...current,
                              durationMinutes: event.target.value
                            }))
                          }
                        />
                      </Field>
                      <Field label="Max quantity">
                        <input
                          type="number"
                          min="0"
                          value={unit.maxQuantity}
                          onChange={(event) =>
                            updateCarpetShampooingUnit(unit.code, (current) => ({
                              ...current,
                              maxQuantity: event.target.value
                            }))
                          }
                        />
                      </Field>
                      <Field label="Active">
                        <label className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={unit.active}
                            onChange={(event) =>
                              updateCarpetShampooingUnit(unit.code, (current) => ({
                                ...current,
                                active: event.target.checked
                              }))
                            }
                          />
                          <span>Offer this item</span>
                        </label>
                      </Field>
                    </div>
                  </Card>
                ))}
            </div>
          ) : null}
        </Card>
        <div className="stack">
          <div className="customer-directory-actions">
            <div>
              <h4 className="booking-section-title">Pricing rules</h4>
              <p className="card-copy">Use rule rows when this extra should price differently by service, square footage, bedrooms, or levels.</p>
            </div>
            <Button variant="secondary" onClick={addExtraPricingRule}>Add rule</Button>
          </div>
          {extraPricingRules.length ? (
            extraPricingRules
              .slice()
              .sort((left, right) => Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0))
              .map((rule, index) => (
                <Card key={rule.id} title={`Rule ${index + 1}`} eyebrow="Pricing rule" actions={<Button variant="ghost" onClick={() => removeExtraPricingRule(rule.id)}>Remove</Button>}>
                  <div className="settings-fields-grid">
                    <Field label="Display order">
                      <input
                        type="number"
                        value={String(rule.displayOrder ?? index)}
                        onChange={(event) =>
                          updateExtraPricingRule(rule.id, (current) => ({
                            ...current,
                            displayOrder: Number(event.target.value ?? index)
                          }))
                        }
                      />
                    </Field>
                    <Field label="Rule price ($)">
                      <input
                        type="number"
                        step="0.01"
                        value={rule.priceDeltaDollars}
                        onChange={(event) =>
                          updateExtraPricingRule(rule.id, (current) => ({
                            ...current,
                            priceDeltaDollars: event.target.value
                          }))
                        }
                      />
                    </Field>
                    <Field label="Rule duration (minutes)">
                      <input
                        type="number"
                        min="0"
                        value={rule.durationMinutes}
                        onChange={(event) =>
                          updateExtraPricingRule(rule.id, (current) => ({
                            ...current,
                            durationMinutes: event.target.value
                          }))
                        }
                      />
                    </Field>
                    <Field label="Active">
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={rule.active}
                          onChange={(event) =>
                            updateExtraPricingRule(rule.id, (current) => ({
                              ...current,
                              active: event.target.checked
                            }))
                          }
                        />
                        <span>Use this rule</span>
                      </label>
                    </Field>
                    <Field label="Bedrooms from">
                      <select
                        value={rule.bedroomsFrom}
                        onChange={(event) =>
                          updateExtraPricingRule(rule.id, (current) => ({
                            ...current,
                            bedroomsFrom: event.target.value
                          }))
                        }
                      >
                        <option value="">Any</option>
                        {extraRuleBedroomOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Bedrooms to">
                      <select
                        value={rule.bedroomsTo}
                        onChange={(event) =>
                          updateExtraPricingRule(rule.id, (current) => ({
                            ...current,
                            bedroomsTo: event.target.value
                          }))
                        }
                      >
                        <option value="">Any</option>
                        {extraRuleBedroomOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Levels from">
                      <select
                        value={rule.levelsFrom}
                        onChange={(event) =>
                          updateExtraPricingRule(rule.id, (current) => ({
                            ...current,
                            levelsFrom: event.target.value
                          }))
                        }
                      >
                        <option value="">Any</option>
                        {extraRuleLevelOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Levels to">
                      <select
                        value={rule.levelsTo}
                        onChange={(event) =>
                          updateExtraPricingRule(rule.id, (current) => ({
                            ...current,
                            levelsTo: event.target.value
                          }))
                        }
                      >
                        <option value="">Any</option>
                        {extraRuleLevelOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Square footage from">
                      <select
                        value={rule.squareFootageFrom}
                        onChange={(event) =>
                          updateExtraPricingRule(rule.id, (current) => ({
                            ...current,
                            squareFootageFrom: event.target.value
                          }))
                        }
                      >
                        <option value="">Any</option>
                        {extraRuleSquareFootageOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Square footage to">
                      <select
                        value={rule.squareFootageTo}
                        onChange={(event) =>
                          updateExtraPricingRule(rule.id, (current) => ({
                            ...current,
                            squareFootageTo: event.target.value
                          }))
                        }
                      >
                        <option value="">Any</option>
                        {extraRuleSquareFootageOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="Applies to services" hint="Leave empty to let this rule match any service that already offers the extra.">
                    <div className="checkbox-grid">
                      {services.map((service) => {
                        const id = String(service.id ?? "");
                        return (
                          <label key={`${rule.id}-${id}`} className="checkbox-row">
                            <input
                              type="checkbox"
                              checked={rule.serviceIds.includes(id)}
                              onChange={(event) =>
                                updateExtraPricingRule(rule.id, (current) => ({
                                  ...current,
                                  serviceIds: event.target.checked
                                    ? Array.from(new Set([...current.serviceIds, id]))
                                    : current.serviceIds.filter((entry) => entry !== id)
                                }))
                              }
                            />
                            <span>{String(service.name ?? id)}</span>
                          </label>
                        );
                      })}
                    </div>
                  </Field>
                </Card>
              ))
          ) : (
            <p className="card-copy">No pricing rules yet. Default extra price and duration will be used until you add a rule.</p>
          )}
        </div>
        <Field label="Exclude from">
          <div className="checkbox-grid">
            <label className="checkbox-row">
              <input type="checkbox" checked={Boolean(getPathValue(draft, "excludeFromFrequencyDiscount"))} onChange={(event) => setDraft((current) => setPathValue(current, "excludeFromFrequencyDiscount", event.target.checked))} />
              <span>Frequency discounts</span>
            </label>
            <label className="checkbox-row">
              <input type="checkbox" checked={Boolean(getPathValue(draft, "excludeFromPromoDiscount"))} onChange={(event) => setDraft((current) => setPathValue(current, "excludeFromPromoDiscount", event.target.checked))} />
              <span>Discount code discounts</span>
            </label>
          </div>
        </Field>
        <Field label="Recurring policy">
          <select value={String(getPathValue(draft, "recurringPolicy") ?? "every_booking")} onChange={(event) => setDraft((current) => setPathValue(current, "recurringPolicy", event.target.value))}>
            <option value="every_booking">Add to every booking in a recurring series</option>
            <option value="first_booking_only">Add to first booking only</option>
          </select>
        </Field>
        {saveError ? <p className="error-text">{saveError}</p> : null}
        {deleteMutation.error ? <p className="error-text">{(deleteMutation.error as Error).message}</p> : null}
      </Card>
    </div>
  );
}

function BookingSpotsSettingsEditor() {
  const config = relationalSectionConfigs["booking-spots"];
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<BookingSpotTabKey>("general");
  const [dateOverrideRangeDays, setDateOverrideRangeDays] = useState<7 | 14 | 30>(7);
  const [selectedDateOverrideDate, setSelectedDateOverrideDate] = useState<string>(getLocalIsoDate());
  const [presetWeekdays, setPresetWeekdays] = useState<string[]>(["monday", "tuesday", "wednesday", "thursday", "friday"]);
  const [presetStartTime, setPresetStartTime] = useState<string>("07:30");
  const [presetEndTime, setPresetEndTime] = useState<string>("08:00");
  const [presetMaxJobs, setPresetMaxJobs] = useState<string>("6");
  const [draft, setDraft] = useState<SettingsValue>(getBookingSpotDraftBase(undefined, config.defaults));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [diagnosticDate, setDiagnosticDate] = useState<string>(getLocalIsoDate());
  const [diagnosticServiceId, setDiagnosticServiceId] = useState<string>("");
  const query = useQuery({
    queryKey: ["settings", "booking-spots"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/booking-spots")
  });
  const locationsQuery = useQuery({
    queryKey: ["settings", "locations"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/locations")
  });
  const servicesQuery = useQuery({
    queryKey: ["settings", "services"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/services")
  });

  const items = query.data?.items ?? [];
  const locations = (locationsQuery.data?.items ?? []).filter((item) => Boolean(item.active ?? true));
  const services = (servicesQuery.data?.items ?? []).filter((item) => Boolean(item.active ?? true));
  const selectedRecord = useMemo(
    () => items.find((item) => String(item.id ?? "") === selectedId),
    [items, selectedId]
  );

  useEffect(() => {
    if (!selectedId && items.length) {
      setSelectedId(String(items[0]?.id ?? ""));
      return;
    }

    if (selectedId !== "new" && selectedId && !selectedRecord) {
      setSelectedId(items[0]?.id ? String(items[0].id) : "new");
    }
  }, [items, selectedId, selectedRecord]);

  useEffect(() => {
    const base = selectedRecord ?? config.defaults;
    setDraft(getBookingSpotDraftBase(base, config.defaults));
    setSaveError(null);
  }, [config.defaults, selectedRecord]);

  const linkedServiceIds = normalizeStringList(getPathValue(draft, "linkedServiceIds"));
  const dailySchedule = (getPathValue(draft, "dailySchedule") as Array<Record<string, unknown>> | undefined) ?? [];
  const holidayOverrides = (getPathValue(draft, "holidayOverrides") as Array<Record<string, unknown>> | undefined) ?? [];
  const capacityRules = (getPathValue(draft, "capacityRules") as Array<Record<string, unknown>> | undefined) ?? [];
  const dateOverrides = normalizeBookingSpotDateOverrides(getPathValue(draft, "dateOverrides"));

  const buildBookingSpotPayload = () => buildBookingSpotPayloadFromDraft(draft, config.defaults, selectedRecord);

  const diagnosticsQuery = useQuery({
    queryKey: [
      "settings",
      "booking-spots",
      "diagnostics",
      selectedId,
      diagnosticDate,
      diagnosticServiceId,
      JSON.stringify({
        availabilityMode: getPathValue(draft, "availabilityMode") ?? "manual",
        teamSize: getPathValue(draft, "teamSize") ?? 2,
        fallbackTeamSizes: getPathValue(draft, "fallbackTeamSizes") ?? [],
        teamBasedCapacityCap: getPathValue(draft, "teamBasedCapacityCap") ?? "",
        intervalMinutes: getPathValue(draft, "intervalMinutes") ?? 30,
        dailySchedule: getPathValue(draft, "dailySchedule") ?? [],
        holidayOverrides: getPathValue(draft, "holidayOverrides") ?? [],
        dateOverrides: getPathValue(draft, "dateOverrides") ?? [],
        durationPolicyEnabled: getPathValue(draft, "durationPolicyEnabled") ?? true,
        allowDurationOverlapIntoNextSpot: getPathValue(draft, "allowDurationOverlapIntoNextSpot") ?? false,
        blockIfDurationExceedsAvailableTime: getPathValue(draft, "blockIfDurationExceedsAvailableTime") ?? true,
        outsideNormalAvailabilityPolicy: getPathValue(draft, "outsideNormalAvailabilityPolicy") ?? "count_against_previous_slot",
        durationRoundingMode: getPathValue(draft, "durationRoundingMode") ?? "round_up_to_next_slot"
      })
    ],
    enabled:
      activeTab === "diagnostics" &&
      Boolean(selectedRecord?.id) &&
      Boolean(String(getPathValue(draft, "locationId") ?? "").trim()) &&
      Boolean(diagnosticServiceId) &&
      Boolean(diagnosticDate),
    queryFn: () =>
      apiFetch<AvailabilityDiagnosticsResponse>("/api/settings/booking-spots/diagnostics", {
        method: "POST",
        body: JSON.stringify({
          bookingSpotId: String(selectedRecord?.id ?? ""),
          locationId: String(getPathValue(draft, "locationId") ?? ""),
          serviceId: diagnosticServiceId,
          date: diagnosticDate,
          bookingSpot: buildBookingSpotPayload()
        })
      })
  });

  useEffect(() => {
    if (!diagnosticServiceId && linkedServiceIds.length) {
      setDiagnosticServiceId(linkedServiceIds[0] ?? "");
      return;
    }

    if (diagnosticServiceId && linkedServiceIds.length && !linkedServiceIds.includes(diagnosticServiceId)) {
      setDiagnosticServiceId(linkedServiceIds[0] ?? "");
    }
  }, [diagnosticServiceId, linkedServiceIds]);

  const mutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      const payload = buildBookingSpotPayload();

      try {
        return await apiFetch(`/api/settings/booking-spots${selectedRecord ? `/${selectedRecord.id}` : ""}`, {
          method: selectedRecord ? "PUT" : "POST",
          body: JSON.stringify(payload)
        });
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async (result: any) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "booking-spots"] });
      const nextId = selectedRecord ? String(selectedRecord.id ?? "") : String(result?.id ?? "");
      setSelectedId(nextId || "new");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      apiFetch<{ id: string }>(`/api/settings/booking-spots/${id}`, {
        method: "DELETE"
      }),
    onSuccess: async (_, deletedId) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "booking-spots"] });
      setSelectedId((current) => (current === deletedId ? "new" : current));
    }
  });

  const updateDraftValue = (key: string, value: unknown) => {
    if (saveError) {
      setSaveError(null);
    }
    setDraft((current) => setPathValue(current, key, value));
  };

  const updateDailyScheduleRow = (day: string, nextPatch: Record<string, unknown>) => {
    updateDraftValue(
      "dailySchedule",
      dailySchedule.map((entry) =>
        String(entry.day ?? "") === day
          ? {
              ...entry,
              ...nextPatch
            }
          : entry
      )
    );
  };

  const updateHolidayOverride = (index: number, nextPatch: Record<string, unknown>) => {
    updateDraftValue(
      "holidayOverrides",
      holidayOverrides.map((entry, entryIndex) => (entryIndex === index ? { ...entry, ...nextPatch } : entry))
    );
  };

  const updateCapacityRule = (index: number, nextPatch: Record<string, unknown>) => {
    updateDraftValue(
      "capacityRules",
      capacityRules.map((entry, entryIndex) => (entryIndex === index ? { ...entry, ...nextPatch } : entry))
    );
  };

  const togglePresetWeekday = (day: string, checked: boolean) => {
    setPresetWeekdays((current) =>
      checked ? Array.from(new Set([...current, day])) : current.filter((entry) => entry !== day)
    );
  };

  const applyCapacityPreset = () => {
    const selectedDays = bookingSpotWeekdayOptions
      .map((day) => day.value)
      .filter((day) => presetWeekdays.includes(day));
    if (!selectedDays.length) {
      return;
    }

    const normalizedStart = presetStartTime || "08:00";
    const normalizedEnd = presetEndTime || "10:00";
    const parsedMaxJobs = Number(presetMaxJobs);
    const normalizedMaxJobs = Number.isFinite(parsedMaxJobs) && parsedMaxJobs > 0 ? Math.floor(parsedMaxJobs) : 1;

    const nextRules = [...capacityRules];
    selectedDays.forEach((day) => {
      const existingIndex = nextRules.findIndex(
        (entry) =>
          String(entry.day ?? "") === day &&
          String(entry.start ?? "") === normalizedStart &&
          String(entry.end ?? "") === normalizedEnd &&
          normalizeStringList(entry.linkedServiceIds).length === 0
      );

      if (existingIndex >= 0) {
        nextRules[existingIndex] = {
          ...nextRules[existingIndex],
          maxJobs: normalizedMaxJobs
        };
        return;
      }

      nextRules.push({
        day,
        start: normalizedStart,
        end: normalizedEnd,
        maxJobs: normalizedMaxJobs,
        linkedServiceIds: []
      });
    });

    updateDraftValue("capacityRules", nextRules);
  };

  const applyAccessMaidsDefaults = () => {
    const nextDailySchedule = bookingSpotWeekdayOptions.map((day) => {
      const isSaturday = day.value === "saturday";
      const isSunday = day.value === "sunday";
      return {
        day: day.value,
        enabled: !isSunday,
        start: "07:30",
        end: isSaturday ? "13:00" : "15:30",
        breakStart: "",
        breakEnd: ""
      };
    });

    setDraft((current) => {
      let next = current;
      next = setPathValue(next, "displayMode", "arrival_windows");
      next = setPathValue(next, "availabilityMode", "manual");
      next = setPathValue(next, "capacity", accessMaidsCapacity);
      next = setPathValue(next, "maxSimultaneousBookings", accessMaidsCapacity);
      next = setPathValue(next, "intervalMinutes", 120);
      next = setPathValue(next, "durationPolicyEnabled", true);
      next = setPathValue(next, "allowDurationOverlapIntoNextSpot", false);
      next = setPathValue(next, "blockIfDurationExceedsAvailableTime", false);
      next = setPathValue(next, "dailySchedule", nextDailySchedule);
      next = setPathValue(
        next,
        "dateOverrides",
        dateOverrides.map((entry) => ({
          ...entry,
          maxJobs: entry.maxJobs === "" ? accessMaidsCapacity : Math.max(Number(entry.maxJobs ?? 0) || 0, accessMaidsCapacity)
        }))
      );
      return next;
    });
    setSaveError(null);
    setActiveTab("general");
  };

  const intervalMinutesValue = Number(getPathValue(draft, "intervalMinutes") ?? 30);
  const defaultCapacityValue = Number(getPathValue(draft, "capacity") ?? 1);
  const maxSimultaneousBookingsValue = Number(getPathValue(draft, "maxSimultaneousBookings") ?? 1);
  const availabilityModeValue = String(getPathValue(draft, "availabilityMode") ?? "manual");
  const displayModeValue = String(getPathValue(draft, "displayMode") ?? "specific_times");
  const blockPastClosingValue = Boolean(getPathValue(draft, "blockIfDurationExceedsAvailableTime") ?? true);
  const previewRows = useMemo(
    () =>
      dailySchedule.map((entry) => {
        const dayKey = String(entry.day ?? "");
        const dayLabel = bookingSpotWeekdayOptions.find((day) => day.value === dayKey)?.label ?? dayKey;
        const windows = buildArrivalWindowPreview(entry, intervalMinutesValue);
        return {
          dayKey,
          dayLabel,
          enabled: Boolean(entry.enabled),
          start: String(entry.start ?? ""),
          end: String(entry.end ?? ""),
          windows
        };
      }),
    [dailySchedule, intervalMinutesValue]
  );
  const weekdayWindowLabels = previewRows
    .filter((row) => ["monday", "tuesday", "wednesday", "thursday", "friday"].includes(row.dayKey))
    .flatMap((row) => row.windows.map((window) => window.start));
  const saturdayPreview = previewRows.find((row) => row.dayKey === "saturday");
  const lowDateOverrideCount = dateOverrides.filter(
    (entry) => entry.maxJobs !== "" && Number(entry.maxJobs ?? 0) > 0 && Number(entry.maxJobs ?? 0) < accessMaidsCapacity
  ).length;
  const lowCapacityRuleCount = capacityRules.filter(
    (entry) => Number(entry.maxJobs ?? 0) > 0 && Number(entry.maxJobs ?? 0) < accessMaidsCapacity
  ).length;
  const availabilityWarnings = [
    !selectedRecord ? "You are creating a new spot. To fix the live booking page, edit the existing Chicago spot instead." : "",
    defaultCapacityValue < accessMaidsCapacity ? `Default capacity is ${defaultCapacityValue || 0}; set it to ${accessMaidsCapacity} for 5-6 simultaneous openings.` : "",
    maxSimultaneousBookingsValue < accessMaidsCapacity
      ? `Max simultaneous bookings is ${maxSimultaneousBookingsValue || 0}; set it to ${accessMaidsCapacity}.`
      : "",
    intervalMinutesValue !== 120 ? "Interval minutes should be 120 for 7:30, 9:30, 11:30, and 1:30 start times." : "",
    displayModeValue !== "arrival_windows" ? "Display mode should be Arrival windows only for the customer-facing booking page." : "",
    availabilityModeValue === "team_based" ? "Team-based mode can hide windows when staff availability is incomplete. Manual mode uses the capacity on this page." : "",
    blockPastClosingValue && displayModeValue !== "arrival_windows"
      ? "Past-closing duration blocking is on; long services can hide late exact-time openings."
      : "",
    lowDateOverrideCount ? `${lowDateOverrideCount} date override(s) cap a day below ${accessMaidsCapacity} spots.` : "",
    lowCapacityRuleCount ? `${lowCapacityRuleCount} capacity rule(s) cap a time range below ${accessMaidsCapacity} jobs.` : "",
    weekdayWindowLabels.includes("11:30") && weekdayWindowLabels.includes("13:30")
      ? ""
      : "Weekday preview is missing 11:30 AM or 1:30 PM. Check weekday start/end times and interval.",
    saturdayPreview?.windows.some((window) => window.start === "13:30")
      ? "Saturday preview still includes 1:30 PM. Set Saturday end time to 1:00 PM."
      : ""
  ].filter(Boolean);

  const selectedDateOverride = useMemo(() => {
    const existing = dateOverrides.find((entry) => entry.date === selectedDateOverrideDate);
    if (existing) {
      return existing;
    }

    const weekday = weekdayForIsoDate(selectedDateOverrideDate);
    const schedule = dailySchedule.find((entry) => String(entry.day ?? "") === weekday);
    const holidayOverride = holidayOverrides.find((entry) => String(entry.date ?? "") === selectedDateOverrideDate);
    return {
      date: selectedDateOverrideDate,
      closed: Boolean(holidayOverride?.closed ?? !(schedule?.enabled ?? false)),
      start: String(holidayOverride?.start ?? schedule?.start ?? "08:00"),
      end: String(holidayOverride?.end ?? schedule?.end ?? "17:00"),
      maxJobs: (() => {
        const raw = Number(getPathValue(draft, "capacity") ?? 1);
        return Number.isFinite(raw) && raw > 0 ? raw : "";
      })(),
      note: String(holidayOverride?.note ?? "")
    } satisfies BookingSpotDateOverrideDraft;
  }, [dateOverrides, dailySchedule, draft, holidayOverrides, selectedDateOverrideDate]);

  const upcomingDateRows = useMemo(() => {
    return Array.from({ length: dateOverrideRangeDays }, (_, index) => {
      const date = addDaysToIsoDate(getLocalIsoDate(), index);
      const weekday = weekdayForIsoDate(date);
      const weekdayLabel = bookingSpotWeekdayOptions.find((day) => day.value === weekday)?.label ?? weekday;
      const schedule = dailySchedule.find((entry) => String(entry.day ?? "") === weekday);
      const holidayOverride = holidayOverrides.find((entry) => String(entry.date ?? "") === date);
      const dateOverride = dateOverrides.find((entry) => entry.date === date);
      const closed = Boolean(
        dateOverride?.closed ??
          holidayOverride?.closed ??
          !(schedule?.enabled ?? false)
      );
      const start = String(dateOverride?.start ?? holidayOverride?.start ?? schedule?.start ?? "");
      const end = String(dateOverride?.end ?? holidayOverride?.end ?? schedule?.end ?? "");
      const maxJobs =
        dateOverride?.maxJobs === ""
          ? Number(getPathValue(draft, "capacity") ?? 1)
          : Number(dateOverride?.maxJobs ?? getPathValue(draft, "capacity") ?? 1);

      return {
        date,
        weekday,
        weekdayLabel,
        label: formatDateLabel(date),
        closed,
        start,
        end,
        maxJobs,
        hasOverride: Boolean(dateOverride)
      };
    });
  }, [dailySchedule, dateOverrideRangeDays, dateOverrides, draft, holidayOverrides]);

  const upsertDateOverride = (date: string, nextPatch: Partial<BookingSpotDateOverrideDraft>) => {
    const existing = dateOverrides.find((entry) => entry.date === date);
    const base = existing ?? {
      ...selectedDateOverride,
      date
    };
    const nextEntry: BookingSpotDateOverrideDraft = {
      ...base,
      ...nextPatch,
      date
    };
    const nextOverrides = existing
      ? dateOverrides.map((entry) => (entry.date === date ? nextEntry : entry))
      : [...dateOverrides, nextEntry].sort((left, right) => left.date.localeCompare(right.date));
    updateDraftValue("dateOverrides", nextOverrides);
  };

  const clearDateOverride = (date: string) => {
    updateDraftValue(
      "dateOverrides",
      dateOverrides.filter((entry) => entry.date !== date)
    );
  };

  const toggleLinkedService = (serviceId: string, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...linkedServiceIds, serviceId]))
      : linkedServiceIds.filter((entry) => entry !== serviceId);
    updateDraftValue("linkedServiceIds", next);
  };

  const toggleFallbackTeamSize = (teamMemberCount: number, checked: boolean) => {
    const current = normalizeNumberList(getPathValue(draft, "fallbackTeamSizes")).filter((entry) => entry >= 1 && entry <= 4);
    const next = checked
      ? Array.from(new Set([...current, teamMemberCount])).sort((left, right) => left - right)
      : current.filter((entry) => entry !== teamMemberCount);
    updateDraftValue("fallbackTeamSizes", next);
    updateDraftValue("allowSingleStaffFallback", next.includes(1));
  };

  const draftSpotName = String(getPathValue(draft, "name") ?? "").trim();
  const draftLocationId = String(getPathValue(draft, "locationId") ?? "").trim();
  const isNewSpotMissingRequiredBasics = !selectedRecord && (!draftSpotName || !draftLocationId);
  const newSpotRequirementsMessage = "Enter Spot name and Location in General before creating this spot.";

  const bookingSpotTabs: Array<{ key: BookingSpotTabKey; label: string; description: string }> = [
    { key: "general", label: "Core setup", description: "Capacity, spacing, display mode, and linked services." },
    { key: "daily", label: "Weekly windows", description: "Simple weekday/Saturday customer-facing arrival-window template." },
    { key: "diagnostics", label: "Missing window check", description: "Preview why a date or window is available or blocked." },
    { key: "duration", label: "Advanced duration", description: "Control how booking duration affects capacity, closing-time checks, and off-slot bookings." },
    { key: "holidays", label: "Holiday Overrides", description: "Date-based closures or one-off open-hour overrides." },
    { key: "capacity", label: "Advanced capacity", description: "Optional per-time-range caps for special cases." },
    { key: "date-overrides", label: "Date Overrides", description: "Adjust specific upcoming days without changing the weekly template." },
  ];

  return (
    <div className="stack">
      <SectionHeader
        title="Arrival Windows & Capacity"
        description="Preview the customer-facing arrival windows first, then adjust capacity, weekly hours, and exceptions."
        actions={
          <div className="customer-directory-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedId("new");
                setActiveTab("general");
              }}
            >
              Advanced: new spot
            </Button>
          </div>
        }
      />
      <Card title="Booking spots" eyebrow="Choose what the booking page uses">
        <p className="card-copy">
          Most AccessMaids changes should be made by editing the existing Chicago spot. Create a new spot only when you are adding a separate location or booking lane.
        </p>
        <DataTable
          columns={["Name", "Location", "Display mode", "Capacity", "Interval", "Status", "Action"]}
          rows={items.map((item) => [
            String(item.name ?? ""),
            String(locations.find((location) => String(location.id ?? "") === String(item.locationId ?? ""))?.name ?? item.locationId ?? ""),
            String(item.displayMode ?? "specific_times").replace(/_/g, " "),
            String(item.capacity ?? 1),
            `${Number(item.intervalMinutes ?? 30)} min`,
            <Badge tone={item.active ? "success" : "warning"}>{item.active ? "Active" : "Inactive"}</Badge>,
            <div key={String(item.id ?? "")} className="table-action-row">
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedId(String(item.id ?? ""));
                  setActiveTab("general");
                }}
              >
                Edit
              </Button>
              <Button variant="ghost" onClick={() => deleteMutation.mutate(String(item.id ?? ""))} disabled={deleteMutation.isPending}>Delete</Button>
            </div>
          ])}
        />
      </Card>
      <Card
        title="Customer-visible arrival windows"
        eyebrow={selectedRecord ? `Preview for ${String(selectedRecord.name ?? "selected spot")}` : "Preview"}
        actions={
          <div className="customer-directory-actions">
            <Button variant="secondary" onClick={applyAccessMaidsDefaults}>
              Apply AccessMaids defaults
            </Button>
            <Button variant="secondary" onClick={() => setActiveTab("diagnostics")} disabled={!selectedRecord}>
              Why is a window missing?
            </Button>
          </div>
        }
      >
        <div className="availability-preview-grid">
          {previewRows.map((row) => (
            <div key={row.dayKey} className={`availability-preview-row ${row.enabled ? "" : "is-closed"}`}>
              <div>
                <strong>{row.dayLabel}</strong>
                <p className="card-copy">
                  {row.enabled ? `${formatClockTime(row.start)} to ${formatClockTime(row.end)}` : "Closed"}
                </p>
              </div>
              <div className="arrival-window-chip-row">
                {row.windows.length ? (
                  row.windows.map((window) => (
                    <span className="arrival-window-chip" key={`${row.dayKey}-${window.start}`}>
                      {window.label}
                    </span>
                  ))
                ) : (
                  <span className="arrival-window-chip arrival-window-chip--muted">
                    No customer windows
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        {availabilityWarnings.length ? (
          <div className="availability-warning-panel">
            <strong>Needs attention before these windows reliably show on /book</strong>
            {availabilityWarnings.map((warning) => (
              <p key={warning} className="card-copy">{warning}</p>
            ))}
          </div>
        ) : (
          <p className="card-copy">This setup matches the AccessMaids weekday/Saturday arrival-window pattern.</p>
        )}
      </Card>
      <Card
        title={selectedRecord ? `Edit ${String(selectedRecord.name ?? "spot")}` : "New spot"}
        eyebrow={selectedRecord ? "Spot details" : "Advanced create record"}
        actions={
          <div className="customer-directory-actions">
            {selectedRecord ? (
              <Button variant="ghost" onClick={() => deleteMutation.mutate(String(selectedRecord.id ?? ""))} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            ) : null}
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || isNewSpotMissingRequiredBasics}>
              {mutation.isPending ? "Saving..." : selectedRecord ? "Save spot" : "Create spot"}
            </Button>
          </div>
        }
      >
        <div className="customer-tab-bar">
          {bookingSpotTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`customer-tab-button ${activeTab === tab.key ? "is-active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <p className="card-copy">{bookingSpotTabs.find((tab) => tab.key === activeTab)?.description}</p>

        {!selectedRecord && isNewSpotMissingRequiredBasics ? (
          <div className="availability-warning-panel">
            <strong>Create spot is disabled because required basics are missing.</strong>
            <p className="card-copy">{newSpotRequirementsMessage}</p>
            <p className="card-copy">If you are trying to fix the booking page, choose the existing Chicago row above and click Edit.</p>
          </div>
        ) : null}

        {activeTab === "general" ? (
          <div className="stack">
            <div className="availability-quick-panel">
              <div>
                <strong>Recommended AccessMaids setup</strong>
                <p className="card-copy">
                  Capacity 6, 120-minute spacing, weekday windows through 1:30 PM, Saturday stops after 11:30 AM.
                </p>
              </div>
              <Button variant="secondary" onClick={applyAccessMaidsDefaults}>
                Apply recommended setup
              </Button>
            </div>
            <div className="settings-fields-grid">
              <Field label="Spot name">
                <input value={String(getPathValue(draft, "name") ?? "")} onChange={(event) => updateDraftValue("name", event.target.value)} />
              </Field>
              <Field label="Location">
                <select value={String(getPathValue(draft, "locationId") ?? "")} onChange={(event) => updateDraftValue("locationId", event.target.value)}>
                  <option value="">Select a location</option>
                  {locations.map((location) => (
                    <option key={String(location.id ?? "")} value={String(location.id ?? "")}>
                      {String(location.name ?? location.id ?? "")}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Display mode">
                <select value={String(getPathValue(draft, "displayMode") ?? "specific_times")} onChange={(event) => updateDraftValue("displayMode", event.target.value)}>
                  <option value="specific_times">Specific times only</option>
                  <option value="arrival_windows">Arrival windows only</option>
                  <option value="mixed">Combination of specific times and arrival windows</option>
                </select>
              </Field>
              <Field label="Schedule availability mode" hint="Choose whether spots are managed manually or calculated from available staff.">
                <div className="stack">
                  <label className="checkbox-row">
                    <input
                      type="radio"
                      name="booking-spot-availability-mode"
                      checked={String(getPathValue(draft, "availabilityMode") ?? "manual") === "manual"}
                      onChange={() => updateDraftValue("availabilityMode", "manual")}
                    />
                    <span>Manual capacity from this page</span>
                  </label>
                  <label className="checkbox-row">
                    <input
                      type="radio"
                      name="booking-spot-availability-mode"
                      checked={String(getPathValue(draft, "availabilityMode") ?? "manual") === "team_based"}
                      onChange={() => updateDraftValue("availabilityMode", "team_based")}
                    />
                    <span>Based on team availability and assignments</span>
                  </label>
                </div>
              </Field>
              <Field label="Default capacity" hint="How many bookings can start in the same arrival window. Use 6 for AccessMaids.">
                <input type="number" min="1" value={String(getPathValue(draft, "capacity") ?? 1)} onChange={(event) => updateDraftValue("capacity", event.target.value)} />
              </Field>
              <Field label="Spacing between window starts" hint="Use 120 to show 7:30, 9:30, 11:30, and 1:30.">
                <input type="number" min="15" step="15" value={String(getPathValue(draft, "intervalMinutes") ?? 30)} onChange={(event) => updateDraftValue("intervalMinutes", event.target.value)} />
              </Field>
              <Field label="Max simultaneous bookings" hint="Overall cap for one arrival window. Use 6 for AccessMaids.">
                <input type="number" min="1" value={String(getPathValue(draft, "maxSimultaneousBookings") ?? 1)} onChange={(event) => updateDraftValue("maxSimultaneousBookings", event.target.value)} />
              </Field>
            </div>
            {String(getPathValue(draft, "availabilityMode") ?? "manual") === "team_based" ? (
              <div className="settings-fields-grid">
                <Field label="Staff per team" hint="Your company usually sends 2 maids per booking team.">
                  <input
                    type="number"
                    min="1"
                    value={String(getPathValue(draft, "teamSize") ?? 2)}
                    onChange={(event) => updateDraftValue("teamSize", event.target.value)}
                  />
                </Field>
                <Field label="Maximum spots cap" hint="Optional manual ceiling even if more teams are available.">
                  <input
                    type="number"
                    min="1"
                    value={String(getPathValue(draft, "teamBasedCapacityCap") ?? "")}
                    onChange={(event) => updateDraftValue("teamBasedCapacityCap", event.target.value)}
                  />
                </Field>
              </div>
            ) : null}
            {String(getPathValue(draft, "availabilityMode") ?? "manual") === "team_based" ? (
              <Field
                label="Fallback team sizes"
                hint="Choose which alternate crew sizes can still count as a bookable team if your preferred team size is not available."
              >
                <div className="checkbox-grid">
                  {[1, 2, 3, 4]
                    .filter((teamMemberCount) => teamMemberCount !== Number(getPathValue(draft, "teamSize") ?? 2))
                    .map((teamMemberCount) => {
                    const fallbackTeamSizes = normalizeNumberList(getPathValue(draft, "fallbackTeamSizes")).filter((entry) => entry >= 1 && entry <= 4);
                    const active = fallbackTeamSizes.includes(teamMemberCount);
                    return (
                      <label key={teamMemberCount} className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={(event) => toggleFallbackTeamSize(teamMemberCount, event.target.checked)}
                        />
                        <span>{teamMemberCount}-person fallback</span>
                      </label>
                    );
                  })}
                </div>
              </Field>
            ) : null}
            <Field label="Linked services" hint="Only these services can use this booking spot.">
              <div className="checkbox-grid">
                {services.map((service) => {
                  const id = String(service.id ?? "");
                  return (
                    <label key={id} className="checkbox-row">
                      <input type="checkbox" checked={linkedServiceIds.includes(id)} onChange={(event) => toggleLinkedService(id, event.target.checked)} />
                      <span>{String(service.name ?? id)}</span>
                    </label>
                  );
                })}
              </div>
            </Field>
            <Field label="Status">
              <label className="checkbox-row">
                <input type="checkbox" checked={Boolean(getPathValue(draft, "active"))} onChange={(event) => updateDraftValue("active", event.target.checked)} />
                <span>Active</span>
              </label>
            </Field>
          </div>
        ) : null}

        {activeTab === "duration" ? (
          <div className="stack">
            <Field label="Booking duration">
              <div className="stack">
                <label className="checkbox-row">
                  <input
                    type="radio"
                    name="booking-spot-duration-policy"
                    checked={!Boolean(getPathValue(draft, "durationPolicyEnabled") ?? true)}
                    onChange={() => updateDraftValue("durationPolicyEnabled", false)}
                  />
                  <span>Ignore booking duration when calculating availability</span>
                </label>
                <label className="checkbox-row">
                  <input
                    type="radio"
                    name="booking-spot-duration-policy"
                    checked={Boolean(getPathValue(draft, "durationPolicyEnabled") ?? true)}
                    onChange={() => updateDraftValue("durationPolicyEnabled", true)}
                  />
                  <span>Use booking duration in availability</span>
                </label>
              </div>
            </Field>

            {Boolean(getPathValue(draft, "durationPolicyEnabled") ?? true) ? (
              <>
                <Field
                  label="Duration overlap"
                  hint="When enabled, a long booking can consume multiple adjacent availability slots."
                >
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={Boolean(getPathValue(draft, "allowDurationOverlapIntoNextSpot") ?? false)}
                      onChange={(event) => updateDraftValue("allowDurationOverlapIntoNextSpot", event.target.checked)}
                    />
                    <span>Update availability when a booking overlaps into the next available spot</span>
                  </label>
                </Field>
                <Field
                  label="Past closing time"
                  hint="Prevents customers from choosing a start time that would make the job run past the allowed hours."
                >
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={Boolean(getPathValue(draft, "blockIfDurationExceedsAvailableTime") ?? true)}
                      onChange={(event) => updateDraftValue("blockIfDurationExceedsAvailableTime", event.target.checked)}
                    />
                    <span>Prevent a booking if it needs more time than is available for the spot or would extend past closing time</span>
                  </label>
                </Field>
                <Field label="Bookings outside of normal availability">
                  <div className="stack">
                    <label className="checkbox-row">
                      <input
                        type="radio"
                        name="booking-spot-outside-policy"
                        checked={String(getPathValue(draft, "outsideNormalAvailabilityPolicy") ?? "count_against_previous_slot") === "count_against_previous_slot"}
                        onChange={() => updateDraftValue("outsideNormalAvailabilityPolicy", "count_against_previous_slot")}
                      />
                      <span>Count them against the nearest earlier availability slot</span>
                    </label>
                    <label className="checkbox-row">
                      <input
                        type="radio"
                        name="booking-spot-outside-policy"
                        checked={String(getPathValue(draft, "outsideNormalAvailabilityPolicy") ?? "count_against_previous_slot") === "count_against_matching_range"}
                        onChange={() => updateDraftValue("outsideNormalAvailabilityPolicy", "count_against_matching_range")}
                      />
                      <span>Count them against the nearest matching custom time range</span>
                    </label>
                    <label className="checkbox-row">
                      <input
                        type="radio"
                        name="booking-spot-outside-policy"
                        checked={String(getPathValue(draft, "outsideNormalAvailabilityPolicy") ?? "count_against_previous_slot") === "do_not_count"}
                        onChange={() => updateDraftValue("outsideNormalAvailabilityPolicy", "do_not_count")}
                      />
                      <span>Do not count them against availability</span>
                    </label>
                  </div>
                </Field>
                <Field label="Duration rounding">
                  <select
                    value={String(getPathValue(draft, "durationRoundingMode") ?? "round_up_to_next_slot")}
                    onChange={(event) => updateDraftValue("durationRoundingMode", event.target.value)}
                  >
                    <option value="round_up_to_next_slot">Round up to the next slot</option>
                    <option value="exact_minutes">Use exact minutes</option>
                  </select>
                </Field>
              </>
            ) : null}
          </div>
        ) : null}

        {activeTab === "daily" ? (
          <div className="stack">
            <div className="availability-quick-panel">
              <div>
                <strong>Weekly arrival-window template</strong>
                <p className="card-copy">These hours control which customer-facing arrival windows can be generated.</p>
              </div>
              <Button variant="secondary" onClick={applyAccessMaidsDefaults}>
                Restore AccessMaids week
              </Button>
            </div>
            <DataTable
              columns={["Day", "Open", "First start", "Last allowed window end", "Customer windows"]}
              rows={dailySchedule.map((entry) => {
                const dayKey = String(entry.day ?? "");
                const dayLabel = bookingSpotWeekdayOptions.find((day) => day.value === dayKey)?.label ?? dayKey;
                const enabled = Boolean(entry.enabled);
                const windows = buildArrivalWindowPreview(entry, intervalMinutesValue);
                return [
                  dayLabel,
                  <label className="checkbox-row" key={`${dayKey}-enabled`}>
                    <input type="checkbox" checked={enabled} onChange={(event) => updateDailyScheduleRow(dayKey, { enabled: event.target.checked })} />
                    <span>{enabled ? "Open" : "Closed"}</span>
                  </label>,
                  <input
                    key={`${dayKey}-start`}
                    type="time"
                    value={String(entry.start ?? "08:00")}
                    disabled={!enabled}
                    onChange={(event) => updateDailyScheduleRow(dayKey, { start: event.target.value })}
                  />,
                  <input
                    key={`${dayKey}-end`}
                    type="time"
                    value={String(entry.end ?? "17:00")}
                    disabled={!enabled}
                    onChange={(event) => updateDailyScheduleRow(dayKey, { end: event.target.value })}
                  />,
                  <div className="arrival-window-chip-row" key={`${dayKey}-windows`}>
                    {windows.length ? (
                      windows.map((window) => (
                        <span className="arrival-window-chip" key={`${dayKey}-${window.start}`}>
                          {window.label}
                        </span>
                      ))
                    ) : (
                      <span className="arrival-window-chip arrival-window-chip--muted">None</span>
                    )}
                  </div>
                ];
              })}
            />
            <p className="card-copy">
              Saturday should end at 1:00 PM to show 11:30 AM - 1:00 PM and prevent 1:30 PM - 3:30 PM.
            </p>
            {dailySchedule.map((entry) => {
              const dayKey = String(entry.day ?? "");
              const dayLabel = bookingSpotWeekdayOptions.find((day) => day.value === dayKey)?.label ?? dayKey;
              const enabled = Boolean(entry.enabled);
              return (
                <details key={dayKey} className="spot-editor-card">
                  <summary className="spot-editor-card__header">
                    <strong>{dayLabel} advanced fields</strong>
                    <span className="card-copy">{enabled ? "Open" : "Closed"}</span>
                  </summary>
                  <div className="spot-editor-card__header">
                    <strong>{dayLabel}</strong>
                    <label className="checkbox-row">
                      <input type="checkbox" checked={enabled} onChange={(event) => updateDailyScheduleRow(dayKey, { enabled: event.target.checked })} />
                      <span>{enabled ? "Open" : "Closed"}</span>
                    </label>
                  </div>
                  <div className="settings-fields-grid">
                    <Field label="Start time">
                      <input type="time" value={String(entry.start ?? "08:00")} disabled={!enabled} onChange={(event) => updateDailyScheduleRow(dayKey, { start: event.target.value })} />
                    </Field>
                    <Field label="End time">
                      <input type="time" value={String(entry.end ?? "17:00")} disabled={!enabled} onChange={(event) => updateDailyScheduleRow(dayKey, { end: event.target.value })} />
                    </Field>
                    <Field label="Break start">
                      <input type="time" value={String(entry.breakStart ?? "")} disabled={!enabled} onChange={(event) => updateDailyScheduleRow(dayKey, { breakStart: event.target.value })} />
                    </Field>
                    <Field label="Break end">
                      <input type="time" value={String(entry.breakEnd ?? "")} disabled={!enabled} onChange={(event) => updateDailyScheduleRow(dayKey, { breakEnd: event.target.value })} />
                    </Field>
                  </div>
                </details>
              );
            })}
          </div>
        ) : null}

        {activeTab === "holidays" ? (
          <div className="stack">
            <div className="customer-directory-actions">
              <Button
                variant="secondary"
                onClick={() =>
                  updateDraftValue("holidayOverrides", [
                    ...holidayOverrides,
                    { date: "", closed: true, start: "", end: "", note: "" }
                  ])
                }
              >
                Add holiday override
              </Button>
              <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || isNewSpotMissingRequiredBasics}>
                {mutation.isPending ? "Saving..." : selectedRecord ? "Save spot" : "Create spot"}
              </Button>
            </div>
            {holidayOverrides.length ? (
              holidayOverrides.map((entry, index) => (
                <div key={`holiday-${index}`} className="spot-editor-card">
                  <div className="spot-editor-card__header">
                    <strong>Override #{index + 1}</strong>
                    <Button
                      variant="ghost"
                      onClick={() => updateDraftValue("holidayOverrides", holidayOverrides.filter((_, entryIndex) => entryIndex !== index))}
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="settings-fields-grid">
                    <Field label="Date">
                      <DateInput value={String(entry.date ?? "")} onChange={(event) => updateHolidayOverride(index, { date: event.target.value })} />
                    </Field>
                    <Field label="Closed">
                      <label className="checkbox-row">
                        <input type="checkbox" checked={Boolean(entry.closed ?? true)} onChange={(event) => updateHolidayOverride(index, { closed: event.target.checked })} />
                        <span>{Boolean(entry.closed ?? true) ? "Closed all day" : "Open with custom hours"}</span>
                      </label>
                    </Field>
                    <Field label="Custom start">
                      <input type="time" value={String(entry.start ?? "")} disabled={Boolean(entry.closed ?? true)} onChange={(event) => updateHolidayOverride(index, { start: event.target.value })} />
                    </Field>
                    <Field label="Custom end">
                      <input type="time" value={String(entry.end ?? "")} disabled={Boolean(entry.closed ?? true)} onChange={(event) => updateHolidayOverride(index, { end: event.target.value })} />
                    </Field>
                    <Field label="Note">
                      <textarea rows={3} value={String(entry.note ?? "")} onChange={(event) => updateHolidayOverride(index, { note: event.target.value })} />
                    </Field>
                  </div>
                </div>
              ))
            ) : (
              <p className="card-copy">No holiday overrides yet.</p>
            )}
          </div>
        ) : null}

        {activeTab === "capacity" ? (
          <div className="stack">
            <div className="availability-warning-panel">
              <strong>Capacity rules are advanced caps.</strong>
              <p className="card-copy">
                Default capacity and Max simultaneous bookings handle the normal 5-6 openings. Add rules only when a specific day/time should have a lower or higher cap.
              </p>
              {lowCapacityRuleCount ? <p className="card-copy">{lowCapacityRuleCount} rule(s) are below {accessMaidsCapacity} and may reduce visible availability.</p> : null}
            </div>
            <div className="spot-editor-card">
              <div className="spot-editor-card__header">
                <strong>Preset builder</strong>
              </div>
              <p className="card-copy">
                Add the same arrival window to multiple weekdays at once. Existing matching windows will be updated instead of duplicated.
              </p>
              <div className="customer-directory-actions">
                <Button variant="secondary" onClick={() => setPresetWeekdays(["monday", "tuesday", "wednesday", "thursday", "friday"])}>
                  Mon-Fri
                </Button>
                <Button variant="secondary" onClick={() => setPresetWeekdays(["saturday"])}>
                  Saturday
                </Button>
                <Button variant="secondary" onClick={() => setPresetWeekdays(["sunday"])}>
                  Sunday
                </Button>
                <Button variant="secondary" onClick={() => setPresetWeekdays(bookingSpotWeekdayOptions.map((day) => day.value))}>
                  All week
                </Button>
              </div>
              <Field label="Apply to days">
                <div className="checkbox-grid">
                  {bookingSpotWeekdayOptions.map((day) => (
                    <label key={`preset-${day.value}`} className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={presetWeekdays.includes(day.value)}
                        onChange={(event) => togglePresetWeekday(day.value, event.target.checked)}
                      />
                      <span>{day.label}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <div className="settings-fields-grid">
                <Field label="Start time">
                  <input type="time" value={presetStartTime} onChange={(event) => setPresetStartTime(event.target.value)} />
                </Field>
                <Field label="End time">
                  <input type="time" value={presetEndTime} onChange={(event) => setPresetEndTime(event.target.value)} />
                </Field>
                <Field label="Max jobs">
                  <input type="number" min="1" value={presetMaxJobs} onChange={(event) => setPresetMaxJobs(event.target.value)} />
                </Field>
              </div>
              <div className="customer-directory-actions">
                <Button onClick={applyCapacityPreset} disabled={!presetWeekdays.length}>
                  Add preset to selected days
                </Button>
              </div>
            </div>
            <div className="customer-directory-actions">
              <Button
                variant="secondary"
                onClick={() =>
                  updateDraftValue("capacityRules", [
                    ...capacityRules,
                    { day: "monday", start: "08:00", end: "10:00", maxJobs: 1, linkedServiceIds: [] }
                  ])
                }
              >
                Add time range
              </Button>
            </div>
            {capacityRules.length ? (
              capacityRules.map((entry, index) => (
                <div key={`capacity-rule-${index}`} className="spot-editor-card">
                  <div className="spot-editor-card__header">
                    <strong>Capacity rule #{index + 1}</strong>
                    <Button
                      variant="ghost"
                      onClick={() => updateDraftValue("capacityRules", capacityRules.filter((_, entryIndex) => entryIndex !== index))}
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="settings-fields-grid">
                    <Field label="Day">
                      <select value={String(entry.day ?? "monday")} onChange={(event) => updateCapacityRule(index, { day: event.target.value })}>
                        {bookingSpotWeekdayOptions.map((day) => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Start time">
                      <input type="time" value={String(entry.start ?? "08:00")} onChange={(event) => updateCapacityRule(index, { start: event.target.value })} />
                    </Field>
                    <Field label="End time">
                      <input type="time" value={String(entry.end ?? "10:00")} onChange={(event) => updateCapacityRule(index, { end: event.target.value })} />
                    </Field>
                    <Field label="Max jobs">
                      <input type="number" min="1" value={String(entry.maxJobs ?? 1)} onChange={(event) => updateCapacityRule(index, { maxJobs: event.target.value })} />
                    </Field>
                  </div>
                  <Field label="Limit to services" hint="Optional: leave blank to apply this max to all linked services on the spot.">
                    <div className="checkbox-grid">
                      {services.map((service) => {
                        const serviceId = String(service.id ?? "");
                        const selectedServices = normalizeStringList(entry.linkedServiceIds);
                        const checked = selectedServices.includes(serviceId);
                        return (
                          <label key={`${index}-${serviceId}`} className="checkbox-row">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) => {
                                const next = event.target.checked
                                  ? Array.from(new Set([...selectedServices, serviceId]))
                                  : selectedServices.filter((id) => id !== serviceId);
                                updateCapacityRule(index, { linkedServiceIds: next });
                              }}
                            />
                            <span>{String(service.name ?? serviceId)}</span>
                          </label>
                        );
                      })}
                    </div>
                  </Field>
                </div>
              ))
            ) : (
              <p className="card-copy">No capacity rules yet. Add time ranges like 7:30-8:00 = 5 jobs.</p>
            )}
          </div>
        ) : null}

        {activeTab === "date-overrides" ? (
          <div className="stack">
            {lowDateOverrideCount ? (
              <div className="availability-warning-panel">
                <strong>Date overrides are limiting capacity.</strong>
                <p className="card-copy">
                  {lowDateOverrideCount} upcoming override(s) are below {accessMaidsCapacity} spots. Edit the date or clear the override if it was not intentional.
                </p>
              </div>
            ) : null}
            <div className="customer-directory-actions">
              <Button variant={dateOverrideRangeDays === 7 ? "primary" : "secondary"} onClick={() => setDateOverrideRangeDays(7)}>Next 7 days</Button>
              <Button variant={dateOverrideRangeDays === 14 ? "primary" : "secondary"} onClick={() => setDateOverrideRangeDays(14)}>Next 14 days</Button>
              <Button variant={dateOverrideRangeDays === 30 ? "primary" : "secondary"} onClick={() => setDateOverrideRangeDays(30)}>Next 30 days</Button>
              <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || isNewSpotMissingRequiredBasics}>
                {mutation.isPending ? "Saving..." : selectedRecord ? "Save spot" : "Create spot"}
              </Button>
            </div>
            <div className="settings-fields-grid">
              <Field label="Select a date to override" hint="Use the calendar picker to jump to any future date.">
                <DateInput
                  min={getLocalIsoDate()}
                  value={selectedDateOverrideDate}
                  onChange={(event) => setSelectedDateOverrideDate(event.target.value || getLocalIsoDate())}
                />
              </Field>
            </div>
            <Card title="Upcoming days" eyebrow="Preview">
              <DataTable
                columns={["Date", "Hours", "Max spots", "Status", "Action"]}
                rows={upcomingDateRows.map((entry) => [
                  `${entry.label} (${entry.weekdayLabel})`,
                  entry.closed ? "Closed" : `${entry.start || "--:--"} - ${entry.end || "--:--"}`,
                  String(entry.maxJobs),
                  <Badge tone={entry.hasOverride ? "warning" : "neutral"}>{entry.hasOverride ? "Override" : "Template"}</Badge>,
                  <Button key={entry.date} variant="ghost" onClick={() => setSelectedDateOverrideDate(entry.date)}>Edit</Button>
                ])}
              />
            </Card>
            <Card
              title={`Override ${formatDateLabel(selectedDateOverrideDate)}`}
              eyebrow="Date editor"
              actions={
                dateOverrides.some((entry) => entry.date === selectedDateOverrideDate) ? (
                  <Button variant="ghost" onClick={() => clearDateOverride(selectedDateOverrideDate)}>Clear override</Button>
                ) : null
              }
            >
              <div className="settings-fields-grid">
                <Field label="Closed">
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={Boolean(selectedDateOverride.closed)}
                      onChange={(event) => upsertDateOverride(selectedDateOverrideDate, { closed: event.target.checked })}
                    />
                    <span>{Boolean(selectedDateOverride.closed) ? "Closed all day" : "Open with custom settings"}</span>
                  </label>
                </Field>
                <Field label="Earliest start">
                  <input
                    type="time"
                    value={String(selectedDateOverride.start ?? "")}
                    disabled={Boolean(selectedDateOverride.closed)}
                    onChange={(event) => upsertDateOverride(selectedDateOverrideDate, { start: event.target.value })}
                  />
                </Field>
                <Field label="Latest start">
                  <input
                    type="time"
                    value={String(selectedDateOverride.end ?? "")}
                    disabled={Boolean(selectedDateOverride.closed)}
                    onChange={(event) => upsertDateOverride(selectedDateOverrideDate, { end: event.target.value })}
                  />
                </Field>
                <Field label="Max spots for this date">
                  <input
                    type="number"
                    min="1"
                    value={String(selectedDateOverride.maxJobs ?? "")}
                    disabled={Boolean(selectedDateOverride.closed)}
                    onChange={(event) =>
                      upsertDateOverride(selectedDateOverrideDate, {
                        maxJobs: event.target.value === "" ? "" : Number(event.target.value)
                      })
                    }
                  />
                </Field>
              </div>
              <Field label="Notes">
                <textarea
                  rows={3}
                  value={String(selectedDateOverride.note ?? "")}
                  onChange={(event) => upsertDateOverride(selectedDateOverrideDate, { note: event.target.value })}
                />
              </Field>
            </Card>
          </div>
        ) : null}

        {activeTab === "diagnostics" ? (
          <div className="stack">
            {!selectedRecord ? (
              <Card title="Diagnostics" eyebrow="Preview">
                <p className="card-copy">Save this availability record first, then you can preview real team composition for any future date.</p>
              </Card>
            ) : (
              <>
                <div className="settings-fields-grid">
                  <Field label="Service" hint="Pick a service that uses this availability template.">
                    <select value={diagnosticServiceId} onChange={(event) => setDiagnosticServiceId(event.target.value)}>
                      <option value="">Select a service</option>
                      {linkedServiceIds.map((serviceId) => {
                        const service = services.find((entry) => String(entry.id ?? "") === serviceId);
                        return (
                          <option key={serviceId} value={serviceId}>
                            {String(service?.name ?? serviceId)}
                          </option>
                        );
                      })}
                    </select>
                  </Field>
                  <Field label="Date" hint="Preview how this exact day will be staffed.">
                    <DateInput min={getLocalIsoDate()} value={diagnosticDate} onChange={(event) => setDiagnosticDate(event.target.value || getLocalIsoDate())} />
                  </Field>
                </div>

                {diagnosticsQuery.isLoading ? (
                  <p className="card-copy">Calculating team-based availability...</p>
                ) : diagnosticsQuery.error ? (
                  <p className="error-text">{(diagnosticsQuery.error as Error).message}</p>
                ) : diagnosticsQuery.data ? (
                  <>
                    <Card title="Availability summary" eyebrow="Preview results">
                      <DataTable
                        columns={["Time", "Status", "Remaining spots", "Available staff", "Formed teams", "Why"]}
                        rows={diagnosticsQuery.data.diagnostics.map((entry) => [
                          formatDateTimeLabel(entry.startsAt).split(", ").slice(1).join(", "),
                          <Badge tone={entry.status === "available" ? "success" : "warning"}>
                            {entry.status === "available" ? "Available" : "Blocked"}
                          </Badge>,
                          String(entry.remainingCapacity),
                          entry.availableStaffNames.length ? entry.availableStaffNames.join(", ") : "None",
                          entry.formedTeams.length
                            ? entry.formedTeams
                                .map((team) => `${team.memberNames.join(" + ")}${team.usesFallback ? ` (${team.size}-person fallback)` : ""}`)
                                .join(" | ")
                            : "No compatible teams",
                          entry.reason
                        ])}
                      />
                    </Card>

                    {diagnosticsQuery.data.diagnostics.length ? (
                      <div className="booking-list">
                        {diagnosticsQuery.data.diagnostics.map((entry) => (
                          <div key={entry.startsAt} className="booking-meta-card">
                            <strong>{formatDateTimeLabel(entry.startsAt)}</strong>
                            <span>{entry.reason}</span>
                            <span>Derived teams: {entry.derivedCapacity}</span>
                            <span>Effective capacity: {entry.effectiveCapacity}</span>
                            <span>Overlapping bookings: {entry.overlappingBookings}</span>
                            <span>
                              Unassigned staff: {entry.unassignedStaffNames.length ? entry.unassignedStaffNames.join(", ") : "None"}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="card-copy">No diagnostics are available for this date yet.</p>
                    )}
                  </>
                ) : (
                  <p className="card-copy">Select a service and date to preview team composition and availability diagnostics.</p>
                )}
              </>
            )}
          </div>
        ) : null}

        {saveError ? <p className="error-text">{saveError}</p> : null}
        {deleteMutation.error ? <p className="error-text">{(deleteMutation.error as Error).message}</p> : null}
      </Card>
    </div>
  );
}

function RelationalSettingsEditor({ section }: { section: string }) {
  if (section === "locations") {
    return <LocationsSettingsEditor />;
  }

  if (section === "staff") {
    return <StaffSettingsEditor />;
  }

  if (section === "services") {
    return <ServicesSettingsEditor />;
  }

  if (section === "extras") {
    return <ExtrasSettingsEditor />;
  }

  if (section === "booking-spots") {
    return <BookingSpotsSettingsEditor />;
  }

  const config = relationalSectionConfigs[section];
  const queryClient = useQueryClient();
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [promotionSearch, setPromotionSearch] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const query = useQuery({
    queryKey: ["settings", section],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>(`/api/settings/${section}`)
  });
  const frequencySettingsQuery = useQuery({
    queryKey: ["settings", "frequency-settings"],
    queryFn: () => apiFetch<SettingsValue>("/api/settings/frequency-settings"),
    enabled: section === "frequency-rules"
  });
  const items = useMemo(
    () => sortSectionItems(section, query.data?.items ?? []),
    [query.data?.items, section]
  );
  const useSameTabEditor =
    section === "frequency-rules" ||
    section === "promotions" ||
    section === "tags" ||
    section === "notification-rules" ||
    section === "service-scenarios";
  const servicesQuery = useQuery({
    queryKey: ["settings", "services", "lookup"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/services"),
    enabled: section === "promotions" || section === "service-scenarios"
  });
  const extrasQuery = useQuery({
    queryKey: ["settings", "extras", "lookup"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/extras"),
    enabled: section === "service-scenarios"
  });
  const promotionServices = (servicesQuery.data?.items ?? [])
    .filter((item) => item.active !== false && item.staffOnly !== true)
    .slice()
    .sort((left, right) => {
      const orderDelta = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
      if (orderDelta !== 0) {
        return orderDelta;
      }
      return String(left.name ?? "").localeCompare(String(right.name ?? ""));
    });
  const serviceLookup = useMemo(
    () =>
      new Map(
        promotionServices.map((item) => [String(item.id ?? ""), String(item.name ?? "")] as const)
      ),
    [promotionServices]
  );
  const scenarioExtras = useMemo(
    () =>
      (extrasQuery.data?.items ?? [])
        .filter((item) => item.active !== false)
        .slice()
        .sort((left, right) => {
          const orderDelta = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
          if (orderDelta !== 0) {
            return orderDelta;
          }
          return String(left.name ?? "").localeCompare(String(right.name ?? ""));
        }),
    [extrasQuery.data?.items]
  );
  const staffQuery = useQuery({
    queryKey: ["settings", "staff", "lookup"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/staff"),
    enabled: section === "staff-time-off"
  });
  const staffLookup = useMemo(() => {
    const records = staffQuery.data?.items ?? [];
    return new Map(
      records.map((item) => {
        const label =
          `${String(item.firstName ?? "")} ${String(item.lastName ?? "")}`.trim() ||
          String(item.name ?? "") ||
          String(item.code ?? "") ||
          String(item.id ?? "");
        return [String(item.id ?? ""), label] as const;
      })
    );
  }, [staffQuery.data?.items]);
  const filteredItems = useMemo(() => {
    if (section === "promotions") {
      const term = promotionSearch.trim().toLowerCase();
      if (!term) {
        return items;
      }

      return items.filter((item) => String(item.code ?? "").toLowerCase().includes(term));
    }

    if (section === "tags") {
      const term = tagSearch.trim().toLowerCase();
      if (!term) {
        return items;
      }

      return items.filter((item) =>
        [item.name, item.entityType, item.directoryCategory]
          .join(" ")
          .toLowerCase()
          .includes(term)
      );
    }

    return items;
  }, [items, promotionSearch, section, tagSearch]);
  const [selectedId, setSelectedId] = useState<string>(useSameTabEditor ? "" : "new");
  const [duplicateSourceId, setDuplicateSourceId] = useState<string>("");
  const [draft, setDraft] = useState<SettingsValue>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [staffImportFeedback, setStaffImportFeedback] = useState<string | null>(null);
  const [frequencySettingsDraft, setFrequencySettingsDraft] = useState<SettingsValue>({
    recurringDiscountMode: "all_bookings",
    recurringFutureBookingLimit: "12"
  });
  const [frequencySettingsSaveError, setFrequencySettingsSaveError] = useState<string | null>(null);
  const toggleDraftListValue = (key: string, value: string, checked: boolean) => {
    setDraft((current) => {
      const existing = Array.isArray(getPathValue(current, key))
        ? (getPathValue(current, key) as unknown[]).map((entry) => String(entry ?? "").trim()).filter(Boolean)
        : [];
      const next = checked ? Array.from(new Set([...existing, value])) : existing.filter((entry) => entry !== value);
      return setPathValue(current, key, next);
    });
  };
  const scenarioUsesBuildingCommonAreasPricing =
    section === "service-scenarios" && isBuildingCommonAreasScenarioDraft(draft, promotionServices);
  const buildingCommonAreasPricingRules = normalizeBuildingCommonAreasPricingRulesDraft(
    getPathValue(draft, "buildingCommonAreasPricingRules")
  );
  const updateBuildingCommonAreasPricingRule = (
    bucket: string,
    patch: Partial<BuildingCommonAreasPricingRuleDraft>
  ) => {
    setDraft((current) => {
      const currentRules = normalizeBuildingCommonAreasPricingRulesDraft(
        getPathValue(current, "buildingCommonAreasPricingRules")
      );
      const nextRules = currentRules.map((rule) =>
        rule.bucket === bucket ? { ...rule, ...patch } : rule
      );
      return setPathValue(current, "buildingCommonAreasPricingRules", nextRules);
    });
  };

  const selectedRecord = useMemo(
    () => (selectedId && selectedId !== "new" ? items.find((item) => String(item.id ?? "") === selectedId) : undefined),
    [items, selectedId]
  );
  const duplicateSourceRecord = useMemo(
    () => (duplicateSourceId ? items.find((item) => String(item.id ?? "") === duplicateSourceId) : undefined),
    [duplicateSourceId, items]
  );

  useEffect(() => {
    if (!selectedId || selectedId === "new" || selectedRecord) {
      return;
    }

    if (useSameTabEditor) {
      setSelectedId("");
      return;
    }

    setSelectedId(items[0]?.id ? String(items[0].id) : "new");
  }, [items, selectedId, selectedRecord, useSameTabEditor]);

  useEffect(() => {
    const base = selectedRecord ?? duplicateSourceRecord ?? config.defaults;
    const nextDraft =
      section === "service-scenarios"
        ? getServiceScenarioDraftBase(base, config.defaults)
        : hydrateDraft(config, { ...config.defaults, ...base });
    if (!selectedRecord && duplicateSourceRecord && section === "frequency-rules") {
      const existingNames = items.map((item) => String(item.name ?? ""));
      const existingCodes = items.map((item) => String(item.code ?? ""));
      nextDraft.name = buildDuplicateLabel(`${String(duplicateSourceRecord.name ?? "").trim()} Copy`, existingNames);
      nextDraft.code = buildDuplicateLabel(`${String(duplicateSourceRecord.code ?? "").trim() || "FREQUENCY"}_COPY`, existingCodes)
        .replace(/[^A-Za-z0-9_]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();
      nextDraft.isDefault = false;
    }
    setDraft(nextDraft);
    setSaveError(null);
  }, [config, duplicateSourceRecord, items, section, selectedRecord]);

  useEffect(() => {
    if (section !== "frequency-rules") {
      return;
    }

    setFrequencySettingsDraft({
      recurringDiscountMode: String(frequencySettingsQuery.data?.recurringDiscountMode ?? "all_bookings"),
      recurringFutureBookingLimit: String(frequencySettingsQuery.data?.recurringFutureBookingLimit ?? 12)
    });
    setFrequencySettingsSaveError(null);
  }, [section, frequencySettingsQuery.data]);

  const mutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      const base = { ...config.defaults, ...(selectedRecord ?? duplicateSourceRecord ?? {}) };
      const payload = buildPayload(config, base, draft);
      const normalizedPayload =
        section === "promotions"
          ? {
              ...payload,
              startsAt: payload.startsAt == null ? undefined : payload.startsAt,
              endsAt: payload.endsAt == null ? undefined : payload.endsAt,
              serviceDateStart: payload.serviceDateStart == null ? undefined : payload.serviceDateStart,
              serviceDateEnd: payload.serviceDateEnd == null ? undefined : payload.serviceDateEnd
            }
          : section === "service-scenarios"
            ? buildServiceScenarioPayload(payload, draft, promotionServices)
          : payload;
      try {
        return await apiFetch(`/api/settings/${section}${selectedRecord ? `/${selectedRecord.id}` : ""}`, {
          method: selectedRecord ? "PUT" : "POST",
          body: JSON.stringify(normalizedPayload)
        });
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async (result: any) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", section] });
      setDuplicateSourceId("");
      if (useSameTabEditor) {
        const nextId = selectedRecord ? String(selectedRecord.id ?? "") : String(result?.id ?? "");
        setSelectedId(nextId || "");
      }
    }
  });

  const importStaffMutation = useMutation({
    mutationFn: async (csv: string) =>
      apiFetch<{ imported: number; created: number; updated: number; ids: string[] }>("/api/settings/staff/import", {
        method: "POST",
        body: JSON.stringify({ csv })
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", section] });
      setSelectedId(section === "staff" ? "" : result.ids[0] ?? "new");
      setStaffImportFeedback(
        `Imported ${result.imported} row${result.imported === 1 ? "" : "s"}: ${result.created} new, ${result.updated} updated.`
      );
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      apiFetch<{ id: string; mode: "deleted" | "archived" }>(`/api/settings/${section}/${id}`, {
        method: "DELETE"
      }),
    onSuccess: async (result, deletedId) => {
      await queryClient.invalidateQueries({ queryKey: ["settings", section] });
      if (duplicateSourceId === deletedId) {
        setDuplicateSourceId("");
      }
      setSelectedId((current) => (current === deletedId ? "new" : current));
      if (useSameTabEditor && String(selectedId ?? "") === deletedId) {
        setSelectedId("");
      }
      if (section === "staff") {
        setStaffImportFeedback(
          result.mode === "archived"
            ? "Staff record was archived because it is linked to booking history."
            : "Staff record deleted."
        );
      }
    }
  });

  const frequencySettingsMutation = useMutation({
    mutationFn: async () => {
      setFrequencySettingsSaveError(null);
      try {
        return await apiFetch("/api/settings/frequency-settings", {
          method: "PUT",
          body: JSON.stringify({
            recurringDiscountMode:
              String(frequencySettingsDraft.recurringDiscountMode ?? "") === "exclude_first_booking"
                ? "exclude_first_booking"
                : "all_bookings",
            recurringFutureBookingLimit: Math.max(
              1,
              Number.parseInt(String(frequencySettingsDraft.recurringFutureBookingLimit ?? "12"), 10) || 12
            )
          })
        });
      } catch (error) {
        setFrequencySettingsSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "frequency-settings"] });
    }
  });

  return (
    <div className="settings-editor-grid">
      {section === "frequency-rules" ? (
        <Card
          title="Frequency Discounts"
          eyebrow="Recurring bookings"
          actions={
            <Button onClick={() => frequencySettingsMutation.mutate()} disabled={frequencySettingsMutation.isPending}>
              {frequencySettingsMutation.isPending ? "Saving..." : "Save frequency discounts"}
            </Button>
          }
        >
          <div className="stack">
            <Field label="Frequency discount">
              <div className="stack stack--tight">
                <label className="checkbox-row">
                  <input
                    type="radio"
                    name="recurringDiscountMode"
                    checked={String(frequencySettingsDraft.recurringDiscountMode ?? "all_bookings") === "all_bookings"}
                    onChange={() =>
                      setFrequencySettingsDraft((current) => ({
                        ...current,
                        recurringDiscountMode: "all_bookings"
                      }))
                    }
                  />
                  <span>Frequency discount applies to all bookings of a recurring appointment</span>
                </label>
                <label className="checkbox-row">
                  <input
                    type="radio"
                    name="recurringDiscountMode"
                    checked={String(frequencySettingsDraft.recurringDiscountMode ?? "") === "exclude_first_booking"}
                    onChange={() =>
                      setFrequencySettingsDraft((current) => ({
                        ...current,
                        recurringDiscountMode: "exclude_first_booking"
                      }))
                    }
                  />
                  <span>Excludes the first booking of a recurring appointment</span>
                </label>
              </div>
            </Field>
            <Field label="Block">
              <div className="settings-inline-field">
                <input
                  type="number"
                  min="1"
                  value={String(frequencySettingsDraft.recurringFutureBookingLimit ?? "12")}
                  onChange={(event) =>
                    setFrequencySettingsDraft((current) => ({
                      ...current,
                      recurringFutureBookingLimit: event.target.value
                    }))
                  }
                />
                <span className="settings-inline-field__suffix">recurring booking(s) into the future</span>
              </div>
            </Field>
            {frequencySettingsSaveError ? <p className="error-text">{frequencySettingsSaveError}</p> : null}
          </div>
        </Card>
      ) : null}
      {!useSameTabEditor || !selectedId ? (
        <Card
          title={config.title}
          eyebrow="Records"
          actions={
            <div className="customer-directory-actions">
              {section === "staff" ? (
                <>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    style={{ display: "none" }}
                    onChange={async (event) => {
                      const file = event.target.files?.[0];
                      if (!file) {
                        return;
                      }

                      try {
                        const csv = await file.text();
                        const summary = summarizeStaffCsv(csv);
                        setStaffImportFeedback(
                          summary.count
                            ? `Importing ${summary.count} staff row${summary.count === 1 ? "" : "s"} from ${file.name}${summary.sample?.length ? ` (${summary.sample.join(", ")}${summary.count > summary.sample.length ? ", ..." : ""})` : ""}.`
                            : `No staff rows found in ${file.name}.`
                        );
                        if (summary.count) {
                          importStaffMutation.mutate(csv);
                        }
                      } catch (error) {
                        setStaffImportFeedback(error instanceof Error ? error.message : "Staff import failed.");
                      }

                      event.currentTarget.value = "";
                    }}
                  />
                  <Button variant="secondary" onClick={() => importInputRef.current?.click()} disabled={importStaffMutation.isPending}>
                    {importStaffMutation.isPending ? "Importing..." : "Import Staff"}
                  </Button>
                </>
              ) : null}
              {section === "promotions" ? (
                <input
                  className="customer-directory-search"
                  value={promotionSearch}
                  onChange={(event) => setPromotionSearch(event.target.value)}
                  placeholder="Search by discount code"
                />
              ) : null}
              {section === "tags" ? (
                <input
                  className="customer-directory-search"
                  value={tagSearch}
                  onChange={(event) => setTagSearch(event.target.value)}
                  placeholder="Search tags"
                />
              ) : null}
              <Button
                variant="secondary"
                onClick={() => {
                  setDuplicateSourceId("");
                  setSelectedId("new");
                }}
              >
                New record
              </Button>
            </div>
          }
        >
          <p className="card-copy">{config.description}</p>
          {section === "staff" ? (
            <p className="card-copy">Import Staff adds new staff from CSV and updates matching staff by code, email, or name without deleting the rest of your records.</p>
          ) : null}
          {staffImportFeedback ? <p className="card-copy">{staffImportFeedback}</p> : null}
          {importStaffMutation.error ? <p className="error-text">{(importStaffMutation.error as Error).message}</p> : null}
          <DataTable
            columns={[...(config.columns?.map((column) => column.label) ?? []), "Action"]}
            rows={filteredItems.map((item) => [
              ...(config.columns?.map((column) => {
                if (section === "staff-time-off" && column.label === "Staff member") {
                  return staffLookup.get(String(item.staffId ?? "")) ?? String(item.staffId ?? "");
                }
                if (section === "service-scenarios" && column.label === "Base service") {
                  return serviceLookup.get(String(item.baseServiceId ?? "")) ?? String(item.baseServiceId ?? "");
                }
                return column.render(item);
              }) ?? []),
              <div key={String(item.id ?? "")} className="table-action-row">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDuplicateSourceId("");
                    setSelectedId(String(item.id ?? ""));
                  }}
                >
                  Edit
                </Button>
                {section === "frequency-rules" ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDuplicateSourceId(String(item.id ?? ""));
                      setSelectedId("new");
                    }}
                  >
                    Duplicate
                  </Button>
                ) : null}
                <Button
                  variant="ghost"
                  onClick={() => deleteMutation.mutate(String(item.id ?? ""))}
                  disabled={deleteMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            ])}
          />
        </Card>
      ) : null}
      {!useSameTabEditor || selectedId ? (
        <Card
          title={selectedRecord ? `Edit ${singularizeTitle(config.title)}` : `New ${singularizeTitle(config.title)}`}
          eyebrow={selectedRecord ? String(selectedRecord.id ?? "") : "Create record"}
          actions={
            <div className="customer-directory-actions">
              {useSameTabEditor ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDuplicateSourceId("");
                    setSelectedId("");
                  }}
                >
                  {section === "frequency-rules"
                    ? "Back to frequencies"
                    : section === "promotions"
                      ? "Back to discount codes"
                      : section === "service-scenarios"
                        ? "Back to service scenarios"
                      : "Back to tags"}
                </Button>
              ) : null}
              {section === "frequency-rules" && selectedRecord ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDuplicateSourceId(String(selectedRecord.id ?? ""));
                    setSelectedId("new");
                  }}
                >
                  Duplicate
                </Button>
              ) : null}
              {selectedRecord ? (
                <Button
                  variant="ghost"
                  onClick={() => deleteMutation.mutate(String(selectedRecord.id ?? ""))}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? "Deleting..." : "Delete"}
                </Button>
              ) : null}
              <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                {mutation.isPending ? "Saving..." : selectedRecord ? "Save changes" : "Create record"}
              </Button>
            </div>
          }
        >
          <div className="settings-fields-grid">
            {config.fields.map((field) => (
              <Field key={field.key} label={field.label} hint={field.kind === "checkbox" ? undefined : field.hint}>
                {section === "staff-time-off" && field.key === "staffId" ? (
                  <select
                    value={String(getPathValue(draft, field.key) ?? "")}
                    onChange={(event) => setDraft((current) => setPathValue(current, field.key, event.target.value))}
                  >
                    <option value="">Select staff member</option>
                    {(staffQuery.data?.items ?? [])
                      .filter((item) => item.active !== false)
                      .sort((left, right) =>
                        (
                          `${String(left.firstName ?? "")} ${String(left.lastName ?? "")}`.trim() ||
                          String(left.name ?? "") ||
                          String(left.code ?? "")
                        ).localeCompare(
                          `${String(right.firstName ?? "")} ${String(right.lastName ?? "")}`.trim() ||
                            String(right.name ?? "") ||
                            String(right.code ?? "")
                        )
                      )
                      .map((item) => (
                        <option key={String(item.id ?? "")} value={String(item.id ?? "")}>
                          {staffLookup.get(String(item.id ?? "")) ?? String(item.id ?? "")}
                        </option>
                      ))}
                  </select>
                ) : (
                section === "service-scenarios" && field.key === "baseServiceId" ? (
                  <select
                    value={String(getPathValue(draft, field.key) ?? "")}
                    onChange={(event) => setDraft((current) => setPathValue(current, field.key, event.target.value))}
                  >
                    <option value="">Select base service</option>
                    {promotionServices.map((service) => (
                      <option key={String(service.id ?? "")} value={String(service.id ?? "")}>
                        {String(service.name ?? service.id ?? "")}
                      </option>
                    ))}
                  </select>
                ) : section === "service-scenarios" && field.key === "allowedSkipAreas" ? (
                  <div className="checkbox-grid">
                    {scenarioSkipAreaOptions.map((option) => {
                      const selected = Array.isArray(getPathValue(draft, "allowedSkipAreas"))
                        ? (getPathValue(draft, "allowedSkipAreas") as unknown[]).map((entry) => String(entry ?? "")).includes(option.value)
                        : false;
                      return (
                        <label
                          key={option.value}
                          className={`checkbox-row checkbox-row--clickable${selected ? " is-selected" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(event) => toggleDraftListValue("allowedSkipAreas", option.value, event.target.checked)}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : section === "service-scenarios" && field.key === "visibleExtraIds" ? (
                  <div className="checkbox-grid">
                    {scenarioExtras.map((extra) => {
                      const id = String(extra.id ?? "");
                      const selected = Array.isArray(getPathValue(draft, "visibleExtraIds"))
                        ? (getPathValue(draft, "visibleExtraIds") as unknown[]).map((entry) => String(entry ?? "")).includes(id)
                        : false;
                      return (
                        <label key={id} className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(event) => toggleDraftListValue("visibleExtraIds", id, event.target.checked)}
                          />
                          <span>{String(extra.name ?? id)}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : section === "service-scenarios" && field.key === "remodelingPricingRules" ? (
                  <div className="stack">
                    <div className="card-copy">
                      Use these rules when recent renovation is selected so the quote can switch to hourly pricing with
                      a different minimum by renovation option.
                    </div>
                    {getScenarioRemodelPricingOptionsForDraft(draft).map((option) => {
                      const currentRules = Array.isArray(getPathValue(draft, "remodelingPricingRules"))
                        ? ((getPathValue(draft, "remodelingPricingRules") as unknown[]).filter(
                            (entry) => entry && typeof entry === "object"
                          ) as Array<Record<string, unknown>>)
                        : [];
                      const existingRule =
                        currentRules.find((rule) => String(rule.optionCode ?? "") === option.value) ?? {};
                      const nextRules = currentRules.filter((rule) => String(rule.optionCode ?? "") !== option.value);
                      const updateRule = (patch: Record<string, unknown>) => {
                        const nextRule = {
                          optionCode: option.value,
                          optionLabel: option.label,
                          hourlyRatePerMaidHour: Number(existingRule.hourlyRatePerMaidHour ?? 0),
                          minimumMaidCount: Number(existingRule.minimumMaidCount ?? 2),
                          minimumHours: Number(existingRule.minimumHours ?? 0),
                          active: existingRule.active !== false,
                          ...patch
                        };
                        setDraft((current) =>
                          setPathValue(current, "remodelingPricingRules", [...nextRules, nextRule])
                        );
                      };

                      return (
                        <div
                          key={option.value}
                          style={{
                            border: "1px solid #ddd5c8",
                            borderRadius: "16px",
                            padding: "1rem",
                            display: "grid",
                            gap: "0.75rem"
                          }}
                        >
                          <label className="checkbox-row">
                            <input
                              type="checkbox"
                              checked={existingRule.active !== false}
                              onChange={(event) => updateRule({ active: event.target.checked })}
                            />
                            <span>{option.label}</span>
                          </label>
                          <div className="grid three">
                            <Field label="Hourly rate ($)">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={String(existingRule.hourlyRatePerMaidHour ?? 0)}
                                onChange={(event) =>
                                  updateRule({
                                    hourlyRatePerMaidHour:
                                      event.target.value === "" ? 0 : Number(event.target.value)
                                  })
                                }
                              />
                            </Field>
                            <Field label="Minimum maids">
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={String(existingRule.minimumMaidCount ?? 2)}
                                onChange={(event) =>
                                  updateRule({
                                    minimumMaidCount:
                                      event.target.value === "" ? 2 : Number(event.target.value)
                                  })
                                }
                              />
                            </Field>
                            <Field label="Minimum hours">
                              <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={String(existingRule.minimumHours ?? 0)}
                                onChange={(event) =>
                                  updateRule({
                                    minimumHours: event.target.value === "" ? 0 : Number(event.target.value)
                                  })
                                }
                              />
                            </Field>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : section === "service-scenarios" && field.key === "buildingCommonAreasPricingRules" ? (
                  <div className="stack">
                    <div className="card-copy">
                      Override the base quote by apartment-count bucket before extras and discounts. Manual review
                      buckets keep the scenario on its regular follow-up path.
                    </div>
                    {!scenarioUsesBuildingCommonAreasPricing ? (
                      <div className="card-copy">
                        This editor is always visible now. These values only affect scenarios that resolve as Building
                        common areas.
                      </div>
                    ) : null}
                    <Field label="Minimum charge ($)">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={String(getPathValue(draft, "buildingCommonAreasMinimumChargeDollars") ?? "90.00")}
                        onChange={(event) =>
                          setDraft((current) =>
                            setPathValue(
                              current,
                              "buildingCommonAreasMinimumChargeDollars",
                              event.target.value
                            )
                          )
                        }
                      />
                    </Field>
                    {buildingCommonAreasPricingRules.map((rule) => (
                      <div
                        key={rule.bucket}
                        style={{
                          border: "1px solid #ddd5c8",
                          borderRadius: "16px",
                          padding: "1rem",
                          display: "grid",
                          gap: "0.75rem"
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{rule.label}</div>
                        <div className="grid three">
                          <Field label="Pricing mode">
                            <select
                              value={rule.pricingMode}
                              onChange={(event) =>
                                updateBuildingCommonAreasPricingRule(rule.bucket, {
                                  pricingMode: event.target.value === "manual" ? "manual" : "instant"
                                })
                              }
                            >
                              <option value="instant">Instant quote</option>
                              <option value="manual">Manual review</option>
                            </select>
                          </Field>
                          <Field label="Base price ($)">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={rule.basePriceDollars}
                              disabled={rule.pricingMode === "manual"}
                              onChange={(event) =>
                                updateBuildingCommonAreasPricingRule(rule.bucket, {
                                  basePriceDollars: event.target.value
                                })
                              }
                            />
                          </Field>
                          <Field label="Duration minutes">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={rule.durationMinutes}
                              disabled={rule.pricingMode === "manual"}
                              onChange={(event) =>
                                updateBuildingCommonAreasPricingRule(rule.bucket, {
                                  durationMinutes: event.target.value
                                })
                              }
                            />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                section === "promotions" && field.key === "applicableServiceIds" ? (
                  <div className="checkbox-grid">
                    {promotionServices.map((service) => {
                      const id = String(service.id ?? "");
                      const selected = Array.isArray(getPathValue(draft, "applicableServiceIds"))
                        ? (getPathValue(draft, "applicableServiceIds") as unknown[]).map((entry) => String(entry ?? "")).includes(id)
                        : false;
                      return (
                        <label key={id} className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(event) => toggleDraftListValue("applicableServiceIds", id, event.target.checked)}
                          />
                          <span>{String(service.name ?? id)}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : section === "promotions" && field.key === "validWeekdays" ? (
                  <div className="checkbox-grid">
                    {promotionWeekdayOptions.map((option) => {
                      const selected = Array.isArray(getPathValue(draft, "validWeekdays"))
                        ? (getPathValue(draft, "validWeekdays") as unknown[]).map((entry) => String(entry ?? "")).includes(option.value)
                        : false;
                      return (
                        <label key={option.value} className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(event) => toggleDraftListValue("validWeekdays", option.value, event.target.checked)}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  renderFieldValue(field, draft, setDraft)
                )
                )
              )}
            </Field>
          ))}
          </div>
          {saveError ? <p className="error-text">{saveError}</p> : null}
          {deleteMutation.error ? <p className="error-text">{(deleteMutation.error as Error).message}</p> : null}
        </Card>
      ) : null}
    </div>
  );
}

function AvailabilityDiagnosticsResults({ data }: { data: AvailabilityDiagnosticsResponse }) {
  if (!data.diagnostics.length) {
    return <p className="card-copy">No diagnostics are available for this date yet.</p>;
  }

  return (
    <>
      <Card title="Availability summary" eyebrow="Preview results">
        <DataTable
          columns={["Time", "Status", "Remaining spots", "Available staff", "Formed teams", "Why"]}
          rows={data.diagnostics.map((entry) => [
            formatDateTimeLabel(entry.startsAt).split(", ").slice(1).join(", "),
            <Badge tone={entry.status === "available" ? "success" : "warning"}>
              {entry.status === "available" ? "Available" : "Blocked"}
            </Badge>,
            String(entry.remainingCapacity),
            entry.availableStaffNames.length ? entry.availableStaffNames.join(", ") : "None",
            entry.formedTeams.length
              ? entry.formedTeams
                  .map((team) => `${team.memberNames.join(" + ")}${team.usesFallback ? ` (${team.size}-person fallback)` : ""}`)
                  .join(" | ")
              : "No compatible teams",
            entry.reason
          ])}
        />
      </Card>

      <div className="booking-list">
        {data.diagnostics.map((entry) => (
          <div key={entry.startsAt} className="booking-meta-card">
            <strong>{formatDateTimeLabel(entry.startsAt)}</strong>
            <span>{entry.reason}</span>
            <span>Derived teams: {entry.derivedCapacity}</span>
            <span>Effective capacity: {entry.effectiveCapacity}</span>
            <span>Overlapping bookings: {entry.overlappingBookings}</span>
            <span>Unassigned staff: {entry.unassignedStaffNames.length ? entry.unassignedStaffNames.join(", ") : "None"}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function SchedulingSettingsPage({ section }: { section: SchedulingRouteKey }) {
  const queryClient = useQueryClient();
  const bookingSpotConfig = relationalSectionConfigs["booking-spots"];
  const calendarProfileConfig = relationalSectionConfigs["calendar-profiles"];
  const staffConfig = relationalSectionConfigs.staff;
  const staffTimeOffConfig = relationalSectionConfigs["staff-time-off"];
  const bookingSpotsQuery = useQuery({
    queryKey: ["settings", "booking-spots"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/booking-spots")
  });
  const calendarProfilesQuery = useQuery({
    queryKey: ["settings", "calendar-profiles"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/calendar-profiles")
  });
  const servicesQuery = useQuery({
    queryKey: ["settings", "services"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/services")
  });
  const locationsQuery = useQuery({
    queryKey: ["settings", "locations"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/locations")
  });
  const staffQuery = useQuery({
    queryKey: ["settings", "staff"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/staff")
  });
  const staffTimeOffQuery = useQuery({
    queryKey: ["settings", "staff-time-off"],
    queryFn: () => apiFetch<{ items: SettingsValue[] }>("/api/settings/staff-time-off")
  });

  const activeSection = schedulingSections.find((entry) => entry.routeKey === section) ?? schedulingSections[0];
  const bookingSpots = bookingSpotsQuery.data?.items ?? [];
  const activeBookingSpots = bookingSpots.filter((item) => Boolean(item.active ?? true));
  const services = servicesQuery.data?.items ?? [];
  const activeServices = services.filter((item) => Boolean(item.active ?? true));
  const locations = locationsQuery.data?.items ?? [];
  const activeLocations = locations.filter((item) => Boolean(item.active ?? true));
  const staff = staffQuery.data?.items ?? [];
  const activeStaff = staff.filter((item) => Boolean(item.active ?? true) && !Boolean(item.isArchived ?? false));
  const timeOffItems = staffTimeOffQuery.data?.items ?? [];
  const calendarProfiles = calendarProfilesQuery.data?.items ?? [];
  const activeCalendarProfiles = calendarProfiles.filter((item) => Boolean(item.active ?? true));

  const today = getLocalIsoDate();
  const upcomingClosures = useMemo(
    () =>
      activeBookingSpots.reduce((count, item) => {
        const holidayOverrides = Array.isArray(item.holidayOverrides) ? item.holidayOverrides : [];
        const dateOverrides = Array.isArray(item.dateOverrides) ? item.dateOverrides : [];
        return (
          count +
          holidayOverrides.filter((entry) => String((entry as Record<string, unknown>).date ?? "") >= today).length +
          dateOverrides.filter((entry) => {
            const record = entry as Record<string, unknown>;
            return Boolean(record.closed) && String(record.date ?? "") >= today;
          }).length
        );
      }, 0),
    [activeBookingSpots, today]
  );
  const upcomingTimeOff = timeOffItems.filter((entry) => String(entry.endDate ?? entry.startDate ?? "") >= today);
  const [exceptionLocationId, setExceptionLocationId] = useState<string>("");
  const [exceptionBookingSpotId, setExceptionBookingSpotId] = useState<string>("");
  const [exceptionDateRangeDays, setExceptionDateRangeDays] = useState<7 | 14 | 30>(14);
  const [exceptionSelectedDate, setExceptionSelectedDate] = useState<string>(getLocalIsoDate());
  const [exceptionDraft, setExceptionDraft] = useState<SettingsValue>(
    getBookingSpotDraftBase(undefined, bookingSpotConfig.defaults)
  );
  const [exceptionSaveError, setExceptionSaveError] = useState<string | null>(null);
  const [selectedCalendarProfileId, setSelectedCalendarProfileId] = useState<string>("");
  const [calendarProfileDraft, setCalendarProfileDraft] = useState<SettingsValue>(
    hydrateDraft(calendarProfileConfig, calendarProfileConfig.defaults)
  );
  const [calendarProfileSaveError, setCalendarProfileSaveError] = useState<string | null>(null);
  const [selectedStaffId, setSelectedStaffId] = useState<string>("");
  const [staffDraft, setStaffDraft] = useState<SettingsValue>(getStaffDraftBase(undefined, staffConfig.defaults));
  const [staffSaveError, setStaffSaveError] = useState<string | null>(null);
  const [newTimeOffDraft, setNewTimeOffDraft] = useState<SettingsValue>(
    hydrateDraft(staffTimeOffConfig, staffTimeOffConfig.defaults)
  );
  const [timeOffSaveError, setTimeOffSaveError] = useState<string | null>(null);
  const [diagnosticLocationId, setDiagnosticLocationId] = useState<string>("");
  const [diagnosticBookingSpotId, setDiagnosticBookingSpotId] = useState<string>("");
  const [diagnosticServiceId, setDiagnosticServiceId] = useState<string>("");
  const [diagnosticDate, setDiagnosticDate] = useState<string>(getLocalIsoDate());

  useEffect(() => {
    if (!exceptionLocationId && activeLocations.length) {
      setExceptionLocationId(String(activeLocations[0]?.id ?? ""));
    }
  }, [activeLocations, exceptionLocationId]);

  const exceptionBookingSpots = useMemo(
    () =>
      activeBookingSpots.filter((item) =>
        exceptionLocationId ? String(item.locationId ?? "") === exceptionLocationId : true
      ),
    [activeBookingSpots, exceptionLocationId]
  );

  useEffect(() => {
    if (!exceptionBookingSpots.length) {
      if (exceptionBookingSpotId) {
        setExceptionBookingSpotId("");
      }
      return;
    }
    if (
      !exceptionBookingSpotId ||
      !exceptionBookingSpots.some((item) => String(item.id ?? "") === exceptionBookingSpotId)
    ) {
      setExceptionBookingSpotId(String(exceptionBookingSpots[0]?.id ?? ""));
    }
  }, [exceptionBookingSpotId, exceptionBookingSpots]);

  const selectedExceptionBookingSpot = useMemo(
    () => exceptionBookingSpots.find((item) => String(item.id ?? "") === exceptionBookingSpotId),
    [exceptionBookingSpotId, exceptionBookingSpots]
  );

  useEffect(() => {
    const base = selectedExceptionBookingSpot ?? bookingSpotConfig.defaults;
    setExceptionDraft(getBookingSpotDraftBase(base, bookingSpotConfig.defaults));
    setExceptionSaveError(null);
  }, [bookingSpotConfig.defaults, selectedExceptionBookingSpot]);

  const exceptionDailySchedule =
    ((getPathValue(exceptionDraft, "dailySchedule") as Array<Record<string, unknown>> | undefined) ?? []);
  const exceptionHolidayOverrides =
    ((getPathValue(exceptionDraft, "holidayOverrides") as Array<Record<string, unknown>> | undefined) ?? []);
  const exceptionDateOverrides = normalizeBookingSpotDateOverrides(getPathValue(exceptionDraft, "dateOverrides"));

  const selectedExceptionDateOverride = useMemo(() => {
    const existing = exceptionDateOverrides.find((entry) => entry.date === exceptionSelectedDate);
    if (existing) {
      return existing;
    }

    const weekday = weekdayForIsoDate(exceptionSelectedDate);
    const schedule = exceptionDailySchedule.find((entry) => String(entry.day ?? "") === weekday);
    const holidayOverride = exceptionHolidayOverrides.find(
      (entry) => String(entry.date ?? "") === exceptionSelectedDate
    );

    return {
      date: exceptionSelectedDate,
      closed: Boolean(holidayOverride?.closed ?? !(schedule?.enabled ?? false)),
      start: String(holidayOverride?.start ?? schedule?.start ?? "08:00"),
      end: String(holidayOverride?.end ?? schedule?.end ?? "17:00"),
      maxJobs: (() => {
        const raw = Number(getPathValue(exceptionDraft, "capacity") ?? 1);
        return Number.isFinite(raw) && raw > 0 ? raw : "";
      })(),
      note: String(holidayOverride?.note ?? "")
    } satisfies BookingSpotDateOverrideDraft;
  }, [exceptionDailySchedule, exceptionDateOverrides, exceptionDraft, exceptionHolidayOverrides, exceptionSelectedDate]);

  const exceptionUpcomingDateRows = useMemo(
    () =>
      Array.from({ length: exceptionDateRangeDays }, (_, index) => {
        const date = addDaysToIsoDate(getLocalIsoDate(), index);
        const weekday = weekdayForIsoDate(date);
        const weekdayLabel = bookingSpotWeekdayOptions.find((day) => day.value === weekday)?.label ?? weekday;
        const schedule = exceptionDailySchedule.find((entry) => String(entry.day ?? "") === weekday);
        const holidayOverride = exceptionHolidayOverrides.find((entry) => String(entry.date ?? "") === date);
        const dateOverride = exceptionDateOverrides.find((entry) => entry.date === date);
        const closed = Boolean(dateOverride?.closed ?? holidayOverride?.closed ?? !(schedule?.enabled ?? false));
        const start = String(dateOverride?.start ?? holidayOverride?.start ?? schedule?.start ?? "");
        const end = String(dateOverride?.end ?? holidayOverride?.end ?? schedule?.end ?? "");
        const maxJobs =
          dateOverride?.maxJobs === ""
            ? Number(getPathValue(exceptionDraft, "capacity") ?? 1)
            : Number(dateOverride?.maxJobs ?? getPathValue(exceptionDraft, "capacity") ?? 1);

        return {
          date,
          weekdayLabel,
          label: formatDateLabel(date),
          closed,
          start,
          end,
          maxJobs,
          hasOverride: Boolean(dateOverride)
        };
      }),
    [exceptionDailySchedule, exceptionDateOverrides, exceptionDateRangeDays, exceptionDraft, exceptionHolidayOverrides]
  );

  useEffect(() => {
    if (!selectedCalendarProfileId && activeCalendarProfiles.length) {
      setSelectedCalendarProfileId(String(activeCalendarProfiles[0]?.id ?? ""));
    }
  }, [activeCalendarProfiles, selectedCalendarProfileId]);

  const selectedCalendarProfile = useMemo(
    () => calendarProfiles.find((item) => String(item.id ?? "") === selectedCalendarProfileId),
    [calendarProfiles, selectedCalendarProfileId]
  );

  useEffect(() => {
    const base = selectedCalendarProfile ?? calendarProfileConfig.defaults;
    setCalendarProfileDraft(hydrateDraft(calendarProfileConfig, base));
    setCalendarProfileSaveError(null);
  }, [calendarProfileConfig, selectedCalendarProfile]);

  const calendarProfileBlackoutDates = normalizeStringList(getPathValue(calendarProfileDraft, "blackoutDates"))
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));

  useEffect(() => {
    if (!selectedStaffId && activeStaff.length) {
      setSelectedStaffId(String(activeStaff[0]?.id ?? ""));
    }
  }, [activeStaff, selectedStaffId]);

  const selectedStaff = useMemo(
    () => activeStaff.find((item) => String(item.id ?? "") === selectedStaffId),
    [activeStaff, selectedStaffId]
  );

  useEffect(() => {
    const base = selectedStaff ?? staffConfig.defaults;
    setStaffDraft(getStaffDraftBase(base, staffConfig.defaults));
    setStaffSaveError(null);
    setNewTimeOffDraft(
      hydrateDraft(staffTimeOffConfig, {
        ...staffTimeOffConfig.defaults,
        staffId: String(selectedStaff?.id ?? ""),
        startDate: getLocalIsoDate(),
        endDate: getLocalIsoDate(),
        reason: ""
      })
    );
    setTimeOffSaveError(null);
  }, [selectedStaff, staffConfig.defaults, staffTimeOffConfig]);

  const selectedStaffGroupKey = useMemo(() => {
    const selectedStaffTypeId = String(getPathValue(staffDraft, "staffTypeId") ?? "").trim();
    if (selectedStaffTypeId) {
      return `type:${selectedStaffTypeId}`;
    }
    const workerGroup = String(getPathValue(staffDraft, "workerGroup") ?? "").trim().toLowerCase();
    return workerGroup ? `group:${workerGroup}` : "";
  }, [staffDraft]);

  const getSchedulingStaffLabel = (item: SettingsValue) =>
    String(item.code ?? "").trim() ||
    `${String(item.firstName ?? "")} ${String(item.lastName ?? "")}`.trim() ||
    String(item.name ?? "");

  const schedulingSameGroupStaffMembers = useMemo(
    () =>
      activeStaff
        .filter((item) => String(item.id ?? "") !== String(selectedStaff?.id ?? ""))
        .filter((item) => {
          const staffTypeId = String(item.staffTypeId ?? "").trim();
          const itemGroupKey = staffTypeId
            ? `type:${staffTypeId}`
            : (() => {
                const workerGroup = String(item.workerGroup ?? "").trim().toLowerCase();
                return workerGroup ? `group:${workerGroup}` : "";
              })();
          return selectedStaffGroupKey && itemGroupKey === selectedStaffGroupKey;
        })
        .sort((left, right) => getSchedulingStaffLabel(left).localeCompare(getSchedulingStaffLabel(right))),
    [activeStaff, selectedStaff, selectedStaffGroupKey]
  );

  const selectedStaffAvailabilityDays = normalizeAvailabilityDays(
    getPathValue(staffDraft, "availabilityDays"),
    String(getPathValue(staffDraft, "availabilityText") ?? "")
  );

  const selectedStaffCannotWorkWithIds = Array.isArray(getPathValue(staffDraft, "cannotWorkWithStaffIds"))
    ? (getPathValue(staffDraft, "cannotWorkWithStaffIds") as unknown[]).map((entry) => String(entry))
    : [];
  const selectedStaffAssignedLocationIds = Array.isArray(getPathValue(staffDraft, "assignedLocationIds"))
    ? (getPathValue(staffDraft, "assignedLocationIds") as unknown[]).map((entry) => String(entry))
    : [];
  const selectedStaffAssignedServiceIds = Array.isArray(getPathValue(staffDraft, "assignedServiceIds"))
    ? (getPathValue(staffDraft, "assignedServiceIds") as unknown[]).map((entry) => String(entry))
    : [];

  const selectedStaffTimeOffItems = useMemo(
    () =>
      timeOffItems
        .filter((item) => String(item.staffId ?? "") === String(selectedStaff?.id ?? ""))
        .sort((left, right) =>
          String(left.startDate ?? left.endDate ?? "").localeCompare(String(right.startDate ?? right.endDate ?? ""))
        ),
    [selectedStaff, timeOffItems]
  );

  useEffect(() => {
    if (!diagnosticLocationId && activeLocations.length) {
      setDiagnosticLocationId(String(activeLocations[0]?.id ?? ""));
    }
  }, [activeLocations, diagnosticLocationId]);

  const diagnosticBookingSpots = useMemo(
    () =>
      activeBookingSpots.filter((item) =>
        diagnosticLocationId ? String(item.locationId ?? "") === diagnosticLocationId : true
      ),
    [activeBookingSpots, diagnosticLocationId]
  );

  useEffect(() => {
    if (!diagnosticBookingSpots.length) {
      if (diagnosticBookingSpotId) {
        setDiagnosticBookingSpotId("");
      }
      return;
    }
    if (!diagnosticBookingSpotId || !diagnosticBookingSpots.some((item) => String(item.id ?? "") === diagnosticBookingSpotId)) {
      setDiagnosticBookingSpotId(String(diagnosticBookingSpots[0]?.id ?? ""));
    }
  }, [diagnosticBookingSpotId, diagnosticBookingSpots]);

  const selectedDiagnosticBookingSpot = useMemo(
    () => diagnosticBookingSpots.find((item) => String(item.id ?? "") === diagnosticBookingSpotId),
    [diagnosticBookingSpotId, diagnosticBookingSpots]
  );

  const normalizedDiagnosticBookingSpot = useMemo(
    () =>
      selectedDiagnosticBookingSpot
        ? buildBookingSpotPayloadFromDraft(
            getBookingSpotDraftBase(selectedDiagnosticBookingSpot, bookingSpotConfig.defaults),
            bookingSpotConfig.defaults,
            selectedDiagnosticBookingSpot
          )
        : null,
    [bookingSpotConfig.defaults, selectedDiagnosticBookingSpot]
  );

  const diagnosticLinkedServiceIds = useMemo(
    () => normalizeStringList(selectedDiagnosticBookingSpot?.linkedServiceIds),
    [selectedDiagnosticBookingSpot]
  );

  useEffect(() => {
    if (!diagnosticLinkedServiceIds.length) {
      if (diagnosticServiceId) {
        setDiagnosticServiceId("");
      }
      return;
    }
    if (!diagnosticServiceId || !diagnosticLinkedServiceIds.includes(diagnosticServiceId)) {
      setDiagnosticServiceId(diagnosticLinkedServiceIds[0] ?? "");
    }
  }, [diagnosticLinkedServiceIds, diagnosticServiceId]);

  const diagnosticsQuery = useQuery({
    queryKey: [
      "settings",
      "scheduling",
      "diagnostics",
      diagnosticLocationId,
      diagnosticBookingSpotId,
      diagnosticServiceId,
      diagnosticDate
    ],
    enabled: Boolean(selectedDiagnosticBookingSpot?.id) && Boolean(diagnosticServiceId) && Boolean(diagnosticDate),
    queryFn: () =>
      apiFetch<AvailabilityDiagnosticsResponse>("/api/settings/booking-spots/diagnostics", {
        method: "POST",
        body: JSON.stringify({
          bookingSpotId: String(selectedDiagnosticBookingSpot?.id ?? ""),
          locationId: String(selectedDiagnosticBookingSpot?.locationId ?? diagnosticLocationId),
          serviceId: diagnosticServiceId,
          date: diagnosticDate,
          bookingSpot: normalizedDiagnosticBookingSpot
        })
      })
  });

  const exceptionsMutation = useMutation({
    mutationFn: async () => {
      if (!selectedExceptionBookingSpot?.id) {
        throw new Error("Select a booking spot before saving exceptions.");
      }
      setExceptionSaveError(null);
      const payload = buildBookingSpotPayloadFromDraft(
        exceptionDraft,
        bookingSpotConfig.defaults,
        selectedExceptionBookingSpot
      );
      try {
        return await apiFetch(`/api/settings/booking-spots/${selectedExceptionBookingSpot.id}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
      } catch (error) {
        setExceptionSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "booking-spots"] });
    }
  });

  const calendarProfileMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCalendarProfile?.id) {
        throw new Error("Select a calendar profile before saving blackout dates.");
      }
      setCalendarProfileSaveError(null);
      const payload = buildPayload(calendarProfileConfig, selectedCalendarProfile, calendarProfileDraft);
      try {
        return await apiFetch(`/api/settings/calendar-profiles/${selectedCalendarProfile.id}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
      } catch (error) {
        setCalendarProfileSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "calendar-profiles"] });
    }
  });

  const updateExceptionDraftValue = (key: string, value: unknown) => {
    if (exceptionSaveError) {
      setExceptionSaveError(null);
    }
    setExceptionDraft((current) => setPathValue(current, key, value));
  };

  const updateExceptionHolidayOverride = (index: number, nextPatch: Record<string, unknown>) => {
    updateExceptionDraftValue(
      "holidayOverrides",
      exceptionHolidayOverrides.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, ...nextPatch } : entry
      )
    );
  };

  const upsertExceptionDateOverride = (date: string, nextPatch: Partial<BookingSpotDateOverrideDraft>) => {
    const existing = exceptionDateOverrides.find((entry) => entry.date === date);
    const base = existing ?? {
      ...selectedExceptionDateOverride,
      date
    };
    const nextEntry: BookingSpotDateOverrideDraft = {
      ...base,
      ...nextPatch,
      date
    };
    const nextOverrides = existing
      ? exceptionDateOverrides.map((entry) => (entry.date === date ? nextEntry : entry))
      : [...exceptionDateOverrides, nextEntry].sort((left, right) => left.date.localeCompare(right.date));
    updateExceptionDraftValue("dateOverrides", nextOverrides);
  };

  const clearExceptionDateOverride = (date: string) => {
    updateExceptionDraftValue(
      "dateOverrides",
      exceptionDateOverrides.filter((entry) => entry.date !== date)
    );
  };

  const updateCalendarProfileBlackoutDate = (index: number, value: string) => {
    const next = [...calendarProfileBlackoutDates];
    next[index] = value;
    setCalendarProfileDraft((current) =>
      setPathValue(
        current,
        "blackoutDates",
        next.filter(Boolean).sort((left, right) => left.localeCompare(right))
      )
    );
  };

  const removeCalendarProfileBlackoutDate = (index: number) => {
    const next = calendarProfileBlackoutDates.filter((_, entryIndex) => entryIndex !== index);
    setCalendarProfileDraft((current) => setPathValue(current, "blackoutDates", next));
  };

  const addCalendarProfileBlackoutDate = () => {
    setCalendarProfileDraft((current) =>
      setPathValue(current, "blackoutDates", [...calendarProfileBlackoutDates, getLocalIsoDate()])
    );
  };

  const staffMutation = useMutation({
    mutationFn: async () => {
      if (!selectedStaff?.id) {
        throw new Error("Select a staff member before saving availability.");
      }
      setStaffSaveError(null);
      const normalizedAvailability = normalizeAvailabilityDays(
        getPathValue(staffDraft, "availabilityDays"),
        String(getPathValue(staffDraft, "availabilityText") ?? "")
      );
      const nextDraft = setPathValue(
        setPathValue(staffDraft, "availabilityDays", normalizedAvailability),
        "availabilityText",
        describeAvailabilityDays(normalizedAvailability)
      );
      const payload = buildPayload(staffConfig, { ...staffConfig.defaults, ...selectedStaff }, nextDraft);
      try {
        return await apiFetch(`/api/settings/staff/${selectedStaff.id}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
      } catch (error) {
        setStaffSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff"] });
    }
  });

  const staffTimeOffMutation = useMutation({
    mutationFn: async () => {
      if (!selectedStaff?.id) {
        throw new Error("Select a staff member before adding time off.");
      }
      setTimeOffSaveError(null);
      const payload = buildPayload(
        staffTimeOffConfig,
        staffTimeOffConfig.defaults,
        setPathValue(newTimeOffDraft, "staffId", String(selectedStaff.id))
      );
      try {
        return await apiFetch("/api/settings/staff-time-off", {
          method: "POST",
          body: JSON.stringify(payload)
        });
      } catch (error) {
        setTimeOffSaveError(error instanceof Error ? error.message : String(error));
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff-time-off"] });
      setNewTimeOffDraft(
        hydrateDraft(staffTimeOffConfig, {
          ...staffTimeOffConfig.defaults,
          staffId: String(selectedStaff?.id ?? ""),
          startDate: getLocalIsoDate(),
          endDate: getLocalIsoDate(),
          reason: ""
        })
      );
    }
  });

  const deleteTimeOffMutation = useMutation({
    mutationFn: async (id: string) =>
      apiFetch(`/api/settings/staff-time-off/${id}`, {
        method: "DELETE"
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings", "staff-time-off"] });
    }
  });

  const updateSchedulingStaffDraftValue = (key: string, value: unknown) => {
    if (staffSaveError) {
      setStaffSaveError(null);
    }
    setStaffDraft((current) => {
      let next = setPathValue(current, key, value);
      if (key === "availabilityDays") {
        next = setPathValue(next, "availabilityText", describeAvailabilityDays(normalizeAvailabilityDays(value)));
      }
      return next;
    });
  };

  const overviewWarnings = [
    activeBookingSpots.length === 0 ? "No active booking availability spots are ready yet." : null,
    activeLocations.length === 0 ? "No active locations are configured, so scheduling cannot open booking dates." : null,
    activeStaff.length === 0 ? "No active staff records are available for team-based scheduling checks." : null,
    activeCalendarProfiles.length === 0 ? "No active calendar profiles are set up for reusable booking windows." : null
  ].filter((value): value is string => Boolean(value));

  if (
    bookingSpotsQuery.isLoading ||
    calendarProfilesQuery.isLoading ||
    servicesQuery.isLoading ||
    locationsQuery.isLoading ||
    staffQuery.isLoading ||
    staffTimeOffQuery.isLoading
  ) {
    return <div className="page-loading">Loading scheduling...</div>;
  }

  return (
    <OpsPageShell>
      <OpsHeader
        eyebrow="Settings operations"
        title="Scheduling"
        description="Manage booking availability, staffing, closures, and diagnostics from one unified workspace."
      />
      <OpsMetricGrid>
        <OpsMetricCard label="Active booking spots" value={activeBookingSpots.length} icon={CalendarCheck2} tone="blue" />
        <OpsMetricCard label="Active locations" value={activeLocations.length} icon={MapPin} tone="emerald" />
        <OpsMetricCard label="Active staff" value={activeStaff.length} icon={Users} tone="violet" />
        <OpsMetricCard label="Calendar profiles" value={activeCalendarProfiles.length} icon={CalendarDays} tone="slate" />
        <OpsMetricCard label="Upcoming closures" value={upcomingClosures} icon={CalendarClock} tone={upcomingClosures > 0 ? "amber" : "emerald"} />
        <OpsMetricCard label="Diagnostics warnings" value={overviewWarnings.length} icon={AlertTriangle} tone={overviewWarnings.length > 0 ? "amber" : "emerald"} />
      </OpsMetricGrid>
      <Card title="Scheduling" eyebrow="Unified workspace">
        <div className="customer-tab-bar">
          {schedulingSections.map((tab) => (
            <Link
              key={tab.routeKey}
              className={`customer-tab-button ${tab.routeKey === activeSection.routeKey ? "is-active" : ""}`}
              to={`/admin/settings/${tab.routeKey}`}
            >
              {tab.label}
            </Link>
          ))}
        </div>
        <p className="card-copy">{activeSection.description}</p>
      </Card>

      {activeSection.routeKey === "scheduling-overview" ? (
        <>
          <Card title="Warnings" eyebrow="Review now">
            {overviewWarnings.length ? (
              <div className="stack">
                {overviewWarnings.map((warning) => (
                  <p key={warning} className="card-copy" style={{ margin: 0 }}>
                    {warning}
                  </p>
                ))}
              </div>
            ) : (
              <p className="card-copy">Scheduling basics look healthy right now. Use the tabs above to adjust details or run diagnostics.</p>
            )}
          </Card>
          <Card title="Quick actions" eyebrow="Jump in">
            <div className="scheduling-action-grid">
              <Link className="button button--secondary" to="/admin/settings/scheduling-booking-availability">
                Open booking availability
              </Link>
              <Link className="button button--secondary" to="/admin/settings/scheduling-staff-availability">
                Open staff availability
              </Link>
              <Link className="button button--secondary" to="/admin/settings/scheduling-exceptions-holidays">
                Review closures
              </Link>
              <Link className="button button--secondary" to="/admin/settings/scheduling-diagnostics">
                Run diagnostics
              </Link>
            </div>
          </Card>
        </>
      ) : null}

      {activeSection.routeKey === "scheduling-booking-availability" ? (
        <>
          <Card title="Booking Availability" eyebrow="Customer-facing slots">
            <p className="card-copy">
              Use booking spots for daily schedule, duration rules, linked services, and capacity logic. Calendar profiles stay available here as reusable booking windows.
            </p>
            <div className="scheduling-overview-grid">
              <Card title={String(activeBookingSpots.length)} eyebrow="Booking spots">
                <p className="card-copy">Each spot controls time slots, capacity, linked services, and holiday/date exceptions.</p>
                <Link className="button button--secondary" to="/admin/settings/booking-spots">
                  Open booking spots
                </Link>
              </Card>
              <Card title={String(activeCalendarProfiles.length)} eyebrow="Calendar profiles">
                <p className="card-copy">Reusable weekday windows, advance notice, and blackout dates that services can reference.</p>
                <Link className="button button--secondary" to="/admin/settings/calendar-profiles">
                  Open calendar profiles
                </Link>
              </Card>
            </div>
          </Card>
        </>
      ) : null}

      {activeSection.routeKey === "scheduling-staff-availability" ? (
        <>
          <Card title="Staff Availability" eyebrow="Worker coverage">
            <p className="card-copy">
              Edit weekly work days, earliest and latest work windows, team preferences, and upcoming time off without leaving Scheduling.
            </p>
            <div className="settings-fields-grid">
              <Field label="Staff member" hint="Choose the worker whose scheduling rules you want to edit.">
                <select value={selectedStaffId} onChange={(event) => setSelectedStaffId(event.target.value)}>
                  <option value="">Select a staff member</option>
                  {activeStaff
                    .slice()
                    .sort((left, right) => getSchedulingStaffLabel(left).localeCompare(getSchedulingStaffLabel(right)))
                    .map((item) => (
                      <option key={String(item.id ?? "")} value={String(item.id ?? "")}>
                        {getSchedulingStaffLabel(item)}
                      </option>
                    ))}
                </select>
              </Field>
            </div>

            {selectedStaff ? (
              <div className="stack">
                <div className="scheduling-overview-grid">
                  <Card title={getSchedulingStaffLabel(selectedStaff)} eyebrow="Worker">
                    <p className="card-copy">
                      Weekly availability and team rules for {String(selectedStaff.firstName ?? selectedStaff.name ?? "this staff member")}.
                    </p>
                  </Card>
                  <Card title={String(selectedStaffTimeOffItems.length)} eyebrow="Upcoming time off">
                    <p className="card-copy">Future vacation and leave blocks currently attached to this worker.</p>
                  </Card>
                  <Card
                    title={
                      selectedStaff.averageRating == null
                        ? "No ratings yet"
                        : `${Number(selectedStaff.averageRating).toFixed(2)} / 5`
                    }
                    eyebrow="Performance"
                  >
                    <p className="card-copy">
                      {Number(selectedStaff.ratingCount ?? 0)} rating{Number(selectedStaff.ratingCount ?? 0) === 1 ? "" : "s"} and{" "}
                      {Number(selectedStaff.lowRatingCount ?? 0)} low-rating follow-up{Number(selectedStaff.lowRatingCount ?? 0) === 1 ? "" : "s"}.
                    </p>
                  </Card>
                </div>

                <Card title="Weekly availability" eyebrow="Work days and hours">
                  <div className="settings-fields-grid">
                    <Field label="Available work days">
                      <div className="checkbox-grid">
                        {staffWeekdayOptions.map((option) => (
                          <label key={option.value} className="checkbox-row">
                            <input
                              type="checkbox"
                              checked={selectedStaffAvailabilityDays.includes(option.value)}
                              onChange={(event) => {
                                const next = event.target.checked
                                  ? [...selectedStaffAvailabilityDays, option.value]
                                  : selectedStaffAvailabilityDays.filter((entry) => entry !== option.value);
                                updateSchedulingStaffDraftValue("availabilityDays", Array.from(new Set(next)));
                              }}
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </Field>
                    <Field label="Earliest start time">
                      <input
                        type="time"
                        value={String(getPathValue(staffDraft, "availabilityStartTime") ?? "")}
                        onChange={(event) => updateSchedulingStaffDraftValue("availabilityStartTime", event.target.value)}
                      />
                    </Field>
                    <Field label="Latest end time">
                      <input
                        type="time"
                        value={String(getPathValue(staffDraft, "availabilityEndTime") ?? "")}
                        onChange={(event) => updateSchedulingStaffDraftValue("availabilityEndTime", event.target.value)}
                      />
                    </Field>
                  </div>
                </Card>

                <Card title="Assigned locations" eyebrow="Coverage">
                  {activeLocations.length ? (
                    <div className="stack stack--tight">
                      <div className="tag-grid">
                        {activeLocations.map((location) => {
                          const locationId = String(location.id ?? "");
                          const active = selectedStaffAssignedLocationIds.includes(locationId);
                          return (
                            <label key={locationId} className={`tag-choice ${active ? "is-active" : ""}`}>
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={(event) => {
                                  const next = event.target.checked
                                    ? [...selectedStaffAssignedLocationIds, locationId]
                                    : selectedStaffAssignedLocationIds.filter((entry) => entry !== locationId);
                                  updateSchedulingStaffDraftValue(
                                    "assignedLocationIds",
                                    Array.from(new Set(next))
                                  );
                                }}
                              />
                              <span>{String(location.name ?? "")}</span>
                            </label>
                          );
                        })}
                      </div>
                      <p className="card-copy">
                        Leave this empty to allow the worker in all active locations.
                      </p>
                    </div>
                  ) : (
                    <p className="card-copy">No active locations are available yet.</p>
                  )}
                </Card>

                <Card title="Assigned cleaning types" eyebrow="Eligibility">
                  {activeServices.length ? (
                    <div className="stack stack--tight">
                      <div className="tag-grid">
                        {activeServices.map((service) => {
                          const serviceId = String(service.id ?? "");
                          const active = selectedStaffAssignedServiceIds.includes(serviceId);
                          return (
                            <label key={serviceId} className={`tag-choice ${active ? "is-active" : ""}`}>
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={(event) => {
                                  const next = event.target.checked
                                    ? [...selectedStaffAssignedServiceIds, serviceId]
                                    : selectedStaffAssignedServiceIds.filter((entry) => entry !== serviceId);
                                  updateSchedulingStaffDraftValue(
                                    "assignedServiceIds",
                                    Array.from(new Set(next))
                                  );
                                }}
                              />
                              <span>{String(service.name ?? "")}</span>
                            </label>
                          );
                        })}
                      </div>
                      <p className="card-copy">
                        Leave this empty to allow the worker on all active cleaning types.
                      </p>
                    </div>
                  ) : (
                    <p className="card-copy">No active cleaning types are available yet.</p>
                  )}
                </Card>

                <Card title="Team rules" eyebrow="Compatibility">
                  <div className="settings-fields-grid">
                    <Field label="Preferred team member" hint="Optional same-type teammate this worker prefers to work with.">
                      {schedulingSameGroupStaffMembers.length ? (
                        <select
                          value={String(getPathValue(staffDraft, "preferredTeamMemberId") ?? "")}
                          onChange={(event) => updateSchedulingStaffDraftValue("preferredTeamMemberId", event.target.value)}
                        >
                          <option value="">No preference</option>
                          {schedulingSameGroupStaffMembers.map((item) => (
                            <option key={String(item.id ?? "")} value={String(item.id ?? "")}>
                              {getSchedulingStaffLabel(item)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="card-copy">No same-type staff members are available yet.</p>
                      )}
                    </Field>
                    <Field label="Can't work with team member" hint="Choose any same-type teammates this worker should not be paired with.">
                      {schedulingSameGroupStaffMembers.length ? (
                        <div className="tag-grid">
                          {schedulingSameGroupStaffMembers.map((item) => {
                            const staffId = String(item.id ?? "");
                            const active = selectedStaffCannotWorkWithIds.includes(staffId);
                            return (
                              <label key={staffId} className={`tag-choice ${active ? "is-active" : ""}`}>
                                <input
                                  type="checkbox"
                                  checked={active}
                                  onChange={(event) => {
                                    const next = event.target.checked
                                      ? [...selectedStaffCannotWorkWithIds, staffId]
                                      : selectedStaffCannotWorkWithIds.filter((entry) => entry !== staffId);
                                    updateSchedulingStaffDraftValue(
                                      "cannotWorkWithStaffIds",
                                      Array.from(new Set(next))
                                    );
                                  }}
                                />
                                <span>{getSchedulingStaffLabel(item)}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="card-copy">No same-type staff members are available yet.</p>
                      )}
                    </Field>
                  </div>
                </Card>

                <Card title="Time off" eyebrow="Vacation and leave">
                  {selectedStaffTimeOffItems.length ? (
                    <DataTable
                      columns={["Start", "End", "Reason", "Action"]}
                      rows={selectedStaffTimeOffItems.map((item) => [
                        String(item.startDate ?? ""),
                        String(item.endDate ?? ""),
                        String(item.reason ?? "") || "Vacation",
                        <Button
                          key={String(item.id ?? "")}
                          variant="ghost"
                          onClick={() => deleteTimeOffMutation.mutate(String(item.id ?? ""))}
                          disabled={deleteTimeOffMutation.isPending}
                        >
                          Remove
                        </Button>
                      ])}
                    />
                  ) : (
                    <p className="card-copy">No upcoming time off is recorded for this worker yet.</p>
                  )}

                  <div className="settings-fields-grid">
                    <Field label="Start date">
                      <DateInput
                        min={getLocalIsoDate()}
                        value={String(getPathValue(newTimeOffDraft, "startDate") ?? "")}
                        onChange={(event) =>
                          setNewTimeOffDraft((current) => setPathValue(current, "startDate", event.target.value))
                        }
                      />
                    </Field>
                    <Field label="End date">
                      <DateInput
                        min={String(getPathValue(newTimeOffDraft, "startDate") ?? getLocalIsoDate())}
                        value={String(getPathValue(newTimeOffDraft, "endDate") ?? "")}
                        onChange={(event) =>
                          setNewTimeOffDraft((current) => setPathValue(current, "endDate", event.target.value))
                        }
                      />
                    </Field>
                  </div>
                  <Field label="Reason">
                    <textarea
                      rows={3}
                      value={String(getPathValue(newTimeOffDraft, "reason") ?? "")}
                      onChange={(event) =>
                        setNewTimeOffDraft((current) => setPathValue(current, "reason", event.target.value))
                      }
                    />
                  </Field>
                  {timeOffSaveError ? <p className="error-text">{timeOffSaveError}</p> : null}
                  {staffTimeOffMutation.error ? (
                    <p className="error-text">{(staffTimeOffMutation.error as Error).message}</p>
                  ) : null}
                  {deleteTimeOffMutation.error ? (
                    <p className="error-text">{(deleteTimeOffMutation.error as Error).message}</p>
                  ) : null}
                  <div className="customer-directory-actions">
                    <Button onClick={() => staffTimeOffMutation.mutate()} disabled={staffTimeOffMutation.isPending}>
                      {staffTimeOffMutation.isPending ? "Saving..." : "Add time off"}
                    </Button>
                  </div>
                </Card>

                {staffSaveError ? <p className="error-text">{staffSaveError}</p> : null}
                {staffMutation.error ? <p className="error-text">{(staffMutation.error as Error).message}</p> : null}
                <div className="customer-directory-actions">
                  <Button onClick={() => staffMutation.mutate()} disabled={staffMutation.isPending}>
                    {staffMutation.isPending ? "Saving..." : "Save staff availability"}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="card-copy">Select a staff member to edit work days, team rules, and time off.</p>
            )}
          </Card>
        </>
      ) : null}

      {false && activeSection.routeKey === "scheduling-staff-availability" ? (
        <>
          <Card title="Staff Availability" eyebrow="Worker coverage">
            <p className="card-copy">
              Keep weekly work days, earliest/latest work windows, same-type team preferences, and time off together when you’re troubleshooting staffing.
            </p>
            <div className="scheduling-overview-grid">
              <Card title={String(activeStaff.length)} eyebrow="Active staff">
                <p className="card-copy">Open the Staff directory and use the Availability tab on each worker to update weekly scheduling rules.</p>
                <Link className="button button--secondary" to="/admin/settings/staff">
                  Open staff directory
                </Link>
              </Card>
              <Card title={String(upcomingTimeOff.length)} eyebrow="Upcoming time off">
                <p className="card-copy">Vacation and leave records that can remove staff from future team calculations.</p>
                <Link className="button button--secondary" to="/admin/settings/staff-time-off">
                  Open time off
                </Link>
              </Card>
            </div>
          </Card>
        </>
      ) : null}

      {false && activeSection.routeKey === "scheduling-exceptions-holidays" ? (
        <>
          <Card title="Booking Spot Exceptions" eyebrow="One-off closures and special days">
            <p className="card-copy">
              Edit holiday overrides and date-specific exceptions for the exact booking spot customers use. This keeps closures, shorter days, and one-off openings together in Scheduling.
            </p>
            <div className="settings-fields-grid">
              <Field label="Location" hint="Filter booking spots by location first.">
                <select value={exceptionLocationId} onChange={(event) => setExceptionLocationId(event.target.value)}>
                  <option value="">Select a location</option>
                  {activeLocations.map((location) => (
                    <option key={String(location.id ?? "")} value={String(location.id ?? "")}>
                      {String(location.name ?? location.id ?? "")}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Booking spot" hint="Pick the exact booking availability template you want to edit.">
                <select value={exceptionBookingSpotId} onChange={(event) => setExceptionBookingSpotId(event.target.value)}>
                  <option value="">Select a booking spot</option>
                  {exceptionBookingSpots.map((spot) => (
                    <option key={String(spot.id ?? "")} value={String(spot.id ?? "")}>
                      {String(spot.name ?? spot.id ?? "")}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {selectedExceptionBookingSpot ? (
              <div className="stack">
                <Card
                  title="Holiday overrides"
                  eyebrow="Specific dates"
                  actions={
                    <Button
                      variant="secondary"
                      onClick={() =>
                        updateExceptionDraftValue("holidayOverrides", [
                          ...exceptionHolidayOverrides,
                          { date: getLocalIsoDate(), closed: true, start: "", end: "", note: "" }
                        ])
                      }
                    >
                      Add holiday override
                    </Button>
                  }
                >
                  {exceptionHolidayOverrides.length ? (
                    <div className="stack">
                      {exceptionHolidayOverrides.map((entry, index) => (
                        <Card
                          key={`${String(entry.date ?? "")}-${index}`}
                          title={`Override ${formatDateLabel(String(entry.date ?? getLocalIsoDate()))}`}
                          eyebrow={`Holiday override #${index + 1}`}
                          actions={
                            <Button
                              variant="ghost"
                              onClick={() =>
                                updateExceptionDraftValue(
                                  "holidayOverrides",
                                  exceptionHolidayOverrides.filter((_, entryIndex) => entryIndex !== index)
                                )
                              }
                            >
                              Remove
                            </Button>
                          }
                        >
                          <div className="settings-fields-grid">
                            <Field label="Date">
                              <DateInput
                                min={getLocalIsoDate()}
                                value={String(entry.date ?? "")}
                                onChange={(event) => updateExceptionHolidayOverride(index, { date: event.target.value })}
                              />
                            </Field>
                            <Field label="Closed">
                              <label className="checkbox-row">
                                <input
                                  type="checkbox"
                                  checked={Boolean(entry.closed ?? true)}
                                  onChange={(event) =>
                                    updateExceptionHolidayOverride(index, { closed: event.target.checked })
                                  }
                                />
                                <span>
                                  {Boolean(entry.closed ?? true) ? "Closed all day" : "Open with custom hours"}
                                </span>
                              </label>
                            </Field>
                            <Field label="Earliest start">
                              <input
                                type="time"
                                value={String(entry.start ?? "")}
                                disabled={Boolean(entry.closed ?? true)}
                                onChange={(event) => updateExceptionHolidayOverride(index, { start: event.target.value })}
                              />
                            </Field>
                            <Field label="Latest start">
                              <input
                                type="time"
                                value={String(entry.end ?? "")}
                                disabled={Boolean(entry.closed ?? true)}
                                onChange={(event) => updateExceptionHolidayOverride(index, { end: event.target.value })}
                              />
                            </Field>
                          </div>
                          <Field label="Notes">
                            <textarea
                              rows={3}
                              value={String(entry.note ?? "")}
                              onChange={(event) => updateExceptionHolidayOverride(index, { note: event.target.value })}
                            />
                          </Field>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="card-copy">
                      No holiday overrides yet. Add one when this spot needs a one-off closure or custom holiday hours.
                    </p>
                  )}
                </Card>

                <Card
                  title="Date overrides"
                  eyebrow="Upcoming days"
                  actions={
                    <div className="customer-directory-actions">
                      <Button variant={exceptionDateRangeDays === 7 ? "primary" : "secondary"} onClick={() => setExceptionDateRangeDays(7)}>Next 7 days</Button>
                      <Button variant={exceptionDateRangeDays === 14 ? "primary" : "secondary"} onClick={() => setExceptionDateRangeDays(14)}>Next 14 days</Button>
                      <Button variant={exceptionDateRangeDays === 30 ? "primary" : "secondary"} onClick={() => setExceptionDateRangeDays(30)}>Next 30 days</Button>
                    </div>
                  }
                >
                  <div className="settings-fields-grid">
                    <Field label="Select a date to override" hint="Jump to any future day, then customize or close it.">
                      <DateInput
                        min={getLocalIsoDate()}
                        value={exceptionSelectedDate}
                        onChange={(event) => setExceptionSelectedDate(event.target.value || getLocalIsoDate())}
                      />
                    </Field>
                  </div>
                  <DataTable
                    columns={["Date", "Hours", "Max spots", "Status", "Action"]}
                    rows={exceptionUpcomingDateRows.map((entry) => [
                      `${entry.label} (${entry.weekdayLabel})`,
                      entry.closed ? "Closed" : `${entry.start || "--:--"} - ${entry.end || "--:--"}`,
                      String(entry.maxJobs),
                      <Badge tone={entry.hasOverride ? "warning" : "neutral"}>
                        {entry.hasOverride ? "Override" : "Template"}
                      </Badge>,
                      <Button key={entry.date} variant="ghost" onClick={() => setExceptionSelectedDate(entry.date)}>
                        Edit
                      </Button>
                    ])}
                  />
                  <Card
                    title={`Override ${formatDateLabel(exceptionSelectedDate)}`}
                    eyebrow="Date editor"
                    actions={
                      exceptionDateOverrides.some((entry) => entry.date === exceptionSelectedDate) ? (
                        <Button variant="ghost" onClick={() => clearExceptionDateOverride(exceptionSelectedDate)}>
                          Clear override
                        </Button>
                      ) : null
                    }
                  >
                    <div className="settings-fields-grid">
                      <Field label="Closed">
                        <label className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={Boolean(selectedExceptionDateOverride.closed)}
                            onChange={(event) =>
                              upsertExceptionDateOverride(exceptionSelectedDate, { closed: event.target.checked })
                            }
                          />
                          <span>
                            {Boolean(selectedExceptionDateOverride.closed)
                              ? "Closed all day"
                              : "Open with custom settings"}
                          </span>
                        </label>
                      </Field>
                      <Field label="Earliest start">
                        <input
                          type="time"
                          value={String(selectedExceptionDateOverride.start ?? "")}
                          disabled={Boolean(selectedExceptionDateOverride.closed)}
                          onChange={(event) =>
                            upsertExceptionDateOverride(exceptionSelectedDate, { start: event.target.value })
                          }
                        />
                      </Field>
                      <Field label="Latest start">
                        <input
                          type="time"
                          value={String(selectedExceptionDateOverride.end ?? "")}
                          disabled={Boolean(selectedExceptionDateOverride.closed)}
                          onChange={(event) =>
                            upsertExceptionDateOverride(exceptionSelectedDate, { end: event.target.value })
                          }
                        />
                      </Field>
                      <Field label="Max spots for this date">
                        <input
                          type="number"
                          min="1"
                          value={String(selectedExceptionDateOverride.maxJobs ?? "")}
                          disabled={Boolean(selectedExceptionDateOverride.closed)}
                          onChange={(event) =>
                            upsertExceptionDateOverride(exceptionSelectedDate, {
                              maxJobs: event.target.value === "" ? "" : Number(event.target.value)
                            })
                          }
                        />
                      </Field>
                    </div>
                    <Field label="Notes">
                      <textarea
                        rows={3}
                        value={String(selectedExceptionDateOverride.note ?? "")}
                        onChange={(event) =>
                          upsertExceptionDateOverride(exceptionSelectedDate, { note: event.target.value })
                        }
                      />
                    </Field>
                  </Card>
                </Card>

                {exceptionSaveError ? <p className="error-text">{exceptionSaveError}</p> : null}
                {exceptionsMutation.error ? (
                  <p className="error-text">{(exceptionsMutation.error as Error).message}</p>
                ) : null}
                <div className="customer-directory-actions">
                  <Button onClick={() => exceptionsMutation.mutate()} disabled={exceptionsMutation.isPending}>
                    {exceptionsMutation.isPending ? "Saving..." : "Save booking spot exceptions"}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="card-copy">Select a location and booking spot to edit closures and date-specific exceptions.</p>
            )}
          </Card>

          <Card title="Calendar Profile Blackout Dates" eyebrow="Reusable blackout rules">
            <p className="card-copy">
              Calendar profiles still control reusable blackout dates and booking-window limits. Edit blackout dates here without leaving Scheduling.
            </p>
            <div className="settings-fields-grid">
              <Field label="Calendar profile" hint="Pick the reusable profile you want to block dates on.">
                <select
                  value={selectedCalendarProfileId}
                  onChange={(event) => setSelectedCalendarProfileId(event.target.value)}
                >
                  <option value="">Select a calendar profile</option>
                  {activeCalendarProfiles.map((profile) => (
                    <option key={String(profile.id ?? "")} value={String(profile.id ?? "")}>
                      {String(profile.name ?? profile.id ?? "")}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {selectedCalendarProfile ? (
              <div className="stack">
                <DataTable
                  columns={["Blackout date", "Action"]}
                  rows={calendarProfileBlackoutDates.map((date, index) => [
                    <DateInput
                      key={`blackout-${index}`}
                      min={getLocalIsoDate()}
                      value={date}
                      onChange={(event) => updateCalendarProfileBlackoutDate(index, event.target.value)}
                    />,
                    <Button key={`remove-${index}`} variant="ghost" onClick={() => removeCalendarProfileBlackoutDate(index)}>
                      Remove
                    </Button>
                  ])}
                />
                {!calendarProfileBlackoutDates.length ? (
                  <p className="card-copy">
                    No blackout dates yet. Add dates here to close this calendar profile without editing raw JSON.
                  </p>
                ) : null}
                <div className="customer-directory-actions">
                  <Button variant="secondary" onClick={addCalendarProfileBlackoutDate}>
                    Add blackout date
                  </Button>
                  <Button onClick={() => calendarProfileMutation.mutate()} disabled={calendarProfileMutation.isPending}>
                    {calendarProfileMutation.isPending ? "Saving..." : "Save blackout dates"}
                  </Button>
                </div>
                {calendarProfileSaveError ? <p className="error-text">{calendarProfileSaveError}</p> : null}
                {calendarProfileMutation.error ? (
                  <p className="error-text">{(calendarProfileMutation.error as Error).message}</p>
                ) : null}
              </div>
            ) : (
              <p className="card-copy">Select a calendar profile to edit its blackout dates here.</p>
            )}
          </Card>
        </>
      ) : null}

      {activeSection.routeKey === "scheduling-exceptions-holidays" ? (
        <>
          <Card title="Exceptions & Holidays" eyebrow="One-off changes">
            <p className="card-copy">
              Holiday overrides and date overrides still live on each booking spot today, while reusable blackout windows live in calendar profiles. This workspace brings those tools together so you don’t have to remember where each exception lives.
            </p>
            <div className="scheduling-overview-grid">
              <Card title={String(upcomingClosures)} eyebrow="Upcoming closures">
                <p className="card-copy">Review holiday overrides and date overrides inside each booking spot.</p>
                <Link className="button button--secondary" to="/admin/settings/booking-spots">
                  Open booking spot exceptions
                </Link>
              </Card>
              <Card title={String(activeCalendarProfiles.length)} eyebrow="Profile blackout rules">
                <p className="card-copy">Calendar profiles hold reusable blackout dates and booking-window rules.</p>
                <Link className="button button--secondary" to="/admin/settings/calendar-profiles">
                  Open calendar profiles
                </Link>
              </Card>
            </div>
          </Card>
        </>
      ) : null}

      {activeSection.routeKey === "scheduling-diagnostics" ? (
        <>
          <Card title="Diagnostics" eyebrow="Why a date is blocked">
            <p className="card-copy">
              Pick a location, booking spot, service, and date to see exactly why time slots are available or blocked.
            </p>
            <div className="settings-fields-grid">
              <Field label="Location" hint="Choose the location you want to troubleshoot first.">
                <select value={diagnosticLocationId} onChange={(event) => setDiagnosticLocationId(event.target.value)}>
                  <option value="">Select a location</option>
                  {activeLocations.map((location) => (
                    <option key={String(location.id ?? "")} value={String(location.id ?? "")}>
                      {String(location.name ?? location.id ?? "")}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Booking spot" hint="Use the exact booking spot that should be opening times.">
                <select value={diagnosticBookingSpotId} onChange={(event) => setDiagnosticBookingSpotId(event.target.value)}>
                  <option value="">Select a booking spot</option>
                  {diagnosticBookingSpots.map((spot) => (
                    <option key={String(spot.id ?? "")} value={String(spot.id ?? "")}>
                      {String(spot.name ?? spot.id ?? "")}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Service" hint="Pick the service customers are trying to book.">
                <select value={diagnosticServiceId} onChange={(event) => setDiagnosticServiceId(event.target.value)}>
                  <option value="">Select a service</option>
                  {diagnosticLinkedServiceIds.map((serviceId) => {
                    const service = activeServices.find((entry) => String(entry.id ?? "") === serviceId);
                    return (
                      <option key={serviceId} value={serviceId}>
                        {String(service?.name ?? serviceId)}
                      </option>
                    );
                  })}
                </select>
              </Field>
              <Field label="Date" hint="Preview how this exact day will be staffed and filtered.">
                <DateInput min={getLocalIsoDate()} value={diagnosticDate} onChange={(event) => setDiagnosticDate(event.target.value || getLocalIsoDate())} />
              </Field>
            </div>

            {selectedDiagnosticBookingSpot ? (
              <div className="scheduling-overview-grid">
                <Card title={String(selectedDiagnosticBookingSpot.name ?? "") || "Selected spot"} eyebrow="Booking spot">
                  <p className="card-copy">Location: {String(activeLocations.find((location) => String(location.id ?? "") === String(selectedDiagnosticBookingSpot.locationId ?? ""))?.name ?? selectedDiagnosticBookingSpot.locationId ?? "")}</p>
                </Card>
                <Card title={String(diagnosticLinkedServiceIds.length)} eyebrow="Linked services">
                  <p className="card-copy">Only services linked to this booking spot can be diagnosed here.</p>
                </Card>
              </div>
            ) : null}

            {!diagnosticLocationId ? (
              <p className="card-copy">Select a location to begin troubleshooting availability.</p>
            ) : !diagnosticBookingSpots.length ? (
              <p className="card-copy">No active booking spots are assigned to this location yet.</p>
            ) : !selectedDiagnosticBookingSpot ? (
              <p className="card-copy">Select a booking spot to continue.</p>
            ) : !diagnosticLinkedServiceIds.length ? (
              <p className="card-copy">This booking spot does not have any linked services yet.</p>
            ) : diagnosticsQuery.isLoading ? (
              <p className="card-copy">Calculating team-based availability...</p>
            ) : diagnosticsQuery.error ? (
              <p className="error-text">{(diagnosticsQuery.error as Error).message}</p>
            ) : diagnosticsQuery.data ? (
              <AvailabilityDiagnosticsResults data={diagnosticsQuery.data} />
            ) : (
              <p className="card-copy">Select a service and date to preview team composition and availability diagnostics.</p>
            )}
          </Card>
        </>
      ) : null}
    </OpsPageShell>
  );
}

function WaitlistSettingsPage() {
  const queryClient = useQueryClient();
  const [waitlistStatusFilter, setWaitlistStatusFilter] = useState("active");
  const waitlistRequests = useQuery({
    queryKey: ["waitlist", waitlistStatusFilter],
    queryFn: () => apiFetch<{ items: AdminWaitlistItem[] }>(`/api/waitlist?status=${waitlistStatusFilter}`)
  });
  const closeWaitlistMutation = useMutation({
    mutationFn: (requestId: string) => apiFetch(`/api/waitlist/${requestId}/close`, { method: "POST" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["waitlist"] });
    }
  });

  return (
    <OpsPageShell>
      <OpsHeader
        eyebrow="Settings operations"
        title="Waitlist"
        description="Control public waitlist access, alert channels, default message templates, and current requests."
      />
      <SettingsSectionMetrics section="waitlist" isSingleton />
      <OpsMetricGrid>
        <OpsMetricCard
          label="Displayed requests"
          value={waitlistRequests.isLoading ? "..." : waitlistRequests.data?.items.length ?? 0}
          detail={`Current filter: ${waitlistStatusFilter}`}
          icon={CalendarClock}
          tone="blue"
        />
        <OpsMetricCard
          label="Active filter"
          value={waitlistStatusFilter}
          detail="Use the request table to change the filter."
          icon={SlidersHorizontal}
          tone="slate"
        />
        <OpsMetricCard label="Customer alerts" value="Ready" icon={ShieldCheck} tone="emerald" />
        <OpsMetricCard label="Follow-up queue" value={closeWaitlistMutation.isPending ? "Updating" : "Idle"} icon={Clock3} tone="violet" />
      </OpsMetricGrid>
      <SingletonSettingsEditor section="waitlist" />

      <Card title="Waitlist requests" eyebrow="Availability watch">
        <div className="stack">
          <div className="customer-directory-actions">
            <Field label="Status">
              <select value={waitlistStatusFilter} onChange={(event) => setWaitlistStatusFilter(event.target.value)}>
                {["active", "fulfilled", "expired", "closed", "all"].map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {waitlistRequests.isLoading ? <p className="card-copy">Loading waitlist requests...</p> : null}
          {waitlistRequests.error ? <p className="error-text">{(waitlistRequests.error as Error).message}</p> : null}
          {!waitlistRequests.isLoading && !waitlistRequests.error ? (
            waitlistRequests.data?.items.length ? (
              <div className="table-wrap">
                <table className="table customer-table booking-table">
                  <thead>
                    <tr>
                      <th>Requested date</th>
                      <th>Customer</th>
                      <th>Service</th>
                      <th>Contact</th>
                      <th>Status</th>
                      <th>Last alert</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlistRequests.data.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <strong>{formatDateLabel(item.requestedDate)}</strong>
                          <span>{formatWaitlistPreferredWindowLabel(item.preferredWindow)}</span>
                        </td>
                        <td>{[item.customerFirstName, item.customerLastName].filter(Boolean).join(" ").trim() || "Customer"}</td>
                        <td>
                          <strong>{item.serviceName}</strong>
                          <span>{item.locationName}</span>
                        </td>
                        <td>
                          <div className="stack" style={{ gap: "0.25rem" }}>
                            <span>{item.customerEmail || "No email"}</span>
                            <span>{item.customerPhone || "No phone"}</span>
                          </div>
                        </td>
                        <td>
                          <Badge
                            tone={
                              item.status === "active"
                                ? "info"
                                : item.status === "fulfilled"
                                  ? "success"
                                  : item.status === "expired"
                                    ? "warning"
                                    : "neutral"
                            }
                          >
                            {item.status}
                          </Badge>
                        </td>
                        <td>{item.lastNotifiedAt ? formatDateTimeLabel(item.lastNotifiedAt) : "Not yet"}</td>
                        <td>
                          {item.status === "active" ? (
                            <Button
                              variant="secondary"
                              onClick={() => closeWaitlistMutation.mutate(item.id)}
                              disabled={closeWaitlistMutation.isPending}
                            >
                              {closeWaitlistMutation.isPending && closeWaitlistMutation.variables === item.id
                                ? "Closing..."
                                : "Close"}
                            </Button>
                          ) : item.fulfilledPublicReference ? (
                            <span>{item.fulfilledPublicReference}</span>
                          ) : (
                            <span>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="card-copy">No waitlist requests match this filter yet.</p>
            )
          ) : null}
        </div>
      </Card>
    </OpsPageShell>
  );
}

function CustomerPortalSettingsPage() {
  const [copyFeedback, setCopyFeedback] = useState("");
  const generalSettings = useQuery({
    queryKey: ["settings", "general"],
    queryFn: () => apiFetch<SettingsValue>("/api/settings/general")
  });
  const configuredBaseUrl = String(generalSettings.data?.customerFacingBaseUrl ?? "").trim();
  const currentOrigin =
    typeof window === "undefined" ? "https://accessmaids.bookingseconds.com" : window.location.origin;
  const portalBaseUrl = (configuredBaseUrl || currentOrigin).replace(/\/+$/, "");
  const portalLoginUrl = `${portalBaseUrl}/account/login`;
  const plainLinkSnippet = `<a href="${portalLoginUrl}">Customer Login</a>`;
  const buttonLinkSnippet = `<a class="button" href="${portalLoginUrl}">Customer Login</a>`;

  const copyText = async (value: string, label: string) => {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      setCopyFeedback(`${label}: ${value}`);
      return;
    }

    await navigator.clipboard.writeText(value);
    setCopyFeedback(`${label} copied.`);
  };

  return (
    <OpsPageShell>
      <OpsHeader
        eyebrow="Settings operations"
        title="Customer Portal"
        description="Keep the customer-facing portal link and website button snippets in one easy admin place."
        actions={
          <Button variant="secondary" onClick={() => window.open(portalLoginUrl, "_blank", "noopener,noreferrer")}>
            Open portal login
          </Button>
        }
      />
      <OpsMetricGrid>
        <OpsMetricCard
          label="Portal URL"
          value={configuredBaseUrl ? "Configured" : "Current site"}
          detail={portalLoginUrl}
          icon={ShieldCheck}
          tone="emerald"
        />
        <OpsMetricCard label="Website snippets" value={2} detail="Plain link and button-style HTML." icon={ClipboardList} tone="blue" />
        <OpsMetricCard label="Magic-link login" value="Enabled" icon={UserCheck} tone="violet" />
        <OpsMetricCard
          label="Settings source"
          value={generalSettings.isLoading ? "Loading" : "Ready"}
          detail="Uses the existing General settings profile."
          icon={SettingsIcon}
          tone="slate"
        />
      </OpsMetricGrid>

      <Card title="Customer portal link" eyebrow="Website button">
        <div className="stack">
          <p className="card-copy">
            Add this URL to AccessMaids.com, your email signature, or any customer help page. Customers enter their email,
            receive a secure magic link, and land in their portal.
          </p>
          <Field label="Portal login URL" hint="This is the only link the external website needs.">
            <input value={portalLoginUrl} readOnly />
          </Field>
          <div className="customer-directory-actions">
            <Button variant="secondary" onClick={() => copyText(portalLoginUrl, "Portal login URL")}>
              Copy login URL
            </Button>
            <Button variant="secondary" onClick={() => copyText(plainLinkSnippet, "HTML link")}>
              Copy HTML link
            </Button>
          </div>
          {copyFeedback ? <p className="card-copy">{copyFeedback}</p> : null}
        </div>
      </Card>

      <Card title="Website snippets" eyebrow="No embed needed">
        <div className="stack">
          <Field label="Plain HTML link">
            <textarea rows={2} value={plainLinkSnippet} readOnly />
          </Field>
          <Field label="Button-style HTML link">
            <textarea rows={2} value={buttonLinkSnippet} readOnly />
          </Field>
          <div className="booking-meta-grid">
            <div className="booking-meta-card">
              <p className="eyebrow">Recommended labels</p>
              <strong>Customer Login</strong>
              <p>Also good: My Account, Manage My Cleanings.</p>
            </div>
            <div className="booking-meta-card">
              <p className="eyebrow">Why link instead of embed?</p>
              <strong>Safer and simpler</strong>
              <p>The portal page already handles sessions, Turnstile verification, and magic-link email delivery.</p>
            </div>
          </div>
        </div>
      </Card>

      <Card title="Customer-specific access" eyebrow="Where to support customers">
        <p className="card-copy">
          Open a customer profile and use the new Customer portal card to send that customer a fresh sign-in link, check
          invite status, and see their last portal login.
        </p>
      </Card>
    </OpsPageShell>
  );
}

function PricebookSettingsPage() {
  const tabs = [
    { label: "Services", to: "/admin/settings/services" },
    { label: "Service Scenarios", to: "/admin/settings/service-scenarios" },
    { label: "Extras", to: "/admin/settings/extras" },
    { label: "Recurring Discounts", to: "/admin/settings/frequency-rules" },
    { label: "Promo Codes", to: "/admin/settings/promotions" },
    { label: "Advanced Rules", to: "/admin/settings/pricebook", active: true }
  ];

  return (
    <OpsPageShell>
      <OpsHeader
        eyebrow="Settings operations"
        title="Pricebook"
        description="Manage the centralized pricing catalog used by quotes, estimates, booking edits, and duration checks."
      />
      <OpsMetricGrid>
        <OpsMetricCard label="Catalog sections" value={tabs.length} detail="Services, scenarios, extras, discounts, promos, and rules." icon={WalletCards} tone="blue" />
        <OpsMetricCard label="Quote engine" value="Centralized" detail="Public estimates and booking edits use one Pricebook path." icon={BadgeDollarSign} tone="emerald" />
        <OpsMetricCard label="Advanced rules" value="Editable" detail="Typed rules remain in the existing settings JSON editor." icon={SlidersHorizontal} tone="violet" />
        <OpsMetricCard label="Live changes" value="Immediate" detail="New quotes use saved Pricebook changes right away." icon={ShieldCheck} tone="slate" />
      </OpsMetricGrid>
      <div className="customer-tab-bar" aria-label="Pricebook sections">
        {tabs.map((tab) => (
          <Link key={tab.to} className={`customer-tab-button ${tab.active ? "is-active" : ""}`} to={tab.to}>
            {tab.label}
          </Link>
        ))}
      </div>
      <SingletonSettingsEditor section="pricebook" />
    </OpsPageShell>
  );
}

export function SettingsSectionPage() {
  const { section = "general" } = useParams();
  if (section === "scheduling") {
    return <SchedulingSettingsPage section="scheduling-overview" />;
  }
  if (section.startsWith("scheduling-")) {
    return <SchedulingSettingsPage section={section as SchedulingRouteKey} />;
  }
  if (section === "waitlist") {
    return <WaitlistSettingsPage />;
  }
  if (section === "customer-portal") {
    return <CustomerPortalSettingsPage />;
  }
  if (section === "pricebook") {
    return <PricebookSettingsPage />;
  }
  const isSingleton = section in singletonSectionConfigs;
  const label = allSections.find((item) => item.routeKey === section)?.label ?? "Settings";
  const sectionActions =
    section === "booking-form" ? (
      <Button
        variant="secondary"
        onClick={() => window.open("/book", "_blank", "noopener,noreferrer")}
      >
        Preview form
      </Button>
    ) : undefined;

  return (
    <OpsPageShell>
      <OpsHeader
        eyebrow={isStaffSettingsRoute(section) ? "Staff operations" : "Settings operations"}
        title={getSettingsSectionTitle(section, label)}
        description={getSettingsSectionDescription(section, isSingleton)}
        actions={sectionActions}
      />
      <SettingsSectionMetrics section={section} isSingleton={isSingleton} />
      <div className="stack">
        {isSingleton ? <SingletonSettingsEditor section={section} /> : <RelationalSettingsEditor section={section} />}
      </div>
    </OpsPageShell>
  );
}
